/**
 * TEXTOS SEPARADOS DO CÓDIGO (§20.5).
 *
 * Português e inglês. O dicionário começa pelo ESQUELETO — navegação, ações
 * comuns, estados vazios —, que é o que aparece em toda tela: traduzir uma tela
 * inteira antes de traduzir o menu deixaria a pessoa em inglês dentro de um
 * sistema em português, que é pior do que tudo em português.
 *
 * DUAS DECISÕES
 *
 * 1. **Chave sem tradução não vai para a tela.** Ela cai no português — mesma
 *    disciplina do §20.3: `nav.empresas` na tela é um defeito visível, mas é um
 *    defeito que a pessoa lê antes de nós. O teste exige que os dois idiomas
 *    tenham exatamente as mesmas chaves, para que a reserva nunca precise ser
 *    usada.
 * 2. **O idioma ativo mora no módulo**, e não num contexto de React, pelo mesmo
 *    motivo da formatação: estas funções são chamadas de tudo, inclusive de
 *    código que não é componente.
 */

import { UX_PT } from './i18n.ux';
import { PV_PT } from './i18n.pv';
import { PRP_PT } from './i18n.prp';

export type IdiomaDaTela = 'pt-BR' | 'en';

export const PT = {
  // CERES-UX-001: textos da nova experiência, em módulo próprio.
  ...UX_PT,
  // CERES-PRE-004 a 006: fechamento da Pré-Venda, em módulo próprio.
  ...PV_PT,
  ...PRP_PT,
  'nav.inicio': 'Início',
  'playbooks.pedirRotulo': 'Revisar playbooks',
  'playbooks.pedirPedido':
    'Revise nossos playbooks e me diga o que está vencido, incompleto ou sem prova.',
  'oportunidades.pedirRotulo': 'Prioridades de hoje',
  'oportunidades.pedirPedido': 'Quais são as minhas prioridades comerciais de hoje, e por quê?',
  'prontidao.titulo': 'Prontidão da implantação',
  'prontidao.doObrigatorio': 'do que é obrigatório para operar',
  'prontidao.proximoPasso': 'Próximo passo',
  'prontidao.ir': 'Ir para lá',
  'prontidao.resolver': 'resolver',
  'prontidao.obrigatorio': 'Obrigatório para começar',
  'prontidao.recomendado': 'Recomendado para operar bem',
  'prontidao.avancado': 'Avançado',
  'prontidao.externo': 'Depende de ativação externa',
  'nav.empresas': 'Empresas',
  'nav.contatos': 'Contatos',
  'nav.oportunidades': 'Oportunidades',
  'nav.pipeline': 'Pipeline',
  'funil.metricas': 'Desempenho por funil',
  'funil.emAberto': 'Em aberto',
  'funil.taxaDeGanho': 'Taxa de ganho',
  'funil.cicloMediano': 'Ciclo mediano',
  'funil.encerradas': 'Encerradas',
  'funil.ganhas': 'ganhas',
  'funil.perdidas': 'perdidas',
  'funil.semDados': 'sem dados ainda',
  'funil.dias': 'dias',
  'funil.gargalo': 'Gargalo',
  'funil.foraDoPrazo': 'fora do prazo',
  'funil.atrasadas': 'atrasada(s)',
  'nav.atividades': 'Atividades',
  'nav.leads': 'Leads',
  'nav.propostas': 'Propostas',
  'nav.contratos': 'Contratos',
  'contrato.clausulaEditada': 'negociada',
  'contrato.editarClausula': 'Editar',
  'contrato.historicoClausulas': 'Histórico das cláusulas',
  'contrato.verHistorico': 'Ver histórico',
  'contrato.ocultarHistorico': 'Ocultar',
  'contrato.semEdicoes': 'Nenhuma cláusula foi alterada: o texto é o do modelo.',
  'contrato.alterada': 'alterada por',
  'contrato.restaurada': 'restaurada ao modelo por',
  'contrato.verTextoAnterior': 'ver o texto anterior',
  'contrato.textoDaClausula': 'Texto da cláusula',
  'contrato.marcadoresAviso':
    'Mantenha os marcadores {{...}}: eles são substituídos pelos dados do contrato. Apagá-los troca um campo por um valor fixo.',
  'contrato.motivoDaMudanca': 'Motivo da mudança',
  'contrato.motivoDica':
    'Obrigatório. Cláusula alterada sem justificativa é a que ninguém consegue defender depois.',
  'contrato.voltarAoModelo': 'Voltar ao texto do modelo',
  'contrato.erroClausula': 'Não consegui salvar a cláusula.',
  'nav.cadencias': 'Cadências',
  'nav.caixaDeEntrada': 'Caixa de entrada',
  'nav.icp': 'Cliente ideal',
  'importacao.titulo': 'Importar base',
  'importacao.subtitulo':
    'Trazer empresas de uma planilha — com prévia antes de gravar, e desfazer depois.',
  'importacao.passo1': '1. Confira antes de gravar',
  'importacao.passo1Ajuda':
    'A prévia não grava nada. Ela existe para você ver "vai criar 1.200 empresas" antes de confirmar — que é a única defesa contra a coluna trocada.',
  'importacao.arquivo': 'Arquivo CSV',
  'importacao.analisar': 'Analisar o arquivo',
  'importacao.colarNoLugar': 'Ou cole o conteúdo aqui',
  'importacao.erroAnalisar': 'Não foi possível analisar o arquivo.',
  'importacao.novos': 'novos',
  'importacao.duplicados': 'já existem',
  'importacao.possiveis': 'parecidos com o que já existe',
  'importacao.erros': 'com erro',
  'importacao.criados': 'criados',
  'importacao.novo': 'novo',
  'importacao.duplicado': 'já existe',
  'importacao.possivelDuplicado': 'parecido com um já existente',
  'importacao.erro': 'erro',
  'importacao.colunasIgnoradas': 'Colunas que o CRM não reconheceu:',
  'importacao.colunasIgnoradasAjuda':
    'Elas serão ignoradas. Renomeie no arquivo se esses dados devem entrar.',
  'importacao.passo2': '2. Gravar',
  'importacao.passo2Ajuda':
    'Só as linhas novas são criadas. As que já existem e as com erro ficam de fora, e você continua com a planilha para corrigir.',
  'importacao.aplicar': 'Criar as empresas novas',
  'importacao.aplicada': 'Importação concluída. O lote aparece no histórico, e dá para desfazer.',
  'importacao.erroAplicar': 'Não foi possível importar.',
  'importacao.historico': 'Importações anteriores',
  'importacao.desfazer': 'Desfazer o lote',
  'importacao.desfazerBotao': 'Desfazer',
  'importacao.desfazerDescricao':
    'Apaga SÓ o que este lote criou, e só o que continua intocado: empresa que já ganhou oportunidade, contato ou atividade não é apagada — ela deixou de ser uma linha de planilha e virou trabalho de alguém.',
  'importacao.porQueDesfazer': 'Por que está desfazendo',
  'importacao.desfeita': 'Lote desfeito. O que já tinha virado trabalho foi preservado.',
  'importacao.desfeitoEm': 'Desfeito em',
  'importacao.erroDesfazer': 'Não foi possível desfazer.',
  'importacao.vazio': 'Nenhuma importação ainda',
  'importacao.vazioOQueE':
    'A importação traz empresas de uma planilha em dois passos: primeiro a prévia, que não grava nada e diz o que vai acontecer; depois a gravação, que cria só o que é novo.',
  'importacao.vazioExemplo':
    'O arquivo precisa de uma coluna de razão social. CNPJ, cidade, UF, telefone e e-mail entram se estiverem lá — e a prévia avisa quais colunas ficaram de fora.',
  'icp.titulo': 'Perfil de cliente ideal',
  'icp.subtitulo': 'Quem vale a pena procurar — e por quê, empresa por empresa.',
  'icp.abaLista': 'Por onde começar',
  'icp.abaPerfis': 'Perfis',
  'icp.abaVersoes': 'Versões',
  'icp.versoesVazio':
    'Nenhuma versão ainda. Crie um perfil global na aba Perfis para começar o ciclo de revisão, aprovação e publicação.',
  'icp.publicar': 'Publicar perfil',
  'icp.publicarBotao': 'Publicar',
  'icp.publicarDescricao':
    'Os critérios definem o que faz uma empresa valer a pena, e o peso de cada um. Publicar cria uma versão nova; a lista passa a ser calculada por ela.',
  'icp.chave': 'Chave',
  'icp.nome': 'Nome',
  'icp.notaMinimaCampo': 'Nota mínima',
  'icp.criterios': 'Critérios',
  'icp.criterioChave': 'Campo',
  'icp.criterioRotulo': 'Como se lê',
  'icp.criterioTipo': 'Tipo',
  'icp.criterioPeso': 'Peso',
  'icp.criterioValores': 'Valores desejados',
  'icp.criterioValoresAjuda': 'Valores desejados, separados por vírgula (opcional)',
  'icp.maisUmCriterio': 'Mais um critério',
  'icp.versaoAviso':
    'A lista é recalculada na hora com o perfil vigente: nada fica guardado envelhecendo.',
  'icp.publicado': 'Perfil publicado. A lista passa a usar esta versão.',
  'icp.erroPublicar': 'Não foi possível publicar o perfil.',
  'icp.notaMinima': 'Aderente a partir de',
  'icp.peso': 'peso',
  'icp.exclui': 'Exclui:',
  'icp.vigente': 'vigente',
  'icp.substituido': 'substituído',
  'icp.aderentesAcima': 'Aderente é quem passa de',
  'icp.excluidas': 'excluídas por critério (fora da lista)',
  'icp.semNota': 'sem nota por falta de dado — é a fila de enriquecimento.',
  'icp.faltaDado': 'Faltou dado para:',
  'icp.porQue': 'Por que esta nota',
  'icp.fecharConta': 'Fechar',
  'icp.semPerfil': 'Nenhum perfil de cliente ideal publicado',
  'icp.vazioOQueE':
    'O perfil diz o que faz uma empresa valer a pena: setor, porte, maturidade, o que já usa. Cada critério tem peso, e a nota sai da soma do que PÔDE ser avaliado.',
  'icp.vazioExemplo':
    'Critério que não pôde ser avaliado não conta como zero: contar puniria a empresa pela lacuna do nosso cadastro — e a mais promissora costuma ser justamente a que ainda não foi enriquecida.',
  'icp.pedirRotulo': 'Perguntar ao assistente por onde começar',
  'icp.pedirPedido': 'Quais empresas da base são as mais aderentes ao nosso cliente ideal?',
  'nav.catalogo': 'Catálogo',
  'catalogo.titulo': 'Catálogo comercial',
  'catalogo.subtitulo':
    'O que a sua empresa vende: produtos, ofertas, preço oficial e para quem cada coisa é.',
  'catalogo.novaOferta': 'Nova oferta',
  'catalogo.vazioTitulo': 'Cadastre o que você vende',
  'catalogo.vazioOQueE':
    'O catálogo é a fonte única do portfólio: proposta de valor, modelo de receita e preço oficial. Playbooks, propostas e o assistente leem daqui — o que estiver escrito em outro lugar é cópia, e cópia envelhece.',
  'catalogo.vazioExemplo':
    'Ex.: o produto “QA Outsourcing”, com a oferta “squad dedicado” a R$ 49.000 por mês, desconto máximo de 10%.',
  'catalogo.vazioAcao': 'Cadastrar o primeiro produto',
  'catalogo.vazioPedidoRotulo': 'Montar o catálogo com o assistente',
  'catalogo.vazioPedido':
    'Quero cadastrar nosso portfólio comercial. Me pergunte o que você precisa saber sobre os produtos, ofertas e preços.',
  'catalogo.areas': 'Áreas do catálogo',
  'catalogo.abaPrecos': 'Tabela de preços',
  'precos.explicacao':
    'É esta tabela que precifica os itens de uma proposta — e é a versão dela que fica gravada na memória de cálculo do documento enviado.',
  'precos.publicar': 'Publicar tabela',
  'precos.publicarBotao': 'Publicar',
  'precos.publicarDescricao':
    'Publicar cria uma VERSÃO nova. A anterior fica: as propostas já emitidas apontam para a versão com que foram feitas, e conferir a proposta de março precisa dar o preço de março.',
  'precos.chave': 'Chave',
  'precos.chaveAjuda': 'Mesma chave = nova versão da mesma tabela. Chave nova = outra tabela.',
  'precos.nome': 'Nome',
  'precos.itens': 'Preços por oferta',
  'precos.oferta': 'Oferta',
  'precos.escolhaOferta': 'Escolha a oferta…',
  'precos.precoUnitario': 'Preço unitário',
  'precos.descontoMaximo': 'Desconto máx. %',
  'precos.maisUmItem': 'Mais um item',
  'precos.versionamentoAviso': 'Nada do que já foi proposto muda com esta publicação.',
  'precos.publicada': 'Tabela publicada. As propostas novas passam a usar esta versão.',
  'precos.erroPublicar': 'Não foi possível publicar a tabela.',
  'precos.vigenteDe': 'vigente desde',
  'precos.substituidaEm': 'substituída em',
  'precos.tetoDesconto': 'desconto sem alçada até',
  'precos.vigente': 'vigente',
  'precos.substituida': 'substituída',
  'precos.descontoAte': 'desconto até',
  'precos.custo': 'custo',
  'precos.simular': 'Simular um preço',
  'precos.simularDescricao':
    'É a mesma conta que a proposta usa: simular aqui e propor depois dá o mesmo número, porque é o mesmo caminho.',
  'precos.quantidade': 'Quantidade',
  'precos.desconto': 'Desconto %',
  'precos.calcular': 'Calcular',
  'precos.total': 'Total:',
  'precos.erroSimular': 'Não foi possível simular.',
  'precos.vazio': 'Nenhuma tabela de preços publicada',
  'precos.vazioOQueE':
    'A tabela define o preço de cada oferta, o desconto máximo sem precisar de alçada e a margem mínima. Ela é versionada: publicar uma nova não muda o que já foi proposto.',
  'precos.vazioExemplo':
    'Sem tabela, a proposta usa o valor estimado da oportunidade — e a memória de cálculo fica sem a linha que explica de onde veio o número.',
  'catalogo.abaOfertas': 'Ofertas',
  'catalogo.abaProdutos': 'Produtos',
  'catalogo.abaEstrutura': 'Unidades e personas',
  'catalogo.buscar': 'Buscar oferta ou produto',
  'catalogo.pedirRotulo': 'Perguntar ao assistente sobre o catálogo',
  'catalogo.pedirPedido':
    'Quais ofertas nós temos cadastradas e qual é o preço oficial de cada uma?',
  'catalogo.catalogoComum': 'catálogo comum',
  'catalogo.catalogoComumAjuda': 'Vem do catálogo comum da plataforma e não é editável aqui.',
  'catalogo.semPreco': 'Sem preço oficial publicado',
  'catalogo.versaoNumero': 'versão',
  'catalogo.descontoAte': 'desconto até',
  'catalogo.definirPreco': 'Definir preço',
  'catalogo.novaVersaoPreco': 'Nova versão de preço',
  'catalogo.nenhumaOferta': 'Nenhuma oferta encontrada para',
  'catalogo.novoProduto': 'Novo produto ou serviço',
  'catalogo.resolve': 'Resolve:',
  'catalogo.semCategoria': 'Sem categoria',
  'catalogo.ofertasContagem': 'oferta(s)',
  'catalogo.produtosContagem': 'produto(s)',
  'catalogo.nenhumProduto':
    'Nenhum produto cadastrado. O produto é o que você vende; a oferta é como você vende.',
  'catalogo.unidades': 'Unidades de negócio',
  'catalogo.novaUnidade': 'Nova unidade',
  'catalogo.semUnidades': 'Só faz sentido quando a empresa vende por mais de uma frente ou marca.',
  'catalogo.personas': 'Personas compradoras',
  'catalogo.novaPersona': 'Nova persona',
  'catalogo.semPersonas':
    'A persona diz para QUEM cada oferta serve — e é o que separa um pitch genérico de um que conversa.',
  'catalogo.dores': 'Dores',
  'catalogo.nome': 'Nome',
  'catalogo.comoVendida': 'Como é vendida',
  'catalogo.produtoOpcional': 'Produto (opcional)',
  'catalogo.semProduto': 'Sem produto associado',
  'catalogo.propostaDeValor': 'Proposta de valor',
  'catalogo.propostaDica':
    'Uma frase que o cliente entenderia. Ela vai para o playbook e para a proposta.',
  'catalogo.categoria': 'Categoria',
  'catalogo.unidadeNegocio': 'Unidade de negócio',
  'catalogo.semUnidade': 'Sem unidade',
  'catalogo.problema': 'Que problema resolve',
  'catalogo.problemaDica': 'Na dor do cliente, não na sua capacidade técnica.',
  'catalogo.papel': 'Papel na decisão',
  'catalogo.papelDica': 'Quem decide, quem influencia, quem usa, quem veta.',
  'catalogo.doresDica': 'Uma por linha.',
  'catalogo.cancelar': 'Cancelar',
  'catalogo.salvar': 'Salvar',
  'catalogo.precoDe': 'Preço de',
  'catalogo.valor': 'Valor',
  'catalogo.valorDica': 'Ex.: 49000 ou 49.000,00',
  'catalogo.unidadePreco': 'Unidade',
  'catalogo.unidadePrecoDica': 'Ex.: por mês, por hora, por licença.',
  'catalogo.modeloReceita': 'Modelo de receita',
  'catalogo.modeloDica': 'Recorrente, projeto, horas, licenças, créditos.',
  'catalogo.descontoMax': 'Desconto máximo (%)',
  'catalogo.avisoPublicar':
    'Publicar substitui a versão que está no ar hoje. A anterior fica no histórico, e as propostas já emitidas não mudam.',
  'catalogo.salvarRascunho': 'Salvar rascunho',
  'catalogo.salvarPublicar': 'Salvar e publicar',
  'catalogo.ofertaCriada':
    'Oferta cadastrada. Defina o preço oficial para ela valer nas propostas.',
  'catalogo.cadastroSalvo': 'Cadastro salvo.',
  'catalogo.precoPublicado':
    'Preço publicado. Ele passa a valer como oficial nas propostas e para o assistente.',
  'catalogo.rascunhoSalvo': 'Rascunho salvo. Ele ainda NÃO vale como preço oficial.',
  'catalogo.erroSalvar': 'Não consegui salvar. Tente de novo.',
  'catalogo.tituloUnidade': 'Nova unidade de negócio',
  'catalogo.tituloPersona': 'Nova persona compradora',
  'nav.playbooks': 'Playbooks',
  'playbooks.sinaisCompra': 'Sinais de compra',
  'playbooks.desqualificacao': 'Quando desqualificar',
  'playbooks.proximosPassos': 'Próximos passos',
  'playbooks.naoDizer': 'O que não dizer',
  'playbooks.secaoContexto': 'Para quem e quando',
  'playbooks.secaoDiscurso': 'O discurso',
  'playbooks.secaoObjecoes': 'Objeções e respostas',
  'playbooks.secaoConversa': 'Conduzir a conversa',
  'playbooks.secaoProvas': 'Provas',
  'playbooks.secaoNaoDizer': 'O que não dizer',
  'playbooks.nome': 'Nome do playbook',
  'playbooks.nomeDica': 'Diga para quem ele serve. Ex.: "QA Outsourcing — Head de Engenharia".',
  'playbooks.oferta': 'Oferta',
  'playbooks.ofertaGeral': 'Geral — vale para qualquer oferta',
  'playbooks.publico': 'Público',
  'playbooks.publicoTodos': 'Todos os públicos',
  'playbooks.publicoDica':
    'O mesmo produto conversa diferente com cada público. Escolher aqui é o que permite ter mais de um discurso para a mesma oferta.',
  'playbooks.situacao': 'Situação comercial',
  'playbooks.situacaoDica': 'Ex.: primeira conversa, concorrente instalado, renovação em risco.',
  'playbooks.finalidade': 'Finalidade',
  'playbooks.objecoesDica':
    'Pelo menos uma objeção COM resposta é obrigatória para aprovar. Objeção sem resposta é pior do que objeção nenhuma: anuncia o problema e não ajuda a resolver.',
  'playbooks.objecao': 'Objeção',
  'playbooks.objecaoExemplo': 'Como o cliente diz. Ex.: "já temos QA interno"',
  'playbooks.resposta': 'Resposta aprovada',
  'playbooks.respostaExemplo': 'O que responder, com o que sustenta',
  'playbooks.maisUmaObjecao': '+ outra objeção',
  'playbooks.descoberta': 'Perguntas de descoberta',
  'playbooks.umaPorLinha': 'Uma por linha.',
  'playbooks.provasDica': 'Caso, número ou depoimento — com a fonte, para poder ser citado.',
  'playbooks.provaTitulo': 'Título da prova',
  'playbooks.provaEvidencia': 'O que ela mostra',
  'playbooks.provaFonte': 'Fonte',
  'playbooks.maisUmaProva': '+ outra prova',
  'playbooks.naoDizerDica':
    'Uma por linha. Promessa de prazo que a operação não cumpre, comparação sem evidência, cliente citado sem autorização.',
  'playbooks.avisoSemObjecao':
    'Sem objeção com resposta, este rascunho não poderá ser aprovado — ele ficará salvo, mas não vira material oficial.',
  'playbooks.publicoGeral': 'para todos os públicos',
  'playbooks.publicoEspecifico': 'público específico',
  'nav.mapeamento': 'Inteligência de mercado',
  'nav.relatorios': 'Relatórios',
  'relatorios.abaFunil': 'Funil',
  'relatorios.abaDesempenho': 'Quem está com o quê',
  'relatorios.abaMotivos': 'Por que ganhamos e perdemos',
  'relatorios.abaOfertas': 'O que vende melhor',
  'relatorios.responsavel': 'Responsável',
  'relatorios.valorEmAberto': 'Valor em aberto',
  'relatorios.valorGanho': 'Valor ganho',
  'relatorios.taxa': 'Taxa',
  'relatorios.ciclo': 'Ciclo',
  'relatorios.oferta': 'Oferta',
  'relatorios.produto': 'Produto',
  'relatorios.porQueGanhamos': 'Por que ganhamos',
  'relatorios.porQuePerdemos': 'Por que perdemos',
  'relatorios.nenhum': 'Nenhum no período.',
  'relatorios.semDados': 'Nenhuma oportunidade no período — nem em aberto, nem encerrada.',
  'relatorios.semEncerrados': 'Nenhum negócio foi encerrado no período.',
  'relatorios.semOfertaNoPeriodo':
    'Nenhum negócio encerrado no período tinha oferta do catálogo associada.',
  'relatorios.erroExportar':
    'Não consegui exportar. Verifique se você tem permissão para exportar dados.',
  'nav.receita': 'Receita e forecast',
  'nav.customerSuccess': 'Customer Success',
  'cs.mapeamentoTitulo': 'Contas nos sistemas de origem',
  'cs.fonteProduto': 'Uso do produto',
  'cs.fonteFaturamento': 'Faturamento',
  'cs.fonteSuporte': 'Suporte',
  'cs.fontePesquisa': 'Pesquisa de satisfação',
  'cs.semIntegracao': 'sem integração',
  'cs.fatoresContagem': 'fator(es)',
  'cs.pontosDoModelo': 'pontos do modelo',
  'cs.de': 'de',
  'cs.contasMapeadas': 'contas mapeadas',
  'cs.faltamMapear': 'Faltam mapear:',
  'cs.mapear': 'Mapear',
  'cs.coletarAgora': 'Coletar agora',
  'cs.mapeamentoSalvo': 'Mapeamento salvo.',
  'cs.erroMapeamento': 'Não consegui salvar o mapeamento.',
  'cs.erroColeta': 'Não consegui coletar os sinais.',
  'cs.identificador': 'Identificador da conta',
  'cs.ondeEncontrar': 'Onde encontrar esse valor',
  'cs.ondeEncontrarDica':
    'Ex.: código do cliente na tela de cadastro do sistema. Ajuda quem preencher o próximo.',
  'cs.mapeamentoAjuda':
    'Sem esse identificador, a integração não sabe de quem são as leituras — e os fatores desta fonte ficam fora da nota da conta.',
  'nav.reunioes': 'Reuniões',
  'reunioes.painelEquipe': 'Coaching da equipe',
  'reunioes.pessoa': 'Pessoa',
  'reunioes.conversas': 'Conversas',
  'reunioes.falou': 'Falou',
  'reunioes.perguntas': 'Perguntas',
  'reunioes.comProximoPasso': 'Com próximo passo',
  'reunioes.semMedida': 'sem medida',
  'reunioes.tendenciaAjuda':
    'Comparação entre a metade mais antiga e a mais recente do período. Só aparece com quatro conversas ou mais.',
  'reunioes.painelAjuda':
    'Os números são do período dos últimos 90 dias. Eles ajudam a decidir com quem sentar — não são avaliação de desempenho, e não substituem ouvir a conversa.',
  'nav.aprovacoes': 'Aprovações',
  'nav.assistente': 'Assistente',
  'nav.configuracoes': 'Configurações',
  'nav.ajuda': 'Ajuda',

  'acao.salvar': 'Salvar',
  'acao.cancelar': 'Cancelar',
  'acao.confirmar': 'Confirmar',
  'acao.corrigir': 'Corrigir',
  'acao.excluir': 'Excluir',
  'acao.novo': 'Novo',
  'acao.buscar': 'Buscar',
  'acao.filtrar': 'Filtrar',
  'acao.fechar': 'Fechar',
  'acao.voltar': 'Voltar',
  'acao.continuar': 'Continuar',
  'acao.tentarDeNovo': 'Tentar novamente',

  'estado.carregando': 'Carregando…',
  'estado.semResultados': 'Nada por aqui ainda.',
  'estado.erro': 'Não consegui carregar. Tente de novo.',

  'sessao.sair': 'Sair',
  'sessao.meuPerfil': 'Meu perfil',
  'sessao.preferencias': 'Preferências',

  'preferencias.idioma': 'Idioma',
  'preferencias.fuso': 'Fuso horário',
  'preferencias.idiomaAjuda':
    'Vale em qualquer aparelho: a escolha fica na sua conta, e não neste navegador.',
  'preferencias.fusoAjuda':
    'Datas e horas aparecem neste fuso. Os valores continuam na moeda de cada registro.',

  'comum.novo': 'Novo',
  'comum.buscar': 'Buscar',
  'comum.todas': 'Todas',
  'comum.todos': 'Todos',
  'comum.responsavel': 'Responsável',
  'comum.situacao': 'Situação',
  'comum.atualizada': 'Atualizada',
  'comum.canal': 'Canal',
  'comum.local': 'Local',
  'comum.empresa': 'Empresa',
  'comum.pessoa': 'Pessoa',
  'comum.valor': 'Valor',
  'comum.etapa': 'Etapa',
  'comum.previsao': 'Previsão',
  'comum.prazo': 'Prazo',
  'comum.tipo': 'Tipo',
  'comum.assunto': 'Assunto',

  'empresas.titulo': 'Empresas',
  'empresas.subtitulo': 'Pesquise por razão social, nome fantasia, CNPJ ou domínio.',
  'empresas.buscar': 'Buscar empresa, CNPJ ou domínio…',
  'empresas.filtrarSituacao': 'Filtrar por situação',
  'empresas.filtrarParceiro': 'Filtrar por parceiro',
  'empresas.todasSituacoes': 'Todas as situações',
  'empresas.todosCanais': 'Todos os canais',
  'empresas.nova': 'Nova empresa',
  'empresas.segmento': 'Segmento',
  'empresas.grupoEconomico': 'Grupo econômico',
  'empresas.regra1': 'Prospect sem avaliação, engajamento ou oportunidade',
  'empresas.regra2': 'Pré-venda ainda em “Avaliar aderência”',
  'empresas.regra3': 'Aderência aprovada ou abordagem iniciada',
  'empresas.regra4': 'Interesse confirmado ou qualificação iniciada',
  'empresas.regra5': 'Qualificação concluída',
  'empresas.regra6': 'Oportunidade ativa',
  'empresas.regra7': 'Venda ganha ou contrato ativo',
  'empresas.classSuspect': 'Suspect',
  'empresas.classProspect': 'Prospect',
  'empresas.classLead': 'Lead',
  'empresas.classQualified': 'Lead qualificado',
  'empresas.classOpportunity': 'Oportunidade',
  'empresas.classCustomer': 'Cliente',
  'empresas.colunaCnpj': 'CNPJ',
  'empresas.subtituloNovo':
    'Encontre, organize e trabalhe as empresas de acordo com o momento do relacionamento.',
  'empresas.todas': 'Todas as empresas',
  'empresas.todasHint': 'Visão completa',
  'empresas.visoesRapidas': 'Visões rápidas',
  'empresas.visaoTodas': 'Todas',
  'empresas.minhaCarteira': 'Minha carteira',
  'empresas.semResponsavel': 'Sem responsável',
  'empresas.semProximaAcao': 'Sem próxima ação',
  'empresas.cadastroIncompleto': 'Cadastro incompleto',
  'empresas.colunas': 'Colunas',
  'empresas.colunasTitulo': 'Colunas da consulta',
  'empresas.colunasAjuda': 'Escolha o que deseja visualizar.',
  'empresas.colunaObrigatoria': 'obrigatória',
  'empresas.colunasOrdem':
    'Ordem e largura também ficam salvas. Arraste os cabeçalhos para reorganizar.',
  'empresas.restaurarPadrao': 'Restaurar padrão',
  'empresas.aplicar': 'Aplicar',
  'empresas.subirColuna': 'Mover para cima',
  'empresas.descerColuna': 'Mover para baixo',
  'empresas.largura': 'Largura',
  'empresas.preferenciasSalvas': 'preferências de colunas salvas por usuário',
  'empresas.nestaVisao': 'empresas nesta visão',
  'empresas.classificacao': 'Classificação',
  'empresas.processoAtual': 'Processo atual',
  'empresas.semProcesso': 'Sem processo ativo',
  'empresas.proximaAcao': 'Próxima ação',
  'empresas.situacaoCadastral': 'Situação cadastral',
  'empresas.ativo': 'Ativo',
  'empresas.inativo': 'Inativo',
  'empresas.completude': 'Cadastro',
  'empresas.completo': 'Completo',
  'empresas.incompleto': 'Incompleto',
  'empresas.origem': 'Origem',
  'empresas.site': 'Site',
  'empresas.oportunidades': 'Oportunidades',
  'empresas.classificacaoComercial': 'Classificação comercial',
  'empresas.atribuidaAutomaticamente': 'Atribuída automaticamente',
  'empresas.ajustadaPorAdmin': 'Ajustada na regularização',
  'empresas.porQueAssim': 'Por que está assim?',
  'empresas.evidencias': 'Evidências',
  'empresas.abrirRegistro': 'Abrir',
  'empresas.evolucao': 'Evolução da classificação',
  'empresas.oQueFalta': 'Para avançar',
  'empresas.historico': 'Histórico',
  'empresas.semHistorico': 'Sem mudanças registradas ainda.',
  'empresas.semEdicaoLivre':
    'Correções administrativas são auditadas; não há edição livre da classificação.',
  'empresas.cadastroMestre': 'Cadastro mestre da empresa',
  'empresas.abrirNaPreVenda': 'Abrir na pré-venda',
  'empresas.abrirOportunidade': 'Abrir a oportunidade',
  'empresas.abrirFicha': 'Abrir a ficha da empresa',
  'empresas.maisProcessos': 'outro processo ativo',
  'empresas.maisProcessosPlural': 'outros processos ativos',
  'empresas.regularizar': 'Regularizar histórico comercial',
  'empresas.regularizarTitulo': 'Regularizar classificações legadas',
  'empresas.regularizarAviso': 'Ajuste administrativo com simulação obrigatória',
  'empresas.regularizarAvisoTexto':
    'Nenhum cliente, oportunidade ou histórico válido será rebaixado. Toda mudança será auditada.',
  'empresas.regrasDaSimulacao': 'Regras da simulação',
  'empresas.previa': 'Prévia',
  'empresas.analisadas': 'empresas analisadas',
  'empresas.alteracoesPropostas': 'alterações propostas',
  'empresas.preservadas': 'preservadas',
  'empresas.clientesRebaixados': 'clientes rebaixados',
  'empresas.motivoObrigatorio': 'Motivo da regularização',
  'empresas.exemploMotivo': 'Ex.: classificação legada herdada do cadastro antigo',
  'empresas.simular': 'Simular',
  'empresas.aplicarRegularizacao': 'Aplicar regularização',
  'empresas.regularizacaoAplicada': 'Regularização aplicada',
  'empresas.amostra': 'Amostra das empresas afetadas',
  'empresas.simuleAntes': 'Simule antes de aplicar.',
  'empresas.selecionadas': 'selecionadas',
  'empresas.atribuirResponsavel': 'Atribuir responsável',
  'empresas.adicionarPreVenda': 'Adicionar à pré-venda',
  'empresas.inativar': 'Inativar cadastro',
  'empresas.reativarCadastro': 'Reativar cadastro',
  'empresas.limparSelecao': 'Limpar seleção',
  'empresas.acoesAplicadas': 'Ação aplicada às empresas selecionadas',
  'empresas.vazioTitulo': 'Nenhuma empresa cadastrada',
  'empresas.vazioBuscaTitulo': 'Nenhuma empresa encontrada',
  'empresas.vazioTexto':
    'Cadastre a primeira empresa para começar a registrar oportunidades. Só a razão social é obrigatória — informe o CNPJ quando tiver, é a melhor defesa contra cadastro duplicado.',
  'empresas.vazioBuscaTexto':
    'Revise os termos da busca ou limpe os filtros. A busca cobre razão social, nome fantasia, CNPJ e domínio.',

  'contatos.titulo': 'Contatos',
  'contatos.subtitulo':
    'Pessoas com quem você fala. Um contato pode existir sem empresa associada.',
  'contatos.buscar': 'Buscar por nome, e-mail, telefone, cargo ou empresa…',
  'contatos.vinculo': 'Vínculo com empresa',
  'contatos.papel': 'Papel na decisão',
  'contatos.todosPapeis': 'Todos os papéis',
  'contatos.contato': 'Contato',
  'contatos.email': 'E-mail',
  'contatos.telefone': 'Telefone',
  'contatos.consentimento': 'Consentimento',

  'oportunidades.titulo': 'Oportunidades',
  'oportunidades.subtitulo':
    'Serviços, projetos, alocação, recorrência, licenças, planos e créditos.',
  'oportunidades.buscar': 'Buscar por título ou empresa…',
  'oportunidades.todosTipos': 'Todos os tipos',
  'oportunidades.todosResponsaveis': 'Todos os responsáveis',
  'oportunidades.vazio': 'Nenhuma oportunidade encontrada',
  'oportunidades.oportunidade': 'Oportunidade',
  'oportunidades.proximoPasso': 'Próximo passo',
  'oportunidades.vencida': 'Atividade vencida',

  'atividades.titulo': 'Atividades',
  'atividades.subtitulo': 'Tarefas, ligações, reuniões, e-mails e lembretes.',
  'atividades.todosTipos': 'Todos os tipos',
  'atividades.todosResponsaveis': 'Todos os responsáveis',
  'atividades.cancelar': 'Cancelar atividade',
  // CERES-001 — triagem operacional por tempo e prioridade.
  'atividades.atrasadas': 'Atrasadas',
  'atividades.hoje': 'Hoje',
  'atividades.proximos7': 'Próximos 7 dias',
  'atividades.concluidas': 'Concluídas',
  'atividades.todas': 'Todas',
  'atividades.novoFollowUp': 'Novo follow-up',
  'atividades.proximoPasso': 'Próximo passo',
  'atividades.grupoMaisDe7': 'Vencidas há mais de uma semana',
  'atividades.grupoAte7': 'Vencidas nesta semana',
  'atividades.grupoHojeOntem': 'Venceram entre ontem e hoje',
  'atividades.nadaAtrasado': 'Nada em atraso',
  'atividades.agendaEmDia': 'Toda a agenda está em dia.',
  'atividades.nadaHoje': 'Nada marcado para hoje',
  'atividades.nada7dias': 'Nada nos próximos 7 dias',
  'atividades.semCompromissos': 'Agende o próximo passo das suas oportunidades.',
  'atividades.nenhumaEncontrada': 'Nenhuma atividade encontrada',
  'atividades.altaPrioridade': 'alta prioridade',
  'atividades.somenteMinhas': 'Somente minhas',
  'atividades.concluir': 'Concluir',
  'pipeline.titulo': 'Pipeline',
  'pipeline.buscar': 'Buscar por título ou empresa…',
  'pipeline.filtrarResponsavel': 'Filtrar por responsável',
  'pipeline.semEtapasTitulo': 'Pipeline sem etapas',
  'pipeline.semEtapasTexto': 'Configure as etapas do pipeline em Administração › Pipelines.',
  'pipeline.confirmarMovimento': 'Confirmar movimentação',
  'pipeline.movimentoBloqueado': 'Movimentação bloqueada',
  'pipeline.informeMotivo': 'Informe o motivo',
  'pipeline.motivoAjuda': 'O motivo alimenta a análise de ganhos e perdas.',
  'pipeline.motivo': 'Motivo',
  'pipeline.moverPara': 'Mover para',
  'pipeline.semOportunidade': 'Nenhuma oportunidade',
  'pipeline.semProximoPasso': 'Sem próximo passo',
  'pipeline.padrao': 'Pipeline padrão',
  'pipeline.todosResponsaveis': 'Todos os responsáveis',
  'pipeline.entendi': 'Entendi',
  'pipeline.completePendencias':
    'Abra a oportunidade para completar as pendências e tente novamente.',
  'pipeline.selecioneMotivo': 'Selecione o motivo…',
  'pipeline.atividadeVencida': 'Possui atividade vencida',
  'pipeline.subtituloArrastar': 'Arraste os cartões entre as etapas ou use "Mover para" no cartão.',
  'pipeline.subtituloLeitura': 'Visualização do funil. Seu perfil não movimenta oportunidades.',
  'pipeline.abraOportunidade':
    'Abra a oportunidade para completar as pendências e tente novamente.',
  'painel.titulo': 'Painel comercial',
  'painel.oportunidadesAbertas': 'Oportunidades abertas',
  'painel.atividadesVencidas': 'Atividades vencidas',
  'painel.reservasAVencer': 'Reservas a vencer',
  'painel.porEtapa': 'Oportunidades por etapa',
  'painel.abrirKanban': 'Abrir Kanban',
  'painel.semOportunidadeAberta': 'Nenhuma oportunidade aberta no seu escopo.',
  'painel.previsao': 'Previsão de fechamento',
  'painel.semPrevisao': 'Nenhuma oportunidade com previsão informada.',
  'painel.ponderadoExplicacao': 'Ponderado = Σ(valor estimado × probabilidade da etapa).',
  'painel.ganhas': 'Ganhas',
  'painel.perdidas': 'Perdidas',
  'painel.reservasSemAtualizacao': 'Reservas sem atualização',
  'painel.paradas': 'Oportunidades paradas',
  'painel.semMovimento': 'Sem mudança de etapa',
  'painel.fluxoAtividades': 'Fluxo de atividades',
  'painel.concluidas': 'Concluídas',
  'painel.evolucao': 'Evolução comercial',
  'painel.criadas': 'Criadas',
  'painel.abrirAtividades': 'Abrir agenda',
  'painel.abrirOportunidades': 'Ver oportunidades',
  // CERES-001 — nova Home: pulso comercial, jornada e central de ação.
  'painel.saudacaoManha': 'Bom dia',
  'painel.saudacaoTarde': 'Boa tarde',
  'painel.saudacaoNoite': 'Boa noite',
  'painel.criar': 'Criar',
  'painel.pipelineAberto': 'Pipeline aberto',
  'painel.previsaoPonderada': 'Previsão ponderada',
  'painel.fechamentosRecentes': 'Fechamentos',
  'painel.precisaDeAcao': 'Precisa de ação',
  'painel.abertas': 'abertas',
  'painel.bruto': 'bruto',
  'painel.hoje': 'hoje',
  'painel.visaoComercial': 'Visão comercial',
  'painel.semFechamentos': 'sem fechamentos no período',
  'painel.atividade': 'Atividade',
  'jornada.titulo': 'Jornada CERES',
  'jornada.subtitulo': 'Semear, cultivar, colher — clique em um passo para executá-lo.',
  'jornada.faseSemear': 'Semear',
  'jornada.faseSemearDescricao': 'novas oportunidades',
  'jornada.faseCultivar': 'Cultivar',
  'jornada.faseCultivarDescricao': 'relacionamento e negociação',
  'jornada.faseColher': 'Colher',
  'jornada.faseColherDescricao': 'fechamento e crescimento',
  'jornada.cadastrarEmpresa': 'Cadastrar empresa',
  'jornada.adicionarContato': 'Adicionar contato',
  'jornada.criarOportunidade': 'Criar oportunidade',
  'jornada.registrarInteracao': 'Registrar interação',
  'jornada.fazerFollowUp': 'Fazer follow-up',
  'jornada.acompanharPipeline': 'Acompanhar o pipeline',
  'jornada.fechar': 'Fechar negócio',
  'jornada.semEmpresa': 'sem empresa',
  'jornada.abertas': 'abertas',
  'jornada.aberta': 'aberta',
  'jornada.agendada': 'agendada',
  'jornada.vencida': 'vencida',
  'jornada.ganhaRecente': 'ganha',
  'jornada.agendadas': 'agendadas',
  'jornada.vencidas': 'vencidas',
  'jornada.paradas': 'paradas',
  'jornada.ganhasRecentes': 'ganhas',
  'acao.titulo': 'Central de ação',
  'acao.critico': 'Crítico',
  'acao.importante': 'Importante',
  'acao.proximo': 'Próximo',
  'acao.porQueEstes': 'Por que estes?',
  'acao.nadaPendente':
    'Nada vencido nem marcado para hoje. É uma boa notícia, e não uma tela vazia.',
  'acao.mostrarTudo': 'Mostrar tudo',
  'acao.mostrarMenos': 'Mostrar menos',
  'acao.verTodas': 'Ver todas as atividades',
  'acao.aprovacoesEsperando': 'Aprovações esperando você',
  'acao.mensagensParadas': 'Mensagens que não saíram',
  'acao.entregasSemDono': 'Entregas sem dono',
  'oportunidade.carregando': 'Carregando oportunidade…',
  'oportunidade.titulo': 'Oportunidade',
  'oportunidade.valorEstimado': 'Valor estimado',
  'oportunidade.probabilidade': 'Probabilidade',
  'oportunidade.recorrencia': 'Recorrência',
  'oportunidade.previsaoFechamento': 'Previsão de fechamento',
  'oportunidade.parceiroOrigem': 'Parceiro de origem',
  'oportunidade.origem': 'Origem',
  'oportunidade.criadaEm': 'Criada em',
  'oportunidade.fechadaEm': 'Fechada em',
  'oportunidade.motivo': 'Motivo',
  'oportunidade.concorrente': 'Concorrente',
  'oportunidade.semAtividade': 'Nenhuma atividade',
  'oportunidade.semAtividadeTexto': 'Agende o próximo compromisso para não perder o timing.',
  'oportunidade.transferir': 'Mudar de pipeline',
  'oportunidade.transferirBotao': 'Transferir',
  'oportunidade.transferirDescricao':
    'O negócio muda de funil sem perder histórico, atividades nem data de início. A alternativa — encerrar e recriar — perderia as três, e deixar no lugar errado faz o funil contar uma história que não é a que está acontecendo.',
  'oportunidade.paraQualPipeline': 'Para qual pipeline',
  'oportunidade.escolhaPipeline': 'Escolha o pipeline…',
  'oportunidade.emQualEtapa': 'Em qual etapa',
  'oportunidade.primeiraEtapa': 'Primeira etapa do pipeline',
  'oportunidade.probabilidadeMuda':
    'A probabilidade passa a ser a da etapa de destino: ela é característica da etapa, e não do negócio.',
  'oportunidade.porQueTransferir': 'Por que está mudando',
  'oportunidade.motivoTransferenciaAjuda':
    'Quem olhar o funil no mês que vem vai encontrar um negócio que mudou de lugar. A resposta para "por quê?" não pode depender da memória de alguém.',
  'oportunidade.transferidaOk': 'Oportunidade transferida.',
  'oportunidade.erroTransferir': 'Não foi possível transferir.',
  'oportunidade.congelar': 'Congelar',
  'oportunidade.moverKanban': 'Mover no Kanban',
  'oportunidade.dadosComerciais': 'Dados comerciais',
  'oportunidade.proximoPasso': 'Próximo passo',
  'oportunidade.semProximaAcao': 'Nenhuma próxima ação registrada.',
  'oportunidade.contexto': 'Contexto',
  'oportunidade.propostas': 'Propostas',
  'oportunidade.novaProposta': 'Nova proposta',
  'oportunidade.atividades': 'Atividades e follow-up',
  'oportunidade.agendar': 'Agendar',
  'oportunidade.agendarFollowUp': 'Agendar follow-up',
  'oportunidade.movimentacao': 'Movimentação no pipeline',
  'oportunidade.semMovimentacao': 'Nenhuma movimentação registrada.',
  'oportunidade.contatosEnvolvidos': 'Contatos envolvidos',
  'oportunidade.semContato': 'Nenhum contato vinculado.',
  'oportunidade.produtos': 'Produtos e serviços',
  'oportunidade.semOferta': 'Nenhuma oferta informada.',
  'contato.editar': 'Editar contato',
  'contato.identificacao': 'Identificação',
  'contato.nome': 'Nome',
  'contato.cargo': 'Cargo',
  'contato.area': 'Área',
  'contato.principal': 'Contato principal',
  'contato.canais': 'Canais',
  'contato.celular': 'Celular',
  'contato.linkedin': 'LinkedIn',
  'contato.consentimentoTitulo': 'Consentimento e restrições',
  'contato.consentimentoTexto': 'Registro exigido pela LGPD: finalidade e base legal do contato.',
  'contato.baseLegal': 'Base legal',
  'contato.observacoes': 'Observações',
  'contato.trilha': 'Trilha',
  'contato.excluir': 'Excluir',
  'contato.anonimizar': 'Anonimizar o titular',
  'contato.anonimizarDescricao':
    'Anonimizar tira QUEM era a pessoa e mantém O QUE aconteceu: a oportunidade continua existindo, o histórico continua fazendo sentido, e os dados pessoais saem. É o que se faz quando alguém exerce o direito de eliminação.',
  'contato.anonimizarIrreversivel':
    'É IRREVERSÍVEL. Excluir, ao contrário, apagaria o registro e levaria junto o fato comercial — a oportunidade ficaria sem contato.',
  'contato.motivoDaAnonimizacao': 'Por que está anonimizando',
  'contato.motivoAnonimizacaoAjuda':
    'Pedido do titular, prazo de retenção vencido, decisão jurídica. Sem motivo, a trilha guarda uma perda de dado sem causa.',
  'contato.anonimizarBotao': 'Anonimizar em definitivo',
  'contato.anonimizado': 'Titular anonimizado. O fato comercial foi preservado.',
  'contato.erroAnonimizar': 'Não foi possível anonimizar.',
  'contato.semEmpresa': 'Este contato não tem empresa associada',
  'contato.origemConsentimento': 'Origem do consentimento',
  'contato.registradoEm': 'Registrado em',
  'contato.criadoEm': 'Criado em',
  'contato.ultimaAlteracao': 'Última alteração',
  'contato.removerVinculoPergunta': 'Remover vínculo com a empresa?',
  'contato.principalDaEmpresa': 'Principal da empresa',
  'contato.exigeEmpresa': 'Exige empresa associada.',
  'contato.naoContatarPor': 'Não contatar por',
  'contato.carregando': 'Carregando contato…',
  'contato.registrarAtividade': 'Registrar atividade',
  'contato.dados': 'Dados do contato',
  'contato.abrirPerfil': 'Abrir perfil',
  'contato.nenhumaEmpresa': 'Nenhuma empresa associada.',
  'contato.filtroSemEmpresa': 'O contato aparece no filtro “Sem empresa” da lista.',
  'contato.removerVinculo': 'Remover vínculo',
  'contato.registro': 'Registro',
  'contato.agendarRetorno': 'Agendar retorno',
  'contato.abrirEmpresa': 'Abrir empresa',
  'empresa.semHistorico': 'Sem histórico ainda',
  'empresa.semHistoricoTexto':
    'Contatos, oportunidades, movimentações e atividades aparecem aqui em ordem cronológica.',
  'empresa.carregando': 'Carregando empresa…',
  'empresa.cnpj': 'CNPJ',
  'empresa.dominio': 'Domínio',
  'empresa.site': 'Site',
  'empresa.siteOficial': 'Site oficial',
  'empresa.siteNaoIdentificado': 'Não identificado',
  'empresa.localizarSiteOficial': 'Localizar site oficial',
  'empresa.localizandoSite': 'Procurando site oficial…',
  'empresa.localizarSiteDica': 'Não encontramos um site cadastrado para esta empresa.',
  'empresa.possivelSite': 'Possível site encontrado',
  'empresa.sitesParaRevisao': 'Outros candidatos para revisão',
  'empresa.nenhumSiteEncontrado': 'Não identificamos um site oficial com evidência suficiente.',
  'empresa.confiancaDoSite': 'Confiança:',
  'empresa.confirmarSite': 'Confirmar',
  'empresa.verEvidencias': 'Ver evidências',
  'empresa.descartarSite': 'Descartar',
  'empresa.siteConfirmadoToast': 'Site oficial gravado no cadastro.',
  'empresa.siteDescartado': 'Candidato descartado.',
  'empresa.siteConfirmadoPessoas': 'Site confirmado. Deseja atualizar pessoas-chave?',
  'empresa.buscarPessoasAgora': 'Buscar pessoas-chave agora',
  'empresa.buscaPessoasIniciada': 'Busca de pessoas-chave iniciada.',
  'empresa.evidenciaCnpj': 'CNPJ encontrado no site',
  'empresa.evidenciaNome': 'Nome da empresa compatível',
  'empresa.evidenciaTelefone': 'Telefone compatível',
  'empresa.evidenciaCidade': 'Cidade/UF compatível',
  'empresa.evidenciaEmail': 'E-mail corporativo de contato conhecido',
  'empresa.evidenciaEmailPublico': 'E-mail corporativo público encontrado',
  'empresa.evidenciaPresenca': 'Presença digital pré-existente',
  'empresa.evidenciaDominio': 'Domínio semelhante ao nome',
  'empresa.evidenciaEndereco': 'Endereço compatível',
  'empresa.evidenciaCep': 'CEP compatível',
  'empresa.porte': 'Porte',
  'empresa.cidadeUf': 'Cidade / UF',
  'empresa.origem': 'Origem',
  'empresa.grupoEconomico': 'Grupo econômico',
  'empresa.responsavelInterno': 'Responsável interno',
  'empresa.parceiroResponsavel': 'Parceiro responsável',
  'empresa.cadastradaEm': 'Cadastrada em',
  'empresa.semOportunidade': 'Nenhuma oportunidade',
  'empresa.semOportunidadeTexto': 'Registre a primeira oportunidade desta conta.',
  'empresa.contatoNaoEhDecisor': 'Contato cadastrado não é o mesmo que decisor.',
  'empresa.semContato': 'Nenhum contato',
  'empresa.semContatoTexto': 'O primeiro contato cadastrado se torna o principal.',
  'empresa.abrirOportunidade': 'Abrir oportunidade',
  'empresa.observacoes': 'Observações',
  'empresa.reservaVigente': 'Reserva vigente',
  'empresa.verReserva': 'Ver reserva',
  'empresa.novaOportunidade': 'Nova oportunidade',
  'empresa.visao360': 'Visão 360',
  'empresa.rel.titulo': 'Relacionamento com outras operações',
  'empresa.rel.semValores': 'Sem valores nem contratos',
  'empresa.rel.venda': 'Venda',
  'empresa.rel.contrato': 'Contrato',
  'empresa.rel.preVendas': 'Pré-vendas',
  'empresa.rel.posVenda': 'Pós-venda',
  'empresa.rel.outraEquipe': 'outra equipe',
  'empresa.propostas': 'Propostas',
  'empresa.contratos': 'Contratos',
  'empresa.mensagens': 'Mensagens',
  'empresa.cadencias': 'Cadências',
  'empresa.aprendeu': 'O que o sistema aprendeu',
  'empresa.enriquecer': 'Enriquecer dados',
  'empresa.enriquecer.titulo': 'Buscar dados da empresa',
  'empresa.enriquecer.buscando': 'Consultando fontes externas…',
  'empresa.enriquecer.identidadeExata': 'Empresa identificada pelo CNPJ no registro oficial.',
  'empresa.enriquecer.identidadeAlta': 'Empresa identificada com alta confiança.',
  'empresa.enriquecer.ambigua':
    'Mais de uma empresa possível. Escolha qual é a certa — nada será usado antes disso.',
  'empresa.enriquecer.usarEsta': 'Usar esta',
  'empresa.enriquecer.naoEncontrada':
    'Não foi possível identificar a empresa com segurança nas fontes consultadas.',
  'empresa.enriquecer.reaproveitado':
    'Resultado de consulta recente reaproveitado — nenhuma fonte foi consultada de novo.',
  'empresa.enriquecer.atualizarAgora': 'Consultar de novo',
  'empresa.enriquecer.campo': 'Campo',
  'empresa.enriquecer.encontrado': 'Encontrado',
  'empresa.enriquecer.atual': 'No cadastro',
  'empresa.enriquecer.igual': 'Igual',
  'empresa.enriquecer.vazio': 'Vazio no cadastro',
  'empresa.enriquecer.conflito': 'Diferente do cadastro',
  'empresa.enriquecer.confirmado': 'Confirmado',
  'empresa.enriquecer.provavel': 'Provável',
  'empresa.enriquecer.estimado': 'Estimado',
  'empresa.enriquecer.conflitante': 'Fontes divergem',
  'empresa.enriquecer.informativos': 'Inteligência encontrada e guardada no perfil da empresa',
  'empresa.enriquecer.grupo.cadastro': 'Cadastro oficial',
  'empresa.enriquecer.grupo.atividade': 'Atividade econômica',
  'empresa.enriquecer.grupo.governo': 'Presença no governo',
  'empresa.enriquecer.grupo.financas': 'Informações financeiras públicas',
  'empresa.enriquecer.grupo.digital': 'Presença digital',
  'empresa.enriquecer.grupo.outros': 'Outras informações',
  'empresa.enriquecer.fontes': 'Fontes consultadas',
  'empresa.enriquecer.falhas': 'Fontes que falharam',
  'empresa.enriquecer.aplicar': 'Aplicar selecionados',
  'empresa.enriquecer.aplicado': 'Campos aplicados ao cadastro.',
  'empresa.enriquecer.nadaSelecionado': 'Nenhum campo selecionado.',
  'empresa.enriquecer.nadaParaAplicar': 'Nenhum campo novo ou divergente para aplicar.',
  'empresa.enriquecer.aviso':
    'Nada é gravado sem a sua confirmação. Estimativas continuam marcadas como estimativas.',
  'prospeccao.campanhas': 'Campanhas',
  'prospeccao.naoEnvia':
    'A campanha organiza a fila — ela não fala com ninguém. Quem fala é a cadência, com consentimento, janela e frequência.',
  'prospeccao.nova': 'Nova campanha',
  'prospeccao.novaDescricao':
    'A campanha aplica o perfil de cliente ideal sobre a base e devolve a fila de quem procurar, com o motivo de quem ficou de fora.',
  'prospeccao.nome': 'Nome',
  'prospeccao.objetivo': 'O que esta campanha quer alcançar',
  'prospeccao.objetivoAjuda':
    'Sem objetivo, ninguém sabe quando a campanha deu certo — e a lista vira trabalho sem fim.',
  'prospeccao.perfil': 'Perfil de cliente ideal',
  'prospeccao.perfilAjuda':
    'A chave do perfil publicado em Cliente ideal. É ele que decide quem entra na lista.',
  'prospeccao.criar': 'Criar campanha',
  'prospeccao.criada': 'Campanha criada. Monte a lista para ver quem entra.',
  'prospeccao.montarLista': 'Montar a lista',
  'prospeccao.listaMontada': 'Lista montada. Ela é um retrato: não se monta de novo.',
  'prospeccao.montadaEm': 'montada em',
  'prospeccao.verLista': 'Ver a lista',
  'prospeccao.fechar': 'Fechar',
  'prospeccao.naLista': 'na lista',
  'prospeccao.abordados': 'abordados',
  'prospeccao.qualificados': 'qualificados',
  'prospeccao.convertidos': 'convertidos',
  'prospeccao.excluidos': 'fora do perfil',
  'prospeccao.icp': 'ICP',
  'prospeccao.abordado': 'Abordei',
  'prospeccao.qualificado': 'Qualificou',
  'prospeccao.convertido': 'Virou oportunidade',
  'prospeccao.descartado': 'Descartar',
  'prospeccao.descartar': 'Descartar o alvo',
  'prospeccao.descartarDescricao':
    'O alvo sai da fila e continua na lista, com o motivo. É o que impede a mesma empresa de voltar na próxima campanha sem ninguém lembrar por que saiu.',
  'prospeccao.porQueDescartar': 'Por que está descartando',
  'prospeccao.descartarBotao': 'Descartar com o motivo',
  'prospeccao.alvoMovido': 'Alvo atualizado.',
  'prospeccao.erro': 'Não foi possível concluir.',
  'prospeccao.vazio': 'Nenhuma campanha de prospecção',
  'prospeccao.vazioOQueE':
    'A campanha aplica o perfil de cliente ideal sobre a base e monta a fila de quem procurar — registrando o motivo de quem ficou de fora.',
  'prospeccao.vazioExemplo':
    'A lista é montada uma vez e vira um retrato: montar de novo apagaria a resposta para "por que esta empresa entrou?". Para outro recorte, crie outra campanha.',
  'leads.titulo': 'Leads',
  'leads.subtitulo': 'Por onde começar hoje — com a conta do score ao lado.',
  'leads.vazio': 'Nenhum lead com nota',
  'leads.vazioTexto':
    'Defina o perfil de cliente ideal e registre o que se sabe das empresas: sem sinal, não há nota — e sem nota não há fila.',
  'propostas.novo': 'Nova proposta',
  'propostas.novoDescricao':
    'Escolha a oportunidade: empresa, contato e valores vêm dela. Desconto e validade são opcionais — sem validade, vale o prazo padrão da configuração.',
  'propostas.oportunidade': 'Oportunidade',
  'propostas.escolhaOportunidade': 'Escolha a oportunidade…',
  'propostas.buscarOportunidade': 'Buscar por empresa ou negócio…',
  'propostas.desconto': 'Desconto (%)',
  'propostas.descontoAviso':
    'Acima do teto configurado, a proposta segue para alçada em vez de sair.',
  'propostas.validade': 'Válida até',
  'propostas.criar': 'Criar proposta',
  'propostas.erroCriar': 'Não foi possível criar a proposta.',
  'propostas.vazioOQueE':
    'Uma proposta é o documento com preço, escopo e validade que vai para o cliente. Ela nasce de uma oportunidade — empresa, contato e valores vêm do que já está no CRM, e cada versão enviada fica registrada.',
  'propostas.vazioExemplo':
    'Você escolhe a oportunidade da Alfa, define 10% de desconto e validade em 30 dias. O sistema monta a proposta, gera o PDF e, quando o cliente abre, a situação muda para “Vista pelo cliente”. Passada a validade, ela vence sozinha em vez de inflar o funil.',
  'propostas.pedidoAgente': 'Monte uma proposta para a oportunidade em que estou trabalhando',
  'contratos.gerar': 'Gerar contrato',
  'contratos.gerarBotao': 'Gerar',
  'contratos.gerarDescricao':
    'A minuta é montada a partir da proposta aceita: escopo, valores e condições vêm da versão que o cliente aprovou.',
  'contratos.propostaAceita': 'Proposta aceita',
  'contratos.escolhaProposta': 'Escolha a proposta…',
  'contratos.semPropostaAceita':
    'Nenhuma proposta aceita ainda. O contrato só nasce depois do aceite — antes disso o que existe é uma proposta em negociação.',
  'contratos.geradoOk': 'Minuta gerada a partir da proposta aceita.',
  'contratos.erroGerar': 'Não foi possível gerar o contrato.',
  'contratos.vazioOQueE':
    'O contrato nasce da proposta aceita, com o hash da versão exata que o cliente aprovou. Daí em diante a tela acompanha a minuta, a revisão jurídica, a assinatura e a vigência.',
  'contratos.vazioExemplo':
    'A Alfa aceita a proposta; você gera a minuta, o jurídico revisa, a assinatura entra e o contrato passa a vigente. A 90 dias do fim a cadência de renovação começa sozinha — e esta lista mostra o que ela vai alcançar antes que o desconto de última hora seja a única saída.',
  'contratos.pedidoAgente': 'Gere o contrato da proposta que o cliente aceitou',
  'contrato.verProposta': 'Ver a proposta de origem',
  'contrato.salvar': 'Salvar minuta',
  'contrato.salvo': 'Minuta salva.',
  'contrato.erroSalvar': 'Não foi possível salvar.',
  'contrato.congelar': 'Congelar a versão',
  'contrato.congelado': 'Versão congelada: o texto que vai para assinatura está fixo.',
  'contrato.erroCongelar': 'Não foi possível congelar.',
  'contrato.erroTransicao': 'O contrato não pôde mudar de situação.',
  'contrato.oQuePede': 'O que o contrato pede',
  'contrato.vigenciaInicio': 'Vigência — início',
  'contrato.vigenciaFim': 'Vigência — fim',
  'contrato.renovacaoAviso': 'A 90 dias do fim a cadência de renovação começa sozinha.',
  'contrato.textoFechado':
    'O texto está fechado nesta situação. Para mudar, volte o contrato para minuta.',
  'contrato.previa': 'Como o documento está ficando',
  'contrato.paraOnde': 'Para onde o contrato pode ir agora',
  'contrato.assinaturaAviso':
    'As opções vêm do servidor. “Registrar assinatura” não assina nada: ela registra que a assinatura aconteceu fora do sistema — não há provedor de assinatura ligado neste ambiente, e um botão que fingisse assinar seria pior do que a ausência dele.',
  'contrato.semTransicao':
    'Este contrato chegou ao fim do caminho: não há transição possível a partir daqui.',
  'reunioes.registrar': 'Registrar reunião',
  'reunioes.registrarBotao': 'Registrar',
  'reunioes.registrarDescricao':
    'Depois de registrada, você cola a transcrição e o sistema separa os compromissos, com o trecho em que cada um foi dito.',
  'reunioes.campoTitulo': 'Título',
  'reunioes.exemploTitulo': 'Reunião com a Alfa — apresentação da proposta',
  'reunioes.quando': 'Quando aconteceu',
  'reunioes.comoFoi': 'Como foi',
  'reunioes.online': 'Reunião online',
  'reunioes.presencial': 'Presencial',
  'reunioes.ligacao': 'Ligação',
  'reunioes.nota': 'Anotação',
  'reunioes.email': 'E-mail',
  'reunioes.quemEstava': 'Quem estava na sala',
  'reunioes.quemEstavaAjuda':
    'Um nome por linha. Sem saber quem participou, a conversa não é processada — consentimento não se presume.',
  'reunioes.faltaTituloOuGente':
    'Falta o título ou quem estava na sala. Sem saber quem participou, a conversa não é processada — consentimento não se presume.',
  'reunioes.registradaOk': 'Reunião registrada. Cole a transcrição para extrair os compromissos.',
  'reunioes.erroRegistrar': 'Não foi possível registrar a reunião.',
  'reunioes.colar': 'Colar a transcrição',
  'reunioes.colarAjuda':
    'Uma fala por linha, começando pelo nome de quem falou e dois-pontos — é como qualquer ferramenta de chamada exporta. O tempo de cada fala é estimado pela posição na conversa: serve para localizar o trecho, não para cronometrar.',
  'reunioes.enviarTranscricao': 'Enviar transcrição',
  'reunioes.transcricaoIlegivel':
    'Não reconheci nenhuma fala. Cada linha precisa começar com o nome de quem falou, seguido de dois-pontos — é o formato que sai da ferramenta de chamada.',
  'reunioes.erroTranscricao': 'Não foi possível enviar a transcrição.',
  'reunioes.vazioOQueE':
    'Aqui ficam as conversas com o cliente. Você registra a reunião, cola a transcrição e o sistema separa o que foi combinado: cada compromisso vem com o trecho em que foi dito, o dono e o prazo.',
  'reunioes.vazioExemplo':
    'Na chamada, a Ana pede a proposta até sexta. O compromisso aparece na lista com a frase dela; você confirma, e ele vira tarefa com dono e prazo — em vez de virar uma anotação que ninguém releu.',
  'reunioes.pedidoAgente': 'Resuma a última reunião e liste os compromissos assumidos',
  'cadencias.inscrever': 'Inscrever numa cadência',
  'cadencias.inscreverBotao': 'Inscrever',
  'cadencias.inscreverDescricao':
    'A pessoa passa a receber a sequência a partir de hoje. Uma cadência viva por pessoa: se ela já estiver nesta, o servidor recusa em vez de somar mensagens que ninguém contou.',
  'cadencias.campoCadencia': 'Cadência',
  'cadencias.escolhaCadencia': 'Escolha a cadência…',
  'cadencias.campoPessoa': 'Pessoa',
  'cadencias.escolhaPessoa': 'Escolha a pessoa…',
  'cadencias.buscarPessoa': 'Buscar por nome, e-mail ou empresa…',
  'cadencias.inscritoOk': 'Pessoa inscrita. A primeira etapa entra na fila.',
  'cadencias.erroInscrever': 'Não foi possível inscrever.',
  'cadencias.vazioOQueE':
    'Uma cadência é uma sequência programada de contatos e tarefas para um cliente — e-mail, WhatsApp, ligação, lembrete — para que nenhum follow-up dependa de alguém lembrar.',
  'cadencias.vazioExemplo':
    'Proposta enviada: e-mail no 2º dia, ligação no 5º e uma tarefa de retomada no 10º. Se o cliente responder, a cadência para sozinha.',
  'cadencias.pedidoAgente': 'Quero inscrever um contato numa cadência de acompanhamento',
  'comum.cancelar': 'Cancelar',
  'comum.salvar': 'Salvar',
  'comum.pedirAoAssistente': 'Pedir ao assistente',
  'comum.buscando': 'Buscando…',
  'playbooks.novo': 'Novo playbook',
  'playbooks.criarRascunho': 'Criar rascunho',
  'playbooks.novoDescricao':
    'O rascunho nasce incompleto de propósito: o que falta aparece na lista, e só o aprovado responde ao vendedor.',
  'playbooks.propostaDeValor': 'Proposta de valor',
  'playbooks.posicionamento': 'Posicionamento',
  'playbooks.pitchCurto': 'Pitch curto',
  'playbooks.pitchMedio': 'Pitch médio',
  'playbooks.pitchCompleto': 'Pitch completo',
  'playbooks.rascunhoOk': 'Rascunho criado. Complete o que falta e mande aprovar.',
  'playbooks.erroRascunho': 'Não foi possível criar o rascunho.',
  'playbooks.veioDe': 'Resposta do playbook',
  'playbooks.validoAte': 'válido até',
  'playbooks.outrosServiam': 'Outros playbooks também serviam a esta pergunta:',
  'playbooks.sobreQualOferta': 'Sobre qual oferta',
  'playbooks.qualquerOferta': 'Qualquer uma',
  'playbooks.comQuemVaiFalar': 'Com quem você vai falar',
  'playbooks.naoSeiAinda': 'Não sei ainda',
  'playbooks.tamanhoDoPitch': 'Tamanho',
  'playbooks.comoApresentar': 'Como apresentar',
  'playbooks.semPitch': 'Não há playbook aprovado e dentro da validade para este contexto.',
  'playbooks.aprovar': 'Aprovar',
  'playbooks.aprovarBotao': 'Aprovar e publicar',
  'playbooks.aprovarDescricao':
    'A partir da aprovação é este texto que responde às consultas e ao assistente. O anterior do mesmo público passa a substituído — e continua no histórico.',
  'playbooks.validoAteCampo': 'Válido até (opcional)',
  'playbooks.validoAteAjuda':
    'Conteúdo comercial vence: preço muda, concorrente muda, o argumento de ontem vira mentira. Depois desta data o playbook para de responder — e a lista avisa antes.',
  'playbooks.venceEm': 'Vence em',
  'playbooks.dias': 'dias — revise antes que ele pare de responder.',
  'playbooks.venceu': 'Vencido: parou de responder às consultas e ao assistente.',
  'playbooks.aprovadoEm': 'Aprovado em',
  'playbooks.naHora': 'Na hora da conversa',
  'playbooks.naHoraAjuda':
    'Escreva o que o cliente disse, com as palavras dele. A busca casa por sentido aproximado: “tá caro” encontra “preço acima do concorrente”.',
  'playbooks.oQueDisse': 'O que o cliente disse',
  'playbooks.responder': 'Responder',
  'playbooks.semResposta':
    'Nenhuma objeção cadastrada corresponde ao que você descreveu. Vale acrescentá-la ao playbook depois da conversa — foi assim que as outras entraram.',
  'playbooks.vazioOQueE':
    'O playbook é o material oficial de venda: proposta de valor, pitch por persona, respostas de objeção, sinais de compra e de risco. Só a versão APROVADA responde — rascunho na frente do cliente é argumento que ninguém revisou com peso de material oficial.',
  'playbooks.vazioExemplo':
    'O cliente diz “tá caro”. Você escreve isso aqui em cima e recebe a resposta aprovada — a mesma que todo o time usa, com a versão à vista. Sem correspondência, o sistema diz que não achou em vez de inventar uma resposta que você repetiria na frente do cliente.',
  'playbooks.pedidoAgente':
    'Escreva um playbook com pitch e respostas de objeção para a nossa oferta',
  'recebidas.abaFila': 'Precisa de identificação',
  'recebidas.abaIdentificadas': 'Já identificadas',
  'recebidas.abaDescartadas': 'Fora da fila',
  'recebidas.semRemetente': 'Ninguém reconhecido',
  'recebidas.identificar': 'Dizer de quem é',
  'recebidas.identificarDescricao':
    'O CRM guarda toda mensagem que chega, mesmo sem reconhecer quem escreveu — a do cliente novo é justamente essa. Aqui você diz de quem é, e a conversa entra na linha do tempo da pessoa.',
  'recebidas.descartar': 'Tirar da fila',
  'recebidas.descartarDescricao':
    'A mensagem continua guardada e continua legível: tirar da fila é diferente de apagar. O motivo fica junto, e é ele que responde depois por que ninguém respondeu a este endereço.',
  'recebidas.restaurar': 'Devolver à fila',
  'recebidas.motivo': 'Por que esta mensagem sai da fila',
  'recebidas.confirmarDescarte': 'Tirar da fila e registrar o motivo',
  'recebidas.confirmarIdentificacao': 'Confirmar',
  'recebidas.jaCadastrado': 'Contato já cadastrado',
  'recebidas.contatoNovo': 'Contato novo',
  'recebidas.dequem': 'De quem é esta mensagem',
  'recebidas.escolhaContato': 'Escolha a pessoa…',
  'recebidas.buscarContato': 'Buscar por nome, e-mail ou empresa…',
  'recebidas.nomeDaPessoa': 'Nome de quem escreveu',
  'recebidas.empresaOpcional': 'Empresa (opcional)',
  'recebidas.semEmpresa': 'Sem empresa por enquanto',
  'recebidas.buscarEmpresa': 'Buscar empresa…',
  'recebidas.enderecoVaiJunto':
    'O endereço desta mensagem entra no cadastro, no campo do canal — é isso que faz a próxima mensagem da mesma pessoa já chegar reconhecida.',
  'recebidas.foraDaFila': 'Fora da fila:',
  'recebidas.identificadaOk': 'Pronto. A conversa está na linha do tempo do contato.',
  'recebidas.descartadaOk': 'Mensagem fora da fila. Ela continua guardada.',
  'recebidas.restauradaOk': 'Mensagem de volta à fila.',
  'recebidas.erro': 'Não foi possível concluir.',
  'recebidas.vazioFila': 'Nada esperando identificação',
  'recebidas.vazioLista': 'Nenhuma mensagem aqui',
  'recebidas.vazioOQueE':
    'É o que chega de fora: resposta de e-mail, WhatsApp e SMS. Quando o CRM reconhece o remetente, a mensagem vira atividade do contato e interrompe a cadência sozinha. Quando não reconhece, ela espera aqui — em vez de ser descartada.',
  'recebidas.vazioExemplo':
    'Alguém responde de um celular que não está no cadastro. A mensagem fica nesta fila com o texto inteiro; você diz de quem é — ou cadastra a pessoa aqui mesmo — e a conversa passa a existir na linha do tempo dela.',
  'caixa.abaEnviadasAtencao': 'Precisa de atenção',
  'caixa.abaEnviadasTodas': 'Enviadas',
  'caixa.abaRecebidas': 'Recebidas',
  'caixa.nova': 'Escrever mensagem',
  'caixa.enviar': 'Enviar',
  'caixa.novaDescricao':
    'A mensagem entra na fila: a hora certa de falar com alguém raramente é o instante do clique, e uma requisição não é lugar de esperar provedor externo. Consentimento e janela de contato são conferidos antes de sair.',
  'caixa.canal': 'Canal',
  'caixa.finalidade': 'Finalidade',
  'caixa.transacional': 'Transacional — resposta a algo que a pessoa pediu',
  'caixa.relacionamento': 'Relacionamento — acompanhamento de quem já é cliente',
  'caixa.prospeccao': 'Prospecção — primeiro contato',
  'caixa.finalidadeAjuda':
    'A finalidade decide o consentimento exigido: prospecção para quem pediu para não ser procurado não sai, e é assim que tem de ser.',
  'caixa.destinatario': 'Para quem',
  'caixa.escolhaContato': 'Escolha a pessoa…',
  'caixa.buscarContato': 'Buscar por nome, e-mail ou empresa…',
  'caixa.assunto': 'Assunto',
  'caixa.corpo': 'Mensagem',
  'caixa.enfileiradaOk': 'Mensagem na fila. Ela aparece aqui com a situação de cada tentativa.',
  'caixa.erroEnviar': 'Não foi possível enfileirar a mensagem.',
  'caixa.cancelar': 'Cancelar o envio',
  'caixa.canceladaOk': 'Envio cancelado.',
  'caixa.erroCancelar': 'Não foi possível cancelar.',
  'caixa.vazioOQueE':
    'Aqui está tudo o que o CRM e as cadências falam com o cliente — e principalmente o que NÃO saiu: bloqueio por opt-out, endereço faltando, provedor ausente. O motivo vem escrito, porque “falhou” não diz o que fazer.',
  'caixa.vazioExemplo':
    'A cadência tenta o e-mail de acompanhamento e o contato havia retirado o consentimento para marketing. A mensagem aparece aqui como bloqueada, com essa frase — e insistir seria errado, não um detalhe técnico.',
  'caixa.pedidoAgente': 'Escreva uma mensagem de acompanhamento para o cliente com quem falei hoje',
  'leads.qualificar': 'Qualificar',
  'leads.converter': 'Converter em oportunidade',
  'leads.vazioOQueE':
    'Leads são as empresas que ainda não viraram negócio e já dão sinal. A nota vem do perfil de cliente ideal e da qualificação — e a conta do score fica à vista, para você discordar dela com argumento.',
  'leads.vazioExemplo':
    'A Alfa aparece com nota 82 porque tem o porte e o setor do perfil ideal e alguém baixou o material de preços. Você qualifica pelo framework, e só quando o critério é atendido ela vira oportunidade — lead que vira negócio sem isso enche o funil de conversa que não existe.',
  'leads.pedidoAgente': 'Quais leads valem meu tempo hoje, e por quê?',
  'cs.vazioOQueE':
    'Customer Success é a carteira depois da venda: passagem de bastão, implantação, saúde da conta e expansão. A saúde não é um número solto — ela vem de sinais com data, e cada nota diz de onde veio.',
  'cs.vazioExemplo':
    'A Alfa assina; a passagem de bastão chega aqui com o que foi prometido na venda. Você aceita, a implantação começa com etapas e responsáveis, e a saúde da conta passa a somar uso, chamados e relacionamento. Quando ela cai, a expansão sai da mesa e a conversa vira retenção.',
  'cs.pedidoAgente': 'Quais contas da minha carteira estão em risco, e o que fazer nesta semana?',
  'relatorios.vazioOQueE':
    'Os relatórios respondem às perguntas de fechamento: quanto entrou, o que travou, onde o funil vaza. Cada número traz a conta que o produziu — número sem memória de cálculo vira discussão sem fim na reunião.',
  'relatorios.vazioExemplo':
    'No período escolhido, a taxa de vitória caiu de 31% para 22%. O relatório mostra as consideradas, as ganhas e as perdidas com motivo — e as perdas sem motivo registrado aparecem separadas, porque elas é que estragam a leitura.',
  'relatorios.pedidoAgente': 'Explique o que mudou no funil neste período e onde estamos perdendo',
  'relatorios.exportar': 'Exportar CSV',
  'receita.vazioOQueE':
    'Receita é a previsão do período: o que está em aberto, o que dá para comprometer e a distância até a meta. O commit é uma promessa de quem vende, e fica registrado com quem prometeu.',
  'receita.vazioExemplo':
    'Você marca a meta do trimestre e o commit da semana. O sistema mostra o ponderado pelo estágio, o gap e quantos negócios faltam para fechá-lo — e a simulação deixa testar “e se a Alfa fechar?” sem mexer no que está registrado.',
  'receita.pedidoAgente': 'Como está a previsão do trimestre e o que fazer para fechar o gap?',
  'leads.qualificarTitulo': 'Qualificar o lead',
  'leads.qualificarDescricao':
    '“Não perguntei” não é “não tem”: campo sem resposta fica em aberto, e não como negativo — tratar os dois igual desqualifica quem ninguém entrevistou. Resposta com evidência (quem disse, quando) vale mais do que a opinião de quem vendeu.',
  'leads.framework': 'Framework',
  'leads.evidencia': 'Quem disse isso, e quando',
  'leads.salvarQualificacao': 'Salvar qualificação',
  'leads.qualificadoOk': 'Qualificação registrada.',
  'leads.erroQualificar': 'Não foi possível registrar a qualificação.',
  'leads.converterTitulo': 'Converter em oportunidade',
  'leads.converterDescricao':
    'Só depois de o critério do framework ser atendido. Um lead promovido sem isso infla o funil com o que nunca teve chance — e o forecast paga a conta três meses depois.',
  'leads.tituloDaOportunidade': 'Nome do negócio',
  'leads.valorEstimado': 'Valor estimado',
  'leads.fechamentoPrevisto': 'Fechamento previsto',
  'leads.convertidoOk':
    'Lead convertido. O briefing inteiro foi junto — inclusive o que não foi perguntado.',
  'leads.erroConverter': 'Não foi possível converter o lead.',
  'leads.fecharConta': 'Fechar a conta',
  'leads.verScore': 'Ver como o score saiu',
  'cs.registrarSinal': 'Registrar sinal de expansão',
  'cs.registrar': 'Registrar',
  'cs.sinalDescricao':
    '“Eles perguntaram do módulo fiscal” ainda não é negócio: fica registrado aqui até alguém decidir. Criar oportunidade a cada comentário encheria o funil do que ninguém conduz.',
  'cs.conta': 'Conta',
  'cs.escolhaConta': 'Escolha a conta…',
  'cs.buscarConta': 'Buscar por nome da empresa…',
  'cs.oQueAconteceu': 'O que aconteceu',
  'cs.origemDoSinal': 'De onde veio',
  'cs.valorEstimado': 'Valor estimado (opcional)',
  'cs.sinalOk': 'Sinal registrado. Ele fica aberto até alguém converter ou descartar com motivo.',
  'cs.erroSinal': 'Não foi possível registrar o sinal.',
  'cs.converterSinal': 'Virar oportunidade',
  'cs.descartarSinal': 'Descartar',
  'cs.motivoDoDescarte': 'Por que não virou negócio?',
  'cs.descartadoOk': 'Sinal descartado, com o motivo registrado.',
  'cs.convertidoOk': 'Sinal convertido em oportunidade.',
  'cs.erroDecidir': 'Não foi possível concluir.',
  'cs.assumir': 'Assumir esta entrega',
  'cs.assumidaOk': 'Entrega assumida. O onboarding começou com os marcos do contrato.',
  'cs.erroAssumir': 'Não foi possível assumir a entrega.',
  'receita.definirMetaAtalho': 'Definir a meta do período',
  'leads.cadastrarEmpresa': 'Cadastrar empresa',
  'agentesUso.titulo': 'Agentes',
  'agentesUso.subtitulo':
    'Quem faz o quê — e como pedir. Cada agente opera um conjunto fechado de ferramentas.',
  'agentesUso.comoFunciona':
    'Você conversa com o gerente, e ele chama o especialista certo. Toda ação que muda dado passa por confirmação sua antes de acontecer, e o que foi feito fica registrado com autor e horário.',
  'agentesUso.foraDoAr': 'A camada de inteligência está fora do ar.',
  'agentesUso.foraDoArComplemento':
    'Enquanto isso, o assistente recusa em vez de inventar resposta — e o resto do sistema continua funcionando normalmente.',
  'agentesUso.oQuePodeFazer': 'O que ele pode fazer',
  'agentesUso.assumeAConversa':
    'Este assume a conversa quando entra: o que ele traz vem de fora do CRM, e vem com as fontes.',
  'agentesUso.conversarCom': 'Conversar com',
  'agentesUso.entenderMelhor': 'Entender melhor como o assistente funciona',
  'nav.agentes': 'Agentes',
  'nav.grupoHoje': 'Hoje',
  'nav.grupoClientes': 'Clientes',
  'nav.grupoNegocios': 'Negócios',
  'nav.grupoRelacionamento': 'Relacionamento',
  'nav.grupoConhecimento': 'Conhecimento',
  'nav.grupoResultado': 'Resultado',
  'nav.grupoCanais': 'Canais',
  'cs.fontes': 'De onde vem a saúde do cliente',
  'cs.fontesAjuda':
    'Cada fator diz de qual sistema ele vem. Fator sem fonte conectada NÃO conta como zero: ele sai da conta, e o peso dele aparece aqui — é isso que separa "o cliente está mal" de "não temos como saber".',
  'cs.fator': 'Fator',
  'cs.peso': 'Peso',
  'cs.fonte': 'Fonte',
  'cs.conectada': 'Conectada',
  'cs.naoConectada': 'Falta conectar',
  'cs.menosEMelhor': 'quanto menos, melhor',
  'cadencias.nova': 'Nova cadência',
  'cadencias.novaDescricao':
    'Uma cadência é a sequência que o sistema vai executar sozinho. Escreva o que ela quer conseguir, e depois as etapas na ordem em que acontecem.',
  'cadencias.campoChave': 'Identificador',
  'cadencias.chaveAjuda':
    'Letras minúsculas, números e hífen. Ele acompanha a cadência para sempre — inclusive no histórico de quem já foi inscrito.',
  'cadencias.campoNome': 'Nome',
  'cadencias.campoObjetivo': 'O que esta cadência quer conseguir',
  'cadencias.objetivoAjuda':
    'Sem objetivo escrito, ninguém sabe quando ela venceu — e a cadência vira mensagem por mensagem.',
  'cadencias.etapas': 'Etapas',
  'cadencias.acrescentarEtapa': 'Acrescentar etapa',
  'cadencias.removerEtapa': 'Remover esta etapa',
  'cadencias.espera': 'Espera (dias úteis)',
  'cadencias.esperaAjuda':
    'Contada a partir da etapa anterior. Zero só na primeira: depois dela, zero faz duas mensagens saírem juntas.',
  'cadencias.canal': 'Como',
  'cadencias.assunto': 'Assunto',
  'cadencias.texto': 'Texto',
  'cadencias.textoAjuda':
    'Aceita {{nome}} e {{empresa}}, resolvidos na hora de enviar. Ligação e tarefa viram lembrete para uma pessoa: aí o texto é opcional.',
  'cadencias.publicar': 'Publicar cadência',
  'cadencias.publicadaOk':
    'Cadência publicada. Quem já está inscrito continua na versão em que entrou.',
  'cadencias.erroPublicar': 'Não foi possível publicar a cadência.',
  'cadencias.canalEmail': 'E-mail',
  'cadencias.canalWhatsapp': 'WhatsApp',
  'cadencias.canalLigacao': 'Ligação (lembrete para você)',
  'cadencias.canalTarefa': 'Tarefa (lembrete para você)',
  'propostas.titulo': 'Propostas',
  'propostas.subtitulo': 'O que está com o cliente agora, com a validade à vista.',
  'propostas.vazioTexto':
    'As propostas nascem da oportunidade — empresa, contato e valores já estão lá.',
  'propostas.soComCliente': 'Só as que estão com o cliente',
  'propostas.validaAte': 'Válida até',
  'contratos.titulo': 'Contratos',
  'contratos.subtitulo': 'O que espera assinatura, e o que vence antes de você lembrar.',
  'contratos.vigencia': 'Vigência',
  'cadencias.titulo': 'Cadências',
  'cadencias.subtitulo': 'O que o sistema está falando com o cliente, e em que ponto.',
  'cadencias.vazioTexto':
    'A cadência de acompanhamento começa sozinha quando uma proposta é enviada.',
  'nav.comissoes': 'Comissões',
  'comissoes.titulo': 'Comissões',
  'comissoes.subtitulo': 'O que você tem a receber, com a conta à vista — e não só o total.',
  'comissoes.abaMeu': 'Meu extrato',
  'comissoes.abaEquipe': 'Da equipe',
  'comissoes.abaPlanos': 'Planos',
  'comissoes.abaFinanceiro': 'Financeiro',
  'comissoes.fin.explicacao': 'Recebimentos das vendas desta organização, a comissão que nasce deles e o repasse aos parceiros. Não é a cobrança da assinatura do CERES.',
  'comissoes.fin.resumoTitulo': 'Comissão por parceiro',
  'comissoes.fin.resumoVazio': 'Nenhum parceiro com recebimento previsto ou comissão ainda.',
  'comissoes.fin.parceiro': 'Parceiro',
  'comissoes.fin.prevista': 'Prevista',
  'comissoes.fin.devida': 'Devida',
  'comissoes.fin.paga': 'Paga',
  'comissoes.fin.condicoesTitulo': 'Condições de pagamento da venda',
  'comissoes.fin.buscarVenda': 'Buscar venda',
  'comissoes.fin.venda': 'Venda',
  'comissoes.fin.escolha': 'Escolha…',
  'comissoes.fin.valorTotal': 'Valor total',
  'comissoes.fin.recorrencia': 'Recorrência',
  'comissoes.fin.mensal': 'Mensal',
  'comissoes.fin.unica': 'Parcela única',
  'comissoes.fin.parcelas': 'Parcelas',
  'comissoes.fin.primeiroVencimento': 'Primeiro vencimento',
  'comissoes.fin.ciclo': 'Ciclo',
  'comissoes.fin.cicloDica': '0 = contrato inicial; 1 = primeira renovação; 2 = segunda…',
  'comissoes.fin.renovacaoAutomatica': 'Renovação automática',
  'comissoes.fin.gerarParcelas': 'Gerar recebimentos previstos',
  'comissoes.fin.condicoesCriadas': 'Recebimentos previstos criados.',
  'comissoes.fin.parcelasTitulo': 'Recebimentos',
  'comissoes.fin.parcelasVazio': 'Nenhum recebimento previsto.',
  'comissoes.fin.parcela': 'Parcela',
  'comissoes.fin.confirmarRecebimento': 'Confirmar recebimento',
  'comissoes.fin.valorRecebido': 'Valor recebido',
  'comissoes.fin.recebidoEm': 'Recebido em',
  'comissoes.fin.referencia': 'Referência',
  'comissoes.fin.confirmar': 'Confirmar',
  'comissoes.fin.cancelar': 'Cancelar',
  'comissoes.fin.recebimentoConfirmado': 'Recebimento confirmado.',
  'comissoes.fin.estornar': 'Estornar',
  'comissoes.fin.estornado': 'estornado',
  'comissoes.fin.motivoDoEstorno': 'Motivo do estorno',
  'comissoes.fin.motivoDica': 'Ao menos 10 caracteres.',
  'comissoes.fin.recebimentoEstornado': 'Recebimento estornado; a comissão recebeu contra-lançamento.',
  'comissoes.fin.repassesTitulo': 'Repasses aos parceiros',
  'comissoes.fin.semAprovados': 'Nenhuma comissão aprovada para este parceiro.',
  'comissoes.fin.pagoEm': 'Pago em',
  'comissoes.fin.registrarRepasse': 'Registrar repasse',
  'comissoes.fin.repasseRegistrado': 'Repasse registrado.',
  'comissoes.fin.repassesVazio': 'Nenhum repasse registrado.',
  'comissoes.fin.lancamentos': 'lançamento(s)',
  'comissoes.fin.erro': 'Não foi possível concluir.',
  'comissoes.fin.situacao.prevista': 'Prevista',
  'comissoes.fin.situacao.parcial': 'Recebida em parte',
  'comissoes.fin.situacao.recebida': 'Recebida',
  'comissoes.fin.situacao.cancelada': 'Cancelada',
  'comissoes.competencia': 'Competência',
  'comissoes.simular': 'Simular comissão',
  'comissoes.simularDescricao':
    'Aplica o plano vigente hoje ao valor que você digitar. Não cria lançamento e não promete valor: o que vale é o plano vigente na data da assinatura.',
  'comissoes.valorDoNegocio': 'Valor do negócio',
  'comissoes.seuPapel': 'Seu papel no negócio',
  'comissoes.calcular': 'Calcular',
  'comissoes.resultado': 'Você receberia',
  'comissoes.simulacaoNaoVincula':
    'Simulação. O lançamento nasce na assinatura do contrato, com o plano que estiver vigente naquele dia.',
  'comissoes.erroSimular': 'Não foi possível simular.',
  'comissoes.verMemoria': 'Ver a memória de cálculo',
  'comissoes.memoria': 'Memória de cálculo',
  'comissoes.memoriaDescricao':
    'A conta como ela foi feita, guardada no dia do fato. É o que transforma conferir em possível: sem ela, o valor é um número que se aceita.',
  'comissoes.memoriaDoDia': 'Calculada em',
  'comissoes.sobre': 'sobre',
  'comissoes.statusPrevista': 'Prevista',
  'comissoes.statusDevida': 'A receber',
  'comissoes.statusPaga': 'Paga',
  'comissoes.statusEstornada': 'Estornada',
  'comissoes.estornoPorque': 'Estornada:',
  'comissoes.pagaEm': 'Paga em',
  'comissoes.marcarParaBaixa': 'Incluir na baixa',
  'comissoes.darBaixa': 'Registrar pagamento',
  'comissoes.selecionarTudo': 'Marcar tudo o que está a receber',
  'comissoes.referenciaDoPagamento': 'Referência do pagamento',
  'comissoes.baixaAjuda':
    'A baixa registra que o dinheiro saiu — quem registrou e quando. Ela não paga: quem paga é a folha.',
  'comissoes.baixaFeita': 'lançamento(s) baixado(s), somando',
  'comissoes.baixaParcial': 'Baixa parcial:',
  'comissoes.baixaNaoPagos': 'não pago(s). Veja os motivos abaixo.',
  'comissoes.baixaNenhuma': 'Nenhum lançamento foi pago. Veja os motivos abaixo.',
  'comissoes.naoPagosTitulo': 'Não pagos nesta baixa',
  'comissoes.erroPagar': 'Não foi possível registrar o pagamento.',
  'comissoes.estornar': 'Estornar',
  'comissoes.estornarBotao': 'Estornar com contra-lançamento',
  'comissoes.estornarDescricao':
    'O estorno NÃO apaga: entra uma linha nova, com valor negativo, e a soma das duas é zero. É o único jeito de "a comissão foi estornada" e "a comissão nunca existiu" continuarem sendo coisas diferentes — e a primeira é a que alguém vai questionar.',
  'comissoes.motivoDoEstorno': 'Por que está estornando',
  'comissoes.motivoEstornoAjuda':
    'Contrato desfeito, valor corrigido, participante errado. Quem conferir o extrato depois lê isto.',
  'comissoes.estornoOk':
    'Estorno registrado. A linha original continua no extrato, com o contra-lançamento ao lado.',
  'comissoes.erroEstornar': 'Não foi possível estornar.',
  'comissoes.colPessoa': 'Pessoa',
  'comissoes.colPrevisto': 'Previsto para',
  'comissoes.colDevido': 'A receber',
  'comissoes.colPago': 'Pago',
  'comissoes.colTotal': 'Total',
  'comissoes.calculadaSobre': 'Calculada sobre',
  'comissoes.baseContrato': 'o valor do contrato',
  'comissoes.gatilho': 'vale a partir da',
  'comissoes.gatilhoAssinatura': 'assinatura',
  'comissoes.piso': 'mínimo',
  'comissoes.teto': 'máximo',
  'comissoes.vazio': 'Nenhuma comissão neste período',
  'comissoes.vazioOQueE':
    'A comissão é calculada no dia em que o contrato é assinado, com a memória de cálculo daquele dia: qual plano valia, qual a base e como se chegou ao valor. O fechamento do mês vira uma soma, e não um cálculo refeito semanas depois.',
  'comissoes.vazioExemplo':
    'Um contrato de R$ 200.000 assinado hoje, com plano de 5% para quem vende, gera um lançamento de R$ 10.000 — a receber, com a conta anexada. Se o contrato for desfeito, entra um estorno de R$ 10.000 negativos, e a linha original fica.',
  'comissoes.semPlano': 'Nenhum plano de comissão publicado',
  'comissoes.semPlanoOQueE':
    'O plano define quanto cada papel recebe, sobre qual base e a partir de qual gatilho. Sem plano vigente, nenhuma assinatura gera comissão — e ninguém descobre isso no fim do mês.',
  'comissoes.semPlanoExemplo':
    'Vendedor 5% sobre o valor do contrato, a partir da assinatura; SDR 1%, com mínimo de R$ 200. Publicar uma versão nova não muda o que já foi apurado.',
  'comissoes.pedirRotulo': 'Perguntar ao assistente sobre a minha comissão',
  'comissoes.pedirPedido': 'Quanto tenho de comissão a receber, e de quais negócios?',
  'cadencias.abaVivas': 'Em andamento',
  'cadencias.abaTodas': 'Todas',
  'cadencias.abaDesempenho': 'Funciona?',
  'cadencias.atualizada': 'Cadência atualizada.',
  'cadencias.contatoDesconhecido': 'Contato sem nome',
  'cadencias.verHistorico': 'Ver o histórico',
  'cadencias.pausar': 'Pausar',
  'cadencias.retomar': 'Retomar',
  'cadencias.historico': 'O que já aconteceu nesta cadência',
  'cadencias.historicoDescricao':
    'Tudo o que a cadência fez com esta pessoa, em ordem: as mensagens, as tarefas preparadas, as decisões de quem pausou ou encerrou, e as respostas dela. No fim, o que vem a seguir e quando.',
  'cadencias.etapasCongeladas': 'A sequência como estava no dia da inscrição',
  'cadencias.eventoInicio': 'Início',
  'cadencias.eventoMensagem': 'Mensagem',
  'cadencias.eventoTarefa': 'Tarefa',
  'cadencias.eventoResposta': 'Resposta do cliente',
  'cadencias.eventoDecisao': 'Decisão',
  'cadencias.eventoFim': 'Fim',
  'cadencias.eventoProxima': 'A seguir',
  'cadencias.pausarTitulo': 'Pausar a cadência',
  'cadencias.pausarDescricao':
    'Pausa com data volta a andar sozinha. Sem data, alguém precisa lembrar de retomar — e é assim que uma conversa some por meses.',
  'cadencias.pausarAte': 'Voltar a falar depois de',
  'cadencias.pausarAteAjuda':
    'Sem data, a pausa não tem prazo e a cadência só volta quando alguém retomar.',
  'cadencias.pausarBotao': 'Confirmar a pausa',
  'cadencias.encerrarTitulo': 'Encerrar a cadência',
  'cadencias.encerrarDescricao':
    'Encerrar para de falar com esta pessoa nesta sequência. O motivo fica junto: é ele que responde, um mês depois, por que paramos.',
  'cadencias.encerrarMotivo': 'Por que estamos parando',
  'cadencias.encerrarBotao': 'Encerrar a cadência',
  'cadencias.colCadencia': 'Cadência',
  'cadencias.colInscricoes': 'Inscrições',
  'cadencias.colAndamento': 'Em andamento',
  'cadencias.colResponderam': 'Responderam',
  'cadencias.colSemResposta': 'Terminaram sem resposta',
  'cadencias.colOptOut': 'Pediram para não receber',
  'cadencias.colParadas': 'Paradas por alguém',
  'cadencias.colTaxa': 'Taxa de resposta',
  'cadencias.foraDeUso': '(fora de uso)',
  'cadencias.desempenhoVazio': 'Nenhuma inscrição no período',
  'cadencias.desempenhoVazioOQueE':
    'Aqui se compara o que cada cadência produziu: quantas pessoas entraram, quantas responderam durante a sequência, quantas pediram para não receber mais e quantas foram paradas por alguém da equipe.',
  'cadencias.desempenhoVazioExemplo':
    'Uma cadência de cinco toques com resposta alta e opt-out alto não é uma cadência boa: é uma que queima base. As duas colunas aparecem lado a lado por isso.',
  'cadencias.encerrar': 'Encerrar',
  'cadencias.disponiveis': 'Cadências disponíveis',
  'caixa.titulo': 'Caixa de entrada',
  'caixa.subtitulo': 'O que saiu, o que não saiu e por quê — que é a pergunta das seis da tarde.',
  'reunioes.titulo': 'Reuniões e conversas',
  'reunioes.subtitulo':
    'O que ficou combinado, com o trecho em que foi dito. Nada vira tarefa sem alguém revisar.',
  'reunioes.vazio': 'Nenhuma conversa registrada',
  'reunioes.vazioTexto':
    'Registre a reunião e envie a transcrição: a fila processa em segundo plano.',
  'reunioes.escolha': 'Escolha uma conversa',
  'reunioes.escolhaTexto': 'Cada item extraído aparece com o trecho e o instante em que foi dito.',
  'reunioes.nadaExtraido': 'Nada foi extraído',
  'reunioes.nadaExtraidoTexto':
    'Nenhuma fala trouxe marcador explícito. Extrair pouco e certo é melhor do que extrair tudo e errado.',
  'reunioes.registradas': 'Conversas registradas',
  'reunioes.coaching': 'Seu retorno de coaching',
  'reunioes.confirmar': 'Confirmar',
  'reunioes.corrigir': 'Corrigir',
  'reunioes.corrigirDescricao':
    'A extração propõe; quem esteve na conversa decide. Corrigir aqui é o que permite confirmar o item — e o trecho em que ele foi dito fica à vista para conferir sem reabrir a gravação.',
  'reunioes.oQueFicouCombinado': 'O que ficou combinado',
  'reunioes.quemFaz': 'Quem faz',
  'reunioes.escolhaResponsavel': 'Escolha a pessoa…',
  'reunioes.buscarPessoa': 'Buscar por nome…',
  'reunioes.ateQuando': 'Até quando',
  'reunioes.salvarCorrecao': 'Salvar a correção',
  'reunioes.itemCorrigido': 'Item corrigido. Agora ele pode ser confirmado.',
  'reunioes.erroCorrigir': 'Não foi possível corrigir.',
  'reunioes.descartar': 'Descartar',
  'reunioes.descartarDescricao':
    'O item sai da lista de propostos e fica registrado como descartado. Some da fila, não do histórico.',
  'reunioes.porQueDescartar': 'Por que está descartando',
  'reunioes.descarteAjuda':
    'A extração entendeu errado, o assunto mudou, já estava combinado antes. Quem revisar a próxima reunião do mesmo cliente lê isto.',
  'reunioes.descartarBotao': 'Descartar o item',
  'reunioes.itemDescartado': 'Item descartado.',
  'reunioes.erroDescartar': 'Não foi possível descartar.',
  'reunioes.reprocessar': 'Tentar de novo',
  'reunioes.reenviada': 'Conversa devolvida à fila.',
  'reunioes.erroReprocessar': 'Não foi possível reenviar.',
  'reunioes.marcarAviso': 'Marcar como avisado',
  'reunioes.avisoRegistrado': 'Ciência registrada.',
  'reunioes.erroAviso': 'Não foi possível registrar o aviso.',
  'reunioes.virouTarefa': 'Virou tarefa no CRM.',
  'cs.titulo': 'Customer Success',
  'cs.subtitulo':
    'O que foi prometido na venda, quem assumiu a entrega e o que o cliente perguntou depois.',
  'cs.semDono': 'Nenhuma entrega sem dono',
  'cs.semDonoTexto': 'Toda venda ganha já tem quem responda pela entrega.',
  'cs.semAndamento': 'Nenhuma entrega em andamento',
  'cs.semAndamentoTexto': 'As entregas assumidas aparecem aqui, com o onboarding aberto.',
  'cs.semSinal': 'Nenhum sinal em aberto',
  'cs.semSinalTexto': 'O que o cliente perguntar em reunião entra aqui.',
  'cs.esperandoDono': 'Esperando dono',
  'cs.prometido': 'O que foi prometido',
  'cs.marcos': 'Marcos do contrato',
  'cs.emAndamento': 'Entregas em andamento',
  'cs.sinaisExpansao': 'Sinais de expansão em aberto',
  'receita.titulo': 'Receita e forecast',
  'receita.subtitulo':
    'O número com a conta ao lado. Pipeline é o teto; ponderado é o modelo; commit é humano.',
  'receita.semRisco': 'Nada em risco no período',
  'receita.semRiscoTexto': 'Toda oportunidade tem próxima ação, decisor e data coerente.',
  'receita.valoresSimular': 'Valores para simular',
  'receita.valorCommit': 'Valor do commit',
  'receita.metaPeriodo': 'Meta do período',
  'receita.comoCalculado': 'Como este número foi calculado',
  'receita.central': 'Central executiva',
  'receita.eSeFecharmos': 'E se fecharmos (valores separados por vírgula)',
  'receita.simular': 'Simular',
  'receita.assinarCommit': 'Assinar o commit do período',
  'receita.definirMeta': 'Definir meta',
  'relatorios.titulo': 'Relatórios',
  'relatorios.subtitulo': 'Funil por coorte, ciclo típico e por que os negócios terminam.',
  'relatorios.ganhas': 'Ganhas',
  'relatorios.perdidas': 'Perdidas',
  'relatorios.taxaVitoria': 'Taxa de vitória',
  'relatorios.taxaExplicacao': 'Sobre o que FECHOU. As abertas ainda não ganharam nem perderam.',
  'relatorios.cicloTipico': 'Ciclo típico',
  'relatorios.medianaNaoMedia': 'Mediana, e não média.',
  'relatorios.funil': 'Funil',
  'relatorios.passaram': 'Passaram',
  'relatorios.estaoAgora': 'Estão agora',
  'relatorios.avancaram': 'Avançaram',
  'relatorios.emAberto': 'Em aberto',
  'relatorios.porquePerdemos': 'Por que perdemos',
  'relatorios.semPerdaComMotivo': 'Nenhuma perda com motivo no período.',
  'relatorios.porqueGanhamos': 'Por que ganhamos',
  'relatorios.semGanhoComMotivo': 'Nenhum ganho com motivo no período.',
  'playbooks.titulo': 'Playbooks',
  'playbooks.subtitulo': 'O argumento que vale — e só o aprovado, dentro da validade, responde.',
  'playbooks.vazio': 'Nenhum playbook',
  'playbooks.vazioTexto':
    'O playbook de uma oferta vence o da casa; o da casa existe para o que não tem playbook próprio.',
  'playbooks.pitch': 'Pitch curto',
  'playbooks.objecoes': 'Objeções e respostas',
  'playbooks.sinaisRisco': 'Sinais de risco',
  // CERES-029 — entrada, wizard, áreas do projeto, execução e monitoramento.
  'cs.tituloDaConta': 'Pós-venda',
  'cs.recalcular': 'Recalcular a saúde',
  'cs.saudeRecalculada': 'Saúde recalculada com os sinais de hoje.',
  'cs.semAvaliacao':
    'Esta conta ainda não foi avaliada. Registre um sinal e recalcule para ter a primeira nota.',
  'cs.avaliadaEm': 'Avaliada em',
  'cs.faixaSaudavel': 'Saudável',
  'cs.faixaAtencao': 'Atenção',
  'cs.faixaRisco': 'Risco',
  'cs.registrarSinalDeSaude': 'Registrar um sinal',
  'cs.registrarSinalBotao': 'Registrar',
  'cs.sinalDeSaudeDescricao':
    'Sinais são o que a nota de saúde soma: uso do produto, NPS, chamados abertos, atraso de pagamento. Cada um com a data em que foi observado.',
  'cs.qualSinal': 'Qual sinal',
  'cs.valorDoSinal': 'Valor (0 a 100)',
  'cs.quandoFoiObservado': 'Quando foi observado',
  'cs.dataAjuda':
    'Um NPS de dezembro não descreve o cliente de agosto. Sem data, o sinal entra como de hoje — e é assim que o score passa a mentir.',
  'cs.fonteDoSinal': 'De onde veio (opcional)',
  'cs.sinalRegistrado': 'Sinal registrado.',
  'cs.onboarding': 'Onboarding',
  'cs.concluirEtapa': 'Concluir',
  'cs.concluidaEm': 'concluída em',
  'cs.etapaConcluida':
    'Etapa concluída. O status do onboarding vem das etapas — ele fecha quando todas fecharem.',
  'cs.planoDeSucesso': 'Plano de sucesso',
  'cs.semObjetivos':
    'Nenhum objetivo combinado com o cliente. Sem plano, a renovação vira uma conversa sobre preço.',
  'cs.novoObjetivo': 'Novo objetivo',
  'cs.criarObjetivo': 'Acrescentar ao plano',
  'cs.objetivoDescricao':
    'O que o cliente quer alcançar com o produto, e como se saberá que aconteceu. As duas coisas são separadas de propósito.',
  'cs.oQueSeQuer': 'O que o cliente quer alcançar',
  'cs.comoSeSabeCampo': 'Como saberemos que aconteceu',
  'cs.comoSeSabe': 'Como saberemos:',
  'cs.resultadoAjuda':
    '"Melhorar a adoção" não é verificável na renovação. "80% dos usuários entrando ao menos uma vez por semana" é.',
  'cs.ateQuando': 'Até quando',
  'cs.prazo': 'Prazo:',
  'cs.concluirObjetivo': 'Marcar como alcançado',
  'cs.objetivoCriado': 'Objetivo acrescentado ao plano.',
  'cs.objetivoConcluido': 'Objetivo marcado como alcançado.',
  'cs.erro': 'Não foi possível concluir.',
  'aprovacoes.titulo': 'Aprovações',
  'aprovacoes.vazio': 'Nada esperando você',
  'aprovacoes.vazioTexto': 'Nenhuma ação acima da alçada está pendente nesta organização.',
  'aprovacoes.motivoRecusa': 'Motivo da recusa',
  'aprovacoes.motivoAjuda':
    'Quem pediu recebe este texto. Sem ele, a pessoa repete o pedido amanhã.',
  'aprovacoes.voltar': 'Voltar',
  'aprovacoes.recusar': 'Recusar',
  'assistente.converse': 'Converse com o Assistente Comercial',
  'assistente.converseTexto':
    'Peça em português: cadastrar empresa, criar oportunidade, registrar reunião ou marcar um follow-up.',
  'assistente.conversas': 'Conversas',
  'assistente.nova': 'Nova',
  'assistente.semConversa': 'Nenhuma conversa ainda. Escreva um pedido ao lado para começar.',
  'assistente.falhaDesconhecida':
    'Não sei se o pedido foi concluído: o servidor demorou demais para responder ou a conexão caiu. Confira no CRM antes de repetir, para não duplicar.',
  'assistente.falhaSemResposta':
    'O servidor não respondeu, e não sei se o pedido chegou a ser processado. Consulte a operação ou confira no CRM antes de repetir.',
  'assistente.falhaContinuePelasTelas':
    'Você pode continuar pelas telas normalmente.',
  'assistente.falhaGenerica':
    'O assistente está indisponível agora. O CRM continua funcionando pelas telas.',
  'assistente.confirmeComOTermo':
    'Ação difícil de desfazer: para confirmar, digite no campo abaixo',
  'assistente.historicoDaConversa':
    'Conversa com o Assistente Comercial',
  'assistente.emProcessamento':
    'Este pedido ainda está sendo processado. Aguarde um instante e consulte o resultado antes de repetir.',
  'assistente.nome': 'Assistente Comercial',
  'assistente.nomeCurto': 'Assistente',
  'assistente.abrir': 'Abrir o Assistente Comercial',
  'assistente.fechar': 'Fechar o Assistente Comercial',
  'assistente.conversaEmAndamento': 'conversa em andamento',
  'assistente.historico': 'Histórico',
  'assistente.novaConversa': 'Nova conversa',
  'assistente.semTitulo': 'Conversa sem título',
  'assistente.consultarOperacao': 'Consultar a operação',
  'assistente.emConferencia': 'Em conferência: não repita antes de consultar.',
  'assistente.executando': 'Executando no CRM…',
  'assistente.entendendo': 'Entendendo o pedido…',
  'assistente.feedbackAjudou': 'Ajudou',
  'assistente.feedbackNaoAjudou': 'Não ajudou',
  'assistente.feedbackPergunta': 'O que estava errado, e o que seria o certo?',
  'assistente.feedbackEnviar': 'Enviar avaliação',
  'assistente.feedbackDepois': 'Deixar para depois',
  'assistente.passoConcluido': 'Concluído',
  'assistente.passoFalhou': 'Falhou',
  'assistente.passoPulado': 'Pulado',
  'assistente.passoPendente': 'Pendente',
  'assistente.passoNaoTentado': 'Não tentado',
  'assistente.passoEmConferencia': 'Em conferência',
  'parceiros.titulo': 'Parceiros comerciais',
  'parceiros.subtitulo': 'Canais, representantes, contrato e uso da cota de contas reservadas.',
  'parceiros.buscar': 'Buscar parceiro ou CNPJ…',
  'parceiros.situacaoContratual': 'Situação contratual',
  'parceiros.vazio': 'Nenhum parceiro',
  'parceiros.vazioTexto':
    'Cadastre representantes, canais, indicadores ou empresas que originam e apoiam negócios.',
  'parceiros.todasSituacoes': 'Todas as situações',
  'parceiros.parceiro': 'Parceiro',
  'parceiros.contratoVigente': 'Contrato vigente',
  'parceiros.responsavelInterno': 'Responsável interno',
  'parceiros.carteira': 'Carteira',
  'parceiros.reservas': 'Reservas',
  'parceiro.carregando': 'Carregando parceiro…',
  'parceiro.razaoSocial': 'Razão social',
  'parceiro.tier': 'Tier',
  'parceiro.emailContato': 'E-mail de contato',
  'parceiro.cadastradoEm': 'Cadastrado em',
  'parceiro.contasCarteira': 'Contas na carteira',
  'parceiro.reservasVigentes': 'Reservas vigentes',
  'parceiro.cadastro': 'Cadastro',
  'parceiro.notasInternas': 'Notas internas',
  'parceiro.contratos': 'Contratos de parceria',
  'parceiro.semContrato': 'Nenhum contrato cadastrado.',
  'parceiro.codigo': 'Código',
  'parceiro.exclusividade': 'Exclusividade',
  'parceiro.territorio': 'Território',
  'parceiro.semUsuario': 'Nenhum usuário vinculado.',
  'parceiro.usoCota': 'Uso da cota',
  'parceiro.limitePolitica': 'Limite definido pela política vigente.',
  'parceiro.regrasVigentes': 'Regras vigentes',
  'reservas.solicitar': 'Solicitar reserva de conta',
  'reservas.solicitarTexto':
    'A reserva protege a conta contra prospecção independente após a aprovação.',
  'reservas.empresaEscopo': 'Empresa e escopo',
  'reservas.buscarEmpresa': 'Buscar empresa…',
  'reservas.escopo': 'Escopo da reserva',
  'reservas.detalheEscopo': 'Detalhe do escopo',
  'reservas.detalhePlaceholder': 'Unidade, departamento ou projeto',
  'reservas.contribuicao': 'Contribuição e evidência',
  'reservas.justificativaTexto':
    'A justificativa é obrigatória e faz parte da trilha de auditoria.',
  'reservas.modalidade': 'Modalidade de contribuição',
  'reservas.justificativa': 'Justificativa',
  'reservas.origemDocumentada': 'Origem documentada',
  'reservas.origemPlaceholder': 'Como a oportunidade chegou até você',
  'reservas.titulo': 'Reservas de conta',
  'reservas.subtitulo': 'Solicitação, aprovação, ativação, atualização, prorrogação e liberação.',
  'reservas.filtrarEstado': 'Filtrar por estado',
  'reservas.filtrarParceiro': 'Filtrar por parceiro',
  'reservas.vazio': 'Nenhuma reserva',
  'reservas.vazioTexto': 'As reservas de conta protegem o trabalho do canal por prazo determinado.',
  'reservas.selecioneEmpresa': 'Selecione a empresa…',
  'reservas.selecione': 'Selecione…',
  'reservas.solicitarBotao': 'Solicitar reserva',
  'reservas.emAndamento': 'Em andamento',
  'reservas.todosEstados': 'Todos os estados',
  'reservas.todosCanais': 'Todos os canais',
  'reservas.estado': 'Estado',
  'reservas.solicitada': 'Solicitada',
  'reservas.prazos': 'Prazos',
  'reservas.alertas': 'Alertas',
  'reserva.carregando': 'Carregando reserva…',
  'reserva.solicitadaEm': 'Solicitada em',
  'reserva.solicitadaPor': 'Solicitada por',
  'reserva.contribuicao': 'Contribuição',
  'reserva.decididaEm': 'Decidida em',
  'reserva.decididaPor': 'Decidida por',
  'reserva.contrato': 'Contrato',
  'reserva.ativarAte': 'Ativar até',
  'reserva.ativadaEm': 'Ativada em',
  'reserva.venceEm': 'Vence em',
  'reserva.proximaAtualizacao': 'Próxima atualização',
  'reserva.protecaoResidualAte': 'Proteção residual até',
  'reserva.prorrogacoes': 'Prorrogações',
  'reserva.duracao': 'Duração da reserva',
  'reserva.prazoAtivacao': 'Prazo de ativação',
  'reserva.intervaloAtualizacao': 'Intervalo de atualização',
  'reserva.protecaoResidual': 'Proteção residual',
  'reserva.prazoProrrogacao': 'Prazo de prorrogação',
  'reserva.sobreposicao': 'Sobreposição detectada — decisão humana necessária',
  'reserva.justificativa': 'Justificativa',
  'reserva.origemDocumentada': 'Origem documentada',
  'reserva.notasDecisao': 'Notas da decisão (interno)',
  'reserva.historico': 'Histórico da reserva',
  'reserva.prazos': 'Prazos',
  'reserva.regraAplicada': 'Regra aplicada',
  'reserva.excedeuLimite':
    'O parceiro excedeu o limite de contas reservadas. Confirmo a aprovação excepcional.',
  'reserva.conflitoAtribuicao':
    'Há conflito de atribuição registrado. Confirmo a aprovação com a justificativa acima.',
  'perfil.titulo': 'Meu perfil',
  'perfil.subtitulo':
    'Seus dados neste CRM. Alterações de perfil e permissão são feitas por quem administra usuários.',
  'perfil.seguranca': 'Segurança',
  'perfil.trocarSenha': 'Trocar a senha',
  'naoEncontrada.titulo': 'Página não encontrada',
  'naoEncontrada.subtitulo': 'Não encontramos esta página',
  'naoEncontrada.voltar': 'Voltar ao painel',
  'ajuda.titulo': 'Central de Ajuda',
  'ajuda.subtitulo':
    'Como usar o CRM no dia a dia, o que cada regra significa e o que fazer quando algo não sai como esperado.',
  'ajuda.buscar': 'Buscar na ajuda: contato sem empresa, etapa recusada, reserva…',
  'ajuda.nadaEncontrado': 'Nada encontrado para esse termo',
  'ajuda.primeiraVez': 'Primeira vez aqui?',
  'ajuda.limparBusca': 'Limpar busca',
  'artigo.naoEncontrado': 'Artigo não encontrado',
  'artigo.naoExiste': 'Este artigo de ajuda não existe',
  'artigo.naoExisteTexto': 'O endereço pode ter mudado. Volte à Central de Ajuda e use a busca.',
  'artigo.trilha': 'Trilha',
  'artigo.nestaPagina': 'Nesta página',
  'artigo.relacionados': 'Relacionados',
  'artigo.irParaAjuda': 'Ir para a Central de Ajuda',
  'artigo.centralAjuda': 'Central de Ajuda',
  'artigo.foraDoPlano':
    'Este artigo descreve um recurso que não faz parte do seu plano atual. O conteúdo fica disponível para consulta, mas as telas citadas não aparecem para você.',
  'entrar.email': 'E-mail',
  'entrar.senha': 'Senha',
  'entrar.esqueciSenha': 'Esqueci minha senha',
  'entrar.tenhoLink': 'Tenho um link de redefinição',
  'redefinir.token': 'Token do link',
  'redefinir.novaSenha': 'Nova senha',
  'redefinir.confirmeNova': 'Confirme a nova senha',
  'redefinir.titulo': 'Redefinir senha',
  'redefinir.texto': 'Informe o link recebido e escolha uma nova senha.',
  'redefinir.voltarLogin': 'Voltar para o login',
  'redefinir.tokenDica': 'Preenchido pelo link recebido; pode ser colado manualmente.',
  'conectar.titulo': 'Conectar agente externo',
  'conectar.texto': 'Um assistente pediu acesso para operar o CERES em seu nome.',
  'conectar.semParametros':
    'Este endereço só funciona quando aberto por um conector OAuth (parâmetros ausentes).',
  'conectar.carregando': 'Verificando o pedido do conector…',
  'conectar.pedeAcesso': 'pede autorização para operar o CERES como VOCÊ, com as suas permissões:',
  'conectar.oQueSignifica': 'O que isso significa',
  'conectar.aviso':
    'O agente terá exatamente as suas permissões — nada além delas. Toda alteração continua passando por prévia e confirmação, fica registrada na auditoria como ação sua via conector, e você pode revogar este acesso a qualquer momento com um administrador.',
  'conectar.semPermissao':
    'Seu perfil não tem a permissão de conectar agentes externos (agent:connector). Fale com um administrador.',
  'conectar.autorizar': 'Autorizar',
  'conectar.recusar': 'Recusar',
  'conectar.destinoAviso': 'Ao decidir, você voltará para',
  'conectar.erroGenerico': 'Não foi possível concluir. Tente novamente.',
  'convite.titulo': 'Bem-vindo ao CERES CRM',
  'convite.texto': 'Você foi convidado. Defina sua senha para concluir o primeiro acesso.',
  'convite.tokenDica': 'Preenchido pelo link do convite; pode ser colado manualmente.',
  'convite.escolhaSenha': 'Escolha sua senha',
  'convite.botao': 'Definir senha e concluir',
  'convite.sucesso': 'Senha definida. Entre com seu e-mail e a nova senha.',
  'trocarSenha.atual': 'Senha atual (a que você recebeu)',
  'trocarSenha.nova': 'Nova senha',
  'trocarSenha.repita': 'Repita a nova senha',
  'trocarSenha.titulo': 'Troque sua senha para continuar',
  'trocarSenha.sair': 'Sair',
  'segundoFator.codigoApp': 'Código do aplicativo',
  'segundoFator.codigoRecuperacao': 'Código de recuperação',
  'segundoFator.confirme': 'Confirme que é você',
  'segundoFator.sairOutraConta': 'Sair e entrar com outra conta',
  'aceite.idiomaDocumentos': 'Idioma dos documentos',
  'aceite.antesDeContinuar': 'Antes de continuar',
  'aceite.semPendentes': 'Não há documentos pendentes.',
  'aceite.liEAceito': 'Li e aceito',
  'aceite.sairSemAceitar': 'Sair sem aceitar',
  'aceite.marqueCada': 'Marque o aceite de cada documento para continuar.',
  'aceite.versao': 'versão',
  'admin.titulo': 'Administração',
  'nav.playbook': 'Playbook',
  'nav.parceiros': 'Parceiros',
  'nav.reservas': 'Reservas de conta',
  'nav.modCrm': 'CRM',
  'nav.modEnablement': 'Enablement',
  'nav.modProdutividade': 'Produtividade',
  'nav.modPropostas': 'Propostas e Fechamento',
  'nav.modAnalytics': 'Analytics',
  'nav.modCeresAi': 'Ceres AI',
  'nav.modAutomacao': 'Automação',
  'nav.modIntegracoes': 'Integrações',
  'nav.modLegado': 'Legado',
  'nav.expandirMenu': 'Expandir menu',
  'nav.recolherMenu': 'Recolher menu',
  'nav.fecharMenu': 'Fechar menu',
  'nav.abrirNavegacao': 'Abrir navegação',
  'nav.navegacaoPrincipal': 'Navegação principal',
  'nav.domVisaoGeral': 'Visão Geral',
  'nav.domInteligencia': 'Inteligência',
  'nav.domRelacionamento': 'Relacionamento',
  'nav.domComercial': 'Comercial',
  'nav.domGestao': 'Gestão',
  'nav.acervo': 'Acervo',
  'nav.catalogoDeOfertas': 'Catálogo de ofertas',
  'nav.discursosAprovados': 'Discursos aprovados',
  'nav.centralDeAjuda': 'Central de Ajuda',
  'painel.atencaoHoje': 'O que merece sua atenção hoje',
  'painel.saudePipeline': 'Saúde do pipeline',
  'painel.saudePipelineSubtitulo': 'Oportunidades abertas por etapa, em quantidade e valor',
  'painel.emAberto': 'em aberto',
  'painel.oportunidades': 'oportunidades',
  'painel.semPipelineTitulo': 'Ainda não há oportunidades abertas',
  'painel.semPipelineTexto':
    'É aqui que você vê onde está o dinheiro e onde ele trava. A primeira oportunidade já começa a desenhar o pipeline.',
  'painel.criarOportunidade': 'Criar oportunidade',
  'painel.comPrevisao': 'com previsão',
  'painel.carteiraDe': 'Carteira de',
  'painel.suaCarteira': 'Sua carteira',
  'painel.visaoConsolidada': 'Visão consolidada',
  'painel.carregando': 'Carregando o painel',
  'painel.parada': 'parada',
  'painel.oportunidade': 'oportunidade',
  'painel.ticketMedio': 'ticket médio',
  'painel.aberta': 'aberta',
  'saude.tituloTodos': 'Saúde dos pipelines',
  'saude.subtituloTodos': 'Oportunidades abertas por pipeline, em quantidade e valor',
  'saude.seletor': 'Pipeline',
  'saude.todos': 'Todos os pipelines',
  'saude.verTodosPipelines': 'Ver todos os pipelines',
  'saude.verPrincipaisPipelines': 'Mostrar só os principais',
  'saude.verTodasEtapas': 'Ver todas as etapas',
  'saude.verMenosEtapas': 'Mostrar menos etapas',
  'saude.detalhar': 'Detalhar',
  'saude.voltarTodos': 'Todos os pipelines',
  'saude.porQuantidade': 'Distribuição baseada na quantidade, pois não há valores informados.',
  'saude.valorNaoInformado': 'Valor não informado',
  'saude.semValor': 'sem valor',
  'saude.semValorSingular': 'oportunidade sem valor informado',
  'saude.semValorPlural': 'oportunidades sem valor informado',
  'saude.totalSoInformado': 'o total soma só os valores informados',
  'saude.vazioTitulo': 'Nenhuma oportunidade aberta neste pipeline',
  'saude.vazioTexto':
    'As etapas já estão configuradas. A primeira oportunidade passa a mostrar aqui onde está o valor e onde ele trava.',
  'saude.pipelineVazio': 'sem oportunidades abertas',
  'saude.nenhumPipelineTitulo': 'Nenhum pipeline configurado',
  'saude.nenhumPipelineAdmin':
    'Configure o processo de vendas para acompanhar etapas, quantidades e valores aqui.',
  'saude.nenhumPipelineSemAdmin':
    'Peça ao administrador da sua organização para configurar um pipeline.',
  'saude.configurarPipelines': 'Configurar pipelines',
  'saude.erro': 'Não foi possível carregar a saúde do pipeline agora.',
  'saude.carregando': 'Carregando a saúde do pipeline',
  'saude.parcial': 'Dados parciais',
  'saude.parcialParadas': 'a contagem de oportunidades paradas está indisponível agora',
  'saude.parcialSemValor': 'a contagem de oportunidades sem valor está indisponível agora',
  'saude.parcialEstrutura': 'a configuração dos pipelines não pôde ser lida agora',
  'saude.foraSingular': 'oportunidade aberta fora dos pipelines ativos — incluída só no total',
  'saude.foraPlural': 'oportunidades abertas fora dos pipelines ativos — incluídas só no total',
  'saude.contexto': 'Pipeline',
  'acao.subtitulo': 'O que precisa de você, em ordem de prioridade',
  'acao.erro': 'Não foi possível carregar a Central de Ação agora.',
  'acao.tipoVencida': 'Vencida',
  'acao.tipoHoje': 'Hoje',
  'acao.tipoFechamento': 'Fechamento',
  'acao.tipoParada': 'Parada',
  'acao.tipoSemPasso': 'Sem próximo passo',
  'oportunidade.playbookTitulo': 'Método comercial desta venda',
  'oportunidade.produtoDoPlaybook': 'Produto consultado',
  'oportunidade.materiaisRecomendados': 'Materiais recomendados',
  'oportunidade.abrirPlaybook': 'Abrir o método comercial no Catálogo',
  'hub.titulo': 'Playbook',
  'hub.subtitulo':
    'A central de conhecimento comercial da sua organização — pesquisável, por produto e por situação.',
  'hub.buscaPlaceholder': 'O que você precisa para vender?',
  'hub.buscando': 'Procurando…',
  'hub.resultados': 'resultado(s)',
  'hub.nadaTitulo': 'Nada encontrado',
  'hub.nadaTexto': 'Tente outra palavra, ou navegue pelas seções abaixo.',
  'hub.rascunho': 'rascunho',
  'hub.publicado': 'publicado',
  'hub.arquivado': 'arquivado',
  'hub.vencido': 'vencido',
  'hub.produtosTitulo': 'Produtos e serviços',
  'hub.produtosVazio': 'Nenhuma oferta no catálogo ainda.',
  'hub.secoesTitulo': 'Conhecimento comercial',
  'hub.secaoPerguntas': 'Perguntas de descoberta',
  'hub.secaoPerguntasDesc': 'O que perguntar para entender o cenário, a dor e a urgência.',
  'hub.secaoObjecoes': 'Objeções',
  'hub.secaoObjecoesDesc': 'Como interpretar e responder as objeções mais comuns.',
  'hub.secaoQualificacao': 'Qualificação',
  'hub.secaoQualificacaoDesc': 'Sinais positivos e negativos para decidir onde investir tempo.',
  'hub.secaoProcesso': 'Processo comercial',
  'hub.secaoProcessoDesc': 'O combinado de cada etapa: objetivo, atividades e critérios.',
  'hub.secaoCases': 'Cases',
  'hub.secaoCasesDesc': 'Histórias reais de cliente: desafio, solução e resultado.',
  'hub.secaoFaq': 'FAQ',
  'hub.secaoFaqDesc': 'Respostas prontas para as perguntas que sempre voltam.',
  'hub.secaoTreinamento': 'Treinamento',
  'hub.secaoTreinamentoDesc': 'Artigos, vídeos e roteiros para se preparar.',
  'hub.secaoJuridico': 'Jurídico',
  'hub.secaoJuridicoDesc': 'NDA, minutas e o que pode ou não ser prometido.',
  'hub.secaoTemplates': 'Templates',
  'hub.secaoTemplatesDesc': 'Mensagens prontas por canal, contexto e etapa.',
  'hub.secaoMateriais': 'Materiais',
  'hub.materiaisSubtitulo':
    'Apresentações, one-pages, vídeos e documentos — com versão, validade e aprovação.',
  'hub.materiaisVazioTexto': 'Nenhum material cadastrado para esta consulta ainda.',
  'hub.filtrarMateriais': 'Filtrar materiais…',
  'hub.novoMaterial': 'Novo material',
  'hub.linkDiscursos': 'Discursos e aprovação',
  'hub.linkIcp': 'Cliente ideal',
  'hub.linkPersonas': 'Personas e catálogo',
  'hub.voltar': 'Playbook',
  'hub.novaEntrada': 'Novo conteúdo',
  'hub.editarEntrada': 'Editar conteúdo',
  'hub.salvar': 'Salvar',
  'hub.salvo': 'Conteúdo salvo.',
  'hub.alteracao.aviso': 'Este conteúdo está aprovado. A edição fica como alteração pendente: quem vende continua vendo a versão aprovada até a alteração ser aprovada.',
  'hub.alteracao.publicarDireto': 'Publicar diretamente (sem fila de aprovação)',
  'hub.alteracao.rascunho': 'Alteração pendente',
  'hub.alteracao.emRevisao': 'Alteração aguardando aprovação',
  'hub.alteracao.enviar': 'Enviar para aprovação',
  'hub.alteracao.aprovar': 'Aprovar alteração',
  'hub.alteracao.devolver': 'Devolver',
  'hub.alteracao.publicar': 'Publicar alteração',
  'hub.alteracao.motivo': 'Motivo da devolução',
  'hub.alteracao.decidida': 'Alteração atualizada.',
  'hub.estadoAlterado': 'Situação alterada.',
  'hub.erroGenerico': 'Não foi possível concluir a ação.',
  'hub.publicar': 'Publicar',
  'hub.arquivar': 'Arquivar',
  'hub.restaurar': 'Restaurar',
  'hub.editar': 'Editar',
  'hub.verArquivadas': 'Ver conteúdo arquivado',
  'hub.secaoInexistente': 'Esta seção não existe',
  'hub.secaoVaziaTitulo': 'Ainda não há conteúdo aqui',
  'hub.secaoVaziaTexto': 'Quem produz conteúdo pode começar agora pelo botão acima.',
  'hub.umPorLinha': 'Um item por linha',
  'hub.tagsAjuda': 'Separadas por vírgula.',
  'hub.chaveAjuda': 'Identificador estável do material — versões novas usam a mesma chave.',
  'hub.urlAjuda': 'Link externo. Opcional: dá para enviar um arquivo depois de salvar.',
  'hub.campoTitulo': 'Título',
  'hub.campoSumario': 'Resumo em uma frase',
  'hub.campoTags': 'Tags',
  'hub.campoVisibilidade': 'Quem vê',
  'hub.visComercial': 'Todo o comercial',
  'hub.visGestores': 'Gestores',
  'hub.visAdministradores': 'Administradores',
  'hub.visRestrito': 'Restrito (quem aprova)',
  'hub.campoOQueE': 'O que é',
  'hub.campoProblema': 'Problema que resolve',
  'hub.campoPropostaValor': 'Proposta de valor',
  'hub.campoBeneficios': 'Principais benefícios',
  'hub.campoDiferenciais': 'Diferenciais',
  'hub.campoQuandoOferecer': 'Quando oferecer',
  'hub.campoQuandoNao': 'Quando não oferecer',
  'hub.campoPosicionamento': 'Posicionamento',
  'hub.campoPitchCurto': 'Pitch curto',
  'hub.campoPitchMedio': 'Pitch médio',
  'hub.campoPitchCompleto': 'Pitch completo',
  'hub.campoProdutos': 'Produtos relacionados',
  'hub.produtosAjuda':
    'Conteúdo geral fica sem produto; marque um ou vários itens do Catálogo quando fizer sentido.',
  'hub.novoProduto': 'Novo produto ou serviço',
  'hub.verCatalogoCompleto': 'Ver Catálogo completo',
  'hub.verNoCatalogo': 'Ver no Catálogo',
  'hub.semCatalogoTitulo': 'Você ainda não possui produtos ou serviços cadastrados.',
  'hub.semCatalogoTexto':
    'O Playbook é construído a partir do seu Catálogo Comercial: cadastre o que a empresa vende e ele aparece aqui, nas oportunidades e nas propostas automaticamente.',
  'hub.cadastrarPrimeiroProduto': 'Cadastrar produto ou serviço',
  'hub.escreverVisao': 'Escrever a visão comercial',
  'hub.semVisaoTexto':
    'A visão comercial deste produto ainda não foi escrita: o que é, para quem, e como apresentar.',
  'hub.servico': 'Serviço',
  'hub.produto': 'Produto',
  'hub.campoPergunta': 'Pergunta',
  'hub.campoObjetivo': 'Objetivo',
  'hub.campoCategoria': 'Categoria',
  'hub.campoEtapa': 'Etapa comercial',
  'hub.campoObjecao': 'Objeção',
  'hub.campoInterpretacao': 'Interpretação',
  'hub.campoResposta': 'Resposta recomendada',
  'hub.campoAprofundamento': 'Perguntas de aprofundamento',
  'hub.campoArgumentos': 'Argumentos',
  'hub.campoEvidencias': 'Evidências',
  'hub.campoCuidados': 'Cuidados',
  'hub.campoSinaisPositivos': 'Sinais positivos',
  'hub.campoSinaisNegativos': 'Sinais negativos',
  'hub.campoOrientacao': 'Orientação',
  'hub.campoAtividades': 'Atividades',
  'hub.campoEntrada': 'Critérios de entrada',
  'hub.campoSaida': 'Critérios de saída',
  'hub.campoBoasPraticas': 'Boas práticas',
  'hub.campoErros': 'Erros comuns',
  'hub.campoChecklist': 'Checklist',
  'hub.campoCliente': 'Cliente',
  'hub.campoSegmento': 'Segmento',
  'hub.campoDesafio': 'Desafio',
  'hub.campoSolucao': 'Solução',
  'hub.campoResultados': 'Resultados',
  'hub.campoDepoimento': 'Depoimento',
  'hub.campoFormato': 'Formato',
  'hub.campoUrl': 'URL',
  'hub.campoDuracao': 'Duração',
  'hub.campoDescricao': 'Descrição',
  'hub.campoTipoDocumento': 'Tipo de documento',
  'hub.campoResponsavel': 'Responsável',
  'hub.campoObservacoes': 'Observações',
  'hub.campoCanal': 'Canal',
  'hub.campoAssunto': 'Assunto',
  'hub.campoCorpo': 'Corpo da mensagem',
  'hub.campoContexto': 'Contexto de uso',
  'hub.campoChave': 'Chave',
  'hub.campoTipoMaterial': 'Tipo',
  'hub.abrirLink': 'Abrir link',
  'hub.baixarArquivo': 'Baixar arquivo',
  'hub.enviarArquivo': 'Enviar arquivo',
  'hub.arquivoEnviado': 'Arquivo enviado.',
  'hub.produtoTitulo': 'Playbook do produto',
  'hub.visaoComercial': 'Visão comercial',
  'hub.propostaDeValor': 'Proposta de valor',
  'hub.problemaQueResolve': 'Problema que resolve',
  'hub.diferenciais': 'Diferenciais',
  'hub.discursosTitulo': 'Como apresentar',
  'hub.semDiscursos': 'Nenhum discurso aprovado para esta oferta ainda.',
  'entrar.aindaNaoUsa': 'Ainda não usa o Ceres?',
  'cadastro.indisponivelTitulo': 'Cadastro indisponível',
  'cadastro.indisponivelTexto':
    'O cadastro público do CERES ainda não está aberto. Se a sua empresa já usa o CERES, peça um convite a quem administra a conta.',
  'entrar.criarConta': 'Criar minha conta',
  'cadastro.titulo': 'Criar minha conta',
  'cadastro.subtitulo':
    'Crie sua conta, cadastre sua empresa e comece a usar. Sem implantação obrigatória.',
  'cadastro.nome': 'Seu nome',
  'cadastro.email': 'Seu e-mail',
  'cadastro.senha': 'Senha',
  'cadastro.senhaAjuda': 'Mínimo de 10 caracteres, com letras e números.',
  'cadastro.empresa': 'Nome da empresa',
  'cadastro.cnpj': 'CNPJ',
  'cadastro.criar': 'Criar minha conta',
  'cadastro.jaTem': 'Já tem conta?',
  'cadastro.entrar': 'Entrar',
  'cadastro.enviadoTitulo': 'Confirme seu e-mail para continuar.',
  'cadastro.enviadoTexto':
    'Se estiver tudo certo, você receberá um e-mail com o link de confirmação. Ele vale por 48 horas.',
  'cadastro.voltarEntrar': 'Voltar para a entrada',
  'cadastro.confirmarTitulo': 'Confirme seu e-mail',
  'cadastro.confirmarTexto': 'Para sua segurança, digite a senha que você escolheu no cadastro.',
  'cadastro.confirmarBotao': 'Confirmar e continuar',
  'cadastro.irParaEntrar': 'Ir para a entrada',
  'cadastro.semToken': 'Este endereço precisa do link completo do e-mail de confirmação.',
  'cadastro.confirmarSenha': 'Confirme a senha',
  'cadastro.senhasDiferentes': 'As senhas não conferem.',
  'cadastro.aceitePrefixo': 'Li e aceito:',
  'cadastro.aceiteIndisponivel': 'Não foi possível carregar os termos. Recarregue a página.',
  'cadastro.aceiteObrigatorio': 'Para criar a conta, aceite os termos.',
  'cadastro.reenviar': 'Reenviar e-mail',
  'cadastro.reenviado':
    'Se houver um cadastro aguardando confirmação para este e-mail, enviamos um novo link.',
  'cadastro.jaConfirmei': 'Já confirmei meu e-mail',
  'cadastro.retomarTitulo': 'Retomar cadastro',
  'cadastro.retomarTexto': 'Entre com o e-mail e a senha do cadastro para continuar de onde parou.',
  'cadastro.retomarBotao': 'Continuar cadastro',
  'cadastro.passoConta': 'Conta',
  'cadastro.passoEmpresa': 'Empresa',
  'cadastro.passoModalidade': 'Modalidade',
  'cadastro.empresaTitulo': 'Vamos cadastrar sua empresa.',
  'cadastro.empresaTexto':
    'Comece pelo CNPJ. Se a consulta pública responder, preenchemos o nome para você.',
  'cadastro.verificarCnpj': 'Consultar CNPJ',
  'cadastro.razaoSocial': 'Nome da empresa (razão social)',
  'cadastro.nomeFantasia': 'Nome fantasia (opcional)',
  'cadastro.site': 'Site (opcional)',
  'cadastro.consultaPreencheu':
    'Preenchemos com os dados públicos da Receita Federal. Confira antes de continuar.',
  'cadastro.consultaIndisponivel':
    'A consulta pública não respondeu agora. Preencha o nome da empresa para continuar.',
  'cadastro.situacaoCadastral': 'Situação cadastral:',
  'cadastro.empresaExistenteTitulo': 'Esta empresa já usa o CERES.',
  'cadastro.empresaExistenteTexto':
    'Por segurança, o acesso só é concedido por convite de quem administra a empresa no CERES. Podemos avisar essa pessoa de que você pediu acesso.',
  'cadastro.pedirAcesso': 'Pedir acesso a quem administra',
  'cadastro.outroCnpj': 'Usar outro CNPJ',
  'cadastro.continuar': 'Continuar',
  'cadastro.voltar': 'Voltar',
  'cadastro.modalidadeTitulo': 'Escolha como usar o CERES',
  'cadastro.modalidadeTexto':
    'Sem fidelidade contratual obrigatória. Nenhuma cobrança é feita nesta etapa.',
  'cadastro.modalidadeAvaliacao':
    'Avaliação gratuita de {dias} dias, para até {usuarios} usuários.',
  'cadastro.modalidadeAtivacao':
    'Ativação pela equipe CERES: a conta é criada agora e os recursos são liberados quando a assinatura for ativada.',
  'cadastro.preco': 'Valor por usuário/mês:',
  'cadastro.precoNaoPublicado':
    'Valor ainda não publicado — condições combinadas com a equipe CERES.',
  'cadastro.limiteUsuarios': 'Usuários incluídos:',
  'cadastro.semLimitePublicado': 'definido na ativação',
  'cadastro.funcionalidades': 'Recursos incluídos',
  'cadastro.semModalidades':
    'Nenhuma modalidade está disponível para cadastro agora. Tente novamente mais tarde.',
  'cadastro.escolhida': 'Modalidade escolhida',
  'cadastro.escolher': 'Escolher',
  'cadastro.concluir': 'Criar a conta da empresa',
  'cadastro.prontaTitulo': 'Sua conta está pronta.',
  'cadastro.prontaAvaliacao':
    'A avaliação gratuita da {empresa} vai até {data}. Entre com seu e-mail e senha para começar.',
  'cadastro.pendenteTitulo': 'Estamos aguardando a ativação da sua assinatura.',
  'cadastro.pendenteTexto':
    'A conta da {empresa} foi criada e é você quem a administra. Os recursos do CRM ficam disponíveis assim que a equipe CERES ativar a assinatura. Você já pode entrar para conferir os dados e convidar sua equipe.',
  'cadastro.entrarAgora': 'Entrar no CERES',
  'usuarios.membershipSuspensa': 'acesso suspenso aqui',
  'usuarios.membershipSuspensaTitulo':
    'O acesso desta pessoa a ESTA organização está suspenso. As demais organizações dela não são afetadas.',
  'usuarios.deOutraOrganizacao': 'de outra organização',
  'usuarios.deOutraOrganizacaoTitulo':
    'A identidade desta pessoa pertence a outra organização: aqui administra-se apenas o acesso e os perfis locais.',
  'usuarios.reativarAcesso': 'Reativar acesso',
  'usuarios.suspenderAcesso': 'Suspender acesso',
  'usuarios.removerAcesso': 'Remover acesso',
  'usuarios.removerAcessoConfirmar': 'Confirmar remoção?',
  'org.escolherTitulo': 'Escolha a empresa em que deseja trabalhar',
  'org.escolherSubtitulo':
    'Você tem acesso a mais de uma organização. Tudo que fizer valerá para a organização escolhida — e dá para trocar depois, pelo menu do usuário.',
  'org.entrar': 'Entrar',
  'org.trocando': 'Entrando…',
  'org.sair': 'Sair e entrar com outra conta',
  'org.trocarDe': 'Trocar de organização',
  'org.atual': 'atual',
  'orgAdmin.titulo': 'Organizações',
  'orgAdmin.subtitulo':
    'Os tenants da plataforma: cada organização é um CRM independente e isolado.',
  'orgAdmin.nova': 'Nova organização',
  'orgAdmin.nome': 'Nome fantasia',
  'orgAdmin.razaoSocial': 'Razão social',
  'orgAdmin.cnpj': 'CNPJ',
  'orgAdmin.email': 'E-mail',
  'orgAdmin.telefone': 'Telefone',
  'orgAdmin.site': 'Site',
  'orgAdmin.donoEmail': 'E-mail do proprietário',
  'orgAdmin.donoNome': 'Nome do proprietário',
  'orgAdmin.donoAjuda':
    'Se o e-mail já existir no sistema, a pessoa recebe acesso a esta organização — nenhum usuário duplicado é criado.',
  'orgAdmin.criar': 'Criar organização',
  'orgAdmin.criando': 'Criando…',
  'orgAdmin.membros': 'membros',
  'orgAdmin.empresas': 'empresas',
  'orgAdmin.ativa': 'Ativa',
  'orgAdmin.inativa': 'Inativa',
  'orgAdmin.desativar': 'Desativar',
  'orgAdmin.reativar': 'Reativar',
  'orgAdmin.criadaIdentidadeNova':
    'Organização criada. O proprietário recebeu um convite por e-mail.',
  'orgAdmin.criadaIdentidadeReusada':
    'Organização criada. O proprietário já existia e recebeu acesso a ela.',
  'orgAdmin.vazio': 'Nenhuma organização cadastrada.',
  'orgAdmin.plano': 'Plano',
  'orgAdmin.planoAlterado': 'Plano da organização alterado. Peça para recarregar a página no CRM.',
  'orgAdmin.moduloAlterado':
    'Módulos da organização atualizados. O menu reflete no próximo carregamento.',
  'admin.subtitulo': 'Configuração do CRM. O trabalho do dia a dia continua no menu lateral.',
  'admin.naoAdministra': 'Você não administra este CRM',
  'admin.voltarPainel': 'Voltar ao painel',
  'agentes.titulo': 'Agentes',
  'agentes.subtitulo':
    'O que cada agente é, quem responde por ele, o que pode dar errado — e como desligá-lo.',
  'agentes.motivoPlaceholder': 'Um agente pausado sem motivo ninguém sabe religar.',
  'agentes.motivoPausa': 'Motivo da pausa',
  'agentes.desligadoPorCompleto': 'O assistente está desligado por completo',
  'agentes.alcanca': 'Alcança',
  'agentes.ultimos7': 'Últimos 7 dias',
  'agentes.saiDaRede':
    'O conteúdo enviado ao modelo sai da rede do Ceres (vai ao serviço do provedor de IA).',
  'agentes.naoSaiDaRede': 'Regras internas: nada sai da rede do Ceres.',
  'agentes.autonomiaEfetiva': 'Autonomia em vigor: {nivel} (máximo do agente: {maximo})',
  'agentes.tetoDaGovernanca': 'teto da governança: {nivel}',
  'agentes.provedor': 'provedor {provedor}',
  'agentes.nivel0': 'observa',
  'agentes.nivel1': 'prepara',
  'agentes.nivel2': 'executa o que foi confirmado ou aprovado',
  'agentes.nivel3': 'opera sozinho por política',
  'agentes.podeDarErrado': 'O que pode dar errado',
  'agentes.porQuePausando': 'Por que está pausando',
  'agentes.pausarEste': 'Pausar este agente',
  'auditoria.titulo': 'Auditoria',
  'auditoria.acessoRestrito': 'Acesso restrito',
  'auditoria.acessoRestritoTexto': 'A trilha de auditoria exige permissão específica.',
  'auditoria.trilha': 'Trilha de auditoria',
  'auditoria.subtitulo':
    'Registro imutável de operações relevantes. Não pode ser alterado nem apagado.',
  'auditoria.entidade': 'Entidade',
  'auditoria.acao': 'Ação',
  'auditoria.correlationId': 'Correlation id da requisição…',
  'auditoria.vazio': 'Nenhum evento encontrado',
  'auditoria.vazioTexto': 'Ajuste os filtros para localizar a operação.',
  'auditoria.antes': 'Antes',
  'auditoria.depois': 'Depois',
  'auditoria.todasEntidades': 'Todas as entidades',
  'auditoria.todasAcoes': 'Todas as ações',
  'auditoria.quando': 'Quando',
  'auditoria.resumo': 'Resumo',
  'auditoria.autor': 'Autor',
  'auditoria.detalhe': 'Detalhe',
  'demo.titulo': 'Dados de demonstração',
  'demo.subtitulo':
    'Registros fictícios para apresentar o CRM ou treinar a equipe, criados e removidos sem risco para o dado real.',
  'demo.comoSegura': 'Como a remoção é segura',
  'demo.remover': 'Remover os dados de demonstração',
  'demo.removerTexto':
    'Confira a prévia. A remoção acontece em uma única transação: ou tudo sai, ou nada sai.',
  'demo.entidade': 'Entidade',
  'demo.registros': 'Registros',
  'demo.carregando': 'Carregando…',
  'demo.situacaoAtual': 'Situação atual',
  'demo.semDados': 'Nenhum dado de demonstração na base',
  'demo.verRemovido': 'Ver o que será removido',
  'demo.historico': 'Histórico',
  'demo.historicoTexto':
    'Cargas anteriores e sua remoção. A trilha de auditoria guarda o registro completo.',
  'demo.nenhumaRemovida': 'Nenhuma carga foi removida ainda.',
  'demo.calculando': 'Calculando…',
  'demo.seraRemovido': 'Será removido',
  'demo.permaneceIntacto': 'Permanece intacto (dados reais)',
  'demo.fraseIdentica': 'A frase precisa ser idêntica, com acentos e em maiúsculas.',
  'demo.remocaoBloqueada':
    'A remoção está bloqueada: existem registros reais vinculados à demonstração. Resolva os vínculos abaixo e atualize a prévia.',
  'eventos.titulo': 'Fila de eventos',
  'eventos.subtitulo':
    'Cada mudança relevante do CRM vira um evento, e cada evento é entregue a quem o acompanha. Aqui aparece o que não chegou.',
  'eventos.situacaoEntregas': 'Situação das entregas',
  'integracoes.titulo': 'Integrações',
  'integracoes.subtitulo': 'O que o CRM conecta — por capacidade, nunca por fornecedor.',
  'integracoes.vazio': 'Nenhuma integração conectada',
  'integracoes.vazioTexto': 'Conecte um adaptador do catálogo abaixo.',
  'integracoes.revogaSegredos':
    'Revoga os segredos na hora: o parceiro para de conseguir entregar.',
  'integracoes.guardeSegredo': 'Guarde este segredo agora',
  'integracoes.jaGuardei': 'Já guardei',
  'integracoes.conexoes': 'Conexões',
  'integracoes.desconectar': 'Desconectar',
  'integracoes.semEvento': 'Nenhum evento recebido ainda.',
  'integracoes.reprocessar': 'Reprocessar',
  'integracoes.catalogo': 'Catálogo',
  'memoria.titulo': 'Memória do assistente',
  'memoria.situacao': 'Situação das memórias',
  'memoria.recusar': 'Recusar',
  'memoria.reverter': 'Reverter',
  'proposta.registrarDesfecho': 'Registrar o que o cliente respondeu',
  'proposta.registrarDesfechoDescricao':
    'É este registro que fecha o ciclo: ele libera o contrato, encerra o acompanhamento e alimenta o relatório de ganhos e perdas. Sem ele, a proposta fica em aberto para sempre.',
  'proposta.desfechoAceita': 'Aceita pelo cliente',
  'proposta.aceitaConsequencia':
    'Libera a geração do contrato, encerra o acompanhamento e conta como ganho no relatório.',
  'proposta.desfechoRecusada': 'Recusada pelo cliente',
  'proposta.recusadaConsequencia':
    'Encerra o acompanhamento e entra no relatório de motivos de perda — que é o que ajuda quem for propor de novo.',
  'proposta.desfechoNegociacao': 'Em negociação',
  'proposta.negociacaoConsequencia':
    'O cliente respondeu, mas ainda não decidiu. A proposta volta a ser editável e uma versão nova pode ser enviada.',
  'proposta.desfechoRetirada': 'Retirada por nós',
  'proposta.retiradaConsequencia':
    'Decisão nossa, e definitiva. Mudar de ideia depois é uma proposta nova, com data própria.',
  'proposta.motivoDoDesfecho': 'O que houve',
  'proposta.motivoAjuda':
    'Quem for propor para este cliente daqui a um mês é quem vai ler isto. "Não" sem explicação não ensina nada.',
  'proposta.registrarBotao': 'Registrar',
  'proposta.semDesfechoPossivel':
    'Esta proposta já chegou ao fim do caminho. Mudar de ideia agora é uma proposta nova, com data própria — e é assim que o histórico continua fazendo sentido.',
  'proposta.erroDesfecho': 'Não foi possível registrar o desfecho.',
  'proposta.desfechoOk': 'Desfecho registrado.',
  'proposta.aceitaOk': 'Aceite registrado. O contrato já pode ser gerado a partir desta proposta.',
  'proposta.venceEm': 'Vence em',
  'proposta.venceHoje': 'Vence hoje — depois disso ela expira sozinha.',
  'proposta.dias': 'dias.',
  'proposta.expirou': 'Expirou sem resposta. Para retomar, é uma proposta nova.',
  'proposta.titulo': 'Proposta',
  'proposta.comoCalculado': 'Como este valor foi calculado',
  'proposta.entrega': 'Entrega',
  'proposta.acompanhamento': 'Acompanhamento',
  'proposta.cadenciaPara': 'A cadência para sozinha quando o cliente responde.',
  'proposta.encerrar': 'Encerrar',
  'proposta.faltaPreencher': 'Falta preencher:',
  'proposta.voceEscreve': 'O que você escreve',
  'proposta.clienteLe': 'Como o cliente vai ler',
  // CERES-EST-002 — plano, assinatura, assentos e módulos contratados.
  'plano.titulo': 'Plano e uso',
  'plano.subtitulo': 'O que a sua organização contratou e quanto está usando.',
  'plano.plano': 'Plano',
  'plano.situacao': 'Situação',
  'plano.assentosUsados': 'Usuários ativos',
  'plano.assentosLivres': 'Assentos livres',
  'plano.ilimitado': 'ilimitado',
  'plano.modulos': 'Módulos habilitados',
  'plano.semModulos': 'Nenhum módulo habilitado.',
  'plano.moduloLigado': 'habilitado',
  'plano.moduloParcial': 'parcial',
  'plano.moduloDesligado': 'não contratado',
  'plano.situacaoAtiva': 'Ativa',
  'plano.situacaoTeste': 'Período de teste',
  'plano.situacaoInadimplente': 'Pagamento em atraso',
  'plano.situacaoSuspensa': 'Suspensa',
  'plano.situacaoCancelada': 'Cancelada',
  'plano.situacaoExpirada': 'Expirada',
  'plano.situacaoSemAssinatura': 'Sem assinatura',
  'plano.situacaoPendente': 'Aguardando ativação',
  'assinatura.avisoPendente':
    'A empresa foi cadastrada no plano {plano} e a assinatura aguarda ativação pela equipe CERES. Até lá, os recursos comerciais ficam bloqueados; você já pode convidar sua equipe.',
  'assinatura.avisoAvaliacao': 'Avaliação gratuita do plano {plano} até {data}.',
  'assinatura.convidarEquipe': 'Convidar a equipe',
  'outraOrganizacao.link': 'Cadastrar outra empresa',
  'outraOrganizacao.texto':
    'Você continua com o mesmo login. A nova empresa fica separada das que você já acessa, com perfis próprios, e é você quem vai administrá-la.',
  'outraOrganizacao.senha': 'Sua senha atual',
  'outraOrganizacao.senhaAjuda': 'Por segurança, confirme a senha com que você entra no CERES.',
  'outraOrganizacao.criar': 'Cadastrar a empresa',
  'outraOrganizacao.prontaTexto':
    'A {empresa} foi cadastrada com avaliação gratuita até {data}, e é você quem a administra.',
  'outraOrganizacao.pendenteTexto':
    'A {empresa} foi cadastrada e é você quem a administra. Os recursos do CRM ficam disponíveis quando a equipe CERES ativar a assinatura; você já pode entrar nela e convidar sua equipe.',
  'outraOrganizacao.jaExistia': 'Você já tem acesso à {empresa}. Nenhuma empresa nova foi criada.',
  'outraOrganizacao.entrar': 'Entrar na {empresa}',
  'outraOrganizacao.voltar': 'Voltar',
  'outraOrganizacao.titulo': 'Cadastrar outra empresa',
  'assinatura.verPlano': 'Ver plano',
  'plano.avisoNaoVigente':
    'A assinatura não está vigente. Os seus dados continuam preservados; o acesso aos módulos volta assim que ela for reativada.',
  'plano.avisoExcedente':
    'A organização está acima do limite contratado. Ninguém foi desativado, mas novas ativações estão bloqueadas. Excedente:',
  'plano.avisoLimiteAlcancado':
    'Todos os assentos do plano estão ocupados. Para ativar mais alguém, amplie os assentos ou suspenda um acesso.',
  'plano.administracao': 'Administração da assinatura',
  'plano.administracaoAviso':
    'Toda alteração exige motivo, fica auditada e recalcula os módulos na requisição seguinte. Nenhum dado é apagado.',
  'plano.motivo': 'Motivo da alteração',
  'plano.motivoExemplo': 'Ex.: contrato renegociado em reunião com o cliente',
  'plano.trocarPlano': 'Trocar o plano',
  'plano.escolha': 'Escolha…',
  'plano.limiteAssentos': 'Limite de assentos',
  'plano.vazioIlimitado': 'vazio = ilimitado',
  'plano.situacaoDaAssinatura': 'Situação da assinatura',
  'plano.suspender': 'Suspender',
  'plano.reativar': 'Reativar',
  'plano.aplicar': 'Aplicar',
  'plano.historico': 'Histórico das mudanças',
  'plano.erroCarregar': 'Não foi possível carregar o plano desta organização.',
  'plano.erroGenerico': 'Não foi possível concluir a alteração.',
  'orgAdmin.versaoContratada': 'versão',
  'orgAdmin.motivoDaTroca': 'Por que está trocando o plano?',
  'orgAdmin.confirmarTroca': 'Confirmar troca',
  'orgAdmin.cancelarTroca': 'Cancelar',
  'orgAdmin.planoFalhou': 'Não foi possível trocar o plano.',
  'plano.versao': 'Versão contratada',
  'plano.versaoVigente': 'Versão vigente para novas contratações',
  'plano.versaoDesatualizada':
    'Esta organização está numa versão anterior do plano. Isso é normal: quem contratou antes continua nas condições que contratou. A migração é um ato explícito.',
  'plano.opcoes': 'Opções de plano',
  'plano.opcoesAviso':
    'A solicitação não altera o plano nem ativa recursos. A equipe comercial precisa aprovar as condições.',
  'plano.atual': 'Atual',
  'plano.limiteNaoDefinido': 'Limite de usuários ainda não definido',
  'plano.limiteDeUsuarios': 'Limite de usuários',
  'plano.condicaoSobConsulta': 'Condições comerciais sob consulta',
  // CERES-ACC-007: gestão da assinatura pelo tenant.
  'cobranca7.inadimplencia': 'A última cobrança não foi aprovada.',
  'cobranca7.prazo': 'Prazo de regularização previsto na política de cobrança:',
  'cobranca7.alterarTitulo': 'Alterar plano ou quantidade de usuários',
  'cobranca7.alterarAjuda':
    'O valor é calculado pelas condições comerciais vigentes. A alteração vale a partir da próxima cobrança, sem cobrança proporcional, e as funcionalidades mudam quando essa cobrança for confirmada.',
  'cobranca7.semCobrancaAtiva':
    'Sem cobrança recorrente em dia: use a solicitação de alteração de plano abaixo.',
  // CERES-DP-015: agente de implantação (aplicadas, liberação, revisar).
  'dp015.concluida': 'Implantação concluída.',
  'dp015.revisar': 'Revisar ou configurar um módulo novo',
  'dp015.pedidoDeRevisao': 'Quero revisar a configuração do CERES CRM ou configurar um módulo que contratei depois.',
  'dp015.aguardandoMinimo': 'As automações (cadências, distribuição e envios automáticos) começam quando o mínimo estiver pronto. Falta:',
  'dp015.falta.FUNIL': 'revisar o funil de vendas',
  'dp015.falta.EQUIPE': 'convidar a equipe',
  'dp015.aplicadas': 'Aplicado pelo Assistente',
  // CERES-DP-014: assinatura por módulos, assentos e bloqueio por inadimplência.
  'dp014.contratar.titulo': 'Contratar por módulos',
  'dp014.contratar.ajuda': 'Cada módulo tem um preço inicial que inclui até 3 usuários e um valor por usuário adicional. A quantidade de usuários vale para todos os módulos. Pagamento só com cartão de crédito, mensal.',
  'dp014.contratar.obrigatorio': 'sempre incluído',
  'dp014.contratar.semPreco': 'preço ainda não definido',
  'dp014.contratar.inclui': 'inclui até',
  'dp014.contratar.usuariosAbrev': 'usuários',
  'dp014.contratar.adicional': 'por usuário adicional',
  'dp014.contratar.usuarios': 'Quantidade de usuários',
  'dp014.contratar.mensalidade': 'Mensalidade',
  'dp014.contratar.nome': 'Nome ou razão social do contratante',
  'dp014.contratar.documento': 'CPF ou CNPJ',
  'dp014.contratar.email': 'E-mail de cobrança (conta Mercado Pago)',
  'dp014.contratar.aceite': 'Confirmo a contratação mensal pelo valor acima, com cobrança recorrente no cartão de crédito.',
  'dp014.contratar.irParaPagamento': 'Ir para o pagamento',
  'dp014.contratar.cartao': 'O cartão é digitado no ambiente do Mercado Pago; o CERES não recebe nem guarda dados de cartão.',
  'dp014.assentos.titulo': 'Usuários (assentos)',
  'dp014.assentos.pagos': 'Pagos',
  'dp014.assentos.emUso': 'Em uso',
  'dp014.assentos.renovacao': 'Renovação',
  'dp014.assentos.reducaoAgendada': 'Redução agendada para a renovação',
  'dp014.assentos.novaQuantidade': 'Nova quantidade',
  'dp014.assentos.verPrevia': 'Ver valores',
  'dp014.assentos.proporcional': 'Cobrança agora (proporcional até a renovação)',
  'dp014.assentos.novaMensalidade': 'Nova mensalidade',
  'dp014.assentos.dias': 'dias restantes de',
  'dp014.assentos.pagarAmpliacao': 'Pagar e ampliar',
  'dp014.assentos.liberacao': 'Os novos assentos são liberados depois que o Mercado Pago confirmar o pagamento.',
  'dp014.assentos.aguardando': 'Ampliação aguardando pagamento',
  'dp014.assentos.continuarPagamento': 'Continuar o pagamento',
  'dp014.assentos.reduzir': 'Reduzir na renovação',
  'dp014.assentos.reduzirAjuda': 'Reduzir não gera devolução nem crédito: os assentos pagos valem até o fim do ciclo, e a nova quantidade passa a valer na renovação.',
  'dp014.assentos.cancelarReducao': 'Cancelar redução',
  'dp014.assentos.semRecorrencia': 'A alteração de assentos fica disponível quando a assinatura estiver com a cobrança recorrente em dia.',
  'dp014.bloqueio.titulo': 'Acesso suspenso por falta de pagamento',
  'dp014.bloqueio.texto': 'A organização ficou mais de três dias após o vencimento sem pagamento confirmado. Os dados estão preservados. Assim que o pagamento for confirmado, o acesso volta automaticamente.',
  'dp014.bloqueio.irParaCobranca': 'Ir para Plano e cobrança',
  'dp014.bloqueio.semPermissao': 'Peça ao responsável pela cobrança da organização para regularizar o pagamento.',
  'cobranca7.novoPlano': 'Novo plano',
  'cobranca7.usuarios': 'Usuários (vazio = manter)',
  'cobranca7.manter': 'Manter a atual',
  'cobranca7.simular': 'Ver condições',
  'cobranca7.ganha': 'Passa a incluir',
  'cobranca7.perde': 'Deixa de incluir (dados preservados)',
  'cobranca7.aceito': 'Li e aceito as condições acima.',
  'cobranca7.confirmar': 'Confirmar alteração',
  'cobranca7.dataPrevista': 'Data prevista',
  'cobranca7.cancelarAlteracao': 'Cancelar alteração',
  'cobranca7.alt.CONFIRMADA': 'Aceita; aguardando habilitação da cobrança',
  'cobranca7.alt.PENDENTE_NO_PROVEDOR': 'Aceita; aguardando confirmação do Mercado Pago',
  'cobranca7.alt.PROGRAMADA': 'Programada para a próxima cobrança',
  'cobranca7.alt.PENDENTE_DE_TRATAMENTO':
    'Registrada; o Mercado Pago não permite alterar a periodicidade automaticamente',
  'cobranca7.alt.CONCLUIDA': 'Concluída',
  'cobranca7.resumo': 'Resumo de pagamento',
  'cobranca7.imprimir': 'Imprimir',
  'cobranca7.fechar': 'Fechar',
  'cobranca7.numero': 'Número',
  'cobranca7.contratante': 'Contratante',
  'cobranca7.pagoEm': 'Pago em',
  'cobranca7.refMp': 'Referência no Mercado Pago',
  'cobranca7.verResumo': 'Resumo',
  'cobranca7.exportarCsv': 'Exportar pagamentos (CSV)',
  // CERES-ACC-006: contratação e cobrança pelo Mercado Pago.
  'cobranca.titulo': 'Contratação e cobrança',
  'cobranca.subtitulo':
    'Assinatura recorrente pelo Mercado Pago. O valor vem da condição comercial aceita pela organização; o pagamento é feito no ambiente seguro do Mercado Pago, e a assinatura só é ativada depois da confirmação do pagamento.',
  'cobranca.ambienteDeTeste': 'Ambiente de teste (homologação): nenhuma cobrança real é feita.',
  'cobranca.retornoDoCheckout':
    'Você voltou do Mercado Pago. Estamos confirmando o pagamento com o provedor; a assinatura é ativada somente depois dessa confirmação.',
  'cobranca.erro': 'Não foi possível concluir. Tente novamente em instantes.',
  'cobranca.situacao': 'Situação da cobrança',
  'cobranca.periodicidade': 'Periodicidade',
  'cobranca.valor': 'Valor',
  'cobranca.proximaCobranca': 'Próxima cobrança',
  'cobranca.cancelamentoPendente':
    'Cancelamento solicitado. Aguardando a confirmação do Mercado Pago; a tentativa é repetida automaticamente.',
  'cobranca.canceladaEm': 'Cancelada em',
  'cobranca.acessoAte': 'Acesso já pago até',
  'cobranca.continuarNoMercadoPago': 'Continuar no Mercado Pago',
  'cobranca.atualizar': 'Atualizar situação',
  'cobranca.indisponivel': 'A contratação on-line não está disponível no momento.',
  'cobranca.escolhaPeriodicidade': 'Escolha a periodicidade',
  'cobranca.emailDoPagador':
    'E-mail da conta Mercado Pago que fará o pagamento (opcional; padrão: o seu e-mail)',
  'cobranca.aceite': 'Autorizo a cobrança recorrente no Mercado Pago no valor de',
  'cobranca.semFidelidade':
    'Sem fidelidade e sem multa: o cancelamento pode ser feito a qualquer momento nesta tela.',
  'cobranca.contratar': 'Contratar pelo Mercado Pago',
  'cobranca.cancelarTitulo': 'Cancelar a assinatura',
  'cobranca.efeitoComPeriodo': 'Nenhuma nova cobrança será feita. O acesso já pago continua até',
  'cobranca.efeitoImediato': 'Nenhuma nova cobrança será feita, com efeito imediato.',
  'cobranca.dadosPreservados': 'Os dados da organização são preservados.',
  'cobranca.motivoOpcional': 'Motivo (opcional)',
  'cobranca.confirmoCancelamento': 'Confirmo o cancelamento da cobrança recorrente.',
  'cobranca.cancelar': 'Cancelar assinatura',
  'cobranca.pagamentos': 'Pagamentos',
  'cobranca.semPagamentos': 'Nenhum pagamento registrado.',
  'cobranca.historico': 'Histórico financeiro',
  'cobranca.semHistorico': 'Sem movimentação financeira.',
  'cobranca.per.MONTHLY': 'Mensal',
  'cobranca.per.ANNUAL': 'Anual',
  'cobranca.cs.CREATING': 'Iniciando no Mercado Pago',
  'cobranca.cs.AWAITING_AUTHORIZATION': 'Aguardando sua autorização no Mercado Pago',
  'cobranca.cs.AUTHORIZED': 'Autorizada, aguardando confirmação do pagamento',
  'cobranca.cs.ACTIVE': 'Paga e ativa',
  'cobranca.cs.PAST_DUE': 'Cobrança recusada',
  'cobranca.cs.PAUSED': 'Pausada',
  'cobranca.cs.CANCEL_REQUESTED': 'Cancelamento pendente de confirmação',
  'cobranca.cs.CANCELED': 'Cancelada',
  'cobranca.cs.FAILED': 'Não iniciada',
  'cobranca.ps.PENDING': 'Pendente',
  'cobranca.ps.IN_PROCESS': 'Em análise',
  'cobranca.ps.APPROVED': 'Aprovado',
  'cobranca.ps.REJECTED': 'Recusado',
  'cobranca.ps.CANCELLED': 'Cancelado',
  'cobranca.ps.REFUNDED': 'Reembolsado',
  'cobranca.ps.CHARGED_BACK': 'Contestado',
  'cobranca.ps.UNKNOWN': 'Em verificação',
  'plano.solicitar': 'Solicitar alteração',
  'plano.justificativa': 'Por que sua organização precisa deste plano?',
  'plano.registrarSolicitacao': 'Registrar solicitação',
  'plano.erroCarregarOpcoes': 'Não foi possível carregar as opções de plano.',
  'catalogo.usarModelo': 'Usar este modelo',
  'catalogo.modeloUsado':
    'Oferta criada a partir do modelo. Agora ela é sua: edite e precifique à vontade.',
  'medidas.titulo': 'Medidas da operação',
  'medidas.subtitulo':
    'Contagem do que aconteceu, com a fonte de cada número. Nada aqui é estimado.',
  'medidas.janela': 'Janela',
  'medidas.erro': 'Não consegui carregar as medidas.',
  'medidas.medida': 'Medida',
  'medidas.fonte': 'Fonte',
  'pipelines.titulo': 'Pipelines',
  'pipelines.acessoRestrito': 'Acesso restrito',
  'pipelines.acessoRestritoTexto':
    'A configuração de pipelines é exclusiva do administrador da plataforma.',
  'pipelines.configuracao': 'Configuração de pipelines',
  'pipelines.subtitulo':
    'Etapas, ordem, probabilidade e exigências por etapa. Nada disso é fixo no código.',
  'pipelines.vazio': 'Nenhum pipeline cadastrado',
  'pipelines.moverCima': 'Mover para cima',
  'pipelines.moverBaixo': 'Mover para baixo',
  'pipelines.probabilidade': 'Probabilidade (%)',
  'pipelines.sla': 'SLA (dias)',
  'pipelines.mensagemConfirmacao': 'Mensagem de confirmação exibida ao usuário',
  'pipelines.camposObrigatorios': 'Campos obrigatórios para entrar na etapa',
  'pipelines.acoesObrigatorias': 'Ações obrigatórias',
  'pipelines.exigirConfirmacao': 'Exigir confirmação ao mover para esta etapa',
  'pipelines.etapaAtiva': 'Etapa ativa',
  'regras.titulo': 'Regras comerciais',
  'regras.acessoRestrito': 'Acesso restrito',
  'regras.acessoRestritoTexto':
    'As regras comerciais são visíveis a gestão, auditoria e administração da plataforma.',
  'regras.escopo': 'Escopo da política',
  'regras.identificacao': 'Identificação desta vigência',
  'regras.vigenteAPartir': 'Vigente a partir de',
  'regras.semVigencia': 'Nenhuma vigência registrada',
  'regras.padraoGlobal': 'Padrão global (vale para todos)',
  'regras.confirmarVigencia': 'Confirmar nova vigência',
  'regras.voltarRevisar': 'Voltar e revisar',
  'regras.valoresEmVigor': 'Valores em vigor',
  'regras.sim': 'Sim',
  'regras.nao': 'Não',
  'regras.semLimite': 'Sem limite',
  'regras.historico': 'Histórico de vigências',
  'qualidade.titulo': 'Qualidade dos dados',
  'qualidade.subtitulo':
    'O que falta, o que está errado e o que está duplicado — com o que fazer em cada caso.',
  'qualidade.vazio': 'Nada pendente',
  'qualidade.vazioTexto':
    'As fichas da sua carteira estão completas, válidas e sem duplicidade aparente.',
  'qualidade.motivoReversao': 'Motivo da reversão',
  'qualidade.mesclagens': 'Mesclagens',
  'qualidade.mesclagensTexto':
    'Cada uma guarda o que moveu, um a um — é isso que torna a reversão possível.',
  'qualidade.porQueRevertendo': 'Por que está revertendo',
  'qualidade.reverter': 'Reverter',
  'recursos.titulo': 'Recursos e integrações',
  'recursos.subtitulo':
    'Situação real de cada capacidade. Nenhuma chave ou segredo aparece aqui — apenas se a dependência está satisfeita.',
  'recursos.assistente': 'Assistente inteligente',
  'recursos.quando': 'Quando',
  'recursos.resultado': 'Resultado',
  'recursos.modo': 'Modo',
  'recursos.tempo': 'Tempo',
  'recursos.acoes': 'Ações',
  'recursos.descartadas': 'Descartadas',
  'recursos.dependeDe': 'Depende de',
  'recursos.motivo': 'Motivo',
  'recursos.verificado': 'Verificado',
  'usuarios.novo': 'Novo usuário',
  'usuarios.novoTexto': 'O perfil define as permissões e o escopo de visibilidade.',
  'usuarios.identificacao': 'Identificação',
  'usuarios.senhaInicial': 'Senha inicial',
  'usuarios.perfilVinculo': 'Perfil e vínculo',
  'usuarios.perfil': 'Perfil',
  'usuarios.aoConfirmar': 'O que acontece ao confirmar',
  'usuarios.novaSenha': 'Nova senha',
  'usuarios.repitaSenha': 'Repita a senha',
  'usuarios.resetarSegundoFator': 'Resetar o segundo fator',
  'usuarios.titulo': 'Usuários',
  'usuarios.acessoRestrito': 'Acesso restrito',
  'usuarios.acessoRestritoTexto': 'A administração de usuários exige permissão específica.',
  'usuarios.usuariosPerfis': 'Usuários e perfis',
  'usuarios.subtitulo':
    'Perfis definem permissões e escopo. Desativar encerra as sessões na hora; reativar restaura o acesso.',
  'usuarios.buscar': 'Buscar por nome ou e-mail…',
  'usuarios.vazio': 'Nenhum usuário encontrado',
  'usuarios.senhaAdmin':
    'Senha definida por administrador; o titular precisa trocá-la no primeiro acesso.',
  'usuarios.nuncaTeveSenha': 'Nunca teve senha: sem ela, não consegue entrar.',
  'usuarios.segundoFatorAtivo':
    'Segundo fator ativo: além da senha, exige um código do aplicativo autenticador.',
  'usuarios.selecionePerfil': 'Selecione o perfil…',
  'usuarios.selecioneParceiro': 'Selecione o parceiro…',
  'usuarios.exigirTroca': 'Exigir troca no primeiro acesso',
  'usuarios.resetarDe': 'Resetar o segundo fator de',
  'usuarios.cadastroApagado': 'O cadastro do aplicativo autenticador é apagado.',
  'usuarios.codigosDeixamValer': 'Os códigos de recuperação existentes deixam de valer.',
  'usuarios.sessoesEncerradas': 'As sessões abertas dessa pessoa são encerradas agora.',
  'usuarios.organizacao': 'Organização',
  'usuarios.perfisInternos': 'Perfis internos',
  'usuarios.perfisCanal': 'Perfis de canal',
  'usuarios.senhaInicialDica':
    'Em branco (recomendado): o usuário recebe um convite e define a própria senha.',
  'usuarios.reenviarConvite': 'Reenviar convite',
  'usuarios.conviteEnviado': 'Convite enviado por e-mail.',
  'usuarios.conviteLinkTitulo': 'Entregue o link de acesso',
  'usuarios.conviteLinkTexto':
    'O envio de e-mail não está habilitado neste ambiente. Entregue este link de uso único ao usuário por um canal seguro — ele expira e deixa de valer depois de usado.',
  'usuarios.envioFalhouTexto':
    'O envio do e-mail falhou agora há pouco. O convite continua válido: entregue este link de uso único por um canal seguro, ou use “Reenviar convite” mais tarde.',
  'usuarios.linkDeConvite': 'Link do convite',
  'usuarios.copiarLink': 'Copiar link',
  'usuarios.linkCopiado': 'Link copiado.',
  'usuarios.copieManual': 'Não foi possível copiar automaticamente; selecione o texto e copie.',
  'usuarios.conviteExpira': 'O link expira em',
  'usuarios.alterarPerfil': 'Alterar perfil',
  'usuarios.alterarPerfilTexto':
    'As permissões mudam já na próxima ação do usuário, e a alteração fica registrada na auditoria.',
  'usuarios.perfilAlterado': 'Perfil alterado.',
  'usuarios.desativarTitulo': 'Desativar usuário',
  'usuarios.desativarTexto':
    'O usuário perderá o acesso imediatamente e as sessões abertas serão encerradas. A senha é preservada: reativar restaura o acesso como era.',
  'usuarios.reativarTitulo': 'Reativar usuário',
  'usuarios.reativarTexto':
    'O acesso volta imediatamente, com o perfil e a senha que o usuário já tinha. A reativação fica registrada na auditoria.',
  'usuarios.arquivarTitulo': 'Arquivar usuário',
  'usuarios.arquivarTexto':
    'O usuário será removido da operação, mas seu histórico será preservado. A credencial é destruída; restaurar do arquivo o devolve como desativado.',
  'usuarios.cancelarConviteTitulo': 'Cancelar convite',
  'usuarios.cancelarConviteTexto':
    'O link do convite deixa de valer e o usuário fica desativado, sem nunca ter tido acesso. Sem histórico, ele poderá ser excluído definitivamente.',
  'usuarios.restaurarTitulo': 'Restaurar do arquivo',
  'usuarios.restaurarTexto':
    'O usuário volta como desativado, ainda sem credencial e sem acesso. Reativar é uma decisão separada, com registro próprio.',
  'usuarios.excluirTitulo': 'Excluir definitivamente',
  'usuarios.excluirBotao': 'Excluir definitivamente',
  'usuarios.excluirElegivelTexto':
    'Esta operação é definitiva e só está disponível porque este usuário não possui histórico relevante no CERES.',
  'usuarios.excluirBloqueadoTitulo': 'Exclusão definitiva indisponível',
  'usuarios.excluirBloqueadoTexto':
    'Este usuário possui histórico no CERES e não pode ser excluído definitivamente. Você pode arquivá-lo, preservando a rastreabilidade.',
  'usuarios.apenasArquivar': 'Apenas arquivar',
  'usuarios.excluido': 'Usuário excluído definitivamente.',
  'usuarios.arquivado': 'Usuário arquivado. O histórico permanece.',
  'usuarios.impede': 'impede',
  'usuarios.semVinculos': 'Nenhum vínculo encontrado: este usuário nunca operou no CERES.',
  'usuarios.senhaEfeitos':
    'Todas as sessões abertas desta pessoa são encerradas na hora, e qualquer link de convite ou redefinição pendente deixa de valer.',
  'usuarios.convidadoAtiva': 'O usuário convidado passa a ativo.',
  'usuarios.senhaCanalSeguro': 'Combine a senha por um canal seguro — o sistema nunca a envia.',
  'usuarios.exigenciaContinua':
    'A exigência CONTINUA: ela entra com a senha e o acesso fica limitado ao cadastro do fator até concluir.',
  'usuarios.cadastraQuandoQuiser':
    'Ela poderá entrar apenas com a senha, e cadastrar o fator quando quiser.',
  'usuarios.motivoDica':
    'Ex.: “Titular perdeu o celular e não tem os códigos de recuperação; identidade confirmada por videochamada.” Fica na auditoria.',
  'usuarios.segundoFatorPendente':
    'O perfil exige segundo fator e ele ainda não foi cadastrado. A pessoa consegue entrar: o acesso fica limitado ao próprio cadastro do fator até ela concluir.',
  'mfa.titulo': 'Segundo fator de autenticação',
  'mfa.qrLabel': 'Código QR para cadastrar o segundo fator no aplicativo autenticador',
  'mfa.codigoApp': 'Código que o aplicativo está mostrando',
  'mfa.renovarCodigos': 'Renovar os códigos de recuperação',
  'mfa.confirmeSenha': 'Confirme sua senha para remover o segundo fator',
  'mfa.guardeCodigos': 'Guarde estes códigos agora',
  'mfa.copiarTodos': 'Copiar todos',
  'mfa.imprimir': 'Imprimir',
  'mfa.guardeiCodigos': 'Guardei os códigos em local seguro, fora deste computador.',
  'mfa.concluir': 'Concluir',
  'mfa.cadastreApp': 'Cadastre no aplicativo autenticador',
  'mfa.aponteCamera': 'Aponte a câmera para o código abaixo e confirme com o código de 6 dígitos.',
  'mfa.naoConsegueEscanear': 'Não consegue escanear? Digitar a chave',
  'mfa.chave': 'Chave',
  'mfa.copiarChave': 'Copiar chave',
  'mfa.copiarOtpauth': 'Copiar endereço otpauth',
  'mfa.segundoFatorAtivo': 'Segundo fator ativo',
  'mfa.seuPerfil': 'Seu perfil',
  'mfa.removerSegundoFator': 'Remover o segundo fator',
  'mfa.aindaNaoCadastrado': 'Ainda não cadastrado',
  'legal.idiomaDocumento': 'Idioma do documento',
  'legal.documentos': 'Documentos legais',
  'status.titulo': 'Status do CERES CRM',
  'status.verificadoAgora':
    'Verificado agora, quando você abriu esta página. Não é um histórico de incidentes.',
  'status.consultando': 'Consultando o sistema…',
  'status.respondendo': 'A aplicação está respondendo.',
  'status.banco': 'Banco de dados',
  'status.versaoNoAr': 'Versão no ar',
  'status.ambiente': 'Ambiente',
  'status.semResposta': 'Sem resposta.',
  'status.semRespostaTexto':
    'Pode ser a aplicação, pode ser a sua conexão — desta página não dá para distinguir as duas, e afirmar uma delas seria chute. Tente recarregar; se continuar, fale com o suporte.',
  'status.indisponivel': 'Serviço indisponível.',
  'status.indisponivelTexto':
    'O sistema respondeu, mas não está pronto para atender agora: o banco de dados não está acessível. Tente de novo em alguns minutos; se continuar, fale com o suporte.',
  'sobre.oQueFaz': 'O que ele faz',
  'sobre.oQueNaoFaz': 'O que ele não faz',
  'sobre.comoSeEntra': 'Como se entra',
  'sobre.faz1':
    'Organiza a venda consultiva: empresas, pessoas, oportunidades, propostas, contratos e o que ficou combinado em cada conversa.',
  'sobre.faz2':
    'Executa o que você pede em português — e mostra o que vai fazer antes de gravar, com confirmação sua.',
  'sobre.faz3':
    'Explica os números. Previsão, comissão, preço e prioridades vêm com a conta à vista, e não como um total que ninguém consegue conferir.',
  'sobre.faz4':
    'Registra quem fez o quê, quando e com qual permissão — inclusive o que a inteligência artificial fez.',
  'sobre.naoFaz1': 'Não grava nada sem confirmação humana quando a ação altera dados.',
  'sobre.naoFaz2': 'Não envia mensagem para quem pediu para não ser contatado.',
  'sobre.naoFaz3':
    'Não finge entrega: sem credencial de provedor, a mensagem fica registrada e a tela diz que ela não saiu.',
  'sobre.naoFaz4': 'Não é uma loja: ele fala em esforço, contrato, recorrência e parceria.',
  'sobre.comoSeEntraTexto':
    'As contas são criadas por quem administra a organização — não há cadastro aberto. Se você deveria ter acesso e ainda não tem, fale com quem administra o CERES CRM na sua empresa.',
  'sobre.entrar': 'Entrar',
  'sobre.falarConosco': 'Falar com a gente',
  'acessibilidade.titulo': 'Acessibilidade',
  'acessibilidade.verificado': 'O que está verificado',
  'acessibilidade.naoVerificado': 'O que ainda não está verificado',
  'acessibilidade.barreira': 'Encontrou uma barreira?',
  'contato.titulo': 'Contato e suporte',
  'contato.jaUsa': 'Se você já usa o sistema',
  'contato.falarBugKillers': 'Para falar com a BugKillers',
  'contato.ajudaResolver': 'O que ajuda a resolver mais rápido:',
  'contato.segurancaPrivacidade': 'Segurança e privacidade',
  'aceite.condicaoDeUso':
    'Estes documentos são condição de uso da plataforma. Leia cada um e confirme o aceite. O registro guarda a versão exata e o texto que você leu.',
  'acessibilidade.intro':
    'O CERES CRM busca conformidade com as WCAG 2.2, nível AA. Esta página diz onde estamos, com a mesma honestidade dos dois lados.',
  'acessibilidade.cabecalhoNivel1':
    'Cada tela tem um cabeçalho de nível 1, e a hierarquia de títulos é verificada.',
  'acessibilidade.bateriaAxe':
    'As telas passam por uma bateria automatizada de regras (axe): alternativa em imagem, atributos',
  'acessibilidade.contrasteTexto':
    'A verificação automatizada roda sem renderização real, então a regra de contraste ainda não foi executada de fato.',
  'acessibilidade.tabulacao': 'Ordem de tabulação e foco visível',
  'acessibilidade.tabulacaoTexto': 'em todas as telas, com teclado, por uma pessoa.',
  'acessibilidade.leitorTela': 'Leitor de tela nos fluxos críticos',
  'acessibilidade.zoomTexto': 'em todas as telas de tabela.',
  'acessibilidade.naoGarantidas':
    'Enquanto estiverem nesta lista, considere-as não garantidas. Preferimos dizer isso a descobrir junto com você.',
  'acessibilidade.descreva':
    'Descreva o que você tentou fazer e o que aconteceu — inclusive a tecnologia assistiva que usa. Isso vira caso de trabalho, e não um agradecimento.',
  'admin.semPermissao':
    'Seu perfil não tem nenhuma permissão administrativa. Se precisar de alguma, fale com quem administra os acessos.',
  'agentes.killSwitchGlobal':
    'O kill switch global está ligado nas regras comerciais. Nenhum agente responde, e o estado individual abaixo não muda isso.',
  'trocarSenha.explicacao':
    'Sua senha atual foi definida por um administrador. Escolha uma senha que só você conheça — até então, o acesso ao sistema fica bloqueado.',
  'contato.situacaoValida':
    'Isso é uma situação válida — indicações e contatos autônomos entram assim. Para criar oportunidade a partir dele, associe uma empresa primeiro.',
  'contato.associeEmpresa':
    'associe uma empresa para poder criar uma oportunidade — ou agende o retorno agora e complete o cadastro depois.',
  'contato.registreConversa': 'registre a próxima conversa como atividade na ficha da empresa.',
  'contato.faleAdministrador':
    'Fale primeiro com quem administra o CERES CRM na sua organização: permissão, perfil, acesso a uma carteira e configuração comercial são decisões que pertencem a ela, e chegar por lá é mais rápido do que passar por nós.',
  'contato.oQueTentou': 'o que você tentou fazer, e o que apareceu no lugar;',
  'contato.codigoCurto':
    'o código curto que aparece na mensagem de erro, quando houver — é por ele que achamos o registro exato.',
  'demo.marcaDoLote':
    'Cada registro criado aqui recebe a marca do lote de demonstração, e a remoção usa exclusivamente essa marca — nunca nome, e-mail, domínio ou prefixo. Um cliente real chamado “Demonstração Tecnologia” jamais seria alcançado.',
  'demo.apenasReais':
    'A base contém apenas dados reais. Carregue a demonstração quando precisar mostrar o sistema funcionando — e remova depois, em um clique.',
  'demo.somenteDemo':
    'Esta tela remove apenas registros de demonstração. Esvaziar a base comercial é procedimento técnico executado no servidor, com backup verificado exigido antes — deliberadamente, não existe botão para isso aqui.',
  'demo.naoDesfeita':
    'A operação não pode ser desfeita pela interface. A trilha de auditoria permanece: fica registrado o que foi carregado, quando foi removido e por quem.',
  'ajuda.roteiroPrimeiroDia':
    'O roteiro do primeiro dia leva você de uma base vazia até um follow-up agendado em seis passos. Se preferir, use a busca acima — ela procura no texto completo de todos os artigos.',
  'integracoes.naoExibidoDeNovo':
    'Ele não é exibido de novo: guardá-lo em claro para poder reexibir seria o mesmo que não ter segredo. Se perder, rotacione.',
  'integracoes.porCapacidade':
    'Cada adaptador declara CAPACIDADE, e não nome de fornecedor: é isso que torna a substituição possível sem tocar em domínio.',
  'entrar.recuperacaoEnviada':
    'Se houver uma conta ativa com este e-mail, você receberá em instantes um link para redefinir a senha. O link vale por 30 minutos e só pode ser usado uma vez. Confira também a caixa de spam.',
  'entrar.recuperacaoTexto':
    'Se houver uma conta correspondente, siga o procedimento interno de recuperação ou procure um administrador autorizado. Nesta etapa o sistema não envia e-mail: quem administra define a sua senha diretamente.',
  'perfil.exigeSegundoFator':
    'Seu perfil exige segundo fator. Enquanto ele não for cadastrado, o acesso fica restrito.',
  'segundoFator.semOsDois':
    'Sem os dois — aplicativo e códigos de recuperação — apenas a administração da plataforma pode liberar o acesso, e o pedido fica registrado.',
  'mfa.versaoIrreversivel':
    'que eles aparecem. Depois disto o sistema guarda apenas uma versão irreversível — nem você nem a administração conseguem lê-los de novo. Se você perder o celular e não tiver estes códigos, só um reset administrativo devolve seu acesso.',
  'mfa.naoConfigurado':
    'O segundo fator não está configurado neste ambiente e não pode ser cadastrado agora. Acione a administração da plataforma.',
  'mfa.instaleApp':
    'Instale um aplicativo autenticador no celular — Google Authenticator, Microsoft Authenticator, Authy ou o gerenciador de senhas que você já usa.',
  'mfa.escaneie':
    'Escaneie o código com o aplicativo. Ele preenche tudo sozinho — conta, emissor e chave.',
  'mfa.restamPoucos':
    'Restam poucos códigos de recuperação. Renove a lista antes de ficar sem — sem eles e sem o celular, só um reset administrativo devolve seu acesso.',
  'mfa.naoPodeRemover':
    'segundo fator, então ele não pode ser removido. Para trocar de aplicativo sem ter acesso ao código atual, peça um reset à administração da plataforma — a operação fica registrada com o motivo.',
  'mfa.duasEtapas':
    'Você vai precisar de um aplicativo autenticador no celular. O cadastro tem duas etapas: digitar uma chave no aplicativo e confirmar com o código que ele mostrar. Ao final, você recebe códigos de recuperação para o caso de perder o aparelho.',
  'mfa.acessoLimitado':
    'Enquanto o cadastro não estiver concluído, seu acesso fica limitado a esta tela. Não é bloqueio de conta: é a exigência do seu perfil, e ela sai daqui assim que você cadastrar.',
  'parceiro.valoresParametrizados':
    'Valores parametrizados, com vigência. O cálculo de comissão não pertence a esta etapa.',
  'pipelines.desativarPreserva':
    'Desativar um pipeline ou etapa preserva todo o histórico. Uma etapa em uso por oportunidades não pode ser removida.',
  'regras.reservasCongeladas': 'Reservas já aprovadas mantêm os parâmetros congelados',
  'regras.congeladasTexto':
    'no momento da aprovação e não são alteradas por esta mudança — o novo valor passa a valer para as próximas decisões.',
  'regras.quemAltera':
    'Quem administra a plataforma altera; gestão e auditoria consultam. Parceiro não configura a própria política em nenhuma hipótese — definiria o próprio limite de contas.',
  'preferencias.guiaTexto': 'O guia de primeiro acesso. Reabrir não apaga o que você já concluiu.',
  'receita.semDataTexto':
    'Encaixá-las no mês inflaria o forecast com o que ninguém prometeu — e a sem data é justamente a que ninguém está conduzindo. Elas viram fila de higiene, não número.',
  'receita.commitTexto':
    'O modelo sugere; quem responde pelo número o assina. Mudar de 300 para 250 na sexta-feira é um fato — e o histórico dele é o que permite discutir o viés de quem promete.',
  'reserva.parametrosCongelados':
    'Parâmetros congelados na aprovação. Alterações posteriores da política não afetam esta reserva.',
  'reservas.comprovarAtividade':
    'Após a aprovação, é necessário comprovar atividade efetiva no prazo definido pela política vigente. Nome cadastrado não ativa a reserva.',
  'reunioes.coachingSeu':
    'É seu, e só seu. O gestor vê o agregado da equipe; a conversa individual é de quem a teve.',
  'usuarios.senhaTerceiro':
    'Recomendado: uma senha escolhida por terceiro não deveria virar a senha definitiva de ninguém. Até a troca, a pessoa entra mas não acessa o restante do sistema.',

  // ---- Catálogo Comercial (CERES-004) ----
  'nav.catalogoComercial': 'Catálogo comercial',
  'catComercial.titulo': 'Catálogo Comercial',
  'catComercial.subtitulo':
    'Os produtos e serviços que a sua empresa oferece, com preço de referência.',
  'catComercial.novo': 'Novo item',
  'catComercial.buscar': 'Buscar por nome, código ou categoria...',
  'catComercial.filtrarTipo': 'Filtrar por tipo',
  'catComercial.todosTipos': 'Todos os tipos',
  'catComercial.filtrarSituacao': 'Filtrar por situação',
  'catComercial.todasSituacoes': 'Ativos e inativos',
  'catComercial.ativos': 'Ativos',
  'catComercial.inativos': 'Inativos',
  'catComercial.vazioTitulo': 'Seu catálogo comercial ainda está vazio',
  'catComercial.vazioTexto':
    'Cadastre os produtos e serviços que sua empresa comercializa. Eles serão utilizados automaticamente no Playbook, nas Oportunidades e nas Propostas.',
  'catComercial.cadastrarPrimeiro': 'Cadastrar primeiro item',
  'catComercial.vazioBuscaTitulo': 'Nenhum item encontrado',
  'catComercial.vazioBuscaTexto': 'Ajuste a busca ou os filtros para encontrar o item.',
  'catComercial.colItem': 'Item',
  'catComercial.colModelo': 'Modelo comercial',
  'catComercial.colPrecoBase': 'Preço de referência',
  'catComercial.colCategoria': 'Categoria',
  'catComercial.colUso': 'Uso',
  'catComercial.ativo': 'Ativo',
  'catComercial.inativo': 'Inativo',
  'catComercial.editar': 'Editar item',
  'catComercial.novoTitulo': 'Novo item do catálogo',
  'catComercial.nome': 'Nome',
  'catComercial.codigo': 'Código interno',
  'catComercial.tipo': 'Tipo',
  'catComercial.categoria': 'Categoria',
  'catComercial.descricao': 'Descrição',
  'catComercial.modelo': 'Modelo comercial',
  'catComercial.unidade': 'Unidade',
  'catComercial.precoBase': 'Preço de referência',
  'catComercial.precoBaseDica':
    'É o preço sugerido. A negociação de cada oportunidade guarda a própria condição.',
  'catComercial.periodicidade': 'Periodicidade',
  'catComercial.qtdPadrao': 'Quantidade padrão',
  'catComercial.periodosPadrao': 'Duração padrão (períodos)',
  'catComercial.horasEstimadas': 'Horas estimadas',
  'catComercial.horasEstimadasDica': 'Informativas: não participam do cálculo do preço.',
  'catComercial.itemAtivo': 'Item ativo no catálogo',
  'catComercial.avisoPrecoBase':
    'Alterações no preço de referência não alteram negociações existentes.',
  'catComercial.salvar': 'Salvar item',
  'catComercial.identificacao': 'Identificação',
  'catComercial.precificacao': 'Precificação',
  'catComercial.sugestoes': 'Sugestões para a oportunidade',
  // ---- Itens da oportunidade (CERES-004) ----
  'itensOpp.titulo': 'Itens da oportunidade',
  'itensOpp.adicionar': 'Adicionar item',
  'itensOpp.vazioTitulo': 'O que estamos oferecendo?',
  'itensOpp.vazioTexto': 'Adicione itens do catálogo para compor o valor desta oportunidade.',
  'itensOpp.precoReferencia': 'Preço de referência',
  'itensOpp.precoNegociado': 'Preço negociado',
  'itensOpp.variacao': 'Variação',
  'itensOpp.quantidade': 'Quantidade',
  'itensOpp.duracao': 'Duração (períodos)',
  'itensOpp.subtotal': 'Subtotal',
  'itensOpp.total': 'Total da oportunidade',
  'itensOpp.remover': 'Remover item',
  'itensOpp.editarItem': 'Editar item da oportunidade',
  'itensOpp.selecioneItem': 'Selecione um item do catálogo',
  'itensOpp.motivo': 'Motivo da condição comercial',
  'itensOpp.motivoDica': 'Opcional. Ajuda a entender a negociação depois.',
  'itensOpp.semPermissaoPreco': 'Você não tem permissão para alterar o preço negociado.',
  'itensOpp.valorManualAviso': 'Esta oportunidade possui valor informado manualmente.',
  'itensOpp.valorManualExplicacao':
    'Os itens abaixo não alteram o valor exibido até você decidir usar a soma dos itens.',
  'itensOpp.usarSomaDosItens': 'Usar a soma dos itens',
  'itensOpp.voltarParaManual': 'Voltar para valor manual',
  'itensOpp.fonteItens': 'Valor calculado pelos itens',
  'itensOpp.catalogoVazio':
    'Nenhum item disponível no catálogo. Cadastre itens no Catálogo Comercial primeiro.',
  'itensOpp.confirmarRemocao': 'Remover este item da oportunidade?',
  'itensOpp.confirmarRemocaoTexto': 'O total será recalculado. O item do catálogo não é afetado.',

  // CERES-034 — Composição Comercial, condições e alçada.
  'composicao.titulo': 'Composição comercial',
  'composicao.valorReferencia': 'Valor de referência',
  'composicao.valorNegociado': 'Valor negociado',
  'composicao.descontoTotal': 'Desconto total',
  'composicao.maiorDesconto': 'Maior desconto de item',
  'composicao.desconto': 'Desconto',
  'composicao.subtotalReferencia': 'Subtotal de referência',
  'composicao.subtotalNegociado': 'Subtotal negociado',
  'composicao.avisoPrecoManual': 'Preço definido sem referência do catálogo.',

  'condicoes.titulo': 'Condições comerciais',
  'condicoes.validade': 'Validade comercial',
  'condicoes.pagamento': 'Condição de pagamento',
  'condicoes.prazo': 'Prazo de execução',
  'condicoes.modeloCobranca': 'Modelo de cobrança',
  'condicoes.periodicidade': 'Periodicidade',
  'condicoes.moeda': 'Moeda',
  'condicoes.observacoes': 'Observações comerciais',
  'condicoes.avisoMaterial':
    'Alterar estas condições muda o que foi prometido ao cliente e invalida uma aprovação já concedida.',

  'aprovacao.dentroDaAlcada': 'Desconto dentro da sua alçada de {alcada}.',
  'aprovacao.dentroDaAlcadaDetalhe': 'Esta composição não depende de aprovação para seguir.',
  'aprovacao.aprovacaoSubstituidaSemNecessidade':
    'A composição mudou e passou a caber na sua alçada. A aprovação anterior ficou registrada no histórico.',
  'aprovacao.exigeAprovacao':
    'O desconto de {percentual} exige aprovação de um responsável com alçada igual ou superior.',
  'aprovacao.exigeAprovacaoDetalhe':
    'Você pode salvar este cenário e pedir aprovação. Enquanto ela não vier, o cenário não está autorizado.',
  'aprovacao.alteradaDepoisDaAprovacao':
    'Esta composição foi alterada depois da aprovação e precisa ser submetida novamente.',
  'aprovacao.precoManual': 'Preço manual sem referência requer aprovação comercial.',
  'aprovacao.aguardando': 'Aguardando aprovação.',
  'aprovacao.aguardandoDetalhe':
    'O pedido de {percentual} está com quem decide. O cenário ainda não está autorizado.',
  'aprovacao.aprovada': 'Aprovação concedida para este cenário.',
  'aprovacao.aprovadaDetalhe':
    'Qualquer alteração de item, preço ou condição depois daqui invalida esta aprovação.',
  'aprovacao.ajustesSolicitados': 'Ajustes solicitados.',
  'aprovacao.ajustesSolicitadosDetalhe': 'Quem decidiu pediu mudanças antes de autorizar.',
  'aprovacao.recusada': 'Aprovação recusada.',
  'aprovacao.recusadaDetalhe': 'Este cenário não foi autorizado.',
  'aprovacao.solicitar': 'Solicitar aprovação',
  'aprovacao.enviarPedido': 'Enviar pedido',
  'aprovacao.decidir': 'Decidir',
  'aprovacao.registrarDecisao': 'Registrar decisão',
  'aprovacao.historico': 'Histórico',
  'aprovacao.justificativa': 'Por que este cenário precisa de exceção',
  'aprovacao.justificativaDica': 'Quem decide precisa saber por que este desconto, neste negócio.',
  'aprovacao.descontoExigido': 'Desconto a autorizar',
  'aprovacao.suaAlcada': 'Sua alçada',
  'aprovacao.alcadaIlimitada': 'ilimitada',
  'aprovacao.solicitante': 'Solicitante',
  'aprovacao.decisao': 'Decisão',
  'aprovacao.decisaoAprovar': 'Aprovar',
  'aprovacao.decisaoAjustes': 'Solicitar ajustes',
  'aprovacao.decisaoRecusar': 'Recusar',
  'aprovacao.comentario': 'Comentário',
  'aprovacao.comentarioObrigatorio': 'Obrigatório ao recusar ou pedir ajustes.',
  'aprovacao.pedidoPor': 'Pedido por {pessoa} em {quando}.',
  'aprovacao.linhaDoPedido':
    '{pessoa} pediu autorização de {percentual} — referência {referencia}, negociado {negociado}.',
  'aprovacao.linhaDaDecisao': 'Decidido por {pessoa} em {quando}',
  'aprovacao.substituida': 'A composição mudou depois desta decisão. Ela deixou de valer.',
  'aprovacao.estado.NOT_REQUIRED': 'Aprovação não necessária',
  'aprovacao.estado.DRAFT': 'Em edição',
  'aprovacao.estado.PENDING': 'Aguardando aprovação',
  'aprovacao.estado.APPROVED': 'Aprovada',
  'aprovacao.estado.CHANGES_REQUESTED': 'Ajustes solicitados',
  'aprovacao.estado.REJECTED': 'Recusada',
  'aprovacao.estado.SUPERSEDED': 'Substituída por composição posterior',

  'alcadas.titulo': 'Alçadas de desconto',
  'alcadas.descricao':
    'Até quanto cada perfil pode descontar sem pedir autorização. Sem regra escrita, a alçada é zero e todo desconto precisa de aprovação.',
  'alcadas.perfil': 'Perfil',
  'alcadas.pessoa': 'Pessoa',
  'alcadas.teto': 'Teto de desconto',
  'alcadas.ilimitada': 'Alçada ilimitada',
  'alcadas.situacao': 'Situação',
  'alcadas.ativa': 'Vigente',
  'alcadas.encerrada': 'Encerrada',
  'alcadas.nova': 'Nova alçada',
  'alcadas.alvo': 'Vale para',
  'alcadas.alvoPerfil': 'Um perfil',
  'alcadas.alvoPessoa': 'Uma pessoa (exceção)',
  'alcadas.observacao': 'Observação',
  'alcadas.encerrar': 'Encerrar',
  'alcadas.confirmarEncerrar': 'Encerrar esta alçada?',
  'alcadas.confirmarEncerrarTexto':
    'A regra deixa de valer a partir de agora e permanece no histórico. Decisões já tomadas não mudam.',
  'alcadas.vazio': 'Nenhuma alçada configurada.',
  'alcadas.vazioTexto': 'Enquanto não houver regra, nenhum desconto é autorizado sem aprovação.',
  'alcadas.excecaoNominal': 'A exceção da pessoa substitui a alçada do perfil dela.',
  // CERES-035 — gate separado, alçada sem cargo técnico, edição de alçada.
  'composicao.valorInformado': 'Valor informado',
  'composicao.manualTitulo': 'Esta oportunidade compõe o valor manualmente.',
  'composicao.manualTexto':
    'As condições comerciais e a situação da aprovação abaixo continuam valendo.',
  'aprovacao.semAprovadorElegivel':
    'Nenhuma pessoa desta organização tem alçada suficiente — a maior configurada é de {maxima}. Defina um aprovador com alçada suficiente nas Regras comerciais.',
  'aprovacao.semPoliticaDeAlcada':
    'Esta organização ainda não configurou alçadas de desconto. Defina um aprovador com alçada suficiente nas Regras comerciais.',
  'alcadas.editar': 'Editar alçada',
  'alcadas.alvoTravado':
    'O perfil ou a pessoa não muda numa edição. Para trocar, encerre esta alçada e crie a nova.',
  'alcadas.ilimitadaExplicacao':
    'Alçada ilimitada é uma autorização comercial: quem a recebe responde por qualquer desconto, inclusive por preço definido sem referência do catálogo. Ela não acompanha o perfil de administração da plataforma.',
  'alcadas.justificativa': 'Justificativa da alteração',
  'alcadas.justificativaDica':
    'Opcional. Fica na trilha de auditoria, junto do valor anterior e do novo.',
  'alcadas.avisoVigencia':
    'Salvar encerra a alçada atual e passa a valer a nova. A anterior permanece no histórico, e as decisões já tomadas sob ela não mudam.',
  // =========================================================================
  // CRM-VAL-001 a CRM-VAL-005
  // =========================================================================
  'catComercial.codigoDica': 'Gerado automaticamente. Você pode alterar se quiser.',
  'catComercial.colCodigo': 'Código',
  'catComercial.acoes': 'Ações',
  'catComercial.abrirAcoes': 'Abrir ações do item',
  'catComercial.abrir': 'Abrir',
  'catComercial.abrirPlaybook': 'Abrir no Playbook',
  'catComercial.desativar': 'Desativar',
  'catComercial.reativar': 'Reativar',
  'catComercial.excluir': 'Excluir',
  'catComercial.excluirTitulo': 'Excluir este item?',
  'catComercial.excluirTexto':
    'Este item nunca foi usado em registro comercial. A exclusão é definitiva.',
  'catComercial.excluido': 'Item excluído.',
  'catComercial.desativado': 'Item desativado.',
  'catComercial.reativado': 'Item reativado.',
  'catComercial.emUsoTitulo': 'Este item já foi utilizado',
  'catComercial.emUsoTexto':
    'Para preservar o histórico comercial, ele não pode ser excluído. Desativar o retira das sugestões de novos negócios e mantém os registros antigos intactos.',
  'catComercial.emUsoOportunidades': 'oportunidade(s)',
  'catComercial.emUsoPropostas': 'proposta(s)',
  'catComercial.emUsoMateriais': 'material(is)',
  'catComercial.emUsoComissoes': 'comissão(ões)',
  'catComercial.cancelar': 'Cancelar',
  'catComercial.confirmarDesativar': 'Desativar item',
  'catComercial.confirmarExcluir': 'Excluir definitivamente',
  'catComercial.voltar': 'Catálogo Comercial',
  'catComercial.dadosDoCatalogo': 'Dados do catálogo',
  'catComercial.conhecimentoComercial': 'Conhecimento comercial',
  'catComercial.conhecimentoVazio':
    'Ainda não há conhecimento comercial cadastrado para este item.',
  'catComercial.abrirPlaybookCompleto': 'Abrir Playbook completo',
  'catComercial.editarConteudo': 'Editar conteúdo comercial',
  'catComercial.propostaDeValor': 'Proposta de valor',
  'catComercial.pitchCurto': 'Pitch curto',
  'catComercial.objecoes': 'Objeções',
  'catComercial.cases': 'Cases',
  'catComercial.materiais': 'Materiais',
  'catComercial.perguntas': 'Perguntas de descoberta',
  'catComercial.faq': 'FAQ',
  'catComercial.editarItem': 'Editar item',
  'catComercial.usoDetalhe': 'Onde este item é usado',
  'catComercial.nuncaUsado': 'Nunca usado em registro comercial',
  'parceiros.novo': 'Novo parceiro',
  'parceiros.cadastrarPrimeiro': 'Cadastrar primeiro parceiro',
  'parceiros.tipo': 'Tipo',
  'parceiros.todosTipos': 'Pessoas e empresas',
  'parceiros.modalidade': 'Modalidade',
  'parceiros.todasModalidades': 'Todas as modalidades',
  'parceiros.documento': 'Documento',
  'parceiro.novoTitulo': 'Novo parceiro comercial',
  'parceiro.editarTitulo': 'Editar parceiro',
  'parceiro.tipoPessoa': 'Uma pessoa: representante, consultor, indicador.',
  'parceiro.tipoEmpresa': 'Uma empresa: consultoria, agência, revenda, canal.',
  'parceiro.nomePessoa': 'Nome completo',
  'parceiro.nomeEmpresa': 'Razão social',
  'parceiro.nomeFantasia': 'Nome fantasia',
  'parceiro.cpf': 'CPF',
  'parceiro.cnpj': 'CNPJ',
  'parceiro.modalidade': 'Modalidade da parceria',
  'parceiro.modalidadeOutra': 'Qual modalidade?',
  'parceiro.empresaDeOrigem': 'Empresa de origem',
  'parceiro.empresaDeOrigemDica': 'A empresa a partir da qual esta pessoa atua, quando houver.',
  'parceiro.contatoVinculado': 'Contato vinculado',
  'parceiro.jaExiste': 'Já existe no CERES',
  'parceiro.jaExisteTexto':
    'Encontramos registros parecidos. Vincular evita cadastro em duplicidade.',
  'parceiro.vincular': 'Vincular',
  'parceiro.comoParceiro': 'já é parceiro',
  'parceiro.comoContato': 'contato',
  'parceiro.comoEmpresa': 'empresa',
  'parceiro.comoUsuario': 'usuário',
  'parceiro.salvar': 'Salvar parceiro',
  'parceiro.criado': 'Parceiro cadastrado.',
  'parceiro.atualizado': 'Parceiro atualizado.',
  'parceiro.editar': 'Editar',
  'parceiro.observacoes': 'Observações',
  'parceiro.status': 'Situação',
  'parceiro.responsavelInterno': 'Responsável interno',
  'parceiro.identificacao': 'Identificação',
  'parceiro.contato': 'Contato',
  'parceiro.vinculos': 'Vínculos',
  'parceiro.abaVisaoGeral': 'Visão geral',
  'parceiro.abaRegras': 'Regras comerciais',
  'parceiro.abaContas': 'Contas e oportunidades',
  'parceiro.abaComissoes': 'Comissões',
  'parceiro.abaContratos': 'Contratos',
  'parceiro.abaDocumentos': 'Documentos',
  'parceiro.regrasTitulo': 'Regras comerciais vigentes',
  'parceiro.novaVigencia': 'Nova vigência',
  'parceiro.personalizarRegras': 'Personalizar regras deste parceiro',
  'parceiro.verHistorico': 'Ver histórico',
  'parceiro.vigenciaDe': 'Vigente de',
  'parceiro.vigenciaAte': 'até',
  'parceiro.vigenciaIndeterminada': 'sem término',
  'parceiro.vigente': 'Vigente',
  'parceiro.encerrada': 'Encerrada',
  'parceiro.futura': 'Futura',
  // CRM-VAL-009 — taxonomia de tags
  'tags.titulo': 'Tags',
  'tags.subtitulo':
    'A taxonomia da sua empresa: uma tag existe uma vez e vale para empresas, contatos, oportunidades, atividades e parceiros.',
  'tags.nova': 'Nova tag',
  'tags.buscar': 'Buscar tag...',
  'tags.vazioTitulo': 'Nenhuma tag ainda',
  'tags.vazioDescricao': 'Crie a primeira aqui, ou direto na ficha de uma empresa ou oportunidade.',
  'tags.colTag': 'Tag',
  'tags.colGrupo': 'Grupo',
  'tags.colUso': 'Uso',
  'tags.colEntidades': 'Entidades',
  'tags.colSituacao': 'Situação',
  'tags.colAcoes': 'Ações',
  'tags.escopoTodas': 'Todas',
  'tags.situacaoAtiva': 'Ativa',
  'tags.situacaoInativa': 'Inativa',
  'tags.situacaoMesclada': 'Mesclada',
  'tags.editar': 'Editar',
  'tags.desativar': 'Desativar',
  'tags.reativar': 'Reativar',
  'tags.mesclar': 'Mesclar',
  'tags.excluir': 'Excluir',
  'tags.cancelar': 'Cancelar',
  'tags.salvar': 'Salvar',
  'tags.campoNome': 'Nome',
  'tags.campoGrupo': 'Grupo',
  'tags.grupoAjuda': 'Opcional. Ajuda a organizar: Tema, Evento, Campanha, Estratégia.',
  'tags.campoCor': 'Cor',
  'tags.semCor': 'Sem cor',
  'tags.campoEscopo': 'Aplicável a',
  'tags.escopoAjuda': 'Nenhuma marcada = vale para todas as entidades.',
  'tags.mesclarAviso':
    'As marcações da tag atual passam para a de destino. As que o destino já tinha no mesmo registro são descartadas — é a duplicidade sendo eliminada, não perda. A tag atual fica registrada apontando para o destino.',
  'tags.mesclarDestino': 'Mesclar nesta tag',
  'tags.migradas': 'marcações migradas',
  'tags.jaTinham': 'já existiam no destino',
  'tags.adicionar': 'Tag',
  'tags.remover': 'Remover',
  'tags.nenhuma': 'Sem tags',
  'tags.digite': 'Digite para buscar...',
  'tags.comeceADigitar': 'Digite para buscar entre as tags existentes.',
  'tags.criar': 'Criar',
  'tags.jaExistia': 'Essa tag já existia — usamos a que estava lá.',
  'tags.semPermissaoCriar': 'Nenhuma tag encontrada. Criar tags novas é com a gestão.',
  'tags.falhou': 'Não foi possível concluir.',
  'tags.entidadeEmpresas': 'Empresas',
  'tags.entidadeContatos': 'Contatos',
  'tags.entidadeOportunidades': 'Oportunidades',
  'tags.entidadeAtividades': 'Atividades',
  'tags.entidadeParceiros': 'Parceiros',
  // CRM-VAL-008 — pipeline e etapa
  'oportunidade.pipelineEtapa': 'Pipeline / Etapa',
  'oportunidade.nestaEtapa': 'nesta etapa',
  'oportunidade.trilhaDoFunil': 'Trilha do funil',
  'oportunidade.etapaAtual': 'Etapa atual',
  'oportunidade.etapaConcluida': 'Etapa anterior',
  'oportunidade.etapaFutura': 'Próxima etapa',
  'parceiro.origemGlobal': 'Regra padrão',
  'parceiro.origemOrganizacao': 'Padrão da empresa',
  'parceiro.origemParceiro': 'Específica deste parceiro',
  'parceiro.origemContrato': 'Definida no contrato',
  'parceiro.remuneracao': 'Modalidade de remuneração',
  'parceiro.comissoesPercentuais': 'Percentuais de comissão',
  'parceiro.indicacao': 'Indicação simples',
  'parceiro.participacaoAtiva': 'Participação ativa',
  'parceiro.primeiraRenovacao': 'Primeira renovação',
  'parceiro.renovacoesSeguintes': 'Segunda renovação em diante',
  'parceiro.baseDeCalculo': 'Base de cálculo',
  'parceiro.eventoGerador': 'Evento que gera o direito',
  'parceiro.prazoDePagamento': 'Prazo de pagamento',
  'parceiro.diaUtil': 'º dia útil do mês seguinte',
  'parceiro.fee': 'Fee fixo',
  'parceiro.possuiFee': 'Possui fee',
  'parceiro.valorDoFee': 'Valor',
  'parceiro.periodicidadeFee': 'Periodicidade',
  'parceiro.inicioFee': 'Início',
  'parceiro.fimFee': 'Término',
  'parceiro.revisaoFee': 'Revisão após (dias)',
  'parceiro.dataRevisao': 'Data da revisão',
  'parceiro.tetoRevisao': 'Teto previsto na revisão',
  'parceiro.observacoesFee': 'Observações do fee',
  'parceiro.exclusividadeCobertura': 'Exclusividade e cobertura',
  'parceiro.exclusivo': 'Parceria exclusiva',
  'parceiro.naoExclusivo': 'Não exclusiva',
  'parceiro.cobertura': 'Carteira e produtos',
  'parceiro.restricoes': 'Restrições',
  'parceiro.reservaDeConta': 'Reserva de conta',
  'parceiro.operaComReserva': 'Opera com reserva de conta',
  'parceiro.reservaDias': 'Duração da reserva',
  'parceiro.primeiraAcao': 'Prazo da primeira ação',
  'parceiro.dias': 'dias',
  'parceiro.diasUteis': 'dias úteis',
  'parceiro.salvarVigencia': 'Criar nova vigência',
  'parceiro.avisoVigencia':
    'Salvar cria uma NOVA vigência e encerra a anterior. Nada do que já foi apurado é reescrito.',
  'parceiro.inicioVigencia': 'Início da vigência',
  'parceiro.nomeDaRegra': 'Nome da regra',
  'parceiro.vigenciaCriada': 'Nova vigência registrada.',
  'parceiro.semRegraPropria':
    'Este parceiro segue as regras padrão. Criar uma vigência própria registra as condições negociadas com ele.',
  'parceiro.historicoDeVigencias': 'Histórico de vigências',
  'parceiro.comissoesResumo': 'Comissões',
  'parceiro.previstas': 'Previstas',
  'parceiro.emApuracao': 'Em apuração',
  'parceiro.aprovadas': 'Aprovadas',
  'parceiro.pagas': 'Pagas',
  'parceiro.verComissionamento': 'Ver comissionamento',
  'parceiro.gerarContrato': 'Gerar contrato',
  'parceiro.semContratoTexto': 'Nenhum contrato registrado para este parceiro.',
  'parceiro.contratoTitulo': 'Título',
  'parceiro.contratoModelo': 'Modelo',
  'parceiro.contratoVersao': 'Versão',
  'parceiro.contratoAssinado': 'Assinado em',
  'parceiro.contratoVence': 'Vence em',
  'parceiro.contratoVencido': 'Vencido há',
  'parceiro.contratoRenovacao': 'Renovação',
  'parceiro.contratoDocumentos': 'documento(s)',
  'parceiro.semDocumentos': 'Nenhum documento anexado.',
  'parceiro.anexar': 'Anexar documento',
  'parceiro.anexado': 'Documento anexado.',
  'parceiro.contasTitulo': 'Contas na carteira',
  'parceiro.semContas': 'Nenhuma conta associada a este parceiro.',
  'parceiro.oportunidadesTitulo': 'Oportunidades originadas',
  'parceiro.semOportunidades': 'Nenhuma oportunidade originada por este parceiro.',
  'parceiro.reservasTitulo': 'Reservas de conta',
  'parceiro.semReservas': 'Nenhuma reserva de conta ativa.',
  'parceiro.verReservas': 'Ver reservas',
  'contratoParceria.titulo': 'Gerar contrato de parceria',
  'contratoParceria.modelo': 'Modelo contratual',
  'contratoParceria.escolhaModelo': 'Escolha um modelo',
  'contratoParceria.inicio': 'Início da vigência',
  'contratoParceria.fim': 'Término da vigência',
  'contratoParceria.fimDica': 'Em branco: prazo indeterminado.',
  'contratoParceria.tituloDoContrato': 'Título do contrato',
  'contratoParceria.renovacao': 'Como renova',
  'contratoParceria.foro': 'Foro',
  'contratoParceria.observacoes': 'Observações',
  'contratoParceria.gerar': 'Gerar minuta',
  'contratoParceria.gerado': 'Minuta gerada com as condições vigentes.',
  'contratoParceria.avisoPrePreenchido':
    'As condições comerciais são preenchidas pela regra vigente do parceiro. Você não precisa redigitá-las.',
  'contratoParceria.minuta': 'Minuta',
  'contratoParceria.condicoes': 'Condições registradas',
  'contratoParceria.versoes': 'Versões congeladas',
  'contratoParceria.congelar': 'Congelar versão',
  'contratoParceria.congelada': 'Versão congelada.',
  'contratoParceria.faltando': 'Falta preencher antes de congelar:',
  'contratoParceria.completo': 'A minuta está completa.',
  'contratoParceria.avisoSnapshot':
    'Ao congelar, as condições ficam gravadas nesta versão. Mudar a regra do parceiro depois não altera este documento.',
  'contratoParceria.versao': 'Versão',
  'contratoParceria.congeladaEm': 'congelada em',
  'contratoParceria.semVersoes': 'Nenhuma versão congelada ainda.',
  'contratoParceria.salvar': 'Salvar alterações',
  'contratoParceria.salvo': 'Contrato atualizado.',
  'contratoParceria.bloqueadoTexto':
    'Este contrato já tem versão congelada: as condições não mudam mais. Gere um aditivo para registrar alteração.',
  'contratoParceria.alertas': 'Alertas de contrato',
  'contratoParceria.alertaVencendo': 'vence em',
  'contratoParceria.alertaVencido': 'vencido',
  'contratoParceria.alertaSemContrato': 'parceiro ativo sem contrato vigente',
  'contratoParceria.modelosTitulo': 'Modelos contratuais',
  'contratoParceria.variaveis': 'variáveis',
  'contratoParceria.daPlataforma': 'Da plataforma',
  'contratoParceria.daOrganizacao': 'Desta organização',
  'comissoes.abaInterna': 'Equipe',
  'comissoes.abaParceiros': 'Parceiros',
  'comissoes.abaEventos': 'Eventos geradores',
  'comissoes.registrarEvento': 'Registrar evento',
  'comissoes.eventoTitulo': 'Registrar evento gerador',
  'comissoes.eventoTipo': 'O que aconteceu',
  'comissoes.eventoValor': 'Valor bruto',
  'comissoes.eventoData': 'Data do evento',
  'comissoes.eventoEmpresa': 'Empresa',
  'comissoes.eventoOportunidade': 'Oportunidade',
  'comissoes.eventoParceiro': 'Parceiro',
  'comissoes.eventoParceiroDica':
    'Em branco: usamos o parceiro de origem da oportunidade ou da empresa.',
  'comissoes.eventoReferencia': 'Referência (nota, boleto, ordem)',
  'comissoes.eventoCiclo': 'Ciclo do contrato',
  'comissoes.cicloInicial': 'Contrato inicial',
  'comissoes.cicloPrimeira': 'Primeira renovação',
  'comissoes.cicloSegunda': 'Segunda renovação em diante',
  'comissoes.eventoContribuicao': 'Como o parceiro participou',
  'comissoes.eventoObservacoes': 'Observações',
  'comissoes.registrar': 'Registrar',
  'comissoes.eventoRegistrado': 'Evento registrado.',
  'comissoes.semComissao': 'Nenhuma comissão foi gerada por este evento.',
  'comissoes.comissaoGerada': 'Comissão apurada',
  'comissoes.colParceiro': 'Parceiro',
  'comissoes.colCliente': 'Cliente',
  'comissoes.colNegocio': 'Negócio',
  'comissoes.colRegra': 'Regra',
  'comissoes.colBase': 'Base',
  'comissoes.colPercentual': '%',
  'comissoes.colValor': 'Valor',
  'comissoes.colCompetencia': 'Competência',
  'comissoes.colSituacao': 'Situação',
  'comissoes.verDetalhe': 'Ver como foi calculado',
  'comissoes.memoriaTitulo': 'Como este valor foi calculado',
  'comissoes.origemTitulo': 'Origem',
  'comissoes.filtrarParceiro': 'Filtrar por parceiro',
  'comissoes.todosParceiros': 'Todos os parceiros',
  'comissoes.filtrarSituacao': 'Filtrar por situação',
  'comissoes.todasSituacoes': 'Todas as situações',
  'comissoes.vazioParceiros': 'Nenhuma comissão de parceiro ainda',
  'comissoes.vazioParceirosTexto':
    'Registre o evento gerador — a assinatura, a nota ou o recebimento — e a comissão é apurada com a regra vigente do parceiro.',
  'comissoes.vazioEventos': 'Nenhum evento gerador registrado',
  'comissoes.vazioEventosTexto':
    'O evento gerador é o fato que faz nascer o direito à comissão do parceiro.',
  'comissoes.mover': 'Mover',
  'comissoes.moverTitulo': 'Mudar a situação do lançamento',
  'comissoes.moverDestino': 'Nova situação',
  'comissoes.moverObservacoes': 'Observações',
  'comissoes.moverContestacao': 'Diga o que está sendo contestado.',
  'comissoes.movido': 'Situação atualizada.',
  'comissoes.confirmar': 'Confirmar',
  'comissoes.aprovar': 'Aprovar',
  'comissoes.enviarParaAprovacao': 'Enviar para aprovação',
  'comissoes.apurar': 'Iniciar apuração',
  'comissoes.contestar': 'Contestar',
  'comissoes.cancelar': 'Cancelar lançamento',
  'comissoes.eventoDe': 'Evento de',
  'comissoes.colEvento': 'Evento',
  'comissoes.colData': 'Data',
  'comissoes.colComissoes': 'Comissões',
  'comissoes.regraVigente': 'Regra vigente',
  'comissoes.semRegra': 'Este parceiro não tem regra própria: valem os padrões.',
  'empresa.fichaCadastro': 'Cadastro empresarial',
  'empresa.fichaAtividade': 'Atividade econômica',
  'empresa.fichaDimensao': 'Dimensão da empresa',
  'empresa.fichaDigital': 'Presença digital',
  'empresa.fichaInteligencia': 'Perfil comercial',
  'empresa.fichaSetorPublico': 'Setor público',
  'empresa.fichaCapitais': 'Mercado de capitais',
  'empresa.fichaVazia':
    'Nenhuma informação estratégica ainda. Use “Enriquecer dados” para buscar em fontes públicas.',
  'empresa.situacaoCadastral': 'Situação cadastral',
  'empresa.dataAbertura': 'Data de abertura',
  'empresa.naturezaJuridica': 'Natureza jurídica',
  'empresa.matrizFilial': 'Matriz ou filial',
  'empresa.capitalSocial': 'Capital social',
  'empresa.endereco': 'Endereço',
  'empresa.bairro': 'Bairro',
  'empresa.cep': 'CEP',
  'empresa.cnaePrincipal': 'CNAE principal',
  'empresa.cnaeDescricao': 'Atividade principal',
  'empresa.cnaesSecundarios': 'CNAEs secundários',
  'empresa.hierarquiaCnae': 'Classificação (CONCLA)',
  'empresa.subsegmento': 'Subsegmento',
  'empresa.atividadeResumo': 'Resumo da atuação',
  'empresa.faixaFaturamento': 'Faixa de faturamento',
  'empresa.faturamentoAnual': 'Faturamento',
  'empresa.faturamentoExercicio': 'Exercício',
  'empresa.funcionariosFaixa': 'Funcionários',
  'empresa.unidades': 'Unidades',
  'empresa.linkedin': 'LinkedIn',
  'empresa.canaisInstitucionais': 'Canais institucionais',
  'empresa.telefonesPublicos': 'Telefones públicos',
  'empresa.emailsPublicos': 'E-mails públicos',
  'empresa.resumoInstitucional': 'Resumo institucional',
  'empresa.mercadosDeAtuacao': 'Mercados de atuação',
  'empresa.produtosServicos': 'Produtos e serviços',
  'empresa.sinaisComerciais': 'Sinais comerciais',
  'empresa.noticiasRelevantes': 'Notícias relevantes',
  'empresa.contratosPublicosQtd': 'Contratos encontrados',
  'empresa.contratosPublicosValor': 'Valor dos contratos',
  'empresa.contratosPublicosOrgaos': 'Órgãos',
  'empresa.contratosPublicosPeriodo': 'Período analisado',
  'empresa.cvmCompanhiaAberta': 'Companhia aberta',
  'empresa.cvmRegistro': 'Registro CVM',
  'empresa.cvmSituacao': 'Situação na CVM',
  'empresa.cvmSetor': 'Setor (CVM)',
  'empresa.procedencia': 'Fonte',
  'empresa.consultadoEm': 'consultado em',
  'empresa.enriquecidoEm': 'Última busca em',
  'empresa.verProcedencia': 'De onde veio este dado',
  'empresa.enriquecer.resumoTitulo': 'Enriquecimento concluído',
  'empresa.enriquecer.resumoEncontradas': 'informações encontradas',
  'empresa.enriquecer.resumoNovas': 'novas',
  'empresa.enriquecer.resumoDivergencias': 'divergências',
  'empresa.enriquecer.resumoFontes': 'fontes consultadas',
  'empresa.enriquecer.resumoJaIguais': 'já iguais ao cadastro',
  'empresa.enriquecer.revisar': 'Revisar resultados',
  'empresa.enriquecer.manterAtual': 'Manter atual',
  'empresa.enriquecer.ignorar': 'Ignorar',
  'empresa.enriquecer.aplicarSelecionados': 'Aplicar selecionados',
  'empresa.enriquecer.selecionados': 'selecionado(s)',
  'empresa.enriquecer.grupo.ficha': 'Ficha estratégica',
  'empresa.enriquecer.divergenciaAviso':
    'Os dois valores estão à vista. Escolher é decisão de quem conhece o cliente.',
  'catComercial.codigoAuto': 'Gerado automaticamente',
  'catComercial.qtdHoras': 'Horas sugeridas',
  'catComercial.qtdMensal': 'Quantidade mensal sugerida',
  'catComercial.qtdAssinaturas': 'Assinaturas sugeridas',
  'catComercial.qtdUnidades': 'Unidades sugeridas',
  'catComercial.qtdPacotes': 'Pacotes sugeridos',
  'parceiro.telefone': 'Telefone',
  'parceiro.cidade': 'Cidade',
  'parceiro.uf': 'UF',
  'parceiro.vigencia': 'Vigência',
  'parceiro.representantes': 'Representantes',
  'parceiro.papelAdministrador': 'Administrador',
  'parceiro.papelRepresentante': 'Representante',
  'anexos.titulo': 'Anexos',
  'anexos.anexar': 'Anexar arquivo',
  'anexos.enviando': 'Enviando…',
  'anexos.enviado': 'Anexo enviado.',
  'anexos.excluir': 'Excluir',
  'anexos.excluido': 'Anexo excluído.',
  'anexos.falhaAoEnviar': 'Não foi possível enviar.',
  'anexos.falhaAoExcluir': 'Não foi possível excluir.',
  'anexos.vazioEmpresa': 'Nenhum anexo nesta empresa.',
  'anexos.tiposAceitos':
    'PDF, imagem, texto, planilha, documento do Word ou e-mail salvo. Executável não é aceito.',
  // =========================================================================
  // CRM-VAL-006 e CRM-VAL-007
  // =========================================================================
  'novaEmpresa.titulo': 'Nova empresa',
  'novaEmpresa.subtitulo': 'Encontre a empresa para preencher o cadastro automaticamente.',
  'novaEmpresa.placeholder': 'Buscar por CNPJ, razão social ou nome fantasia',
  'novaEmpresa.minimo': 'Digite ao menos 3 caracteres.',
  'novaEmpresa.procurando': 'Procurando a empresa...',
  'novaEmpresa.selecionar': 'Selecionar',
  'novaEmpresa.jaCadastrada': 'Já cadastrada',
  'novaEmpresa.responsavel': 'Responsável',
  'novaEmpresa.abrirEmpresa': 'Abrir empresa',
  'novaEmpresa.nadaEncontrado': 'Nenhuma empresa encontrada com esse termo.',
  'novaEmpresa.naoEncontrou': 'Não encontrou?',
  'novaEmpresa.manual': 'Cadastrar manualmente',
  'novaEmpresa.prePreenchido':
    'Dados preenchidos a partir do registro público. Confira antes de salvar.',
  'novaEmpresa.criada': 'Empresa cadastrada.',
  'novaEmpresa.completarPerfil': 'Completar perfil agora',
  'empresa.inteligencia': 'Inteligência empresarial',
  'empresa.cnaes': 'Atividade econômica',
  'empresa.cnaePrincipalTitulo': 'CNAE principal',
  'empresa.cnaesSecundariosTitulo': 'CNAEs secundários',
  'empresa.indicadores': 'Dimensão',
  'empresa.canais': 'Presença digital',
  'empresa.fatos': 'Fatos de inteligência',
  'empresa.historicoEnriquecimento': 'Histórico de enriquecimento',
  'empresa.semInteligencia': 'Nenhuma informação enriquecida ainda.',
  'empresa.verTodos': 'Ver todos',
  'empresa.fonte': 'Fonte',
  'empresa.periodo': 'Período',
  'empresa.confiancaOficial': 'Oficial',
  'empresa.confiancaProvavel': 'Provável',
  'empresa.confiancaInferido': 'Inferido',
  'empresa.metricaFATURAMENTO': 'Faturamento',
  'empresa.metricaFAIXA_FATURAMENTO': 'Faixa de faturamento',
  'empresa.metricaFUNCIONARIOS': 'Funcionários',
  'empresa.metricaFAIXA_FUNCIONARIOS': 'Faixa de funcionários',
  'empresa.metricaCAPITAL_SOCIAL': 'Capital social',
  'empresa.metricaUNIDADES': 'Unidades',
  'empresa.metricaFILIAIS': 'Filiais',
  'empresa.metricaOUTRO': 'Outro indicador',
  'empresa.fatoCONTRATO_PUBLICO': 'Contrato público',
  'empresa.fatoMERCADO_CAPITAIS': 'Mercado de capitais',
  'empresa.fatoDEMONSTRACAO_FINANCEIRA': 'Demonstração financeira',
  'empresa.fatoMENCAO': 'Menção pública',
  'empresa.fatoNOTICIA': 'Notícia',
  'empresa.fatoEXPANSAO': 'Expansão',
  'empresa.fatoEVENTO': 'Evento',
  'empresa.fatoOUTRO': 'Outro',
  'empresa.execucoesRecentes': 'execução(ões) recente(s)',
  'empresa.informacoesEncontradas': 'informações',
  'empresa.fontesConsultadas': 'fontes',
  'logo.titulo': 'Identidade visual',
  'logo.semLogo': 'Nenhum logotipo definido.',
  'logo.encontrado': 'Logotipo encontrado',
  'logo.encontradoEm': 'Encontrado em',
  'logo.usarEste': 'Usar este logo',
  'logo.trocar': 'Trocar',
  'logo.ignorar': 'Ignorar',
  'logo.enviar': 'Enviar logo',
  'logo.alterar': 'Alterar logotipo',
  'logo.enviando': 'Enviando...',
  'logo.confirmado': 'Logotipo confirmado.',
  'logo.rejeitado': 'Sugestão descartada.',
  'logo.enviado': 'Logotipo enviado.',
  'logo.confirmando': 'Confirmando...',
  'logo.formatos': 'PNG, JPG, WebP ou GIF, até 2 MB. SVG não é aceito por segurança.',
  'logo.naoIdentificado': 'Não foi possível identificar o logotipo com segurança.',
  'logo.podeEnviar': 'Você pode enviar o logotipo manualmente.',
  'logo.novaVersao': 'Encontramos uma possível nova versão do logotipo.',
  'logo.manterAtual': 'Manter atual',
  'logo.atualizar': 'Atualizar',
  'logo.origemManual': 'Enviado manualmente',
  'logo.sugerido': 'Sugerido',
  'logo.statusConfirmado': 'Confirmado',
  'logo.usoEmDocumentos': 'Pode ser usado em propostas e contratos.',
  'logo.somenteConfirmado': 'Só um logotipo confirmado é usado automaticamente em documentos.',
  'empresa.enriquecer.dadosEmpresariais': 'dados empresariais',
  'empresa.enriquecer.novosFatos': 'novos fatos de inteligência',
  'empresa.enriquecer.cnaesGravados': 'atividades econômicas',
  'empresa.enriquecer.canaisGravados': 'canais digitais',
  'empresa.enriquecer.indicadoresGravados': 'indicadores',
  'empresa.enriquecer.logoSugerido': 'logotipo sugerido',
  'empresa.enriquecer.persistido': 'Gravado no conhecimento da empresa',
  'empresa.enriquecer.inteligenciaEncontrada': 'Inteligência encontrada',
  'empresa.enriquecer.conflitoTitulo': 'Os dados encontrados parecem ser de outra empresa',
  'empresa.enriquecer.conflitoTexto':
    'Nada foi gravado no cadastro. O que a consulta trouxe não bate com o que o CRM já sabe sobre esta empresa:',
  'empresa.enriquecer.noCadastro': 'no cadastro',
  'empresa.enriquecer.noResultado': 'no resultado',
  'empresa.enriquecer.conflitoComoResolver':
    'Confira o CNPJ do cadastro em Editar empresa. É por ele que a consulta é feita — e é ele que precisa estar certo antes de enriquecer de novo.',
  'empresa.enriquecer.informacoesEncontradas': 'Informações encontradas',
  'empresa.enriquecer.selecionarTodos': 'Selecionar todos',
  'empresa.enriquecer.limparSelecao': 'Limpar seleção',
  'empresa.enriquecer.substituiManual': 'Substitui um valor informado manualmente',
  'empresa.enriquecer.jaNaFicha': 'Já está na ficha',
  'empresa.enriquecer.grupo.dimensao': 'Dimensão',
  'empresa.enriquecer.grupo.inteligencia': 'Inteligência',
  'empresa.enriquecer.grupo.marca': 'Identidade visual',
  'empresa.informadoManualmente': 'Informado manualmente',
  'empresa.valorDaFonte': 'Valor encontrado na última consulta',
  'empresa.adicionarCnae': 'Informar CNAE',
  'empresa.adicionarCanal': 'Informar canal',
  'empresa.codigoCnae': 'Código CNAE (7 dígitos)',
  'empresa.descricaoCnae': 'Descrição (opcional)',
  'empresa.enderecoDoCanal': 'Endereço',
  'empresa.tipoDoCanal': 'Tipo (LINKEDIN, INSTAGRAM, SITE...)',

  // CERES-011 — Pessoas-chave (People Intelligence)
  'pessoas.titulo': 'Pessoas-chave',
  'pessoas.encontrar': 'Encontrar pessoas-chave',
  'pessoas.atualizar': 'Atualizar pessoas-chave',
  'pessoas.buscando': 'Procurando pessoas...',
  'pessoas.vazioTitulo': 'Nenhuma busca executada',
  'pessoas.vazioDescricao': 'Ainda não procuramos pessoas relacionadas a esta empresa.',
  'pessoas.vazioAposBusca': 'Nenhuma pessoa encontrada',
  'pessoas.vazioSemMotivo': 'As fontes públicas consultadas não trouxeram pessoas.',
  'pessoas.buscaConcluida': 'Busca concluída. Pessoas sugeridas:',
  'pessoas.buscaSemResultado': 'Busca concluída: nenhuma pessoa nova encontrada.',
  'pessoas.buscaFalhou': 'A busca de pessoas falhou. Tente novamente.',
  'pessoas.relevancia': 'Relevância',
  'pessoas.confianca': 'Confiança',
  'pessoas.fontes': 'evidência(s)',
  'pessoas.emailEncontrado': 'E-mail público encontrado',
  'pessoas.verEvidencias': 'Ver evidências',
  'pessoas.adicionarContato': 'Adicionar como contato',
  'pessoas.descartar': 'Descartar',
  'pessoas.descartarTitulo': 'Descartar sugestão',
  'pessoas.aceitar': 'Marcar como aceita',
  'pessoas.aceita': 'Aceita',
  'pessoas.aceitaOk': 'Sugestão marcada como aceita.',
  'pessoas.convertida': 'Contato criado',
  'pessoas.desatualizada': 'Possivelmente desatualizada',
  'pessoas.contatoCriado': 'Contato criado a partir da sugestão. As evidências ficam guardadas.',
  'pessoas.contatoReaproveitado': 'Já existia um contato equivalente: a sugestão foi ligada a ele.',
  'pessoas.descartadaOk': 'Sugestão descartada. Ela não voltará em novas buscas.',
  'pessoas.motivoOpcional': 'Motivo (opcional)',
  'pessoas.motivoNenhum': 'Sem motivo específico',
  'pessoas.motivoNaoRelevante': 'Não relevante',
  'pessoas.motivoPessoaErrada': 'Pessoa errada',
  'pessoas.motivoSaiu': 'Não trabalha mais na empresa',
  'pessoas.motivoDuplicado': 'Duplicado',
  'pessoas.motivoCargo': 'Cargo inadequado',
  'pessoas.motivoOutro': 'Outro',
  'pessoas.observacao': 'Observação (opcional)',
  'pessoas.cargoOriginal': 'Cargo original',
  'pessoas.cargoNormalizado': 'Cargo normalizado',
  'pessoas.senioridade': 'Senioridade',
  'pessoas.persona': 'Persona relacionada',
  'pessoas.dadosPublicos': 'Dados públicos',
  'pessoas.porqueSugerida': 'Por que esta pessoa foi sugerida?',
  'pessoas.abrirFonte': 'abrir fonte',
  'pessoas.primeiraDescoberta': 'Primeira descoberta',
  'pessoas.ultimaVerificacao': 'Última verificação',
  // CERES-012 — web pública, temporalidade e e-mail inferido.
  'pessoas.emAndamento':
    'Buscando em fontes públicas... isto roda em segundo plano; a lista atualiza sozinha.',
  'pessoas.emailHipotese': 'E-mail (hipótese)',
  'pessoas.situacaoAtual': 'Provavelmente atual',
  'pessoas.situacaoProvavelAtual': 'Provavelmente atual',
  'pessoas.situacaoHistorica': 'Cargo histórico',
  'pessoas.situacaoProvavelHistorica': 'Possivelmente histórico',
  'pessoas.situacaoDesconhecida': 'Situação não confirmada',
  'pessoas.situacaoAtual2': 'Situação',
  'pessoas.situacaoConfianca': 'Confiança da situação',
  'pessoas.frescor': 'Frescor',
  'pessoas.publicadoEm': 'publicado em',
  'pessoas.emailInferidoTitulo': 'E-mail inferido (hipótese, não confirmado)',
  'pessoas.emailInferidoAviso':
    'Deduzido do padrão de e-mails públicos da empresa — nunca validado.',
  'pessoas.emailInferidoBaseado': 'Baseado em amostras públicas:',
  // CERES-013 — SPA, personas, diagnósticos.
  'pessoas.viaRender': 'via renderização do site',
  'pessoas.personaNaoConfigurada':
    'Pessoas foram encontradas, mas as personas comerciais desta organização ainda não estão configuradas.',
  'pessoas.configurarPersonas': 'Configurar personas',
  'pessoas.personasConfiguradasOk':
    'Personas configuradas. Rode a busca novamente para calcular a relevância.',
  'pessoas.diagNoWebsite': 'A empresa não tem website cadastrado.',
  'pessoas.diagWebsiteUnreachable': 'O site da empresa não pôde ser acessado.',
  'pessoas.diagRobotsBlocked': 'O robots.txt do site bloqueou a leitura das páginas.',
  'pessoas.diagSpaDetected': 'O site depende de JavaScript (SPA).',
  'pessoas.diagSpaRendered': 'O site depende de JavaScript e foi renderizado para leitura.',
  'pessoas.diagSpaRenderFailed':
    'O site depende de JavaScript, mas não foi possível concluir a renderização.',
  'pessoas.diagNoPeopleInSite': 'O site foi analisado, mas nenhuma pessoa foi identificada nele.',
  'pessoas.diagNoQsa': 'O quadro societário público não trouxe pessoas.',
  'pessoas.diagNoWikidata': 'A empresa não foi localizada no Wikidata.',
  'pessoas.diagGdeltRateLimited':
    'A busca em notícias públicas ficou temporariamente indisponível.',
  'pessoas.diagGdeltNoResults': 'A busca em notícias públicas não trouxe resultados.',
  'pessoas.diagGdeltDisabled': 'A busca em notícias públicas está desligada.',
  'pessoas.diagCommonCrawlNoResults': 'O arquivo público da web não trouxe resultados.',
  'pessoas.diagNoPersonFound': 'Não encontramos pessoas com evidência suficiente.',
  'pessoas.diagNoRelevantPersona':
    'Pessoas foram encontradas, mas nenhuma corresponde às personas priorizadas.',
  'pessoas.diagPersonaNotConfigured':
    'Configure as personas comerciais para o CERES priorizar as pessoas.',
  'pessoas.diagBudgetExhausted': 'O tempo de busca terminou antes de esgotar as fontes.',
  'pessoas.diagPartialFailure': 'Uma das fontes públicas não pôde ser consultada.',
  'plano.recursoForaDoPlano': 'Recurso fora do seu plano',
  'plano.recursoForaDoPlanoTexto':
    'Este módulo não está incluído no plano desta organização. Fale com quem administra a assinatura para habilitá-lo.',
  'plano.voltarAoPainel': 'Voltar ao painel',
  'pessoas.papelProvavel': 'Papel provável',
  'pessoas.papelDecisor': 'Decisor',
  'pessoas.papelInfluenciador': 'Influenciador',
  'pessoas.papelTecnico': 'Técnico',
  'pessoas.papelComprador': 'Compras',
  'pessoas.papelFinanceiro': 'Financeiro',
  'pessoas.papelJuridico': 'Jurídico',
  'pessoas.papelPatrocinador': 'Patrocinador',
  'pessoas.papelUsuario': 'Usuário',
  'pessoas.papelNaoIdentificado': 'Não identificado',
  'pessoas.confiancaConfirmado': 'Confirmado',
  'pessoas.confiancaAlta': 'Confiança alta',
  'pessoas.confiancaMedia': 'Confiança média',
  'pessoas.confiancaBaixa': 'Confiança baixa',
  'pessoas.revisarContato': 'Revisar antes de criar o contato',
  'pessoas.criarContato': 'Criar contato',
  'pessoas.jaExisteContato': 'Já existe alguém parecido nesta empresa',
  'pessoas.vincularAoExistente': 'Vincular a este contato',
  'pessoas.campoNome': 'Nome',
  'pessoas.campoCargo': 'Cargo',
  'pessoas.campoEmail': 'E-mail',
  'pessoas.campoTelefone': 'Telefone',
  'pessoas.emailInferidoNaoEntra':
    'O e-mail inferido é uma hipótese e não é preenchido aqui. Se quiser usá-lo, digite-o.',

  'pessoas.fontesConsultadas': 'Fontes consultadas',
  'pessoas.encontradosPorFonte': 'Encontrados',
  'pessoas.sugestoesBaixaEvidencia': 'Sugestões com pouca evidência',
  'pessoas.sugestoesBaixaEvidenciaDica':
    'Nomes com evidência fraca ou única. Confira antes de abordar.',
  'pessoas.linkedinOficialIndisponivel': 'LinkedIn — integração oficial não disponível',
  'pessoas.origemQsa': 'Quadro societário',
  'pessoas.origemSite': 'Site institucional',
  'pessoas.origemNoticia': 'Notícia',
  'pessoas.origemWeb': 'Web pública',
  'pessoas.origemDocumento': 'Documento institucional',
  'pessoas.origemWikidata': 'Wikidata',
  'pessoas.origemGdelt': 'GDELT',
  'pessoas.origemCommonCrawl': 'Common Crawl',
  'pessoas.fontePessoas': 'pessoas',
  'pessoas.origemEstruturado': 'Dados estruturados do site',
  'pessoas.origemCrm': 'CRM',
  'pessoas.linkedinPublico': 'Perfil profissional referenciado pelo site',
  'pessoas.statusFonteOk': 'consultada',
  'pessoas.statusFonteVazia': 'consultada, sem pessoas',
  'pessoas.statusFontePulada': 'não executada',
  'pessoas.statusFonteFalhou': 'falhou',

  'pessoas.emailRotulo': 'E-mail',
  'pessoas.emailProvavelRotulo': 'E-mail provável',
  'pessoas.telefoneDiretoRotulo': 'Telefone direto',
  'pessoas.telefoneGeralEmpresa': 'Telefone geral da empresa',
  'pessoas.perfilRotulo': 'Perfil profissional',
  'pessoas.estadoEncontrado': 'Encontrado',
  'pessoas.estadoProvavel': 'Provável',
  'pessoas.estadoNaoEncontrado': 'Não encontrado',
  'pessoas.naoConfirmado': 'Não confirmado',
  'pessoas.fonteRegistrosOficiais': 'Registros oficiais',
  'pessoas.fonteSiteOficial': 'Site e canais oficiais',
  'pessoas.fontePresencaPublica': 'Presença pública na web',
  'pessoas.fonteBasesPublicas': 'Bases públicas de referência',
  'pessoas.fonteRedeProfissional': 'Rede profissional',
  'pessoas.fonteDetalhePessoas': 'pessoas',
  'pessoas.fonteDetalheConsultado': 'consultado',
  'pessoas.fonteDetalheConsultada': 'consultada',
  'pessoas.fonteDetalheSemNovosMasc': 'consultado, sem novos resultados',
  'pessoas.fonteDetalheSemNovosFem': 'consultada, sem novos resultados',
  'pessoas.fonteDetalheNaoExecutada': 'não executada',
  'pessoas.fonteDetalheFalhou': 'consultada, sem novos resultados',
  'pessoas.fonteDetalheIndisponivel': 'integração indisponível',
  'pessoas.detalhesTecnicos': 'Detalhes técnicos',
  'pessoas.origemDoCampo': 'Fonte',

  'pessoas.enriquecerContatos': 'Enriquecer contatos',
  'pessoas.enriquecerContatosDica':
    'Procura canais públicos de contato das pessoas-chave relevantes.',
  'pessoas.encontrarContatos': 'Encontrar contatos',
  'pessoas.contatosEnfileirado':
    'Busca de contatos iniciada. Os canais aparecem quando ficarem prontos.',
  'pessoas.contatosTitulo': 'Canais de contato',
  'pessoas.contatosDescobrindo': 'Procurando canais de contato…',
  'pessoas.contatosVazio': 'Nenhum canal de contato encontrado ainda.',
  'pessoas.melhorCaminho': 'Melhor caminho',
  'pessoas.canalPessoal': 'Pessoal',
  'pessoas.canalCorporativo': 'Corporativo',
  'pessoas.canalEmailPublicado': 'E-mail publicado',
  'pessoas.canalEmailInferido': 'E-mail inferido (hipótese)',
  'pessoas.canalEmailInferidoAviso':
    'Deduzido do padrão do domínio; o domínio existe, mas a caixa nunca é verificada.',
  'pessoas.canalDominioValido': 'Domínio com e-mail válido',
  'pessoas.canalTelefone': 'Telefone',
  'pessoas.canalTelefoneMovel': 'Celular',
  'pessoas.canalWhatsapp': 'WhatsApp',
  'pessoas.canalPaginaContato': 'Página de contato',
  'pessoas.canalPerfilPublico': 'Perfil público',
  'pessoas.canalPadrao': 'Padrão',
  'pessoas.canalBaseadoEm': 'baseado em',
  'pessoas.canalAmostras': 'amostras',
  'pessoas.contatabilidade': 'Contactabilidade',

  'empresa.editarCadastro': 'Editar cadastro',
  'empresa.editarFicha': 'Editar informações empresariais',
  'empresa.editarSecao': 'Editar',
  'empresa.novoFollowUp': 'Agendar follow-up',
  'empresa.novaAtividade': 'Criar atividade',
  'empresa.novoContato': 'Adicionar contato',
  'empresa.verTodosContatos': 'Ver todos',
  'empresa.fichaCadastroOficial': 'Cadastro e atividade',
  'empresa.divergenciaTitulo': 'Diferenças entre o cadastro e a fonte',
  'empresa.divergenciaAtual': 'No cadastro',
  'empresa.divergenciaEncontrado': 'A fonte encontrou',
  'empresa.divergenciaAceitar': 'Aceitar atualização',
  'empresa.divergenciaManter': 'Manter atual',
  'empresa.divergenciaFalhou': 'Não foi possível registrar a decisão.',
  'empresa.porteMicro': 'Microempresa',
  'empresa.portePequeno': 'Pequeno',
  'empresa.porteMedio': 'Médio',
  'empresa.porteGrande': 'Grande',
  'empresa.porteEnterprise': 'Multinacional',
  'empresa.abaVisaoGeral': 'Visão geral',
  'empresa.abaInteligencia': 'Dados e enriquecimento',
  'empresa.abaRelacionamentos': 'Relacionamentos',
  'empresa.abaComercial': 'Comercial',
  'empresa.abaArquivos': 'Arquivos',
  'empresa.abaHistorico': 'Histórico',
  'empresa.abasDaEmpresa': 'Seções da empresa',
  'empresa.dadosPrincipais': 'Dados principais',
  'empresa.inteligenciaResumida': 'Resumo do enriquecimento',
  'empresa.verInteligenciaCompleta': 'Ver dados e enriquecimento',
  'empresa.proximaAcao': 'Próxima ação',
  'empresa.semProximaAcao': 'Nenhuma atividade agendada.',
  'empresa.contatoPrincipal': 'Contato principal',
  'empresa.resumoComercial': 'Resumo comercial',
  'empresa.acoesRapidas': 'Ações rápidas',
  'empresa.atividadeRecente': 'Atividade recente',
  'empresa.verHistoricoCompleto': 'Ver histórico completo',
  'empresa.salvarFicha': 'Salvar',
  'empresa.corrigir': 'Corrigir',
  'empresa.usarValorDaFonte': 'Usar valor da fonte',
  'empresa.removerDaFicha': 'Remover',
  'empresa.inteligenciaFalhou': 'Não foi possível concluir a alteração.',
  'empresa.corrigirIndicador': 'Corrigir indicador',
  'empresa.novoValor': 'Novo valor',
  'empresa.motivoDaCorrecao': 'Motivo (opcional)',
  'empresa.motivoAjuda': 'Ajuda quem revisitar a decisão daqui a alguns meses.',
  'empresa.salvarCorrecao': 'Salvar correção',
  'empresa.cancelar': 'Cancelar',
  'empresa.enriquecer.informacoesAplicadas': 'informações aplicadas',
  'empresa.enriquecer.naoAplicadas': 'não puderam ser aplicadas',
  'empresa.enriquecer.confirmarTroca': 'Confirmar alteração de identidade',
  'empresa.enriquecer.confirmarConflito': 'Aplicar mesmo assim',
  'empresa.enriquecer.cnpjAtualLabel': 'CNPJ atual',
  'empresa.enriquecer.cnpjNovoLabel': 'Novo CNPJ',
  'empresa.enriquecer.avisoTroca':
    'A alteração pode exigir revisão das informações empresariais obtidas para o CNPJ anterior. Nada é apagado: o que depender da identidade anterior fica marcado para conferência.',
  'empresa.revisaoNecessaria': 'Conferir',
  'empresa.enriquecer.descartar': 'Descartar',
  'empresa.enriquecer.precisaDecisao': 'Precisam da sua decisão',

  // ---- Pré-Vendas e Qualificação (CERES-021) ----
  'nav.domPreVendas': 'Pré-Vendas',
  'nav.preVendasVisaoGeral': 'Visão geral',
  'nav.prospeccao': 'Prospecção',
  'nav.listasDeProspeccao': 'Listas',
  'nav.cadenciasDePreVendas': 'Cadências e atividades',
  'nav.qualificacao': 'Qualificação',
  'pv.titulo': 'Pré-Vendas e Qualificação',
  'pv.subtitulo':
    'O que acontece antes da oportunidade: encontrar, abordar, entender e entregar um lead pronto.',
  'pv.carregando': 'Carregando',
  'pv.erroAoCarregar': 'Não foi possível carregar',
  'pv.tentarNovamente': 'Tentar novamente',
  'pv.fechar': 'Fechar',
  'pv.cancelar': 'Cancelar',
  'pv.salvar': 'Salvar',
  'pv.confirmar': 'Confirmar',
  'pv.empresa': 'Empresa',
  'pv.responsavel': 'Responsável',
  'pv.semResponsavel': 'Sem responsável',
  'pv.origem': 'Origem',
  'pv.semOrigem': 'Sem origem',
  'pv.prioridade': 'Prioridade',
  'pv.segmento': 'Segmento',
  'pv.contatoPrincipal': 'Contato principal',
  'pv.semContato': 'Sem contato',
  'pv.tentativas': 'Tentativas',
  'pv.ultimaInteracao': 'Última interação',
  'pv.proximaAtividade': 'Próxima atividade',
  'pv.atraso': 'Atraso',
  'pv.dias': 'dias',
  'pv.estagio': 'Estágio',
  'pv.objetivo': 'Objetivo',
  'pv.lista': 'Lista',
  'pv.status': 'Situação',
  'pv.qualificado': 'Qualificado',
  'pv.total': 'Total',
  'pv.vencidas': 'vencidas',
  'pv.nenhumRegistro': 'Nada por aqui ainda',
  'pv.verDetalhe': 'Abrir',
  'pv.filtros': 'Filtros',
  'pv.limparFiltros': 'Limpar filtros',
  'pv.buscar': 'Buscar empresa',
  'pv.apenasAtrasados': 'Só atrasados',
  'pv.apenasSemContato': 'Só sem contato',
  'pv.painel.titulo': 'Visão geral de Pré-Vendas',
  'pv.painel.subtitulo':
    'Geração e qualificação de leads. Receita e taxa de ganho vivem no pipeline comercial.',
  'pv.painel.empresasEmTrabalho': 'Empresas em trabalho',
  'pv.painel.atividadesVencidas': 'Atividades vencidas',
  'pv.painel.suspects': 'Suspects adicionados',
  'pv.painel.prospects': 'Prospects selecionados',
  'pv.painel.leads': 'Leads gerados',
  'pv.painel.qualificados': 'Leads qualificados',
  'pv.painel.conversoes': 'Conversões em oportunidade',
  'pv.painel.taxaResposta': 'Taxa de resposta',
  'pv.painel.taxaEngajamento': 'Taxa de engajamento',
  'pv.painel.taxaQualificacao': 'Taxa de qualificação',
  'pv.painel.taxaConversao': 'Taxa de conversão',
  'pv.painel.emNutricao': 'Em nutrição',
  'pv.painel.descartados': 'Descartados',
  'pv.painel.reativacoes': 'Reativações',
  'pv.painel.tempoPorEstagio': 'Tempo mediano por estágio',
  'pv.painel.conversaoEntreEstagios': 'Conversão entre estágios',
  'pv.painel.porResponsavel': 'Resultado por responsável',
  'pv.painel.porOrigem': 'Resultado por origem',
  'pv.painel.porLista': 'Resultado por lista',
  'pv.painel.porSegmento': 'Resultado por segmento',
  'pv.painel.motivos': 'Motivos de descarte',
  'pv.painel.periodo': 'Período',
  'pv.painel.semAmostra': 'sem amostra',
  'pv.painel.ressalva': 'Receita, ticket médio e taxa de ganho pertencem ao pipeline comercial.',
  'pv.prospeccao.titulo': 'Prospecção',
  'pv.prospeccao.subtitulo':
    'O funil que antecede a oportunidade. Cada cartão é um ciclo de prospecção, nunca uma cópia da empresa.',
  'pv.prospeccao.funil': 'Funil',
  'pv.prospeccao.listaVisao': 'Lista',
  'pv.prospeccao.novoSuspect': 'Novo suspect',
  'pv.prospeccao.vazioTitulo': 'Nenhuma prospecção em andamento',
  'pv.prospeccao.vazioOQueE':
    'Pré-Vendas é o trabalho anterior à oportunidade: encontrar empresas, confirmar aderência, abordar, entender a necessidade e só então entregar um lead pronto ao comercial.',
  'pv.prospeccao.vazioExemplo':
    'Exemplo: a Metalúrgica Andrade entra como suspect vinda de uma feira; vira prospect quando alguém confirma que ela tem a dor que resolvemos; vira lead quando o diretor de operações diz que o time perde dois dias por semana com retrabalho.',
  'pv.prospeccao.pedirRotulo': 'Abrir uma prospecção com o assistente',
  'pv.prospeccao.pedirPedido':
    'Quero prospectar uma empresa nova. Me ajude a abrir o processo e definir a primeira abordagem.',
  'pv.prospeccao.colunaVazia': 'Nenhuma empresa aqui',
  'pv.prospeccao.carregarMais': 'Carregar mais',
  'pv.prospeccao.mover': 'Mover',
  'pv.prospeccao.moverPara': 'Mover para',
  'pv.prospeccao.faltaPara': 'Para avançar, falta:',
  'pv.prospeccao.naoDeuParaMover': 'Não foi possível mover',
  'pv.prospeccao.movido': 'Movido',
  'pv.detalhe.titulo': 'Processo de prospecção',
  'pv.detalhe.resumo': 'Resumo',
  'pv.detalhe.linhaDoTempo': 'Linha do tempo',
  'pv.detalhe.atividades': 'Atividades',
  'pv.detalhe.cadencia': 'Cadência',
  'pv.detalhe.qualificacao': 'Qualificação',
  'pv.detalhe.historico': 'Histórico de mudanças',
  'pv.detalhe.registrarAtividade': 'Registrar atividade',
  'pv.detalhe.iniciarCadencia': 'Iniciar cadência',
  'pv.detalhe.pausarCadencia': 'Pausar cadência',
  'pv.detalhe.enviarParaNutricao': 'Enviar para nutrição',
  'pv.detalhe.descartar': 'Descartar',
  'pv.detalhe.reativar': 'Reativar',
  'pv.detalhe.qualificar': 'Qualificar',
  'pv.detalhe.converter': 'Converter em oportunidade',
  'pv.detalhe.alterarResponsavel': 'Alterar responsável',
  'pv.detalhe.semAtividades': 'Nenhuma atividade registrada.',
  'pv.detalhe.semCadencia': 'Nenhuma cadência iniciada.',
  'pv.detalhe.semHistorico': 'Sem movimentações registradas.',
  'pv.detalhe.engajamento': 'Engajamento',
  'pv.detalhe.tentativa': 'Tentativa',
  'pv.detalhe.oQueSignifica': 'O que este estágio significa',
  'pv.detalhe.criterioEntrada': 'Para entrar',
  'pv.detalhe.criterioSaida': 'Para sair',
  'pv.atividade.tipo': 'Tipo',
  'pv.atividade.assunto': 'Assunto',
  'pv.atividade.descricao': 'Descrição',
  'pv.atividade.desfecho': 'Desfecho',
  'pv.atividade.desfechoAjuda':
    'Só desfecho de conversa conta como engajamento. Tentativa sem resposta é tentativa.',
  'pv.atividade.concluir': 'Registrar como concluída',
  'pv.atividade.registrada': 'Atividade registrada',
  'pv.atividade.ligacao': 'Ligação',
  'pv.atividade.email': 'E-mail',
  'pv.atividade.reuniao': 'Reunião',
  'pv.atividade.mensagem': 'Mensagem',
  'pv.atividade.tarefa': 'Tarefa',
  'pv.atividade.respondeu': 'Respondeu',
  'pv.atividade.atendeu': 'Atendeu e conversou',
  'pv.atividade.reuniaoAceita': 'Reunião aceita',
  'pv.atividade.retornoSolicitado': 'Pediu retorno',
  'pv.atividade.semResposta': 'Sem resposta',
  'pv.atividade.naoAtendeu': 'Não atendeu',
  'pv.nutricao.titulo': 'Enviar para nutrição',
  'pv.nutricao.ate': 'Voltar a olhar em',
  'pv.nutricao.observacao': 'Por quê',
  'pv.nutricao.explicacao':
    'O estágio não muda: o processo fica onde parou, e volta exatamente de onde saiu.',
  'pv.descarte.titulo': 'Descartar prospecção',
  'pv.descarte.motivo': 'Motivo',
  'pv.descarte.observacao': 'Observação',
  'pv.descarte.exigeObservacao': 'Este motivo exige uma observação escrita.',
  'pv.descarte.explicacao':
    'Nada é apagado. O motivo fica no histórico, e a prospecção pode ser reativada depois.',
  'pv.reativar.titulo': 'Reativar prospecção',
  'pv.reativar.explicacao':
    'O motivo anterior permanece no histórico: reativar não apaga a conclusão de antes.',
  'pv.listas.titulo': 'Listas de prospecção',
  'pv.listas.subtitulo':
    'A lista é um retrato de quem entrou, quando e por quê — e não uma consulta que muda sozinha.',
  'pv.listas.nova': 'Nova lista',
  'pv.listas.nome': 'Nome',
  'pv.listas.descricao': 'Descrição',
  'pv.listas.empresas': 'Empresas',
  'pv.listas.processos': 'Processos',
  'pv.listas.importar': 'Importar empresas',
  'pv.listas.previa': 'Ver prévia',
  'pv.listas.aplicar': 'Aplicar importação',
  'pv.listas.colar': 'Cole uma empresa por linha. Nome; CNPJ; site — só o nome é obrigatório.',
  'pv.listas.resumoPrevia': 'Resultado da prévia',
  'pv.listas.linha': 'Linha',
  'pv.listas.situacao': 'Situação',
  'pv.listas.nova_': 'Nova empresa',
  'pv.listas.existente': 'Empresa já cadastrada',
  'pv.listas.duplicidade': 'Possível duplicidade',
  'pv.listas.jaNaLista': 'Já está na lista',
  'pv.listas.invalida': 'Inválida',
  'pv.listas.nenhumaLista': 'Nenhuma lista criada',
  'pv.listas.vazioOQueE':
    'Uma lista reúne as empresas de uma campanha ou de uma origem, com quem entrou e por quê. Importar uma lista abre um processo de prospecção por empresa — sempre como suspect, nunca como lead.',
  'pv.listas.vazioExemplo':
    'Exemplo: "Feira Mecânica 2026" com as 84 empresas que visitaram o estande.',
  'pv.listas.pedirRotulo': 'Montar uma lista com o assistente',
  'pv.listas.pedirPedido':
    'Quero montar uma lista de prospecção. Me ajude a definir o recorte e a primeira abordagem.',
  'pv.listas.nuncaVirLead': 'Nenhuma empresa importada vira lead automaticamente.',
  'pv.cadencias.titulo': 'Cadências e atividades',
  'pv.cadencias.subtitulo':
    'Uma sequência de passos que vira tarefa. Nesta entrega, nada é enviado sozinho.',
  'pv.cadencias.nova': 'Nova cadência',
  'pv.cadencias.passos': 'Passos',
  'pv.cadencias.passo': 'Passo',
  'pv.cadencias.espera': 'Espera (dias úteis)',
  'pv.cadencias.inscricoes': 'Inscrições',
  'pv.cadencias.vazioTitulo': 'Nenhuma cadência criada',
  'pv.cadencias.vazioOQueE':
    'Uma cadência é a sequência combinada de tentativas de contato: pesquisar, ligar, escrever, esperar, ligar de novo. Cada passo vira uma tarefa com data para alguém executar.',
  'pv.cadencias.vazioExemplo':
    'Exemplo: pesquisar a empresa (dia 0), ligar (dia 1), e-mail manual (dia 3), mensagem (dia 6), última ligação (dia 10).',
  'pv.cadencias.pedirRotulo': 'Desenhar uma cadência com o assistente',
  'pv.cadencias.pedirPedido':
    'Me ajude a desenhar uma cadência de prospecção com cinco passos para o nosso público.',
  'pv.cadencias.semEnvioAutomatico': 'A cadência gera tarefa; ela não envia mensagem sozinha.',
  'pv.qualificacao.titulo': 'Qualificação',
  'pv.qualificacao.subtitulo':
    'O que precisa ser verdade para um lead virar oportunidade — escrito, com evidência.',
  'pv.qualificacao.criterios': 'Critérios',
  'pv.qualificacao.criterio': 'Critério',
  'pv.qualificacao.pergunta': 'Pergunta ao cliente',
  'pv.qualificacao.obrigatorio': 'Obrigatório',
  'pv.qualificacao.opcional': 'Opcional',
  'pv.qualificacao.resposta': 'Resposta',
  'pv.qualificacao.evidencia': 'Evidência',
  'pv.qualificacao.evidenciaAjuda':
    'O que o cliente disse, com as palavras dele. É o que sobra quando quem vendeu sai da empresa.',
  'pv.qualificacao.resultado': 'Resultado',
  'pv.qualificacao.aprovado': 'Aprovado',
  'pv.qualificacao.reprovado': 'Reprovado',
  'pv.qualificacao.incompleto': 'Incompleto',
  'pv.qualificacao.emAberto': 'Em aberto',
  'pv.qualificacao.naoPerguntei':
    'Não perguntar não é a mesma coisa que não ter: critério sem resposta fica em aberto.',
  'pv.qualificacao.salvar': 'Salvar qualificação',
  'pv.qualificacao.salva': 'Qualificação salva',
  'pv.qualificacao.vazioTitulo': 'Nenhum lead aguardando qualificação',
  'pv.qualificacao.vazioOQueE':
    'A qualificação é o checklist que decide o que entra no funil comercial. Ele é configurável: cada organização escreve os critérios que importam para ela, e não uma sigla pronta.',
  'pv.qualificacao.vazioExemplo':
    'Exemplo: aderência, necessidade escrita nas palavras do cliente, contato válido, quem decide, urgência, oferta de interesse e próximo passo acordado.',
  'pv.qualificacao.pedirRotulo': 'Preparar a qualificação com o assistente',
  'pv.qualificacao.pedirPedido':
    'Quais leads estão prontos para qualificar, e o que ainda falta em cada um?',
  'pv.qualificacao.aguardando': 'Leads aguardando qualificação',
  'pv.qualificacao.configurar': 'Configurar critérios',
  'pv.conversao.titulo': 'Converter em oportunidade',
  'pv.conversao.explicacao':
    'A conversão cria uma oportunidade comercial de verdade, com responsável e previsão. É explícita, registrada e acontece uma vez só.',
  'pv.conversao.pipeline': 'Pipeline comercial',
  'pv.conversao.etapa': 'Etapa inicial',
  'pv.conversao.nome': 'Nome da oportunidade',
  'pv.conversao.tipo': 'Tipo',
  'pv.conversao.responsavel': 'Responsável comercial',
  'pv.conversao.valor': 'Valor estimado',
  'pv.conversao.previsao': 'Previsão de fechamento',
  'pv.conversao.contatos': 'Contatos participantes',
  'pv.conversao.observacao': 'Observação de passagem',
  'pv.conversao.confirmar': 'Converter',
  'pv.conversao.feita': 'Lead convertido em oportunidade',
  'pv.conversao.jaFeita': 'Este processo já havia sido convertido.',
  'pv.conversao.abrirOportunidade': 'Abrir a oportunidade',
  'pv.conversao.naoPode':
    'Este processo ainda não pode ser convertido: é preciso estar em Qualificados, com a qualificação aprovada.',
  'pv.novo.titulo': 'Nova prospecção',
  'pv.novo.empresaExistente': 'Empresa já cadastrada',
  'pv.novo.empresaNova': 'Cadastrar empresa nova',
  'pv.novo.nomeDaEmpresa': 'Nome da empresa',
  'pv.novo.semCnpj': 'CNPJ, endereço e telefone não são obrigatórios para começar.',
  'pv.novo.duplicidades': 'Empresas parecidas',
  'pv.novo.usarEsta': 'Usar esta',
  'pv.novo.confirmarOutra': 'É outra empresa, cadastrar mesmo assim',
  'pv.novo.criado': 'Prospecção aberta',
  'pv.novo.criar': 'Abrir prospecção',

  // ---- CERES-022: a experiência de Pré-Vendas ----
  'nav.carteiraDePreVendas': 'Carteira de pré-vendas',
  'pv.acao.AVALIAR': 'Avaliar empresa',
  'pv.acao.INICIAR_CONTATO': 'Iniciar contato',
  'pv.acao.REGISTRAR_RESPOSTA': 'Registrar resposta',
  'pv.acao.QUALIFICAR': 'Qualificar lead',
  'pv.acao.CRIAR_OPORTUNIDADE': 'Criar oportunidade no Comercial',
  'pv.acao.REATIVAR': 'Reativar pré-venda',
  'pv.acao.ABRIR_OPORTUNIDADE': 'Abrir a oportunidade',
  'pv.carteira.titulo': 'Carteira de pré-vendas',
  'pv.carteira.subtitulo': 'Empresas que sua equipe está preparando para o Comercial.',
  'pv.carteira.adicionarEmpresas': 'Adicionar empresas',
  'pv.carteira.iniciarPreVenda': 'Iniciar pré-venda',
  'pv.carteira.configurar': 'Configurar pré-vendas',
  'pv.carteira.empresasEmTrabalho': 'empresas em trabalho',
  'pv.carteira.acoesAtrasadas': 'ações atrasadas',
  'pv.carteira.prontasComercial': 'prontas para o Comercial',
  'pv.carteira.visaoInicio': 'Início',
  'pv.carteira.visaoQualificacao': 'Qualificação',
  'pv.carteira.visaoResultados': 'Resultados',
  'pv.carteira.funil': 'Funil',
  'pv.carteira.lista': 'Lista',
  'pv.carteira.buscar': 'Buscar empresa ou contato',
  'pv.carteira.responsavel': 'Responsável',
  'pv.carteira.todos': 'Todos',
  'pv.carteira.filtroLista': 'Lista',
  'pv.carteira.todas': 'Todas',
  'pv.carteira.somenteAtrasadas': 'Somente atrasadas',
  'pv.carteira.semProximaAcao': 'Sem próxima ação',
  'pv.carteira.limpar': 'Limpar filtros',
  'pv.carteira.vazioTitulo': 'Sua carteira ainda está vazia.',
  'pv.carteira.vazioTexto':
    'Adicione empresas existentes, importe uma planilha ou cadastre a primeira empresa.',
  'pv.carteira.colunaVazia': 'Nenhuma empresa aqui',
  'pv.carteira.proximo': 'Próximo',
  'pv.carteira.atrasadaEm': 'Atrasada',
  'pv.carteira.tentativas': 'tentativas',
  'pv.carteira.semResultado': 'Nenhuma empresa com esses filtros.',
  'pv.carteira.semEtapa': 'Sem próxima ação definida',
  'pv.carteira.abrirEmpresa': 'Abrir empresa',
  'pv.painel.eyebrow': 'Empresa em pré-venda',
  'pv.painel.etapaAtual': 'Etapa atual',
  'pv.painel.avancarEtapa': 'Avançar etapa',
  'pv.painel.oQueFalta': 'O que falta agora',
  'pv.painel.nadaFalta': 'Tudo pronto para avançar.',
  'pv.painel.proximaAcao': 'Próxima ação',
  'pv.painel.nenhumaAcao': 'Nenhuma ação agendada',
  'pv.painel.agendar': 'Agendar',
  'pv.painel.cadastroMestre': 'Cadastro mestre compartilhado',
  'pv.painel.estagioDoCiclo': 'O estágio pertence a este ciclo de pré-venda.',
  'pv.painel.contatoPrincipal': 'Contato principal',
  'pv.painel.definirContato': 'Definir contato',
  'pv.painel.historico': 'Histórico',
  'pv.painel.atividades': 'Atividades',
  'pv.atribuicoes.titulo': 'Histórico de atribuição',
  'pv.atribuicoes.carregando': 'Carregando o histórico…',
  'pv.atribuicoes.erro': 'Não foi possível carregar o histórico de atribuição.',
  'pv.atribuicoes.vazio': 'Nenhuma atribuição registrada neste ciclo.',
  'pv.atribuicoes.ninguem': 'Sem responsável',
  'pv.atribuicoes.semNome': 'Pessoa sem nome',
  'pv.atribuicoes.por': 'por',
  'pv.atribuicoes.motivo': 'Motivo',
  'pv.atribuicoes.origem.gestor': 'Atribuído pelo gestor',
  'pv.atribuicoes.origem.rodizio': 'Rodízio automático',
  'pv.atribuicoes.origem.reatribuicao': 'Reatribuição',
  'pv.painel.nutrirDescartar': 'Nutrir ou descartar',
  'pv.painel.maisAcoes': 'Mais ações',
  'pv.painel.atribuir': 'Atribuir responsável',
  'pv.painel.editar': 'Editar pré-venda',
  'pv.painel.fechar': 'Fechar painel',
  'pv.painel.ultimaInteracao': 'Última interação',
  'pv.painel.semInteracao': 'Ainda não houve interação',
  'pv.painel.origemLista': 'Origem',
  'pv.painel.registrarContato': 'Registrar contato',
  'pv.painel.resumo': 'Resumo',
  'pv.plano.titulo': 'Plano de contato',
  'pv.plano.explicacao':
    'Uma sequência de lembretes para sua equipe não esquecer nenhuma tentativa de contato.',
  'pv.plano.aplicar': 'Aplicar plano de contato',
  'pv.plano.nenhum': 'Nenhum plano de contato aplicado.',
  'pv.plano.passo': 'Passo',
  'pv.plano.de': 'de',
  'pv.plano.pausar': 'Pausar',
  'pv.plano.retomar': 'Retomar',
  'pv.plano.encerrar': 'Encerrar',
  'pv.plano.aplicado': 'Plano de contato aplicado',
  'pv.plano.semPlanos': 'Nenhum plano de contato configurado ainda.',
  'pv.plano.novo': 'Novo plano de contato',
  'pv.plano.removerEtapa': 'Remover etapa',
  'pv.plano.acrescentarEtapa': 'Acrescentar etapa',
  'pv.plano.espera': 'Esperar',
  'pv.plano.diasUteis': 'dias úteis',
  'pv.plano.previa': 'Como a sequência fica',
  'pv.plano.semEnvio': 'O plano cria tarefas para a sua equipe. Nada é enviado automaticamente.',
  'pv.iniciar.titulo': 'Iniciar pré-venda',
  'pv.iniciar.passo1': '1. Selecione a empresa',
  'pv.iniciar.pesquiseAntes': 'Pesquise antes de cadastrar para evitar duplicidade.',
  'pv.iniciar.buscarPlaceholder': 'Buscar por nome, CNPJ ou domínio',
  'pv.iniciar.existenteNoCrm': 'Empresa existente no CRM',
  'pv.iniciar.selecionar': 'Selecionar',
  'pv.iniciar.trocar': 'Trocar empresa',
  'pv.iniciar.naoEncontrou': 'Não encontrou?',
  'pv.iniciar.cadastrarNova': 'Cadastrar nova empresa',
  'pv.iniciar.passo2': '2. Dados desta pré-venda',
  'pv.iniciar.objetivo': 'Objetivo ou serviço',
  'pv.iniciar.listaOpcional': 'Lista (opcional)',
  'pv.iniciar.primeiraAcao': 'Primeira ação (opcional)',
  'pv.iniciar.jaExiste': 'Esta empresa já tem uma pré-venda ativa com este objetivo.',
  'pv.iniciar.abrirExistente': 'Abrir a existente',
  'pv.iniciar.criada': 'Pré-venda iniciada',
  'pv.iniciar.digiteParaBuscar': 'Digite pelo menos duas letras para buscar.',
  'pv.iniciar.nenhumaEncontrada': 'Nenhuma empresa encontrada com esse termo.',
  'pv.iniciar.nomeDaEmpresa': 'Nome da empresa',
  'pv.iniciar.site': 'Site ou domínio (opcional)',
  'pv.iniciar.segmentoOpcional': 'Segmento (opcional)',
  'pv.iniciar.semCnpj': 'CNPJ não é obrigatório para começar.',
  'pv.iniciar.voltarBusca': 'Voltar à busca',
  'pv.iniciar.parecidas': 'Empresas parecidas já cadastradas',
  'pv.iniciar.usarEsta': 'Usar esta',
  'pv.iniciar.mesmoAssim': 'É outra empresa, cadastrar mesmo assim',
  'pv.adicionar.titulo': 'Adicionar empresas',
  'pv.adicionar.escolha': 'Escolha como as empresas entrarão na sua carteira.',
  'pv.adicionar.selecionarCrm': 'Selecionar no CRM',
  'pv.adicionar.selecionarCrmTexto': 'Busque e selecione empresas que já estão cadastradas.',
  'pv.adicionar.recomendado': 'Recomendado',
  'pv.adicionar.importar': 'Importar planilha',
  'pv.adicionar.importarTexto': 'Confira duplicidades e erros antes de confirmar.',
  'pv.adicionar.formatos': 'Uma empresa por linha',
  'pv.adicionar.cadastrar': 'Cadastrar empresa',
  'pv.adicionar.cadastrarTexto': 'Crie uma empresa com apenas os dados mínimos.',
  'pv.adicionar.cadastroRapido': 'Cadastro rápido',
  'pv.adicionar.empresasExistentes': 'Empresas existentes',
  'pv.adicionar.buscarEmpresa': 'Buscar empresa',
  'pv.adicionar.naoEstaNaCarteira': 'Não está na carteira',
  'pv.adicionar.jaNaCarteira': 'Já está na carteira',
  'pv.adicionar.adicionarALista': 'Adicionar à lista',
  'pv.adicionar.selecionadas': 'Adicionar selecionadas',
  'pv.adicionar.semLista': 'Sem lista',
  'pv.adicionar.novaLista': 'Criar nova lista',
  'pv.adicionar.nomeDaLista': 'Nome da lista',
  'pv.adicionar.oQueEhLista':
    'Um grupo de empresas que entrou pela mesma origem ou iniciativa, como um evento, indicação ou campanha.',
  'pv.adicionar.iniciarJa': 'Já iniciar a pré-venda das empresas adicionadas',
  'pv.adicionar.adicionadas': 'Empresas adicionadas',
  'pv.adicionar.nenhumaSelecionada': 'Selecione pelo menos uma empresa.',
  'pv.adicionar.minhasListas': 'Suas listas',
  'pv.adicionar.verMembros': 'Ver empresas',
  'pv.adicionar.remover': 'Remover da lista',
  'pv.adicionar.colar':
    'Cole uma empresa por linha: nome; documento; site. Só o nome é obrigatório.',
  'pv.adicionar.conferir': 'Conferir',
  'pv.adicionar.confirmarImportacao': 'Confirmar importação',
  'pv.empresa.titulo': 'Pré-vendas',
  'pv.empresa.subtitulo': 'Ciclos ativos e histórico desta empresa.',
  'pv.empresa.nenhumCiclo': 'Nenhuma pré-venda ativa',
  'pv.empresa.nenhumCicloTexto':
    'Quando alguém começar a preparar esta empresa para o Comercial, o ciclo aparece aqui.',
  'pv.empresa.iniciar': 'Iniciar pré-venda',
  'pv.empresa.iniciarOutra': 'Iniciar outra pré-venda',
  'pv.empresa.abrirNaCarteira': 'Abrir na Carteira',
  'pv.empresa.encerrados': 'Encerrados',
  'pv.empresa.semObjetivo': 'Pré-venda sem objetivo definido',
  'pv.empresa.emPreVenda': 'Em pré-venda',
  'pv.config.titulo': 'Configurar pré-vendas',
  'pv.config.subtitulo': 'Configurações administrativas; não fazem parte da rotina do operador.',
  'pv.config.voltar': 'Voltar à Carteira',
  'pv.config.secoes': 'Configurações',
  'pv.config.etapas': 'Etapas do funil',
  'pv.config.etapasTexto':
    'Nome, ordem e critérios de cada coluna. O significado interno de cada etapa não muda.',
  'pv.config.planos': 'Planos de contato',
  'pv.config.perguntas': 'Perguntas de qualificação',
  'pv.config.perguntasTexto':
    'Defina o que precisa ser respondido antes de enviar uma empresa ao Comercial.',
  'pv.config.motivos': 'Motivos de descarte',
  'pv.config.motivosTexto': 'Por que uma pré-venda pode ser encerrada.',
  'pv.config.novaPergunta': 'Nova pergunta',
  'pv.config.obrigatoria': 'Obrigatória',
  'pv.config.opcional': 'Opcional',
  'pv.config.ativa': 'Em uso',
  'pv.config.inativa': 'Fora de uso',
  'pv.config.editar': 'Editar',
  'pv.config.salvarPergunta': 'Salvar pergunta',
  'pv.config.tipoDaResposta': 'Tipo da resposta',
  'pv.config.pergunta': 'Pergunta',
  'pv.config.ajudaDaPergunta': 'Texto de apoio (opcional)',
  'pv.config.opcoes': 'Opções, separadas por vírgula',
  'pv.config.nomeDaEtapa': 'Nome que a equipe vê',
  'pv.config.criterioEntrada': 'Para entrar nesta etapa',
  'pv.config.criterioSaida': 'Para sair desta etapa',
  'pv.config.significado': 'Significado interno',
  'pv.config.semPermissao': 'Você não tem permissão para configurar pré-vendas.',
  'pv.tipo.BOOLEANO': 'Sim ou não',
  'pv.tipo.TEXTO': 'Resposta em texto',
  'pv.tipo.ESCOLHA': 'Escolha entre opções',
  'pv.tipo.NUMERO': 'Número',
  'pv.tipo.DATA': 'Data',
  'pv.tipo.CONTATO': 'Selecionar contato',
  'pv.tipo.OFERTA': 'Selecionar serviço',
  'pv.qual.titulo': 'Qualificação',
  'pv.qual.concluidos': 'concluídos',
  'pv.qual.responder': 'Responder qualificação',
  'pv.qual.editar': 'Editar qualificação',
  'pv.qual.salvar': 'Salvar qualificação',
  'pv.qual.salva': 'Qualificação salva',
  'pv.qual.evidencia': 'O que sustenta esta resposta',
  'pv.qual.evidenciaAjuda': 'O que o cliente disse, com as palavras dele.',
  'pv.qual.sim': 'Sim',
  'pv.qual.nao': 'Não',
  'pv.qual.semResposta': 'Ainda não respondido',
  'pv.qual.selecioneContato': 'Selecione o contato',
  'pv.qual.selecioneOferta': 'Selecione o serviço',
  'pv.qual.aprovada': 'Esta empresa está pronta para o Comercial.',
  'pv.qual.faltam': 'Ainda faltam respostas obrigatórias.',
  'pv.qual.semContatos': 'Cadastre um contato na empresa para responder.',
  'pv.qual.semOfertas': 'Nenhum serviço cadastrado no catálogo.',
  'pv.resultados.titulo': 'Resultados',
  'pv.resultados.subtitulo': 'Como a geração de leads vem andando.',
  'pv.resultados.periodo': 'Período',
  'pv.resultados.semDados': 'Ainda não há histórico suficiente para este recorte.',
  'pv.prioridadeBaixa': 'Baixa',
  'pv.prioridadeMedia': 'Média',
  'pv.prioridadeAlta': 'Alta',
  'pv.coluna.SUSPECT': 'Suspects · ainda vamos avaliar',
  'pv.coluna.PROSPECT': 'Prospects · vale abordar',
  'pv.coluna.OUTREACH': 'Abordagem iniciada',
  'pv.coluna.ENGAGED': 'Engajadas',
  'pv.coluna.LEAD': 'Leads · entender a necessidade',
  'pv.coluna.QUALIFIED': 'Qualificadas',
  'pv.etapa.SUSPECT': 'Novas empresas',
  'pv.etapa.PROSPECT': 'Selecionadas',
  'pv.etapa.OUTREACH': 'Em contato',
  'pv.etapa.ENGAGED': 'Demonstraram interesse',
  'pv.etapa.LEAD': 'Em qualificação',
  'pv.etapa.QUALIFIED': 'Prontas para o Comercial',
  'pv.passo.PESQUISA': 'Pesquisar a empresa',
  'pv.passo.LIGACAO': 'Ligar',
  'pv.passo.EMAIL_MANUAL': 'Escrever um e-mail',
  'pv.passo.MENSAGEM': 'Mandar mensagem',
  'pv.passo.CONEXAO_SOCIAL': 'Conectar em rede social',
  'pv.passo.FOLLOW_UP': 'Retomar o contato',
  'pv.passo.REUNIAO': 'Reunião',
  'pv.passo.ESPERA': 'Esperar',
  'pv.acao.REGISTRAR_CONTATO': 'Registrar contato',
  'pv.acao.INICIAR_QUALIFICACAO': 'Iniciar qualificação',
  'pv.acao.CONCLUIR_QUALIFICACAO': 'Concluir qualificação',
  'pv.painel.proximaEtapaSera': 'A próxima etapa é',
  'pv.avaliar.oQueAcontece':
    'Ao aprovar, a empresa é classificada como prospect e passa para "Selecionadas".',
  'pv.avaliar.perguntaAderencia': 'Esta empresa tem aderência ao perfil desejado?',
  'pv.avaliar.sim': 'Sim',
  'pv.avaliar.nao': 'Não',
  'pv.avaliar.semInformacao': 'Ainda não tenho informações suficientes',
  'pv.avaliar.perguntaJustificativa': 'Por que esta empresa tem ou não tem aderência?',
  'pv.avaliar.ajudaJustificativa': 'É o que a próxima pessoa vai ler para entender a decisão.',
  'pv.avaliar.exemploJustificativa':
    'Ex.: indústria de médio porte com time de QA próprio, como os clientes que mais crescem conosco.',
  'pv.avaliar.responsavel': 'Responsável pela pré-venda',
  'pv.avaliar.oferta': 'Oferta ou serviço de interesse',
  'pv.avaliar.semOferta': 'Ainda não definida',
  'pv.avaliar.observacoes': 'Observações (opcional)',
  'pv.avaliar.aprovar': 'Aprovar como prospect',
  'pv.avaliar.nutrir': 'Enviar para nutrição',
  'pv.avaliar.descartar': 'Descartar',
  'pv.avaliar.registrada': 'Avaliação registrada',
  'pv.avaliar.aprovadaEMovida': 'Empresa aprovada como prospect e movida para',
  'pv.avaliar.exigeAderencia':
    'Para aprovar como prospect, marque que a empresa tem aderência. Sem aderência, os desfechos são nutrição ou descarte.',
  'pv.avaliar.exigeJustificativa': 'Escreva por que esta empresa tem aderência.',
  'pv.avaliar.exigeResponsavel': 'Escolha quem responde por esta pré-venda.',
  'pv.avaliar.exigeData': 'Informe até quando esta empresa fica em nutrição.',
  'pv.avaliar.exigeMotivo': 'Escolha o motivo do descarte.',
  'pv.ajuste.responsavelDefinido': 'Responsável definido',
  'pv.ajuste.acaoAgendada': 'Próxima ação agendada',
  'pv.ajuste.contatoDefinido': 'Contato principal definido',
  'pv.ajuste.porQueResponsavel': 'Uma empresa sem responsável não entra na rotina de ninguém.',
  'pv.ajuste.porQueProximaAcao':
    'Sem data, a ação não vence nunca — e a empresa some da sua semana.',
  'pv.ajuste.porQueContato': 'É com esta pessoa que o Comercial vai falar quando receber o lead.',
  'pv.ajuste.oQueFazer': 'O que fazer',
  'pv.ajuste.quando': 'Quando',
  'pv.ajuste.exemploAcao': 'Ex.: ligar para o diretor de operações',
  'pv.ajuste.semContatos': 'Esta empresa ainda não tem contatos cadastrados.',
  'pv.carteira.areas': 'Áreas da carteira',
  'pv.carteira.modoDeVisualizacao': 'Ver como',
  'pv.area.prospeccao': 'Prospecção',
  'pv.area.prospeccaoApoio': 'Encontre empresas aderentes e inicie o relacionamento.',
  'pv.area.qualificacao': 'Qualificação',
  'pv.area.qualificacaoApoio': 'Entenda a necessidade e prepare a entrega ao time Comercial.',
  'pv.area.resultados': 'Resultados',
  'pv.area.resultadosApoio': 'Acompanhe a eficiência da geração e qualificação de leads.',
  'pv.contato.aoRegistrar': 'Quando o registro permitir o avanço, a pré-venda passa para',
  'pv.contato.registradoEMovido': 'Contato registrado e pré-venda movida para',
  'pv.qual.aoConcluir': 'Ao concluir a qualificação, a empresa passa para',
  'pv.qual.salvaEMovida': 'Qualificação salva e pré-venda movida para',
  'pv.avaliar.segmento': 'Segmento ou perfil',
  'pv.avaliar.exemploSegmento': 'Ex.: indústria metalúrgica',
  'pv.visao.processo': 'Processo',
  'pv.visao.funil': 'Funil',
  'pv.visao.lista': 'Lista',
  'pv.visao.resultados': 'Resultados',
  'pv.visao.processoApoio': 'Siga o processo e prepare empresas para o Comercial.',
  'pv.visao.funilApoio': 'Onde as empresas estão agora.',
  'pv.visao.listaApoio': 'Consulta e trabalho em volume.',
  'pv.carteira.visualizacao': 'Visualização',
  'pv.carteira.adicionarAPreVenda': 'Adicionar à pré-venda',
  'pv.processo.navegacao': 'Etapas do processo de pré-vendas',
  'pv.processo.oQueFazer': 'O que fazer nesta etapa',
  'pv.processo.empresasNaEtapa': 'Empresas nesta etapa',
  'pv.processo.comoEntram': 'Como as empresas entram',
  'pv.processo.proximosCaminhos': 'Próximos caminhos',
  'pv.processo.filaVazia': 'Nenhuma empresa aguardando este trabalho.',
  'pv.processo.empresa': 'empresa',
  'pv.processo.empresas': 'empresas',
  'pv.processo.naCarteira': 'na carteira',
  'pv.processo.semSegmento': 'Segmento não informado',
  'pv.processo.alternativos': 'Caminhos alternativos',
  'pv.processo.alternativoNutricao': 'Nutrição · acompanhar depois',
  'pv.processo.alternativoDescarte': 'Descartar · encerrar ciclo',
  'pv.processo.entrada': 'Adicionar empresas',
  'pv.processo.entradaResumo': 'Escolher ou cadastrar',
  'pv.processo.entradaA1': 'Procurar a empresa no CRM',
  'pv.processo.entradaA1Texto': 'Ela quase sempre já existe. Cadastrar de novo duplica a base.',
  'pv.processo.entradaA2': 'Cadastrar só o que se sabe',
  'pv.processo.entradaA2Texto': 'O nome basta para começar. CNPJ e endereço vêm depois.',
  'pv.processo.entradaC1': 'Entra como nova empresa',
  'pv.processo.entradaC1Texto': 'A próxima decisão é avaliar a aderência ao perfil.',
  'pv.processo.entradaComoTexto':
    'Escolha uma empresa já cadastrada ou cadastre a que ainda não existe. Também é possível adicionar várias de uma vez, por lista ou planilha.',
  'pv.processo.avaliar': 'Avaliar aderência',
  'pv.processo.avaliarResumo': 'Decidir se vale abordar',
  'pv.processo.avaliarA1': 'Confirmar o perfil',
  'pv.processo.avaliarA1Texto': 'Compare segmento, porte e contexto.',
  'pv.processo.avaliarA2': 'Registrar a justificativa',
  'pv.processo.avaliarA2Texto': 'Explique por que existe ou não aderência.',
  'pv.processo.avaliarA3': 'Escolher o caminho',
  'pv.processo.avaliarA3Texto': 'Aprovar, nutrir ou descartar.',
  'pv.processo.avaliarC1': 'Tem aderência e será trabalhada agora.',
  'pv.processo.avaliarC2': 'Tem potencial, mas será retomada depois.',
  'pv.processo.avaliarC3': 'Não tem aderência ou o ciclo deve terminar.',
  'pv.processo.acao.avaliar': 'Avaliar próxima empresa',
  'pv.processo.agir.avaliar': 'Avaliar',
  'pv.processo.abordar': 'Fazer abordagem',
  'pv.processo.abordarResumo': 'Iniciar contatos',
  'pv.processo.abordarA1': 'Definir quem e quando',
  'pv.processo.abordarA1Texto': 'Contato principal e data da próxima tentativa.',
  'pv.processo.abordarA2': 'Registrar cada tentativa',
  'pv.processo.abordarA2Texto': 'Ligação, e-mail ou mensagem — com o desfecho.',
  'pv.processo.abordarA3': 'Insistir com intervalo',
  'pv.processo.abordarA3Texto': 'Um plano de contato distribui as tentativas.',
  'pv.processo.abordarC1': 'Houve resposta',
  'pv.processo.abordarC1Texto': 'A empresa passa a demonstrar interesse.',
  'pv.processo.abordarC2': 'Sem resposta depois de várias tentativas.',
  'pv.processo.acao.abordar': 'Registrar contato',
  'pv.processo.agir.abordar': 'Registrar',
  'pv.processo.interesse': 'Confirmar interesse',
  'pv.processo.interesseResumo': 'Registrar resposta',
  'pv.processo.interesseA1': 'Confirmar o que foi dito',
  'pv.processo.interesseA1Texto': 'Resposta, reunião aceita ou retorno pedido.',
  'pv.processo.interesseA2': 'Abrir a qualificação',
  'pv.processo.interesseA2Texto': 'Comece a entender a necessidade real.',
  'pv.processo.interesseC1': 'Vira lead em qualificação',
  'pv.processo.interesseC1Texto': 'Há interesse: agora se entende o cenário.',
  'pv.processo.interesseC2': 'O momento não é agora.',
  'pv.processo.acao.interesse': 'Iniciar qualificação',
  'pv.processo.agir.interesse': 'Qualificar',
  'pv.processo.qualificar': 'Qualificar necessidade',
  'pv.processo.qualificarResumo': 'Entender o cenário',
  'pv.processo.qualificarA1': 'Responder as perguntas',
  'pv.processo.qualificarA1Texto': 'Necessidade, decisão, prazo e orçamento.',
  'pv.processo.qualificarA2': 'Registrar a evidência',
  'pv.processo.qualificarA2Texto': 'Quem disse o quê. Opinião não sustenta proposta.',
  'pv.processo.qualificarA3': 'Definir contato e oferta',
  'pv.processo.qualificarA3Texto': 'O Comercial precisa saber com quem falar, sobre o quê.',
  'pv.processo.qualificarC1': 'Fica pronta para o Comercial',
  'pv.processo.qualificarC1Texto': 'Qualificação aprovada: a entrega pode acontecer.',
  'pv.processo.qualificarC2': 'A necessidade não se confirmou.',
  'pv.processo.acao.qualificar': 'Concluir qualificação',
  'pv.processo.agir.qualificar': 'Concluir',
  'pv.processo.entregar': 'Entregar ao Comercial',
  'pv.processo.entregarResumo': 'Criar oportunidade',
  'pv.processo.entregarA1': 'Revisar as evidências',
  'pv.processo.entregarA1Texto': 'O que sustenta a necessidade registrada.',
  'pv.processo.entregarA2': 'Confirmar contato e oferta',
  'pv.processo.entregarA2Texto': 'Quem fala pela empresa e o que interessa a ela.',
  'pv.processo.entregarA3': 'Criar a oportunidade',
  'pv.processo.entregarA3Texto': 'A empresa é a mesma; o histórico vai junto.',
  'pv.processo.entregarC1': 'Vira oportunidade no pipeline',
  'pv.processo.entregarC1Texto': 'O ciclo de pré-venda se encerra, e o Comercial assume.',
  'pv.processo.acao.entregar': 'Criar oportunidade',
  'pv.processo.agir.entregar': 'Entregar',
  'pv.processo.acao.entrada': 'Adicionar à pré-venda',
  'pv.processo.agir.entrada': 'Abrir',
  'pv.catalogo.titulo': 'Produtos ou serviços de interesse',
  'pv.catalogo.fonte': 'Fonte: Catálogo Comercial desta organização.',
  'pv.catalogo.vazio': 'Nenhum produto ou serviço ativo foi encontrado no Catálogo Comercial.',
  'pv.catalogo.abrir': 'Abrir Catálogo Comercial',
  'pv.catalogo.produtos': 'Produtos',
  'pv.catalogo.servicos': 'Serviços',
  'pv.catalogo.buscar': 'Buscar no catálogo',
  'pv.avaliar.motivoNutricao': 'Motivo da nutrição',
  'pv.avaliar.exemploMotivoNutricao': 'Ex.: momento inadequado para iniciar a abordagem',
  'pv.avaliar.retomarEm': 'Retomar acompanhamento em',
  'pv.avaliar.ajudaRetomar': 'Uma atividade será criada para esta data.',
  'pv.avaliar.semAutomacao':
    'O CERES não enviará mensagens automaticamente. A empresa ficará pausada e será lembrada na data escolhida.',
  'pv.avaliar.nutricaoPreserva':
    'A empresa continuará cadastrada e poderá ser reativada no mesmo ciclo.',
  'pv.avaliar.descartePreserva':
    'O descarte encerra apenas este ciclo de pré-venda. A empresa continua no CRM.',
  'pv.avaliar.exigeMotivoNutricao': 'Escreva por que esta empresa não será trabalhada agora.',
  'pv.avaliar.obrigatorio': 'Obrigatório',
  'pv.avaliar.opcional': 'Opcional',
  'pv.avaliar.parcial': 'Salvar avaliação parcial',
  'pv.avaliar.parcialSalva': 'Avaliação parcial salva. A empresa continua em Novas empresas.',
  'pv.avaliar.oQueFalta': 'O que ainda falta descobrir',
  'pv.avaliar.segmentoDoCadastro': 'Dado do cadastro mestre da empresa.',
  'pv.empresa.editar': 'Editar empresa',

  // ==================== CATÁLOGO COMERCIAL UNIFICADO (CERES-028) ============
  //
  // Uma central, quatro áreas. O vocabulário aqui evita nomes internos de
  // tabela e de módulo: quem lê a tela fala de "oferta", "conteúdo" e
  // "material" — nunca de `playbook_entry` ou `content_asset`.
  'cat.titulo': 'Catálogo Comercial',
  'cat.subtitulo': 'Produtos, serviços e o conhecimento necessário para vendê-los.',
  'cat.areasNav': 'Áreas do Catálogo Comercial',
  'cat.areaOfertas': 'Ofertas',
  'cat.areaMetodo': 'Método comercial',
  'cat.areaBiblioteca': 'Biblioteca comercial',
  'cat.areaGovernanca': 'Governança',
  'cat.voltar': 'Voltar ao Catálogo',
  'cat.carregando': 'Carregando',
  'cat.erroGenerico': 'Não foi possível concluir. Tente de novo.',
  'cat.estadoAlterado': 'Estado do conteúdo atualizado.',
  'cat.foraDoPlanoTitulo': 'Conhecimento comercial não está no plano',
  'cat.foraDoPlanoTexto':
    'As áreas de método, biblioteca e governança fazem parte de uma capacidade que esta organização não contratou. O cadastro de ofertas continua disponível.',
  'cat.conhecimentoIndisponivel': 'Conteúdo indisponível nesta organização.',
  'cat.indOfertasAtivas': 'Ofertas ativas',
  'cat.indEmConstrucao': 'Em construção',
  'cat.indIncompletas': 'Conteúdo incompleto',
  'cat.indAguardando': 'Aguardando aprovação',
  'cat.indVencendo': 'Vencendo em 30 dias',
  'cat.indMigracao': 'Revisão de migração',
  'cat.novaOferta': 'Nova oferta',
  'cat.buscar': 'Busque uma oferta, objeção, pergunta, case ou material',
  'cat.resultadosDaBusca': 'Resultados',
  'cat.buscando': 'Buscando…',
  'cat.fonteOferta': 'Oferta',
  'cat.fonteConteudo': 'Conteúdo',
  'cat.fonteMaterial': 'Material',
  'cat.filtroTipo': 'Filtrar por tipo',
  'cat.todosTipos': 'Todos os tipos',
  'cat.filtroSituacao': 'Filtrar por situação',
  'cat.todasSituacoes': 'Todas as situações',
  'cat.ativos': 'Ativas',
  'cat.inativos': 'Em construção',
  'cat.filtroCompletude': 'Filtrar por completude',
  'cat.qualquerCompletude': 'Qualquer completude',
  'cat.completas': 'Prontas para vender',
  'cat.incompletas': 'Com conteúdo incompleto',
  'cat.filtroResponsavel': 'Filtrar por responsável',
  'cat.qualquerResponsavel': 'Qualquer responsável',
  'cat.completude': 'Completude',
  'cat.ultimaRevisao': 'Última revisão',
  'cat.responsavel': 'Responsável',
  'cat.modelo': 'Modelo',
  'cat.tipo': 'Tipo',
  'cat.ativo': 'Ativa',
  'cat.inativo': 'Em construção',
  'cat.acoes': 'Abrir ações da oferta',
  'cat.consultar': 'Consultar',
  'cat.editar': 'Editar',
  'cat.duplicar': 'Duplicar',
  'cat.arquivar': 'Arquivar',
  'cat.reativar': 'Reativar',
  'cat.arquivarTitulo': 'Arquivar esta oferta?',
  'cat.arquivarTexto':
    'A oferta sai da seleção de novos negócios e continua no histórico das negociações já feitas. Nada é excluído.',
  'cat.arquivada': 'Oferta arquivada.',
  'cat.reativada': 'Oferta reativada.',
  'cat.duplicarTitulo': 'Duplicar esta oferta?',
  'cat.duplicarTexto':
    'Uma cópia do cadastro é aberta para edição. O conhecimento comercial não é copiado: ele é vinculado à oferta que você escolher.',
  'cat.vazioTitulo': 'Nenhuma oferta cadastrada',
  'cat.vazioTexto':
    'O Catálogo é a fonte única do que a empresa vende. Cadastre aqui, e o item passa a valer nas oportunidades, propostas e no conhecimento comercial.',
  'cat.vazioBuscaTitulo': 'Nada encontrado',
  'cat.vazioBuscaTexto': 'Ajuste os filtros ou o termo buscado.',
  'cat.cadastrarPrimeira': 'Cadastrar a primeira oferta',
  'cat.conteudosAprovados': 'aprovados e vigentes',
  'cat.conteudosAguardando': 'aguardando',
  'cat.abasDaOferta': 'Conteúdo desta oferta',
  'cat.abaVisaoGeral': 'Visão geral',
  'cat.abaClienteIdeal': 'Cliente ideal',
  'cat.abaDescoberta': 'Descoberta e qualificação',
  'cat.abaArgumentacao': 'Argumentação',
  'cat.abaObjecoes': 'Objeções e FAQ',
  'cat.abaCases': 'Cases e evidências',
  'cat.abaMateriais': 'Materiais',
  'cat.verComoVendedor': 'Ver como vendedor',
  'cat.sairDoModoVendedor': 'Sair do modo vendedor',
  'cat.modoVendedorAviso':
    'Modo vendedor: aparece apenas o conteúdo aprovado e dentro da validade.',
  'cat.editarOferta': 'Editar oferta',
  'cat.editarSecao': 'Editar seção',
  'cat.prontoParaVender': 'Pronto para vender',
  'cat.dimPosicionamento': 'Posicionamento',
  'cat.dimClienteIdeal': 'Cliente ideal',
  'cat.dimDescoberta': 'Descoberta',
  'cat.dimArgumentacao': 'Argumentação',
  'cat.dimObjecoes': 'Objeções',
  'cat.dimCases': 'Cases',
  'cat.dimMateriais': 'Materiais',
  'cat.novoConteudo': 'Novo conteúdo',
  'cat.editarConteudo': 'Editar',
  'cat.semConteudoTitulo': 'Nada escrito ainda',
  'cat.semConteudoTexto':
    'O que for escrito aqui passa a responder a quem vende — depois de aprovado.',
  'cat.geral': 'Geral',
  'cat.geralAjuda': 'Conteúdo da casa, associado a esta oferta sem cópia.',
  'cat.emOutrasOfertas': 'Também em outras ofertas',
  'cat.personasAssociadas': 'Personas associadas',
  'cat.semPersonas': 'Nenhuma persona associada a esta oferta.',
  'cat.papelNaCompra': 'Papel na compra',
  'cat.influencia': 'Influência',
  'cat.perfisDeClienteIdeal': 'Perfis de cliente ideal',
  'cat.perfilGlobal': 'Herdado da casa',
  'cat.perfilDaOferta': 'Específico desta oferta',
  'cat.semPerfis': 'Nenhum perfil de cliente ideal cadastrado.',
  'cat.associarPersona': 'Associar persona',
  'cat.estadoRASCUNHO': 'Rascunho',
  'cat.estadoEM_REVISAO': 'Em revisão',
  'cat.estadoAPROVADO': 'Aprovado',
  'cat.estadoEXPIRADO': 'Vencido',
  'cat.estadoAGENDADO': 'Agendado',
  'cat.estadoARQUIVADO': 'Arquivado',
  'cat.estadoSUBSTITUIDO': 'Substituído',
  'cat.renovarVigencia': 'Renovar validade',
  'cat.vigenciaAtualizada':
    'Validade atualizada. O conteúdo volta a valer sem passar por revisão de novo.',
  'cat.valeDe': 'Vale a partir de',
  'cat.valeAte': 'Vale até',
  'cat.vigenciaAjuda':
    'Deixe em branco para conteúdo sem prazo. Renovar não exige reaprovar o texto.',
  'cat.validoAte': 'Válido até',
  'cat.venceEmDias': 'Vence em',
  'cat.dias': 'dias',
  'cat.versao': 'Versão',
  'cat.assistenteTitulo': 'Assistente de argumentação',
  'cat.assistenteAjuda':
    'Escreva o que o cliente disse, com as palavras dele. A resposta usa apenas conteúdo aprovado e dentro da validade desta oferta, e mostra de onde cada trecho veio.',
  'cat.oQueClienteDisse': 'O que o cliente disse',
  'cat.comQuemVaiFalar': 'Com quem você vai falar',
  'cat.naoSeiAinda': 'Não sei ainda',
  'cat.emQueEtapa': 'Em que etapa da venda',
  'cat.qualquerEtapa': 'Qualquer etapa',
  'cat.tamanhoDaResposta': 'Tamanho da resposta',
  'cat.tamanhoCurto': 'Curta',
  'cat.tamanhoMedio': 'Média',
  'cat.tamanhoCompleto': 'Completa',
  'cat.prepararResposta': 'Preparar resposta',
  'cat.papelPITCH': 'Pitch',
  'cat.papelOBJECAO': 'Resposta de objeção',
  'cat.papelFAQ': 'FAQ',
  'cat.papelMENSAGEM': 'Mensagem',
  'cat.papelPROVA': 'Prova',
  'cat.veioDe': 'Veio de',
  'cat.fontesConsultadas': 'conteúdos aprovados consultados',
  'cat.metodoTitulo': 'Método Comercial',
  'cat.metodoSubtitulo':
    'Como a organização qualifica, conduz, aprova e fecha negócios. Vale para todas as ofertas.',
  'cat.metodoVazioTitulo': 'Método ainda não escrito',
  'cat.metodoVazioTexto':
    'Processo, qualificação, alçadas e treinamento ficam aqui uma vez — e não repetidos dentro de cada oferta.',
  'cat.metodoAlcadas': 'Descontos, exceções e alçadas',
  'cat.metodoAlcadasTexto':
    'As alçadas são configuráveis por papel, escopo e condição, e vivem nas regras comerciais da organização.',
  'cat.metodoAbrirAlcadas': 'Abrir regras comerciais',
  'cat.bibTitulo': 'Biblioteca Comercial',
  'cat.bibSubtitulo': 'Materiais reutilizáveis, ligados às ofertas, personas e etapas da venda.',
  'cat.bibBuscar': 'Busque apresentações, cases, roteiros, vídeos ou documentos',
  'cat.bibColMaterial': 'Material',
  'cat.bibColOferta': 'Oferta',
  'cat.bibColEtapa': 'Etapa',
  'cat.bibColPublico': 'Público',
  'cat.bibColIdioma': 'Idioma',
  'cat.bibColVersao': 'Versão',
  'cat.bibColEstado': 'Estado',
  'cat.bibColValidade': 'Validade',
  'cat.bibFiltroTipo': 'Filtrar por tipo',
  'cat.bibFiltroOferta': 'Filtrar por oferta',
  'cat.bibTodasOfertas': 'Todas as ofertas',
  'cat.bibFiltroEtapa': 'Filtrar por etapa',
  'cat.bibTodasEtapas': 'Todas as etapas',
  'cat.bibFiltroIdioma': 'Filtrar por idioma',
  'cat.bibTodosIdiomas': 'Todos os idiomas',
  'cat.bibFiltroPublico': 'Filtrar por público',
  'cat.bibTodosPublicos': 'Todos os públicos',
  'cat.bibFiltroVigencia': 'Filtrar por validade',
  'cat.bibTodasValidades': 'Qualquer validade',
  'cat.bibVigentes': 'Vigentes',
  'cat.bibVencendo': 'Vencendo',
  'cat.bibVencidos': 'Vencidos',
  'cat.bibSemOferta': 'Serve a todas',
  'cat.bibVazioTitulo': 'Nenhum material encontrado',
  'cat.bibVazioTexto': 'Ajuste os filtros, ou cadastre o primeiro material da biblioteca.',
  'cat.bibTemplatesNota':
    'Modelos de proposta, contrato, mensagem e reunião continuam nos módulos que executam cada processo. A Biblioteca aponta para eles; não guarda cópias.',
  'cat.bibAbrir': 'Abrir',
  'cat.govTitulo': 'Governança Comercial',
  'cat.govSubtitulo': 'Conteúdo oficial, responsável, validade e histórico de decisão.',
  'cat.govAguardando': 'Aguardando revisão',
  'cat.govAprovados': 'Aprovados e vigentes',
  'cat.govVencendo': 'Próximos do vencimento',
  'cat.govVencidos': 'Vencidos',
  'cat.govSemResponsavel': 'Sem responsável',
  'cat.govMigracao': 'Revisão de migração',
  'cat.govFila': 'Fila de revisão',
  'cat.govFilaVaziaTitulo': 'Nada esperando decisão',
  'cat.govFilaVaziaTexto':
    'Todo o conteúdo aprovado está dentro da validade, e nada foi enviado para revisão.',
  'cat.govConteudo': 'Conteúdo',
  'cat.govContexto': 'Contexto',
  'cat.govEstado': 'Estado',
  'cat.govValidade': 'Validade',
  'cat.govAcoes': 'Ações',
  'cat.govSubmeter': 'Enviar para aprovação',
  'cat.govAprovar': 'Aprovar',
  'cat.govRejeitar': 'Devolver',
  'cat.govArquivar': 'Arquivar',
  'cat.govRestaurar': 'Restaurar',
  'cat.govMotivo': 'Motivo',
  'cat.govMotivoAjuda': 'Quem escreveu precisa saber o que corrigir. Sem isso o texto volta igual.',
  'cat.govMotivoObrigatorio': 'Escreva o motivo da devolução.',
  'cat.govConfirmarRejeicao': 'Devolver para ajuste',
  'cat.govHistorico': 'Histórico de decisões',
  'cat.govHistoricoVazio': 'Nenhuma decisão registrada para este conteúdo.',
  'cat.govVigencia': 'Vigência',
  'cat.govInicio': 'Início',
  'cat.govFim': 'Fim',
  'cat.govSalvarVigencia': 'Salvar vigência',
  'cat.govVigenciaSalva': 'Vigência atualizada.',
  'cat.govMigracaoTitulo': 'Revisão de migração',
  'cat.govMigracaoTexto':
    'Conteúdo que a migração não soube classificar com segurança. Associar por semelhança de nome inventaria um dado que ninguém afirmou — por isso a pergunta vem para uma pessoa.',
  'cat.govClassificar': 'Classificar',
  'cat.govEscolherOfertas': 'A quais ofertas este conteúdo pertence?',
  'cat.govManterGeral': 'É conteúdo geral da casa, sem oferta específica',
  'cat.govSalvarClassificacao': 'Salvar classificação',
  'cat.govClassificado': 'Classificação registrada.',
  'cat.govReexecutar': 'Reexecutar reconciliação',
  'cat.govSimular': 'Simular',
  'cat.govRelatorio': 'Relatório da reconciliação',
  'cat.govCriadas': 'criadas',
  'cat.govAtualizadas': 'atualizadas',
  'cat.govExcecoes': 'exceções',
  'cat.govPendencias': 'pendências de revisão',
  'cat.govTransicaoSUBMETIDO': 'Enviado para aprovação',
  'cat.govTransicaoAPROVADO': 'Aprovado',
  'cat.govTransicaoREJEITADO': 'Devolvido',
  'cat.govTransicaoARQUIVADO': 'Arquivado',
  'cat.govTransicaoRESTAURADO': 'Restaurado',
  'cat.vazioOQueE':
    'O Catálogo Comercial é a fonte única do que a empresa vende e de como cada oferta é vendida. O que é cadastrado aqui passa a valer nas oportunidades, nas propostas, na argumentação de quem vende e nas comissões — sem recadastro em nenhum outro lugar.',
  'cat.vazioExemplo':
    'Você cadastra “Automação de Testes” como serviço por hora. A partir daí, a oferta pode ser adicionada a uma oportunidade, entra na proposta com o preço de referência, e recebe a visão comercial, as objeções e os materiais que o time usa na conversa.',
  'cat.pedidoAgente':
    'Quais produtos e serviços nós vendemos, e o que já está escrito sobre como vendê-los?',

  // ======================================================================
  // CERES-036 — Registro documental de propostas (Fase 2).
  // ======================================================================
  'propostas.subtituloRegistro':
    'Os documentos que foram para o cliente — com versão, valor e validade à vista.',
  'propostas.registrar': 'Registrar proposta externa',
  'propostas.registrarPrimeira': 'Registrar primeira proposta externa',
  'propostas.vazioRegistro':
    'Registre aqui propostas produzidas fora do CERES e mantenha arquivos, versões e negociações vinculados às oportunidades.',
  'propostas.vazioExemploRegistro':
    'A proposta da Alfa saiu em Word e foi por e-mail. Você registra o arquivo, o valor e a validade; quando o preço muda, anexa a versão 2 — e a versão 1 continua baixável exatamente como o cliente a recebeu.',
  'propostas.colNumero': 'Número',
  'propostas.colTitulo': 'Título',
  'propostas.colEmpresa': 'Empresa',
  'propostas.colOportunidade': 'Oportunidade',
  'propostas.colVersao': 'Versão',
  'propostas.colValor': 'Valor',
  'propostas.colValidade': 'Validade',
  'propostas.colSituacao': 'Situação',
  'propostas.colResponsavel': 'Responsável',
  'propostas.colAcoes': 'Ações',
  'propostas.abrir': 'Abrir',
  'propostas.continuarEdicao': 'Continuar edição',
  'propostas.verComposicao': 'Ver composição',
  'propostas.filtrosRapidos': 'Filtros rápidos',
  'propostas.rapidoElaboracao': 'Em elaboração',
  'propostas.rapidoProntas': 'Prontas para envio',
  'propostas.rapidoEnviadas': 'Enviadas',
  'propostas.rapidoVencendo': 'Vencendo',
  'propostas.rapidoSemAcao': 'Sem próxima ação',
  'propostas.maisFiltros': 'Mais filtros',
  'propostas.limparFiltros': 'Limpar filtros',
  'propostas.metricas': 'Indicadores de propostas',
  'propostas.metricaGeradas': 'Geradas',
  'propostas.metricaEnviadas': 'Enviadas',
  'propostas.metricaAceitas': 'Aceitas',
  'propostas.metricaTaxa': 'Taxa de aceite',
  'propostas.metricaSemAcao': 'Enviadas sem ação',
  'propostas.colAtualizada': 'Atualizada',
  'propostas.filtroSituacao': 'Situação',
  'propostas.filtroResponsavel': 'Responsável',
  'propostas.filtroValidadeAte': 'Vence até',
  'propostas.filtroTodas': 'Todas',
  'propostas.arquivadasExcluir': 'Sem as arquivadas',
  'propostas.arquivadasIncluir': 'Com as arquivadas',
  'propostas.arquivadasSomente': 'Só as arquivadas',
  'propostas.buscar': 'Buscar por número, título, empresa…',
  'propostas.semResultado': 'Nenhuma proposta atende a esses filtros.',
  'propostas.divergente': 'Valor divergente',
  'propostas.semAprovacao': 'Enviada sem aprovação',
  'propostas.vencida': 'Validade vencida',
  'propostas.arquivada': 'Arquivada',
  'propostas.erroLista': 'Não foi possível carregar as propostas.',
  'proposta.situacao.DRAFT': 'Rascunho',
  'proposta.situacao.READY': 'Pronta para envio',
  'proposta.situacao.SENT': 'Enviada',
  'proposta.situacao.ACCEPTED': 'Aceita',
  'proposta.situacao.REJECTED': 'Recusada',
  'proposta.situacao.EXPIRED': 'Expirada',
  'proposta.situacao.CANCELED': 'Cancelada',
  'proposta.situacao.SUPERSEDED': 'Substituída',
  'proposta.aprovacao.NOT_REQUIRED': 'Não é necessária',
  'proposta.aprovacao.DRAFT': 'Cenário em rascunho',
  'proposta.aprovacao.PENDING': 'Aguardando aprovação',
  'proposta.aprovacao.APPROVED': 'Concedida',
  'proposta.aprovacao.CHANGES_REQUESTED': 'Ajustes solicitados',
  'proposta.aprovacao.REJECTED': 'Recusada',
  'proposta.aprovacao.SUPERSEDED': 'Invalidada por alteração',
  'proposta.canal.EMAIL': 'E-mail',
  'proposta.canal.WHATSAPP': 'WhatsApp',
  'proposta.canal.MEETING': 'Reunião',
  'proposta.canal.CUSTOMER_PORTAL': 'Portal do cliente',
  'proposta.canal.OTHER': 'Outro',
  'registro.titulo': 'Registrar proposta',
  'registro.descricao':
    'A proposta é o documento que vai para o cliente. Anexe o arquivo produzido fora do CERES: empresa, valor e condições vêm da oportunidade.',
  'registro.empresa': 'Empresa',
  'registro.buscarEmpresa': 'Buscar empresa…',
  'registro.escolhaEmpresa': 'Escolha a empresa…',
  'registro.oportunidade': 'Oportunidade',
  'registro.buscarOportunidade': 'Buscar oportunidade…',
  'registro.escolhaOportunidade': 'Escolha a oportunidade…',
  'registro.primeiroEmpresa': 'Escolha a empresa primeiro: a oportunidade vem dela.',
  'registro.tituloDaProposta': 'Título',
  'registro.responsavel': 'Responsável',
  'registro.contato': 'Contato principal',
  'registro.semContato': 'Sem contato definido',
  'registro.referencia': 'Referência do cliente',
  'registro.valor': 'Valor da proposta',
  'registro.validade': 'Válida até',
  'registro.emissao': 'Emitida em',
  'registro.arquivo': 'Arquivo da proposta',
  'registro.arquivoDica': 'PDF, Word, Excel ou PowerPoint.',
  'registro.observacoes': 'Observações',
  'registro.situacaoInicial': 'Situação inicial',
  'registro.prontaBloqueada':
    'A oportunidade ainda não tem aprovação comercial válida: a proposta fica registrada como rascunho.',
  'registro.jaEnviada': 'Esta proposta já foi enviada ao cliente',
  'registro.dataDoEnvio': 'Data e hora do envio',
  'registro.canal': 'Canal',
  'registro.destinatario': 'Destinatário',
  'registro.valorSugerido': 'Sugerido pela oportunidade:',
  'registro.divergenciaAviso':
    'O valor informado nesta proposta é diferente do valor atual da oportunidade. A proposta será registrada com essa divergência para preservar o histórico.',
  'registro.divergenciaJustificativa': 'Por que os valores diferem',
  'registro.semAprovacaoAviso':
    'A oportunidade não tem aprovação comercial válida. Registrar o envio não concede aprovação nenhuma: o fato fica marcado como envio sem aprovação.',
  'registro.semAprovacaoConfirmar': 'Confirmo que este envio aconteceu sem aprovação comercial',
  'registro.semAprovacaoJustificativa': 'Justificativa do envio sem aprovação',
  'registro.salvar': 'Registrar proposta',
  'registro.erro': 'Não foi possível registrar a proposta.',
  'registro.ok': 'Proposta registrada.',
  'proposta.abaResumo': 'Resumo',
  'proposta.abaVersoes': 'Versões',
  'proposta.abaHistorico': 'Histórico',
  'proposta.numero': 'Número',
  'proposta.origem': 'Origem',
  'proposta.origemExterna': 'Documento externo',
  'proposta.origemCeres': 'Gerada no CERES',
  'proposta.empresa': 'Empresa',
  'proposta.oportunidade': 'Oportunidade',
  'proposta.responsavel': 'Responsável',
  'proposta.contato': 'Contato',
  'proposta.valor': 'Valor',
  'proposta.validade': 'Validade',
  'proposta.situacaoRotulo': 'Situação',
  'proposta.aprovacaoRotulo': 'Aprovação comercial',
  'proposta.referencia': 'Referência do cliente',
  'proposta.versaoVigente': 'Versão vigente',
  'proposta.versao': 'Versão',
  'proposta.baixar': 'Baixar arquivo',
  'proposta.novaVersao': 'Criar nova versão a partir da oportunidade atual',
  'proposta.novaVersaoCurta': 'Nova versão',
  'proposta.registrarEnvio': 'Registrar envio',
  'proposta.marcarPronta': 'Marcar como pronta para envio',
  'proposta.registrarDesfechoAcao': 'Registrar desfecho',
  'proposta.arquivar': 'Arquivar',
  'proposta.restaurar': 'Restaurar',
  // ---- CERES-039: prévia com oportunidade real e tabela simples ----------
  'modelo.previaFonte': 'Dados da prévia',
  'modelo.previaDemo': 'Dados demonstrativos',
  'modelo.previaReal': 'Usar uma oportunidade real',
  'modelo.previaEscolhaOportunidade': 'Oportunidade',
  'modelo.previaBuscarOportunidade': 'Buscar por empresa ou título…',
  'modelo.previaSelecioneOportunidade': 'Escolha a oportunidade…',
  'modelo.previaGerar': 'Gerar prévia',
  'modelo.previaAvisoReal':
    'Esta é apenas uma pré-visualização. Nenhuma proposta foi criada e a oportunidade não foi alterada.',
  'modelo.tabelaColunas': 'Colunas',
  'modelo.tabelaLinhas': 'Linhas',
  'modelo.tabelaAdicionarColuna': 'Adicionar coluna',
  'modelo.tabelaRemoverColuna': 'Remover coluna',
  'modelo.tabelaAdicionarLinha': 'Adicionar linha',
  'modelo.tabelaRemoverLinha': 'Remover linha',
  'modelo.tabelaSubirLinha': 'Subir',
  'modelo.tabelaDescerLinha': 'Descer',
  'modelo.tabelaMoverColunaEsquerda': 'Mover para a esquerda',
  'modelo.tabelaMoverColunaDireita': 'Mover para a direita',
  'modelo.tabelaRotuloDaColuna': 'Rótulo',
  'modelo.tabelaAlinhamento': 'Alinhamento',
  'modelo.tabelaLargura': 'Largura relativa',
  'modelo.tabelaComCabecalho': 'Mostrar a linha de cabeçalho',
  'modelo.tabelaEnfatizarPrimeira': 'Primeira coluna em negrito',
  'modelo.tabelaAjuda':
    'Conteúdo digitado no modelo. Para a tabela de itens negociados, use o bloco Investimento com repetição.',
  'modelo.tabelaLimite': 'Limite atingido.',
  'modelo.alinhamentoDIREITA': 'À direita',
  // ---- CERES-038: biblioteca de modelos de proposta ----------------------
  'modelos.acao': 'Modelos de proposta',
  'modelos.titulo': 'Modelos de proposta',
  'modelos.subtitulo':
    'A estrutura, o conteúdo e a identidade visual das propostas geradas pelo CERES.',
  'modelos.vazioOQueE':
    'Crie modelos para padronizar a estrutura, o conteúdo e a identidade visual das propostas geradas pelo CERES.',
  'modelos.vazioExemplo':
    'Um modelo de projeto com capa, contexto, escopo e a tabela de investimento. Ao criar a proposta, o CERES monta o documento com os itens já negociados na oportunidade.',
  'modelos.criarPrimeiro': 'Criar primeiro modelo',
  'modelos.criar': 'Criar modelo',
  'modelos.buscar': 'Buscar por nome…',
  'modelos.filtroSituacao': 'Situação',
  'modelos.filtroFinalidade': 'Finalidade',
  'modelos.filtroEstiloVisual': 'Estilo visual',
  'modelos.filtroCaracteristica': 'Característica',
  'modelos.galeria': 'Galeria de modelos visuais',
  'modelos.miniatura': 'Miniatura do modelo',
  'modelos.daOrganizacao': 'Da organização',
  'modelos.visualizar': 'Visualizar',
  'modelos.usar': 'Usar modelo',
  'modelos.editarCopia': 'Criar e editar cópia',
  'modelos.editar': 'Editar',
  'modelos.definirPadrao': 'Definir como padrão',
  'modelos.arquivar': 'Arquivar',
  'modelos.restaurar': 'Restaurar',
  'modelo.estiloEDITORIAL': 'Editorial',
  'modelo.estiloIMPACT': 'Impacto',
  'modelo.estiloEXECUTIVE': 'Executivo',
  'modelo.estiloESSENTIAL': 'Essencial',
  'modelo.estiloCUSTOM': 'Personalizado',
  'modelo.estiloDescricaoEDITORIAL': 'Narrativo, elegante e com bastante respiro.',
  'modelo.estiloDescricaoIMPACT': 'Alto contraste e mensagens visuais fortes.',
  'modelo.estiloDescricaoEXECUTIVE': 'Sóbrio, preciso e orientado à decisão.',
  'modelo.estiloDescricaoESSENTIAL': 'Minimalista, leve e direto.',
  'modelo.estiloDescricaoCUSTOM': 'Sistema visual criado pela sua organização.',
  'modelo.caracteristica.executivo': 'Executivo',
  'modelo.caracteristica.editorial': 'Editorial',
  'modelo.caracteristica.criativo': 'Criativo',
  'modelo.caracteristica.minimalista': 'Minimalista',
  'modelo.caracteristica.alto-contraste': 'Alto contraste',
  'modelo.caracteristica.institucional': 'Institucional',
  'modelos.filtroAplicabilidade': 'Aplicabilidade',
  'modelos.filtroTodas': 'Todas',
  'modelos.arquivadosExcluir': 'Sem os arquivados',
  'modelos.arquivadosIncluir': 'Com os arquivados',
  'modelos.arquivadosSomente': 'Só os arquivados',
  'modelos.colNome': 'Modelo',
  'modelos.colFinalidade': 'Finalidade',
  'modelos.colAplicabilidade': 'Aplicabilidade',
  'modelos.colVersao': 'Versão publicada',
  'modelos.colSituacao': 'Situação',
  'modelos.colAtualizado': 'Última alteração',
  'modelos.padrao': 'Padrão',
  'modelos.daPlataforma': 'Da plataforma',
  'modelos.rascunhoDaVersao': 'rascunho da versão',
  'modelos.semPublicada': 'Nunca publicado',
  'modelos.semResultado': 'Nenhum modelo com esses filtros.',
  'modelos.duplicar': 'Duplicar',
  'modelos.duplicarTitulo': 'Duplicar modelo',
  'modelos.duplicarDescricao':
    'A cópia nasce como rascunho, sem a marca de padrão e sem as imagens do original.',
  'modelos.nomeDaCopia': 'Nome da cópia',
  'modelos.erroCarregar': 'Não foi possível carregar os modelos.',
  'modelo.novoTitulo': 'Novo modelo de proposta',
  'modelo.novoDescricao': 'Nome e finalidade agora; a estrutura e a identidade visual no editor.',
  'modelo.nome': 'Nome do modelo',
  'modelo.descricao': 'Descrição interna',
  'modelo.descricaoAjuda': 'Para quem administra os modelos. Nunca sai no documento.',
  'modelo.finalidade': 'Finalidade',
  'modelo.aplicabilidade': 'Aplicabilidade',
  'modelo.aplicabilidadeAjuda':
    'Define quais modelos o CERES recomenda ao criar uma proposta. O Catálogo continua sendo a fonte das ofertas.',
  'modelo.escopoGENERIC': 'Serve para qualquer negócio',
  'modelo.escopoKIND': 'Por tipo de oferta',
  'modelo.escopoCATEGORY': 'Por categoria do Catálogo',
  'modelo.escopoITEM': 'Por ofertas específicas',
  'modelo.tiposDeOferta': 'Tipos de oferta',
  'modelo.tipoSERVICE': 'Serviço',
  'modelo.tipoPRODUCT': 'Produto',
  'modelo.categorias': 'Categorias',
  'modelo.ofertas': 'Ofertas',
  'modelo.abaEstrutura': 'Estrutura',
  'modelo.abaIdentidade': 'Identidade visual',
  'modelo.abaCapa': 'Capa',
  'modelo.abaDados': 'Dados do modelo',
  'modelo.abaHistorico': 'Histórico',
  'modelo.blocosDisponiveis': 'Blocos disponíveis',
  'modelo.estruturaDoDocumento': 'Estrutura do documento',
  'modelo.configuracaoDoBloco': 'Configuração do bloco',
  'modelo.semBlocoSelecionado': 'Escolha um bloco da estrutura para configurá-lo.',
  'modelo.estruturaVazia': 'Nenhum bloco ainda. Escolha um da lista ao lado para começar.',
  'modelo.adicionar': 'Adicionar',
  'modelo.subir': 'Subir',
  'modelo.descer': 'Descer',
  'modelo.remover': 'Remover',
  'modelo.tituloDoBloco': 'Título do bloco',
  'modelo.subtituloDoBloco': 'Subtítulo',
  'modelo.mostrarTitulo': 'Mostrar o título no documento',
  'modelo.origem': 'Origem do conteúdo',
  'modelo.origemFIXED': 'Texto fixo do modelo',
  'modelo.origemDYNAMIC': 'Dados do CRM',
  'modelo.origemEDITABLE': 'Escrito ao criar a proposta',
  'modelo.origemAI_ASSISTED': 'Assistido por IA (na fase seguinte)',
  'modelo.origemIaAviso':
    'A redação por IA chega numa fase posterior. Até lá, este bloco se comporta como um bloco escrito ao criar a proposta — o que é a verdade, e não uma promessa.',
  'modelo.conteudo': 'Conteúdo',
  'modelo.conteudoAjuda':
    'Formatação simples: ## título, - lista, 1. numerada, **negrito**, *itálico*, --- separador.',
  'modelo.inserirCampo': 'Inserir campo',
  'modelo.camposDisponiveis': 'Campos disponíveis',
  'modelo.obrigatorio': 'Bloco obrigatório',
  'modelo.editavelNaProposta': 'Pode ser ajustado na proposta',
  'modelo.visivelPorPadrao': 'Aparece por padrão',
  'modelo.quebraAntes': 'Quebrar página antes',
  'modelo.quebraDepois': 'Quebrar página depois',
  'modelo.estilo': 'Ênfase',
  'modelo.estiloNORMAL': 'Normal',
  'modelo.estiloHIGHLIGHT': 'Destaque',
  'modelo.estiloSUBTLE': 'Discreto',
  'modelo.observacaoInterna': 'Observação interna',
  'modelo.condicao': 'Condição para aparecer',
  'modelo.condicaoAjuda':
    'Deixe vazio para sempre aparecer. Exemplo: descontoPercentual mostra o bloco só quando há desconto.',
  'modelo.repeticao': 'Repetir pelos itens da proposta',
  'modelo.colunas': 'Colunas da tabela',
  'modelo.mostrarTotal': 'Mostrar a linha de total',
  'modelo.logotipo': 'Logotipo',
  'modelo.imagemDeCapa': 'Imagem de capa',
  'modelo.enviarImagem': 'Enviar imagem',
  'modelo.removerImagem': 'Remover',
  'modelo.corPrimaria': 'Cor primária',
  'modelo.corSecundaria': 'Cor secundária',
  'modelo.corDeDestaque': 'Cor de destaque',
  'modelo.fonteDeTitulos': 'Fonte dos títulos',
  'modelo.fonteDeCorpo': 'Fonte do corpo',
  'modelo.tamanhoBase': 'Tamanho do texto (pt)',
  'modelo.margem': 'Margem (mm)',
  'modelo.cabecalhoAtivo': 'Cabeçalho nas páginas',
  'modelo.rodapeAtivo': 'Rodapé nas páginas',
  'modelo.ocultarNaCapa': 'Ocultar cabeçalho e rodapé na capa',
  'modelo.numerarPaginas': 'Numerar as páginas',
  'modelo.textoDoCabecalho': 'Texto do cabeçalho',
  'modelo.textoDoRodape': 'Texto do rodapé',
  'modelo.confidencialidade': 'Aviso de confidencialidade',
  'modelo.capaAtiva': 'Usar capa',
  'modelo.layoutDaCapa': 'Layout da capa',
  'modelo.layoutINSTITUCIONAL': 'Institucional',
  'modelo.layoutMINIMALISTA': 'Minimalista',
  'modelo.layoutIMAGEM_DESTAQUE': 'Imagem em destaque',
  'modelo.layoutFAIXA_LATERAL': 'Faixa lateral',
  'modelo.tituloDaCapa': 'Título da capa',
  'modelo.subtituloDaCapa': 'Subtítulo da capa',
  'modelo.corDeFundo': 'Cor de fundo',
  'modelo.alinhamento': 'Alinhamento',
  'modelo.alinhamentoESQUERDA': 'À esquerda',
  'modelo.alinhamentoCENTRO': 'Centralizado',
  'modelo.mostrarNaCapa': 'O que aparece na capa',
  'modelo.mostrarCliente': 'Cliente',
  'modelo.mostrarOportunidade': 'Negócio',
  'modelo.mostrarNumero': 'Número da proposta',
  'modelo.mostrarData': 'Data',
  'modelo.mostrarValidade': 'Validade',
  'modelo.mostrarResponsavel': 'Responsável',
  'modelo.salvar': 'Salvar rascunho',
  'modelo.salvo': 'Rascunho salvo.',
  'modelo.erroSalvar': 'Não foi possível salvar o rascunho.',
  'modelo.previsualizar': 'Pré-visualizar',
  'modelo.previaTitulo': 'Prévia do modelo',
  'modelo.previaDemonstrativa': 'Os dados são de demonstração. Nenhuma proposta foi criada.',
  'modelo.previaErro': 'Não foi possível gerar a prévia.',
  'modelo.previaAbrir': 'Abrir a prévia em outra aba',
  'modelo.publicar': 'Publicar versão',
  'modelo.publicado': 'Versão publicada.',
  'modelo.erroPublicar': 'Não foi possível publicar.',
  'modelo.pendencias': 'Antes de publicar, resolva:',
  'modelo.novaVersao': 'Criar nova versão',
  'modelo.novaVersaoOk': 'Nova versão criada em rascunho.',
  'modelo.definirPadrao': 'Definir como padrão',
  'modelo.padraoOk': 'Este passou a ser o modelo sugerido.',
  'modelo.arquivar': 'Arquivar',
  'modelo.arquivarTitulo': 'Arquivar este modelo?',
  'modelo.arquivarMensagem':
    'O modelo deixará de estar disponível para novas propostas. Propostas já criadas com versões deste modelo não serão alteradas.',
  'modelo.restaurar': 'Restaurar',
  'modelo.somenteLeitura':
    'Este modelo é da plataforma e é comum a todas as organizações. Duplique-o para ter uma versão sua.',
  'modelo.publicadoLeitura':
    'A versão publicada é imutável. Para mudar a estrutura, crie uma versão nova — a publicada continua valendo para as propostas que já a usaram.',
  'modelo.versoes': 'Versões',
  'modelo.versao': 'Versão',
  'modelo.situacaoDRAFT': 'Rascunho',
  'modelo.situacaoPUBLISHED': 'Publicado',
  'modelo.situacaoARCHIVED': 'Arquivado',
  'modelo.finalidadeSERVICES': 'Proposta de serviços',
  'modelo.finalidadePRODUCT': 'Proposta de produto',
  'modelo.finalidadeSUBSCRIPTION': 'Assinatura',
  'modelo.finalidadeLICENSE': 'Licença',
  'modelo.finalidadeOUTSOURCING': 'Outsourcing',
  'modelo.finalidadePROJECT': 'Projeto',
  'modelo.finalidadeRENEWAL': 'Renovação',
  'modelo.finalidadeCOMBINED': 'Proposta combinada',
  'modelo.finalidadeGENERIC': 'Genérica',
  'modelo.erroCarregar': 'Não foi possível carregar o modelo.',
  'modelo.voltar': 'Voltar aos modelos',

  // ---- CERES-040: apresentação do documento ----
  'modelo.abaCabecalho': 'Cabeçalho',
  'modelo.abaRodape': 'Rodapé',
  'modelo.abaTabelas': 'Tabelas',
  'modelo.abaImagens': 'Imagens',
  'modelo.secaoLogotipos': 'Logotipos',
  'modelo.secaoCores': 'Cores',
  'modelo.secaoTipografia': 'Tipografia',
  'modelo.secaoPagina': 'Página',
  'modelo.logoAlternativo': 'Logotipo para fundo escuro',
  'modelo.logoAlternativoAjuda':
    'Usado na capa quando o fundo é escuro. Sem ele, uma marca escura some no fundo escuro.',
  'modelo.corDoTexto': 'Cor do texto',
  'modelo.corDeFundoDasPaginas': 'Cor de fundo das páginas',
  'modelo.semCor': 'Sem cor',
  'modelo.semCorAtual': 'Nenhuma cor definida',
  'modelo.fonteHELVETICA': 'Helvetica',
  'modelo.fonteTIMES': 'Times',
  'modelo.fonteCOURIER': 'Courier',
  'modelo.entrelinha': 'Entrelinha',
  'modelo.escalaDeTitulos': 'Escala dos títulos',
  'modelo.escalaCOMPACTA': 'Compacta',
  'modelo.escalaNORMAL': 'Normal',
  'modelo.escalaAMPLA': 'Ampla',
  'modelo.estiloDosTitulos': 'Estilo dos títulos',
  'modelo.tituloEstiloSOBRIO': 'Sóbrio',
  'modelo.tituloEstiloFIO': 'Com fio embaixo',
  'modelo.tituloEstiloFAIXA': 'Em faixa colorida',
  'modelo.tituloEstiloCAIXA_ALTA': 'Em caixa alta',
  'modelo.espacoAntesDoTitulo': 'Espaço antes do título',
  'modelo.espacoDepoisDoTitulo': 'Espaço depois do título',
  'modelo.tipografiaAjuda':
    'Os tamanhos de título saem do tamanho do texto e da escala escolhida. Cada bloco usa o estilo do modelo, sem formatação manual.',
  'modelo.margemAjuda': 'A área útil da página já desconta a altura do cabeçalho e a do rodapé.',
  'modelo.estiloOBSERVACAO': 'Observação',
  'modelo.estiloAVISO': 'Aviso',

  'modelo.secaoCapaLayout': 'Layout',
  'modelo.secaoCapaTexto': 'Textos da capa',
  'modelo.secaoCapaImagens': 'Imagens da capa',
  'modelo.secaoCapaCores': 'Cores da capa',
  'modelo.secaoCapaFaixas': 'Cabeçalho e rodapé na capa',
  'modelo.layoutAjuda': 'Os layouts são controlados. Não há posicionamento livre por coordenada.',
  'modelo.capaTextoComplementar': 'Texto complementar',
  'modelo.capaTextoComplementarAjuda':
    'Uma linha abaixo dos dados. Aceita campos entre chaves, como no corpo dos blocos.',
  'modelo.capaMostrarLogo': 'Mostrar o logotipo na capa',
  'modelo.imagemDeCapaAjuda': 'A imagem em destaque, usada pelo layout de imagem em destaque.',
  'modelo.capaImagemDeFundo': 'Imagem de fundo',
  'modelo.capaImagemDeFundoAjuda':
    'Cobre a página inteira, sob o texto. Escolha uma cor de texto que contraste.',
  'modelo.capaCorDoTexto': 'Cor do texto da capa',
  'modelo.capaCorDaFaixa': 'Cor da faixa',
  'modelo.capaMostrarFaixa': 'Mostrar a faixa gráfica',
  'modelo.capaContrasteAjuda':
    'Texto e fundo precisam contrastar. A publicação avisa quando os dois ficam claros ou os dois escuros.',
  'modelo.capaMostrarCabecalho': 'Mostrar o cabeçalho na capa',
  'modelo.capaMostrarRodape': 'Mostrar o rodapé na capa',
  'modelo.capaMostrarNumeracao': 'Numerar a capa',
  'modelo.capaFaixasAjuda':
    'Por padrão a capa não tem cabeçalho, rodapé nem número de página. Ligue cada um se a sua organização quiser.',

  'modelo.faixaAjuda': 'A faixa se repete em todas as páginas internas e nunca invade o conteúdo.',
  'modelo.secaoFaixaTextos': 'Textos',
  'modelo.secaoFaixaLogo': 'Logotipo',
  'modelo.secaoFaixaAparencia': 'Aparência',
  'modelo.secaoPaginacao': 'Paginação',
  'modelo.textoEsquerda': 'Texto à esquerda',
  'modelo.textoCentro': 'Texto ao centro',
  'modelo.textoDireita': 'Texto à direita',
  'modelo.faixaTextosAjuda':
    'Aceitam campos entre chaves. Deixe os três vazios para manter a linha de identificação do documento.',
  'modelo.faixaMostrarCliente': 'Incluir o nome do cliente',
  'modelo.faixaMostrarNumero': 'Incluir o número da proposta',
  'modelo.faixaMostrarData': 'Incluir a data',
  'modelo.faixaMostrarLogo': 'Mostrar o logotipo na faixa',
  'modelo.alinhamentoDoLogo': 'Alinhamento do logotipo',
  'modelo.tamanhoDoLogo': 'Tamanho do logotipo',
  'modelo.tamanhoPEQUENO': 'Pequeno',
  'modelo.tamanhoMEDIO': 'Médio',
  'modelo.tamanhoGRANDE': 'Grande',
  'modelo.linhaDivisoria': 'Linha divisória',
  'modelo.faixaCorDoTexto': 'Cor do texto',
  'modelo.faixaCorDaLinha': 'Cor da linha',
  'modelo.espacoSuperior': 'Espaço acima (mm)',
  'modelo.espacoInferior': 'Espaço abaixo (mm)',
  'modelo.rodapeConfidencialidade': 'Incluir o aviso de confidencialidade',
  'modelo.formatoDaPaginacao': 'Formato do número da página',
  'modelo.paginacaoNENHUM': 'Não numerar',
  'modelo.paginacaoNUMERO': '1/10',
  'modelo.paginacaoPAGINA_X': 'Página 1',
  'modelo.paginacaoPAGINA_X_DE_Y': 'Página 1 de 10',

  'modelo.tabelasAjuda':
    'Este estilo vale para as tabelas do documento: produtos e serviços, investimento, cronograma e tabela simples. Os cálculos comerciais não mudam.',
  'modelo.secaoTabelaCabecalho': 'Cabeçalho da tabela',
  'modelo.secaoTabelaLinhas': 'Linhas',
  'modelo.secaoTabelaBordas': 'Bordas',
  'modelo.tabelaCorDoCabecalho': 'Cor de fundo do cabeçalho',
  'modelo.tabelaCorDoTextoDoCabecalho': 'Cor do texto do cabeçalho',
  'modelo.tabelaRepetirCabecalho': 'Repetir o cabeçalho quando a tabela passa de página',
  'modelo.tabelaLinhasAlternadas': 'Linhas alternadas',
  'modelo.tabelaCorAlternada': 'Cor da linha alternada',
  'modelo.tabelaEspacamento': 'Espaçamento interno',
  'modelo.espacamentoCOMPACTO': 'Compacto',
  'modelo.espacamentoNORMAL': 'Normal',
  'modelo.espacamentoCONFORTAVEL': 'Confortável',
  'modelo.tabelaDestacarTotal': 'Destacar a linha de total',
  'modelo.tabelaBordas': 'Bordas',
  'modelo.bordaNENHUMA': 'Sem bordas',
  'modelo.bordaHORIZONTAIS': 'Só linhas horizontais',
  'modelo.bordaCOMPLETAS': 'Grade completa',
  'modelo.tabelaCorDasBordas': 'Cor das bordas',

  'modelo.imagensTitulo': 'Imagens deste modelo',
  'modelo.imagensAjuda':
    'Enviadas uma vez e usadas em qualquer lugar do modelo: capa, cabeçalho, rodapé e blocos de imagem.',
  'modelo.imagensEnviar': 'Enviar imagem',
  'modelo.imagensVazio': 'Nenhuma imagem ainda. Envie a primeira para usá-la na capa ou num bloco.',
  'modelo.imagensNaoUsada': 'Ainda não usada',
  'modelo.imagensRemover': 'Remover',
  'modelo.imagensRemoverTitulo': 'Remover esta imagem?',
  'modelo.imagensRemoverMensagem':
    'A imagem sai da biblioteca e dos lugares do rascunho em que estava. As versões já publicadas não mudam.',
  'modelo.imagensPresa': 'Em uso por uma versão publicada. Crie uma versão nova para trocá-la.',
  'modelo.imagensEscolher': 'Escolher imagem',
  'modelo.imagensTrocar': 'Trocar',
  'modelo.imagensBiblioteca': 'Imagens deste modelo',
  'modelo.imagensNenhumaEscolhida': 'Nenhuma imagem escolhida',
  'modelo.imagemDoBloco': 'Imagem do bloco',
  'modelo.imagemLargura': 'Largura',
  'modelo.larguraPEQUENA': 'Pequena',
  'modelo.larguraMEDIA': 'Média',
  'modelo.larguraGRANDE': 'Grande',
  'modelo.larguraTOTAL': 'Largura total',
  'modelo.imagemAjuste': 'Ajuste',
  'modelo.ajusteCONTER': 'Conter: a imagem inteira aparece',
  'modelo.ajustePREENCHER': 'Preencher: ocupa a área, cortando a sobra',
  'modelo.ajusteLARGURA_DA_PAGINA': 'Largura da página',
  'modelo.ajusteNATURAL': 'Tamanho natural, limitado à página',
  'modelo.imagemAjusteAjuda':
    'A proporção nunca é distorcida. O que muda é o que acontece com a sobra.',
  'modelo.imagemLegenda': 'Legenda',
  'modelo.imagemTextoAlternativo': 'Descrição interna',
  'modelo.imagemTextoAlternativoAjuda': 'Para quem administra o modelo. Não sai no documento.',
  'modelo.imagemManterLegendaJunto': 'Manter a legenda na mesma página da imagem',
  'modelo.blocoOculto': 'Ocultar este bloco no documento',
  'modelo.secaoPaginacaoDoBloco': 'Paginação deste bloco',
  'modelo.paginacaoDoBlocoAjuda':
    'Evita título sozinho no pé da página, bloco partido ao meio e seção que deveria começar em folha nova.',
  'modelo.evitarQuebraInterna': 'Não partir este bloco entre páginas',
  'modelo.manterComProximo': 'Manter junto com o bloco seguinte',
  'modelo.previaBaixar': 'Baixar a prévia',
  'histModelo.MODELO_CRIADO': 'Modelo criado',
  'histModelo.MODELO_ALTERADO': 'Modelo alterado',
  'histModelo.BLOCOS_ALTERADOS': 'Blocos alterados',
  'histModelo.IDENTIDADE_ALTERADA': 'Identidade visual alterada',
  'histModelo.MODELO_DUPLICADO': 'Modelo duplicado',
  'histModelo.VERSAO_PUBLICADA': 'Versão publicada',
  'histModelo.VERSAO_CRIADA': 'Nova versão em rascunho',
  'histModelo.DEFINIDO_COMO_PADRAO': 'Definido como padrão',
  'histModelo.PADRAO_REMOVIDO': 'Deixou de ser o padrão',
  'histModelo.MODELO_ARQUIVADO': 'Modelo arquivado',
  'histModelo.MODELO_RESTAURADO': 'Modelo restaurado',
  'propostas.escolherModelo': 'Modelo da proposta',
  'propostas.modeloRecomendado': 'Recomendado',
  'propostas.modeloPadrao': 'Padrão',
  'propostas.semModelo':
    'Nenhum modelo de proposta está disponível. Crie e publique um modelo antes de gerar propostas no CERES.',
  'propostas.irParaModelos': 'Ir para Modelos de proposta',
  // ---- CERES-037: as duas capacidades, a edição e a comparação -----------
  'propostas.criarNoCeres': 'Gerar proposta',
  'propostas.maisAcoesCriacao': 'Mais opções',
  'propostas.centralDaMarca': 'Central da Marca',
  'propostas.visualizar': 'Visualizar',
  'propostas.colAtualizacao': 'Atualização',
  'propostas.ordenarPor': 'Ordenar por',
  'propostas.ordem.RECENTES': 'Última atualização',
  'propostas.ordem.VALOR': 'Valor da versão vigente',
  'propostas.ordem.VALIDADE': 'Validade da versão vigente',
  'propostas.ordem.NUMERO': 'Número',
  'proposta.abrirComposicao': 'Abrir composição no CERES',
  'proposta.verFichaDoRegistro': 'Ver a ficha do registro desta proposta',
  'metadados.acao': 'Editar dados da proposta',
  'metadados.titulo': 'Dados da proposta',
  'metadados.descricao':
    'Estes dados descrevem o registro, e não o documento. Corrigir um título ou trocar o responsável não muda nada do que já foi para o cliente.',
  'metadados.semResponsavel': 'Sem responsável definido',
  'metadados.oQueNaoMuda':
    'Valor, validade, arquivo, itens e condições pertencem à versão e não são editados aqui: o documento que o cliente recebeu não muda por baixo. Para mudá-los, crie uma nova versão.',
  'metadados.ok': 'Dados da proposta atualizados.',
  'metadados.erro': 'Não foi possível salvar os dados da proposta.',
  'comparacao.titulo': 'O que mudou na oportunidade desde esta versão',
  'comparacao.aproximada':
    'Um ou mais itens congelados não puderam ser identificados na oportunidade atual. A diferença abaixo é parcial, e nenhum detalhe foi inventado.',
  'comparacao.naoIdentificado':
    'item congelado sem origem rastreável — não foi possível dizer o que mudou.',
  'comparacao.foiAdicionado': 'foi adicionada à oportunidade.',
  'comparacao.foiRemovido': 'não está mais na oportunidade.',
  'comparacao.mudouDe': 'mudou de',
  'comparacao.para': 'para',
  'comparacao.aQuantidadeDe': 'A quantidade de',
  'comparacao.oValorNegociadoDe': 'O valor negociado de',
  'comparacao.oPrecoDeReferenciaDe': 'O preço de referência de',
  'comparacao.oSubtotalDe': 'O subtotal de',
  'comparacao.oDescontoDe': 'O desconto de',
  'comparacao.osPeriodosDe': 'A quantidade de períodos de',
  'comparacao.aPeriodicidadeDe': 'A periodicidade de',
  'comparacao.semValor': 'sem valor',
  'comparacao.CONDICAO_DE_PAGAMENTO': 'A condição de pagamento foi alterada.',
  'comparacao.PRAZO_DE_EXECUCAO': 'O prazo de execução foi alterado.',
  'comparacao.MODELO_DE_COBRANCA': 'O modelo de cobrança foi alterado.',
  'comparacao.PERIODICIDADE': 'A periodicidade de cobrança foi alterada.',
  'comparacao.VALIDADE_COMERCIAL': 'A validade comercial da oportunidade foi alterada.',
  'comparacao.APROVACAO': 'A aprovação comercial foi substituída ou deixou de valer.',
  'comparacao.versaoIntacta':
    'Nada disto altera esta versão: os valores, o arquivo e os itens congelados permanecem como estavam.',
  'proposta.driftAviso':
    'A oportunidade foi alterada depois da criação desta versão. Os valores e as condições desta proposta permanecem preservados.',
  'proposta.driftVALOR': 'valor alterado',
  'proposta.driftITENS': 'item incluído ou removido',
  'proposta.driftQUANTIDADE': 'quantidade alterada',
  'proposta.driftCONDICAO_COMERCIAL': 'condição comercial alterada',
  'proposta.driftVALIDADE': 'validade alterada',
  'proposta.driftAPROVACAO': 'aprovação invalidada ou substituída',
  'proposta.divergenciaAviso': 'O valor informado difere do total negociado da oportunidade.',
  'proposta.valorNaOportunidade': 'Na oportunidade agora',
  'proposta.semAprovacaoAlerta': 'Esta versão foi enviada sem aprovação comercial válida.',
  'proposta.itensCongelados': 'Itens congelados nesta versão',
  'proposta.totalReferencia': 'Total de referência',
  'proposta.totalNegociado': 'Total negociado',
  'proposta.condicaoDePagamento': 'Condição de pagamento',
  'proposta.prazoDeExecucao': 'Prazo de execução',
  'proposta.modeloDeCobranca': 'Modelo de cobrança',
  'proposta.periodicidade': 'Periodicidade',
  'proposta.motivoDaVersao': 'Motivo da versão',
  'proposta.criadaPor': 'Registrada por',
  'proposta.criadaEm': 'Registrada em',
  'proposta.enviadaEm': 'Enviada em',
  'proposta.semVersao': 'Esta proposta ainda não tem versão.',
  'proposta.semArquivo': 'Sem arquivo',
  'proposta.semEnvios': 'Nenhum envio registrado.',
  'proposta.semHistorico': 'Nada registrado ainda.',
  'proposta.envios': 'Envios registrados',
  'proposta.arquivarTitulo': 'Arquivar proposta',
  'proposta.arquivarMensagem':
    'A proposta será removida da lista principal, mas suas versões, arquivos e histórico serão preservados.',
  'proposta.arquivarMotivo': 'Motivo do arquivamento',
  'proposta.arquivadaEm': 'Arquivada em',
  'proposta.erroAcao': 'Não foi possível concluir a ação.',
  'proposta.erroBaixar': 'Não foi possível baixar o arquivo.',
  'proposta.itens': 'Itens da proposta',
  'proposta.itemPrecoUnitario': 'Preço unitário',
  'proposta.itemTotal': 'Total da linha',
  'proposta.itemAjustado': 'Item ajustado. O total da proposta foi recalculado.',
  'proposta.itemErro': 'Não foi possível ajustar o item.',
  'proposta.itemQuantidade': 'Qtd.',
  'proposta.itemReferencia': 'Referência',
  'proposta.itemNegociado': 'Negociado',
  'proposta.itemDesconto': 'Desc.',
  'proposta.acoesPendentes':
    'Há uma ação comercial pendente: mover a oportunidade no funil continua sendo decisão sua.',
  'versao.titulo': 'Nova versão da proposta',
  'versao.descricao':
    'A versão atual é preservada e passa a constar como substituída. Anexe o arquivo novo e diga o que mudou.',
  'versao.motivo': 'Motivo da nova versão',
  'versao.salvar': 'Criar versão',
  'versao.erro': 'Não foi possível criar a versão.',
  'versao.ok': 'Versão criada.',
  'envio.titulo': 'Registrar envio',
  'envio.descricao':
    'Aqui se registra um envio que aconteceu FORA do CERES. Para enviar a proposta por e-mail daqui, use "Enviar por e-mail".',
  'envio.versao': 'Versão enviada',
  'envio.quando': 'Data e hora',
  'envio.contato': 'Contato',
  'envio.destinatario': 'E-mail ou identificação do destinatário',
  'envio.observacao': 'Observação',
  'envio.salvar': 'Registrar envio',
  'envio.erro': 'Não foi possível registrar o envio.',
  'envio.ok': 'Envio registrado.',
  'desfecho.titulo': 'Registrar desfecho',
  'desfecho.descricao':
    'O que o cliente respondeu. Isto não move a oportunidade no funil: quem move é você.',
  'desfecho.ACCEPTED': 'Aceita pelo cliente',
  'desfecho.REJECTED': 'Recusada pelo cliente',
  'desfecho.CANCELED': 'Cancelada por nós',
  'desfecho.EXPIRED': 'Validade encerrada',
  'desfecho.motivo': 'Motivo',
  'desfecho.motivoDica': 'Quem for propor para este cliente daqui a um mês é quem vai ler isto.',
  'desfecho.observacao': 'Observação',
  'desfecho.quemAceitou': 'Quem aceitou',
  'desfecho.quando': 'Data',
  'desfecho.salvar': 'Registrar',
  'desfecho.erro': 'Não foi possível registrar o desfecho.',
  'desfecho.ok': 'Desfecho registrado.',
  'hist.PROPOSTA_REGISTRADA': 'Proposta registrada',
  'hist.VERSAO_CRIADA': 'Nova versão criada',
  'hist.ARQUIVO_ANEXADO': 'Arquivo anexado',
  'hist.METADADOS_ALTERADOS': 'Dados da proposta alterados',
  'hist.DIVERGENCIA_DECLARADA': 'Divergência de valor registrada',
  'hist.ENVIO_REGISTRADO': 'Envio registrado',
  'hist.ENVIO_SEM_APROVACAO': 'Envio registrado sem aprovação comercial',
  'hist.SITUACAO_ALTERADA': 'Situação alterada',
  'hist.ACEITE_REGISTRADO': 'Aceite registrado',
  'hist.RECUSA_REGISTRADA': 'Recusa registrada',
  'hist.CANCELAMENTO_REGISTRADO': 'Cancelamento registrado',
  'hist.EXPIRACAO_REGISTRADA': 'Expiração registrada',
  'hist.VERSAO_SUBSTITUIDA': 'Versão substituída',
  'hist.PROPOSTA_ARQUIVADA': 'Proposta arquivada',
  'hist.PROPOSTA_RESTAURADA': 'Proposta restaurada',
  'hist.ARQUIVO_BAIXADO': 'Arquivo baixado',
  'oportunidade.registrarProposta': 'Registrar proposta',
  'oportunidade.semPropostas': 'Nenhuma proposta registrada nesta oportunidade.',
  // ---- CERES-039: geração assistida de proposta ---------------------------
  'hist.GERACAO_INICIADA': 'Rascunho de proposta criado no CERES',
  'hist.BLOCO_GERADO_POR_IA': 'Bloco redigido com assistência de IA',
  'hist.BLOCO_EDITADO': 'Bloco editado',
  'hist.ESTRUTURA_EDITADA': 'Estrutura visual editada',
  'hist.BLOCO_RESTAURADO': 'Bloco restaurado ao conteúdo do modelo',
  'hist.PREVIA_GERADA': 'Prévia gerada',
  'hist.CONTEXTO_ATUALIZADO': 'Dados atualizados a partir da oportunidade',
  'hist.VERSAO_FINALIZADA': 'Versão finalizada',
  'oportunidade.gerarProposta': 'Gerar proposta',
  'proposta.continuarNoEditor': 'Continuar a proposta',
  'proposta.novaVersaoNoCeres': 'Nova versão no CERES',
  'gerar.tituloPadrao': 'Proposta —',
  'word.nomeDoArquivo': 'RASCUNHO-proposta',
  'gerar.titulo': 'Gerar proposta no CERES',
  'gerar.subtitulo':
    'A proposta nasce da oportunidade: empresa, contato, itens e valores vêm do que já está negociado.',
  'gerar.semAcesso': 'A geração de propostas não está disponível para esta organização.',
  'gerar.passoOportunidade': 'Oportunidade',
  'gerar.passoContato': 'Contato principal',
  'gerar.passoComercial': 'Dados comerciais',
  'gerar.passoModelo': 'Modelo de proposta',
  'gerar.comercialSoLeitura':
    'Estes valores vêm da composição comercial da oportunidade e não são editados aqui. Para mudá-los, ajuste a negociação.',
  'gerar.colItem': 'Item',
  'gerar.colQuantidade': 'Quantidade',
  'gerar.colPreco': 'Preço negociado',
  'gerar.colSubtotal': 'Subtotal',
  'gerar.total': 'Total negociado',
  'gerar.condicao': 'Condição de pagamento',
  'gerar.prazo': 'Prazo de execução',
  'gerar.naoInformado': 'Não informado',
  'gerar.semContatos': 'Esta empresa ainda não tem contatos cadastrados.',
  'gerar.semContatoEscolhido': 'Sem contato principal',
  'gerar.contatoPrincipal': 'principal',
  'gerar.pendencias': 'O que falta nas fontes',
  'gerar.pendenciasAjuda':
    'A proposta não inventa o que não está cadastrado. Estes pontos você escreve à mão ou cadastra antes.',
  'gerar.tituloDaProposta': 'Título da proposta',
  'gerar.criar': 'Gerar proposta completa',
  'gerar.processandoTitulo': 'O agente está preparando a proposta completa',
  'gerar.processandoTexto':
    'Reunindo contexto, composição, playbook e estrutura visual. Cliques repetidos não criam outra proposta.',
  'gerar.finalidade': 'Finalidade recomendada',
  'gerar.finalidadeAjuda':
    'Define a estratégia do conteúdo; o modelo escolhido define apenas a aparência.',
  'proposta.finalidade.NEW_SALE': 'Nova venda',
  'proposta.finalidade.UPSELL': 'Upsell',
  'proposta.finalidade.CROSS_SELL': 'Cross-sell',
  'proposta.finalidade.RENEWAL': 'Renovação',
  'proposta.finalidade.PROJECT': 'Projeto',
  'proposta.finalidade.RECURRING_SERVICE': 'Serviço recorrente',
  'proposta.finalidade.TECHNICAL': 'Proposta técnica',
  'proposta.finalidade.EXECUTIVE': 'Proposta executiva',
  'proposta.finalidade.INSTITUTIONAL': 'Proposta institucional',
  'proposta.finalidade.COMBINED': 'Proposta combinada',
  'gerar.erroCriar': 'Não foi possível criar a proposta.',
  'gerar.naoDaParaGerar': 'Ainda não dá para gerar esta proposta:',
  'editor.titulo': 'Proposta',
  'editor.versao': 'versão',
  'editor.voltarParaProposta': 'Ver a ficha da proposta',
  'editor.semRascunho':
    'Esta proposta não tem versão em construção. Abra uma nova versão pela ficha.',
  'editor.blocos': 'Blocos do documento',
  'editor.semBlocos': 'Este modelo não tem blocos.',
  'editor.blocoSemTitulo': 'Bloco sem título',
  'editor.secaoSemTitulo': 'Seção de conteúdo',
  'comum.fechar': 'Fechar',
  'comum.maisAcoes': 'Mais ações',
  'editor.documentoRenderizado': 'Documento renderizado',
  'editor.reduzirZoom': 'Reduzir zoom',
  'editor.aumentarZoom': 'Aumentar zoom',
  'editor.sincronizarPdf': 'Atualizar páginas',
  'editor.valorAusente': '[informação pendente]',
  'editor.confidencial': 'CONFIDENCIAL',
  'editor.bibliotecaMidia': 'Biblioteca de mídia',
  'editor.abrirBiblioteca': 'Abrir biblioteca',
  'editor.enviarImagem': 'Enviar imagem',
  'editor.imagemEnviada': 'Imagem adicionada à biblioteca.',
  'editor.imagemInserida': 'Imagem inserida na seção.',
  'editor.erroImagem': 'Não foi possível usar esta imagem.',
  'editor.gerarComIa': 'Gerar com IA',
  'editor.promptImagem': 'Descreva a cena, sem incluir texto na imagem.',
  'editor.gerarAlternativas': 'Gerar alternativas',
  'editor.iaImagemIndisponivel':
    'A geração por IA não está configurada. Envie uma imagem ou use um ativo existente.',
  'editor.textoDoBloco': 'Texto do bloco',
  'editor.salvarRascunho': 'Salvar rascunho',
  'editor.gerarSugestao': 'Gerar sugestão com IA',
  'editor.gerarOutra': 'Gerar nova sugestão',
  'editor.restaurar': 'Restaurar conteúdo do modelo',
  'editor.previa': 'Pré-visualizar proposta',
  'editor.modos': 'Áreas do editor',
  'editor.modoCONTEUDO': 'Conteúdo',
  'editor.modoDESIGN': 'Design',
  'editor.modoREVISAO': 'Revisão',
  'editor.salvamentoSALVO': 'Alterações salvas',
  'editor.salvamentoSALVANDO': 'Salvando…',
  'editor.salvamentoFALHOU': 'Falha ao salvar — tente novamente',
  'editor.designTitulo': 'Pré-visualização por página',
  'editor.designAjuda':
    'A identidade da organização e as personalizações do modelo são aplicadas com layouts controlados.',
  'editor.atualizarPrevia': 'Atualizar pré-visualização',
  'editor.previaFalhou':
    'Não foi possível exibir o documento. Tente novamente; nenhum conteúdo foi perdido.',
  'editor.previaInstrucao':
    'Gere a pré-visualização para revisar capa, páginas, imagens, tabelas, cabeçalho e rodapé.',
  'editor.abrirPdf': 'Abrir PDF em nova guia',
  'editor.prontidao': 'Prontidão para finalizar',
  'editor.bloqueios': 'Bloqueios',
  'editor.recomendacoes': 'Recomendações',
  'editor.blocosRevisados': 'Blocos preenchidos',
  'comum.tentarNovamente': 'Tentar novamente',
  'editor.finalizar': 'Finalizar versão',
  'editor.finalizarTexto':
    'A versão finalizada gera o PDF e congela o documento. Qualquer alteração depois disso exige uma versão nova.',
  'editor.motivoDaVersao': 'Por que existe esta versão?',
  'editor.salvo': 'Bloco salvo.',
  'editor.erroSalvar': 'Não foi possível salvar o bloco.',
  'editor.sugestaoPronta': 'Sugestão gerada. Revise antes de usar.',
  'editor.erroSugestao': 'Não foi possível gerar a sugestão.',
  'editor.restaurado': 'Conteúdo do modelo restaurado.',
  'editor.erroRestaurar': 'Não foi possível restaurar o bloco.',
  'editor.dadosAtualizados': 'Dados atualizados a partir da oportunidade.',
  'editor.erroAtualizar': 'Não foi possível atualizar os dados.',
  'editor.erroPrevia': 'Não foi possível gerar a prévia.',
  'editor.versaoFinalizada': 'Versão finalizada.',
  'editor.erroFinalizar': 'Não foi possível finalizar a versão.',
  'editor.erroNovaVersao': 'Não foi possível abrir uma nova versão.',
  'editor.aprovacao': 'Aprovação comercial',
  'editor.comercialSoLeitura':
    'Valores, condições e prazo vêm da composição comercial aprovada. Nem o editor nem a IA os alteram.',
  'editor.faltaParaFinalizar': 'Falta isto para finalizar a versão:',
  'editor.atualizarDados': 'Atualizar a partir da oportunidade',
  'editor.orientacao': 'Orientação para esta sugestão',
  'editor.orientacaoExemplo': 'Ex.: deixe mais objetivo, foque no ganho de produtividade',
  'editor.orientacaoAjuda':
    'Vale só para esta geração e muda o texto, nunca os valores comerciais.',
  'editor.geradoPorIa': 'Sugestão gerada por IA',
  'editor.editadoDepois': 'editada depois',
  'editor.naoEditado': 'sem edição posterior',
  'editor.blocoAutomatico':
    'Este bloco vem do modelo e dos dados da oportunidade. Ele não se edita na proposta.',
  'editor.confirmarSubstituicao': 'Substituir o texto que você revisou?',
  'editor.confirmarSubstituicaoTexto':
    'A nova sugestão apaga o texto atual deste bloco, e não dá para desfazer.',
  'estadoDoBloco.AUTOMATICO': 'Preenchido automaticamente',
  'estadoDoBloco.AGUARDANDO': 'Aguardando preenchimento',
  'estadoDoBloco.SUGESTAO_IA': 'Sugestão da IA',
  'estadoDoBloco.EDITADO': 'Editado por você',
  'estadoDoBloco.SEM_INFORMACAO': 'Informação ausente',
  'estadoDoBloco.BLOQUEADO': 'Bloqueado por aprovação',
  // ---- CERES-041: padrões do serviço no Catálogo ------------------------
  'cat.abaEntregaveis': 'Entregáveis padrão',
  'cat.abaMetodologia': 'Metodologia padrão',
  'cat.avisoEntregaveis':
    'Estes são os entregáveis padrão do serviço. Os entregáveis efetivamente contratados são definidos na oportunidade, no briefing da proposta.',
  'cat.avisoMetodologia':
    'Esta é a metodologia padrão do serviço. Ela poderá ser adaptada na proposta sem alterar o método comercial do Catálogo.',
  'hub.campoAplicabilidade': 'Quando se aplica',
  'hub.campoOrdem': 'Ordem de apresentação',
  'hub.campoObservacaoInterna': 'Observação interna',

  // ---- CERES-041: o briefing da proposta --------------------------------
  'briefing.titulo': 'Briefing da proposta',
  'briefing.descricao':
    'O que foi combinado com este cliente. É daqui que a proposta tira escopo, entregáveis e metodologia.',
  'briefing.abrir': 'Abrir',
  'briefing.recolher': 'Recolher',
  'briefing.situacaoNaoIniciado': 'Não iniciado',
  'briefing.situacaoEmPreenchimento': 'Em preenchimento',
  'briefing.situacaoPronto': 'Pronto para proposta',
  'briefing.situacaoDesatualizado': 'Desatualizado',
  'briefing.pendencias': 'pendências',
  'briefing.resumoDePendencias': 'Resumo das pendências',
  'briefing.contextoEObjetivo': 'Contexto e objetivo',
  'briefing.contextoDoCliente': 'Contexto do cliente',
  'briefing.problemaOuNecessidade': 'Problema ou necessidade',
  'briefing.objetivoDaContratacao': 'Objetivo da contratação',
  'briefing.resultadoEsperado': 'Resultado esperado',
  'briefing.observacoes': 'Observações para a proposta',
  'briefing.produtosEServicos': 'Produtos e serviços negociados',
  'briefing.semItens':
    'Esta oportunidade ainda não tem produtos ou serviços. O briefing é por serviço: inclua os itens negociados na composição comercial.',
  'briefing.escopo': 'Escopo incluído',
  'briefing.atividadesIncluidas': 'Atividades incluídas',
  'briefing.atividadesNaoIncluidas': 'Atividades não incluídas',
  'briefing.entregaveis': 'Entregáveis',
  'briefing.metodologia': 'Metodologia',
  'briefing.premissas': 'Premissas',
  'briefing.responsabilidadesContratante': 'Responsabilidades da contratante',
  'briefing.responsabilidadesContratada': 'Responsabilidades da contratada',
  'briefing.cronograma': 'Cronograma e marcos',
  'briefing.equipe': 'Equipe e perfis previstos',
  'briefing.observacoesDoServico': 'Observações deste serviço',
  'briefing.umPorLinha': 'Um por linha',
  'briefing.usarPadroes': 'Usar padrões do método comercial',
  'briefing.padroesAplicados': 'Padrões do método comercial copiados para esta oportunidade.',
  'briefing.padroesCopiados': 'Padrões copiados em',
  'briefing.semPlaybook':
    'O método comercial do Catálogo não está no plano desta organização. O briefing continua disponível; o que não há são padrões para copiar.',
  'briefing.semPadroes':
    'Este serviço ainda não tem entregáveis nem metodologia padrão no método comercial do Catálogo.',
  'briefing.origemPlaybook': 'Padrão do método comercial',
  'briefing.origemAdaptado': 'Adaptado nesta oportunidade',
  'briefing.origemOportunidade': 'Criado nesta oportunidade',
  'briefing.editar': 'Editar',
  'briefing.salvar': 'Salvar',
  'briefing.cancelar': 'Cancelar',
  'briefing.salvo': 'Briefing atualizado.',
  'briefing.adicionar': 'Adicionar',
  'briefing.remover': 'Remover',
  'briefing.nome': 'Nome',
  'briefing.descricaoCampo': 'Descrição',
  'briefing.objetivoCampo': 'Objetivo',
  'briefing.marco': 'Marco',
  'briefing.prazo': 'Prazo',
  'briefing.perfil': 'Perfil',
  'briefing.dedicacao': 'Dedicação',
  'briefing.de': 'de',
  'briefing.preenchido': 'preenchidos',
  'briefing.completo': 'Completo',
  'briefing.atualizadoEm': 'Atualizado em',

  // ---- CERES-041: a seção por serviço no modelo -------------------------
  'modelo.apresentacaoPorServico': 'Uma seção por serviço',
  'modelo.apresentacaoAjuda':
    'Numa proposta com mais de um serviço, este bloco vira uma seção para cada um, com o nome do serviço no título. Com um serviço só, nada muda.',
  'estadoDoBloco.DO_BRIEFING': 'Vindo do briefing',
  // ---- CERES-043: o envio da proposta por e-mail ------------------------
  'proposta.enviarPorEmail': 'Enviar por e-mail',
  'envioEmail.titulo': 'Enviar proposta por e-mail',
  'envioEmail.descricao':
    'Confira o que será enviado. O arquivo anexado é o PDF desta versão, e o remetente é o autorizado pelo provedor.',
  'envio.carregando': 'Preparando o envio…',
  'envio.propostaEVersao': 'Proposta e versão',
  'envio.cliente': 'Cliente',
  'envioEmail.versao': 'Versão',
  'envio.finalizadaEm': 'Finalizada em',
  'envio.valorTotal': 'Valor total',
  'envio.validade': 'Validade',
  'envio.anexo': 'Arquivo anexado',
  'envio.semArquivo': 'Esta versão não tem arquivo PDF. Gere ou anexe o documento antes de enviar.',
  'envio.remetente': 'Remetente',
  'envio.responderPara': 'Responder para',
  'envio.para': 'Para',
  'envio.copia': 'Cópia',
  'envio.copiaOculta': 'Cópia oculta',
  'envio.umPorLinha': 'Um endereço por linha',
  'envio.assunto': 'Assunto',
  'envio.mensagem': 'Mensagem',
  'envio.confirmarEEnviar': 'Confirmar e enviar',
  'envio.enviando': 'Enviando…',
  'envio.cancelar': 'Cancelar',
  'envio.versaoMaisRecente': 'Existe uma versão mais recente desta proposta',
  'envio.confirmarVersaoAnterior': 'Confirmo que quero enviar a versão selecionada.',
  'envio.emAndamento': 'Já existe um envio desta versão em andamento. Aguarde o desfecho.',
  'envio.nadaSePerdeu':
    'A proposta não foi enviada. Os destinatários, o assunto e a mensagem continuam aqui.',
  'envio.peloCeres': 'Enviado pelo CERES',
  'envio.registrado': 'Registrado manualmente',
  'envio.estado.QUEUED': 'Enfileirado',
  'envio.estado.SENDING': 'Enviando',
  'envio.estado.SENT': 'Enviado ao provedor',
  'envio.estado.FAILED': 'Falhou',
  'hist.EMAIL_SOLICITADO': 'Envio por e-mail solicitado',
  'hist.EMAIL_ACEITO_PELO_PROVEDOR': 'Proposta enviada por e-mail',
  'hist.EMAIL_FALHOU': 'Falha ao enviar por e-mail',
  // ---- CERES-044: acompanhamento e desfecho ------------------------------
  'acomp.titulo': 'Acompanhamento',
  'acomp.registrar': 'Registrar acompanhamento',
  'acomp.registrarDescricao':
    'Anote a conversa que aconteceu. Isto não reenvia a proposta e não altera valor, versão nem situação comercial.',
  'acomp.definirProximaAcao': 'Definir próxima ação',
  'acomp.proximaAcaoDescricao':
    'A próxima ação vira uma atividade na agenda do responsável — a mesma caixa de entrada do resto do CRM.',
  'acomp.situacao': 'Situação',
  'acomp.envio': 'Envio',
  'acomp.validade': 'Validade',
  'acomp.proximaAcao': 'Próxima ação',
  'acomp.versaoEnviada': 'Versão enviada',
  'acomp.vigente': 'vigente',
  'acomp.valor': 'Valor',
  'acomp.responsavel': 'Responsável',
  'acomp.contato': 'Contato',
  'acomp.acoesEmAberto': 'Ações em aberto',
  'acomp.concluir': 'Concluir',
  'acomp.acaoConcluida': 'Ação concluída.',
  'acomp.acaoDefinida': 'Próxima ação definida.',
  'acomp.registrado': 'Acompanhamento registrado.',
  'acomp.linhaDoTempo': 'Linha do tempo',
  'acomp.semEventos': 'Nada aconteceu com esta proposta ainda.',
  'acomp.canal': 'Canal',
  'acomp.resumo': 'O que foi conversado',
  'acomp.resultado': 'Resultado',
  'acomp.combinarProxima': 'Combinar a próxima ação agora',
  'acomp.tipoDaAcao': 'Tipo',
  'acomp.quando': 'Quando',
  'acomp.observacao': 'Observação',
  'acomp.salvar': 'Salvar',
  'acomp.cancelar': 'Cancelar',
  'acomp.canal.EMAIL': 'E-mail',
  'acomp.canal.CALL': 'Telefone',
  'acomp.canal.WHATSAPP': 'WhatsApp',
  'acomp.canal.MEETING': 'Reunião',
  'acomp.canal.OTHER': 'Outro',
  'acomp.tipo.CONTATO_DE_ACOMPANHAMENTO': 'Contato de acompanhamento',
  'acomp.tipo.REUNIAO': 'Reunião',
  'acomp.tipo.REVISAO_DA_PROPOSTA': 'Revisão da proposta',
  'acomp.tipo.AGUARDAR_RETORNO': 'Aguardar retorno',
  'acomp.tipo.NEGOCIAR_CONDICOES': 'Negociar condições',
  'acomp.tipo.ENVIAR_NOVA_VERSAO': 'Enviar nova versão',
  'acomp.tipo.OUTRO': 'Outro',
  'acomp.alerta.semProximaAcao': 'Sem próxima ação',
  'acomp.alerta.acaoAtrasada': 'Ação atrasada',
  'acomp.alerta.validadeProxima': 'Validade próxima',
  'acomp.alerta.validadeVencida': 'Validade vencida',
  'acomp.alerta.envioIncerto': 'Envio com resultado incerto',
  'acomp.alerta.versaoNaoEnviada': 'Versão não enviada',
  'acomp.alerta.oportunidadeAlterada': 'Oportunidade alterada',
  'propostas.filtroProximaAcao': 'Próxima ação',
  'propostas.filtroProximaAcao.TODAS': 'Todas',
  'propostas.filtroProximaAcao.COM': 'Com próxima ação',
  'propostas.filtroProximaAcao.SEM': 'Sem próxima ação',
  'propostas.filtroProximaAcao.ATRASADA': 'Ação atrasada',
  'propostas.filtroValidade': 'Validade',
  'propostas.filtroValidade.TODAS': 'Todas',
  'propostas.filtroValidade.VENCENDO': 'Vencendo',
  'propostas.filtroValidade.VENCIDA': 'Vencida',
  'desfecho.versao': 'Versão a que o desfecho se refere',
  'desfecho.cargo': 'Cargo de quem aceitou',
  'desfecho.referencia': 'Referência externa',
  'desfecho.referenciaDica': 'Pedido de compra, protocolo ou contrato, quando houver.',
  'desfecho.motivoControlado': 'Motivo da recusa',
  'desfecho.motivo.PRECO': 'Preço',
  'desfecho.motivo.PRAZO': 'Prazo',
  'desfecho.motivo.ESCOPO': 'Escopo',
  'desfecho.motivo.PRIORIDADE_ALTERADA': 'Prioridade alterada',
  'desfecho.motivo.CONCORRENTE_ESCOLHIDO': 'Concorrente escolhido',
  'desfecho.motivo.ORCAMENTO_INDISPONIVEL': 'Orçamento indisponível',
  'desfecho.motivo.PROJETO_CANCELADO': 'Projeto cancelado',
  'desfecho.motivo.SEM_RETORNO': 'Sem retorno',
  'desfecho.motivo.OUTRO': 'Outro',
  'envio.estado.INCERTO': 'Resultado incerto',
  'hist.EMAIL_RESULTADO_INCERTO': 'Envio por e-mail com resultado incerto',
  'hist.ACOMPANHAMENTO_REGISTRADO': 'Acompanhamento registrado',
  'hist.PROXIMA_ACAO_DEFINIDA': 'Próxima ação definida',
  'hist.PROXIMA_ACAO_CONCLUIDA': 'Próxima ação concluída',
  'hist.NEGOCIACAO_REABERTA': 'Negociação reaberta',
  'hist.DOCX_RASCUNHO_EXPORTADO': 'Cópia em Word do rascunho exportada',
  'hist.DOCX_VERSAO_GERADO': 'Cópia em Word gerada',
  'hist.DOCX_VERSAO_BAIXADO': 'Cópia em Word baixada',
  // ---- CERES-045: a cópia editável em Word ------------------------------
  'word.exportarCopia': 'Exportar cópia em Word',
  'word.exportarRascunho': 'Exportar rascunho em Word',
  'word.exportada': 'Cópia em Word gerada.',
  'word.erro': 'A cópia em Word não pôde ser gerada. O PDF continua disponível.',
  'word.avisoCurto':
    'Cópia auxiliar editável. O PDF é o documento oficial: alterações feitas no Word não modificam a proposta.',
  'word.aviso':
    'O PDF é o documento oficial desta proposta — é ele que vai anexado no envio e é a ele que o aceite se refere. A cópia em Word é auxiliar: alterações feitas nela não modificam a proposta registrada no CERES. Para enviar um documento alterado por fora, registre uma nova versão.',
  'oportunidade.ultimoEnvio': 'Último envio',
  // ---- CERES-048: o primeiro rascunho do cliente ideal ------------------
  'rascunho.comoVerificar': 'Como verificar',
  // ---- CERES-049: revisão, aprovação e vigência do cliente ideal --------
  'revisao.versao': 'Versão',
  'revisao.escopoGlobal': 'Perfil global',
  'revisao.statusGENERATED': 'Gerado',
  'revisao.statusIN_REVIEW': 'Em revisão',
  'revisao.statusAPPROVED': 'Aprovado',
  'revisao.statusSCHEDULED': 'Agendado',
  'revisao.statusACTIVE': 'Vigente',
  'revisao.statusSUPERSEDED': 'Substituído',
  'revisao.statusEXPIRED': 'Expirado',
  'revisao.statusRETURNED': 'Devolvido para ajustes',
  'revisao.explicacaoGENERATED': 'Gerado pelo Analista e ainda não revisado por ninguém.',
  'revisao.explicacaoIN_REVIEW': 'Em revisão humana. Nada aqui tem efeito operacional.',
  'revisao.explicacaoAPPROVED':
    'Conteúdo aprovado — e ainda SEM efeito operacional. Publicar é uma decisão separada.',
  'revisao.explicacaoSCHEDULED': 'Aprovado e com vigência marcada para começar.',
  'revisao.explicacaoACTIVE': 'Publicado e vigente. Esta versão é imutável.',
  'revisao.explicacaoSUPERSEDED':
    'Substituído por uma versão posterior. Continua guardado inteiro.',
  'revisao.explicacaoEXPIRED': 'Fora da vigência. Continua guardado inteiro.',
  'revisao.explicacaoRETURNED': 'Devolvido para ajustes. Reabra para continuar editando.',
  'revisao.devolvidaPorque': 'Motivo da devolução',
  'revisao.bloqueios': 'Bloqueios',
  'revisao.semBloqueios': 'Nenhum bloqueio.',
  'revisao.alertas': 'Alertas (não impedem a aprovação)',
  'revisao.semAlertas': 'Nenhum alerta.',
  'revisao.identificacao': 'Identificação',
  'revisao.nome': 'Nome do perfil',
  'revisao.projecao': 'Projeção para o scoring',
  'revisao.projecaoAjuda':
    'O motor de pontuação lê cinco campos da empresa. Escolha o campo, o operador e os valores de cada critério: o que ficar sem campo continua no documento, fora do cálculo.',
  'revisao.entraNoScoring': 'Entra no cálculo',
  'revisao.naoEntraNoScoring': 'Não utilizado automaticamente no scoring',
  'revisao.obrigatorio': 'Obrigatório',
  'revisao.desejavel': 'Desejável',
  'revisao.desqualificador': 'Desqualificador',
  'revisao.campo': 'Campo do CRM',
  'revisao.semCampo': 'Sem campo (fica no documento)',
  'revisao.campo_segmento': 'Segmento',
  'revisao.campo_porte': 'Porte',
  'revisao.campo_uf': 'UF',
  'revisao.campo_status': 'Situação da empresa',
  'revisao.campo_interacoes90d': 'Interações em 90 dias',
  'revisao.operador': 'Operador',
  'revisao.semOperador': 'Escolha',
  'revisao.operadorEM': 'Está entre os valores',
  'revisao.operadorMINIMO': 'No mínimo',
  'revisao.valores': 'Valores aceitos',
  'revisao.valoresPlaceholder': 'Separe por vírgula',
  'revisao.minimo': 'Mínimo',
  'revisao.peso': 'Peso',
  'revisao.notaMinima': 'Nota mínima',
  'revisao.somaDosPesos': 'Soma dos pesos',
  'revisao.justificativa': 'Por que você mudou',
  'revisao.justificativaPlaceholder': 'Fica no histórico, junto com o valor anterior',
  'revisao.salvar': 'Salvar revisão',
  'revisao.enviarParaAprovacao': 'Enviar para aprovação',
  'revisao.bloqueadoAviso': 'Resolva os bloqueios antes de enviar.',
  'revisao.decisao': 'Decisão',
  'revisao.observacao': 'Observação da aprovação',
  'revisao.motivoDaDevolucao': 'Motivo, se for devolver',
  'revisao.aprovar': 'Aprovar conteúdo',
  'revisao.devolver': 'Devolver para ajustes',
  'revisao.aprovarNaoPublica':
    'Aprovar aceita o conteúdo. Publicar é a decisão seguinte, e separada.',
  'revisao.reabrir': 'Voltar para revisão',
  'revisao.publicacao': 'Publicação e vigência',
  'revisao.publicacaoAjuda':
    'Escolha quando este perfil passa a valer. O horário é o da organização.',
  'editor.classificacao': 'Classificação',
  'editor.tipoFACT': 'Fato',
  'editor.tipoINFERENCE': 'Inferência',
  'editor.tipoHYPOTHESIS': 'Hipótese',
  'editor.tipoUSER_DECISION': 'Decisão sua',
  'editor.fontes': 'Fontes:',
  'editor.fonteCATALOGO': 'Catálogo',
  'editor.fontePLAYBOOK': 'Método comercial',
  'editor.fonteCRM': 'CRM',
  'editor.fonteRESPOSTA': 'Sua resposta',
  'editor.semFonte': 'sem fonte',
  'editor.confianca': 'Confiança',
  'editor.confiancaALTA': 'alta',
  'editor.confiancaMEDIA': 'média',
  'editor.confiancaBAIXA': 'baixa',
  'editor.adicionar': 'Adicionar item',
  'editor.remover': 'Remover',
  'editor.adicionarSecao': 'Adicionar seção',
  'editor.duplicarSecao': 'Duplicar',
  'editor.ocultarSecao': 'Ocultar',
  'editor.mostrarSecao': 'Mostrar',
  'editor.removerSecao': 'Remover',
  'editor.moverCima': 'Mover seção para cima',
  'editor.moverBaixo': 'Mover seção para baixo',
  'editor.acoesDaEstrutura': 'Ações da estrutura',
  'editor.estruturaAtualizada': 'Estrutura atualizada.',
  'editor.erroEstrutura': 'Não foi possível atualizar a estrutura.',
  'editor.oculta': 'Oculta',
  'editor.subir': 'Subir',
  'editor.descer': 'Descer',
  'editor.limpar': 'Deixar em branco',
  'editor.preencher': 'Preencher',
  'editor.naoDefinido': 'Ainda não definido',
  'editor.listaVazia': 'Nada aqui ainda. Lista vazia é resposta.',
  'editor.legenda':
    'Texto alterado por você vira decisão sua, e a fonte anterior sai — ela não comprova o texto novo',
  'editor.afirmacao': 'Afirmação',
  'editor.explicacao': 'De onde veio',
  'editor.descricao': 'Critério',
  'editor.justificativaDoCriterio': 'Por que ele importa',
  'editor.comoVerificar': 'Como verificar numa empresa real',
  'editor.papel': 'Papel',
  'editor.funcao': 'Função',
  'editor.informacao': 'O que falta',
  'editor.impacto': 'Impacto',
  'editor.comoObter': 'Como obter',
  'editor.prioridade': 'Prioridade',
  'editor.prioridadeALTA': 'Alta',
  'editor.prioridadeMEDIA': 'Média',
  'editor.prioridadeBAIXA': 'Baixa',
  'editor.impedeAprovacao': 'Impede a aprovação',
  'editor.objetivoComercial': 'Objetivo comercial',
  'editor.resumoExecutivo': 'Resumo executivo',
  'editor.secaoResumo': 'Identificação e resumo',
  'editor.secaoEmpresa': 'Características das empresas',
  'editor.secaoEmpresaAjuda': 'Faixa só vira fato com fonte. Sem observação, deixe em branco.',
  'editor.secaoContexto': 'Contexto',
  'editor.secaoContextoAjuda': 'O cenário em que este cliente vive.',
  'editor.secaoDores': 'Problemas e dores',
  'editor.secaoDoresAjuda': 'O que dói o bastante para virar projeto.',
  'editor.secaoGatilhos': 'Gatilhos comerciais',
  'editor.secaoGatilhosAjuda': 'O que acontece na empresa pouco antes de ela comprar.',
  'editor.secaoObrigatorios': 'Critérios obrigatórios',
  'editor.secaoObrigatoriosAjuda':
    'Sem isso, não é o cliente. Cada um precisa dizer como ser verificado.',
  'editor.secaoDesejaveis': 'Critérios desejáveis',
  'editor.secaoDesejaveisAjuda': 'Contam pontos, mas não eliminam ninguém.',
  'editor.secaoDesqualificacao': 'Critérios de desqualificação',
  'editor.secaoDesqualificacaoAjuda': 'Quem tem isto sai da lista. Lista vazia é resposta.',
  'editor.secaoSinais': 'Sinais de encaixe',
  'editor.secaoSinaisAjuda': 'O que dá para observar de fora e sugere encaixe.',
  'editor.secaoObjecoes': 'Objeções',
  'editor.secaoObjecoesAjuda': 'O que costuma travar a conversa — e como você responde.',
  'editor.secaoComite': 'Comitê de compra',
  'editor.secaoComiteAjuda': 'Quem decide, quem influencia e quem assina.',
  'editor.secaoLacunas': 'Lacunas de informação',
  'editor.secaoLacunasAjuda': 'O que ainda falta saber, e o que fazer a respeito.',
  'editor.campo_modeloDeNegocio': 'Modelo de negócio',
  'editor.campo_porte': 'Porte',
  'editor.campo_faixaDeFuncionarios': 'Faixa de funcionários',
  'editor.campo_faixaDeFaturamento': 'Faixa de faturamento',
  'editor.campo_maturidade': 'Maturidade',
  'editor.campo_cobertura': 'Cobertura geográfica',
  'editor.campo_complexidadeOperacional': 'Complexidade operacional',
  'editor.campo_dependenciaDeTecnologia': 'Dependência de tecnologia',
  'revisao.pendentes': 'Alterações a gravar',
  'revisao.porQue': 'Por que você mudou isto?',
  'revisao.naoPedeJustificativa': 'Não precisa de justificativa.',
  'revisao.faltaJustificar': 'Explique cada alteração marcada antes de gravar.',
  'revisao.tipoREORDENACAO': 'Reordenação',
  'revisao.modoAGORA': 'Publicar agora',
  'revisao.modoAGENDADO': 'Agendar',
  'revisao.fusoDaOrganizacao': 'Fuso da organização',
  'revisao.vigenteSemScoring':
    'Perfil vigente. A utilização deste perfil no scoring será habilitada após a calibração.',
  'revisao.semPermissaoAprovar':
    'Esta versão está aguardando a decisão de quem aprova perfis de cliente ideal.',
  'calibracao.titulo': 'Calibração do scoring',
  'calibracao.ajuda':
    'Aqui cada critério do perfil vira — ou não vira — uma regra que o sistema consegue verificar. O que não puder ser verificado com dado do CRM fica como avaliação humana, visível e fora da conta.',
  'calibracao.somenteProduto':
    'A calibração vale para perfis de produto. Este perfil é global, e a lista priorizada continua usando o scoring global.',
  'calibracao.versaoFechada':
    'Esta versão não está aberta para edição. Para calibrar um perfil vigente, crie a próxima versão a partir dele — assim nenhum resultado já calculado muda de lógica.',
  'calibracao.notaMinima': 'Nota mínima',
  'calibracao.coberturaMinima': 'Cobertura mínima (%)',
  'calibracao.pontuaveis': 'pontuável(is)',
  'calibracao.obrigatorias': 'obrigatório(s)',
  'calibracao.desqualificadoras': 'desqualificador(es)',
  'calibracao.manuaisContagem': 'de avaliação humana',
  'calibracao.natureza': 'Natureza',
  'calibracao.naturezaPONTUAVEL': 'Soma pontos',
  'calibracao.naturezaOBRIGATORIO': 'Obrigatório',
  'calibracao.naturezaDESQUALIFICADOR': 'Desqualifica',
  'calibracao.naturezaMANUAL': 'Avaliação humana',
  'calibracao.campo': 'Campo da empresa',
  'calibracao.semCampo': 'Escolha um campo',
  'calibracao.condicao': 'Condição',
  'calibracao.semCondicao': 'Escolha a condição',
  'calibracao.valor': 'Valor',
  'calibracao.valoresPlaceholder': 'Separe por vírgula',
  'calibracao.semValorNecessario': 'Esta condição não precisa de valor.',
  'calibracao.e': 'e',
  'calibracao.peso': 'Peso',
  'calibracao.doTotal': 'do total',
  'calibracao.automatica': 'Automática',
  'calibracao.desligada': 'Desligada',
  'calibracao.avaliacaoHumana': 'Necessita avaliação humana',
  'calibracao.desabilitar': 'Desabilitar',
  'calibracao.habilitar': 'Habilitar',
  'calibracao.adicionarRegra': 'Adicionar regra',
  'calibracao.regraNova': 'Novo critério',
  'calibracao.salvar': 'Salvar calibração',
  'calibracao.salva': 'Calibração salva.',
  'calibracao.erroSalvar': 'Não foi possível salvar a calibração.',
  'calibracao.comoVerificarPlaceholder': 'Como conferir isto numa conversa',
  'calibracao.ausenteNaoReprova':
    'Sem este dado, a regra sai do cálculo e derruba a cobertura — ausência não é reprovação.',
  'calibracao.ausenteNaoDesqualifica':
    'Desqualifica só com o dado presente. Ausência nunca desqualifica.',
  'calibracao.foraDoCalculo': 'Fica visível no perfil e fora do cálculo automático.',
  'calibracao.estadoNAO_CALIBRADA': 'Não calibrada',
  'calibracao.estadoEM_CALIBRACAO': 'Em calibração',
  'calibracao.estadoPRONTA': 'Pronta para scoring',
  'calibracao.estadoVIGENTE_E_PRONTA': 'Vigente e pronta para scoring',
  'calibracao.estadoVIGENTE_AGUARDANDO_CALIBRACAO': 'Vigente, aguardando calibração',
  'calibracao.simularTitulo': 'Simular com uma empresa',
  'calibracao.simularAjuda':
    'O cálculo sem consequência: nada é alterado na empresa, no funil ou na classificação comercial.',
  'calibracao.empresa': 'Empresa',
  'calibracao.buscarEmpresa': 'Digite parte do nome',
  'calibracao.simular': 'Simular',
  'calibracao.simulacao': 'Simulação',
  'calibracao.erroSimular': 'Não foi possível simular.',
  'calibracao.nota': 'Aderência',
  'calibracao.cobertura': 'Cobertura',
  'calibracao.formula': 'Como a conta foi feita',
  'calibracao.perfilUsado': 'Perfil usado',
  'calibracao.calibracaoVersao': 'calibração',
  'calibracao.encontrado': 'encontrado',
  'calibracao.contribuicao': 'somou',
  'calibracao.deUmPesoDe': 'de um peso de',
  'calibracao.atendidos': 'Critérios atendidos',
  'calibracao.naoAtendidos': 'Critérios não atendidos',
  'calibracao.semDados': 'Critérios sem dados',
  'calibracao.manuais': 'Avaliação humana',
  'calibracao.desligadas': 'Desligadas do cálculo',
  'calibracao.desatualizado': 'Resultado desatualizado',
  'calibracao.situacaoADERENTE': 'Aderente',
  'calibracao.situacaoNAO_ADERENTE': 'Não aderente',
  'calibracao.situacaoDADOS_INSUFICIENTES': 'Dados insuficientes',
  'calibracao.situacaoDESQUALIFICADA': 'Desqualificada',
  'calibracao.situacaoNAO_CALIBRADO': 'Perfil não calibrado',
  'calibracao.situacaoDESATUALIZADO': 'Desatualizado',
  'aderencia.titulo': 'Aderência ao produto',
  'aderencia.ajuda':
    'Quanto esta empresa se encaixa no perfil de cliente ideal de uma oferta específica. É outra pergunta, e outro número, do scoring geral da lista priorizada.',
  'aderencia.produto': 'Produto',
  'aderencia.escolhaProduto': 'Escolha um produto',
  'aderencia.avaliar': 'Avaliar aderência ao produto',
  'aderencia.avaliada': 'Aderência avaliada.',
  'aderencia.erro': 'Não foi possível avaliar a aderência.',
  'aderencia.semAvaliacao': 'Esta empresa ainda não foi avaliada para este produto.',
  'aderencia.semPerfilPronto':
    'Nenhum produto tem, por enquanto, um perfil vigente e calibrado para scoring.',
  'aderencia.recalcular': 'Recalcular',
  'aderencia.naoAltera':
    'Avaliar não muda a classificação comercial, não move a empresa no funil e não cria oportunidade nem atividade.',
  'icp.criarGlobal': 'Criar perfil global',
  'icp.criarGlobalDescricao':
    'O perfil global nasce em revisão e percorre o mesmo caminho dos demais: revisão, aprovação e publicação.',
  'icp.criarGlobalAviso':
    'Nada é publicado agora. Você escreve o conteúdo na revisão e decide quando ele passa a valer.',
  'icp.criarGlobalBotao': 'Criar e abrir a revisão',
  'icp.perfilGlobalCriado': 'Perfil global criado. Ele está aberto para revisão.',
  'icp.erroCriarGlobal': 'Não foi possível criar o perfil global.',
  'icp.nomePlaceholder': 'Ex.: Cliente ideal — visão geral',
  'orgAdmin.fuso': 'Fuso',
  'orgAdmin.fusoAtualizado': 'Fuso da organização atualizado.',
  'revisao.inicio': 'Início',
  'revisao.fim': 'Término (opcional)',
  'revisao.verPrevia': 'Ver prévia de impacto',
  'revisao.previaTitulo': 'O que acontece ao publicar',
  'revisao.previaVersao': 'Perfil',
  'revisao.previaVigencia': 'Vigência',
  'revisao.previaSubstitui': 'Substitui',
  'revisao.previaRegras': 'Critérios no cálculo',
  'revisao.previaExclusoes': 'Exclusões',
  'revisao.previaNarrativos': 'Fora do cálculo',
  'revisao.previaLacunas': 'Lacunas aceitas',
  'revisao.semTermino': 'sem término',
  'revisao.nenhuma': 'nenhuma',
  'revisao.publicarAgora': 'Publicar agora',
  'revisao.agendar': 'Agendar publicação',
  'revisao.criarNovaVersao': 'Criar nova versão',
  'revisao.novaVersaoAjuda':
    'Copia este conteúdo para uma revisão nova. A versão vigente continua no ar até a nova ser publicada.',
  'revisao.alteracoesHumanas': 'Alterações feitas por pessoas',
  'revisao.semAlteracoes': 'Nenhuma alteração humana registrada.',
  'revisao.tipoEDICAO': 'Edição',
  'revisao.tipoINCLUSAO': 'Inclusão',
  'revisao.tipoREMOCAO': 'Remoção',
  'revisao.tipoRECLASSIFICACAO': 'Reclassificação',
  'revisao.tipoCONFIRMACAO': 'Confirmação',
  'revisao.trilha': 'Quem decidiu o quê',
  'revisao.criadaPor': 'Criada por',
  'revisao.submetidaPor': 'Enviada por',
  'revisao.aprovadaPor': 'Aprovada por',
  'revisao.publicadaPor': 'Publicada por',
  'revisao.substituidaEm': 'Substituída em',
  'revisao.linhagem': 'Versões desta linhagem',
  'revisao.versoes': 'Versões do cliente ideal',
  'revisao.semVersoes': 'Nenhuma versão ainda. Crie o perfil global para começar.',
  'revisao.abrirVersao': 'Abrir',
  'revisao.erroSalvar': 'A revisão não pôde ser salva.',
  'revisao.salva': 'Revisão salva.',
  'revisao.erroDecidir': 'A decisão não pôde ser registrada.',
  'revisao.submetida': 'Enviado para aprovação.',
  'revisao.aprovada': 'Conteúdo aprovado. Publicar continua sendo uma decisão separada.',
  'revisao.devolvida': 'Devolvido para ajustes.',
  'revisao.publicada': 'Perfil publicado.',
  'revisao.agendada': 'Publicação agendada.',

  // CERES-ADM-002 — Administração da plataforma.
  'plat.titulo': 'Administração da plataforma',
  'plat.marca': 'Ceres',
  'plat.faixa': 'Administração da plataforma Ceres',
  'plat.faixaSemTenant':
    'você não está dentro de um tenant; nenhum conteúdo comercial é exibido aqui',
  'plat.voltarAoTenant': 'Entrar em um tenant',
  'plat.sair': 'Sair',
  'plat.nav.visaoGeral': 'Visão geral',
  'plat.nav.tenants': 'Tenants',
  'plat.nav.pessoas': 'Pessoas da plataforma',
  'plat.nav.saude': 'Saúde operacional',
  'plat.nav.eventos': 'Eventos e auditoria',
  'plat.escolha.soPlataforma':
    'Você não tem acesso a nenhuma organização. Entre na administração da plataforma para continuar.',
  'plat.escolha.semTenant': 'Administração central, sem dados comerciais',
  'plat.entrada.semPapel':
    'Esta área é exclusiva de quem administra a plataforma Ceres. O seu acesso continua o mesmo dentro da sua organização.',
  'plat.entrada.voltar': 'Voltar ao painel',
  'plat.entrada.explicacao':
    'Ao entrar, esta sessão deixa o tenant atual e passa a mostrar apenas a administração central: tenants, papéis, planos, saúde e eventos. Nenhum conteúdo comercial dos clientes aparece lá.',
  'plat.entrada.seuPapel': 'Seu papel:',
  'plat.entrada.entrar': 'Entrar na administração da plataforma',
  'plat.entrada.ficar': 'Continuar no tenant',
  'plat.papel.admin': 'Administrador da plataforma',
  'plat.papel.suporte': 'Suporte da plataforma',
  'plat.papel.nenhum': 'Sem papel de plataforma',
  'plat.perfilLegado.rotulo': 'Administrador completo do tenant — legado',
  'plat.perfilLegado.aviso':
    'Este é um perfil legado do tenant e não representa a administração central da plataforma.',
  'plat.naoMonitorado': 'Não monitorado',
  'plat.naoInformado': 'Não informado',
  'plat.semPlano': 'Sem plano',
  'plat.situacao.ativo': 'Ativo',
  'plat.situacao.suspenso': 'Suspenso',
  'plat.situacao.convidado': 'Convidado',
  'plat.todas': 'Todas',
  'plat.todos': 'Todos',
  'plat.mostrar': 'Mostrar',
  'plat.ocultar': 'Ocultar',
  'plat.carregando': 'Carregando…',
  'plat.cancelar': 'Cancelar',
  'plat.confirmar': 'Confirmar',
  'plat.aplicando': 'Aplicando…',
  'plat.motivo': 'Justificativa',
  'plat.motivoDica': 'Explique por que este ato é necessário (mínimo de 5 caracteres).',
  'plat.motivoAuditado':
    'A justificativa fica registrada na trilha de auditoria, com você como autor.',
  'plat.confirmoCiente': 'Revisei o impacto acima e confirmo este ato administrativo.',
  'plat.ato.aplicado': 'Ato administrativo aplicado e registrado.',
  'plat.alerta.filaAcumulada': 'Fila de eventos parada',
  'plat.alerta.cartasMortas24h': 'Cartas mortas nas últimas 24 h',
  'plat.alerta.backupAtrasado': 'Backup atrasado',
  'plat.alerta.backupFalhou': 'Falha no backup ou no exercício de restauração',
  'plat.alerta.discoCheio': 'Pouco espaço em disco',
  'plat.alerta.publicacaoFalhou': 'Última publicação falhou',
  'plat.alerta.executorIa': 'Executor de IA indisponível',
  'plat.alerta.taxaDeErro': 'Taxa de erro da API acima do SLO',
  'plat.alerta.latencia': 'Latência da API acima do SLO',
  'plat.alerta.rotinaFalhou': 'Rotina operacional falhou',
  'plat.alerta.semProprietario': 'Sem proprietário',
  'plat.alerta.semUsuariosAtivos': 'Sem usuários ativos',
  'plat.alerta.suspenso': 'Suspenso',
  'plat.alerta.configuracaoIncompleta': 'Configuração incompleta',
  'plat.alerta.legadoARevisar': 'Legado a revisar',
  'plat.alerta.assentosExcedidos': 'Assentos excedidos',
  'plat.alerta.identidadeInativa': 'Identidade inativa',
  'plat.alerta.perfilLegado': 'Perfil legado',
  'plat.alerta.semSegundoFator': 'Sem segundo fator',
  'plat.alerta.semPerfil': 'Sem perfil',
  'plat.alerta.papelPelaFlag': 'Papel pela marcação legada',
  'plat.alerta.bancoIndisponivel': 'Banco indisponível',
  'plat.alerta.migracaoInacabada': 'Migração inacabada',
  'plat.alerta.migracaoPendente': 'Migração pendente',
  'plat.alerta.entregasMortas': 'Entregas mortas recentes',
  'plat.alerta.eventosComErro': 'Eventos com erro',
  'plat.alerta.integracaoIndisponivel': 'Integração indisponível',
  'plat.alerta.umUnicoAdmin': 'Um único administrador da plataforma',
  'plat.acao.situacao': 'Mudança de situação',
  'plat.acao.permissao': 'Mudança de papel ou perfil',
  'plat.acao.criacao': 'Criação',
  'plat.acao.alteracao': 'Alteração',
  'plat.acao.remocao': 'Remoção',
  'plat.acao.acessoNegado': 'Acesso negado',
  'plat.acao.trocaDeTenant': 'Troca de tenant',
  'plat.acao.trocaRecusada': 'Troca de tenant recusada',
  'plat.vg.titulo': 'Visão geral',
  'plat.vg.subtitulo':
    'Métricas administrativas da plataforma. Nenhum número comercial dos clientes é exibido.',
  'plat.vg.numeros': 'Números da plataforma',
  'plat.vg.tenants': 'Tenants',
  'plat.vg.tenantsAtivos': 'Ativos',
  'plat.vg.tenantsSuspensos': 'Suspensos',
  'plat.vg.semProprietario': 'Sem proprietário',
  'plat.vg.usuariosGlobais': 'Usuários globais',
  'plat.vg.membershipsAtivas': 'Vínculos ativos',
  'plat.vg.admins': 'Administradores',
  'plat.vg.suportes': 'Suporte',
  'plat.vg.alertas': 'Alertas',
  'plat.vg.semAlertas': 'Nenhum alerta no momento.',
  'plat.vg.alertaGlobal': 'Plataforma',
  'plat.vg.aplicacao': 'Aplicação',
  'plat.vg.versao': 'Versão publicada',
  'plat.vg.ambiente': 'Ambiente',
  'plat.vg.estado': 'Estado geral',
  'plat.vg.migracoes': 'Migrações',
  'plat.vg.pendentes': 'pendentes',
  'plat.vg.inacabadas': 'inacabadas',
  'plat.vg.integracoes': 'Integrações globais',
  'plat.vg.operacionais': 'operacionais',
  'plat.vg.aConfigurar': 'a configurar',
  'plat.vg.indisponiveis': 'indisponíveis',
  'plat.vg.verSaude': 'Ver saúde operacional',
  'plat.vg.porPlano': 'Tenants por plano',
  'plat.vg.eventosRecentes': 'Eventos administrativos recentes',
  'plat.vg.verTodos': 'Ver todos',
  'plat.tn.titulo': 'Tenants',
  'plat.tn.subtitulo':
    'Organizações da plataforma, com situação, plano, pessoas e alertas — sem conteúdo comercial.',
  'plat.tn.buscar': 'Buscar por nome, chave ou CNPJ',
  'plat.tn.situacao': 'Situação',
  'plat.tn.todasSituacoes': 'Todas as situações',
  'plat.tn.plano': 'Plano',
  'plat.tn.todosPlanos': 'Todos os planos',
  'plat.tn.semMembershipAtiva': 'Sem vínculo ativo',
  'plat.tn.comAlerta': 'Com alerta',
  'plat.tn.vazio': 'Nenhum tenant encontrado',
  'plat.tn.vazioDica': 'Ajuste a busca ou os filtros.',
  'plat.tn.tenant': 'Tenant',
  'plat.tn.modulos': 'Módulos habilitados',
  'plat.tn.usuarios': 'Usuários',
  'plat.tn.ativas': 'Ativos',
  'plat.tn.proprietarios': 'Proprietários',
  'plat.tn.datas': 'Datas',
  'plat.tn.criado': 'Criado em',
  'plat.tn.ultimaAlteracao': 'Última alteração adm.',
  'plat.tn.alertas': 'Alertas',
  'plat.tn.abrir': 'Abrir',
  'plat.td.voltar': '← Tenants',
  'plat.td.suspender': 'Suspender tenant',
  'plat.td.reativar': 'Reativar tenant',
  'plat.td.suspenso': 'Tenant suspenso. Nenhum dado foi apagado.',
  'plat.td.reativado': 'Tenant reativado.',
  'plat.td.abas': 'Seções do tenant',
  'plat.td.resumo': 'Resumo',
  'plat.td.pessoas': 'Pessoas e acessos',
  'plat.td.plano': 'Plano e módulos',
  'plat.td.saude': 'Saúde',
  'plat.td.eventos': 'Eventos administrativos',
  'plat.td.dados': 'Dados administrativos',
  'plat.td.identificador': 'Identificador técnico',
  'plat.td.chave': 'Chave',
  'plat.td.razaoSocial': 'Razão social',
  'plat.td.cnpj': 'CNPJ',
  'plat.td.situacao': 'Situação',
  'plat.td.memberships': 'Vínculos',
  'plat.td.ativas': 'ativos',
  'plat.td.convidadas': 'convidados',
  'plat.td.suspensas': 'suspensos',
  'plat.td.assentos': 'Assentos',
  'plat.td.criado': 'Criado em',
  'plat.td.ultimaAlteracao': 'Última alteração administrativa',
  'plat.td.suspensoEm': 'Suspenso em',
  'plat.td.motivoSuspensao': 'Motivo da suspensão',
  'plat.td.proprietarios': 'Proprietários',
  'plat.td.gerirProprietarios': 'Gerir proprietários',
  'plat.td.sessoesAtivas': 'Sessões ativas no tenant',
  'plat.td.impacto1':
    'As pessoas deste tenant não poderão operar normalmente, e as sessões abertas nele serão encerradas',
  'plat.td.pessoasAfetadas': 'pessoas com acesso ativo',
  'plat.td.sessoesEncerradas': 'sessões encerradas',
  'plat.td.impacto2': 'Nenhum dado será apagado.',
  'plat.td.impacto3': 'A administração da plataforma continua disponível.',
  'plat.td.impacto4': 'A reativação é possível a qualquer momento, por esta mesma tela.',
  'plat.td.impactoReativar':
    'As pessoas com acesso ativo voltam a operar normalmente neste tenant. Nada mais muda.',
  'plat.pe.titulo': 'Pessoas e acessos',
  'plat.pe.concederAcesso': 'Convidar pessoa',
  'plat.pe.vazio': 'Nenhuma pessoa vinculada',
  'plat.pe.vazioDica':
    'Use “Conceder acesso a um usuário” para criar o primeiro vínculo, de forma controlada.',
  'plat.pe.semDonoTitulo': 'Este tenant está sem proprietário',
  'plat.pe.semDonoComMembros':
    'Há pessoas com acesso ativo. Escolha conscientemente quem será proprietário — nada é designado automaticamente.',
  'plat.pe.semDonoSemMembros':
    'Não há ninguém com acesso ativo. Conceda acesso a um usuário global e, se fizer sentido, designe-o proprietário na mesma operação.',
  'plat.pe.umDono': 'proprietário ativo.',
  'plat.pe.variosDonos': 'proprietários ativos.',
  'plat.pe.revisarDonos':
    'Revise se todos devem continuar; nenhuma remoção é feita automaticamente.',
  'plat.pe.pessoa': 'Pessoa',
  'plat.pe.identidade': 'Situação global',
  'plat.pe.acesso': 'Acesso ao tenant',
  'plat.pe.perfis': 'Perfis (rótulos)',
  'plat.pe.ingresso': 'Ingresso',
  'plat.pe.por': 'por',
  'plat.pe.dono': 'Proprietário',
  'plat.pe.sim': 'Sim',
  'plat.pe.canal': 'Canal',
  'plat.pe.designar': 'Designar proprietário',
  'plat.pe.retirar': 'Retirar proprietário',
  'plat.pe.retirarLegado': 'Retirar perfil legado',
  'plat.pe.designarTitulo': 'Designar proprietário',
  'plat.pe.retirarTitulo': 'Retirar proprietário',
  'plat.pe.legadoTitulo': 'Retirar perfil legado',
  'plat.pe.vinculoTitulo': 'Convidar para o tenant',
  'plat.pe.tenant': 'Tenant',
  'plat.pe.efeito': 'Efeito',
  'plat.pe.designarImpacto':
    'a pessoa passa a administrar usuários, acessos e perfis deste tenant. O perfil de proprietário não concede acesso comercial.',
  'plat.pe.retirarImpacto':
    'a pessoa deixa de ser proprietária. O último proprietário ativo nunca é retirado.',
  'plat.pe.legadoImpacto':
    'a pessoa perde o perfil legado de administrador completo neste tenant. Só é permitido quando o tenant tem proprietário ativo.',
  'plat.pe.vinculoImpacto':
    'A pessoa escolhida recebe um convite e só passa a ter acesso quando aceitar, logada, em "Convites recebidos". O assento é cobrado no aceite. Tenant:',
  'plat.pe.escolherPessoa': 'Usuário global',
  'plat.pe.buscarPessoa': 'Buscar por nome ou e-mail (mínimo de 2 letras)',
  'plat.pe.ninguem': 'Ninguém encontrado.',
  'plat.pe.identidadeInativa': 'identidade inativa',
  'plat.pe.jaTemVinculo': 'já tem vínculo',
  'plat.pe.situacaoInicial': 'Situação inicial do acesso',
  'plat.pe.comoProprietario': 'Propor como proprietário (vale quando a pessoa aceitar)',
  'plat.pe.resumoVinculo': 'Será enviado um convite:',
  'plat.pl.assinatura': 'Assinatura',
  'plat.pl.trocarPlano': 'Trocar plano',
  'plat.pl.assentos': 'Assentos',
  'plat.pl.situacao': 'Situação',
  'plat.pl.plano': 'Plano',
  'plat.pl.versao': 'Versão contratada',
  'plat.pl.vigenteDoPlano': 'versão vigente do plano:',
  'plat.pl.naoConcede': 'não concede módulos',
  'plat.pl.vigencia': 'Vigência',
  'plat.pl.semTermino': 'sem término',
  'plat.pl.ilimitado': 'ilimitado',
  'plat.pl.motivoSuspensao': 'Motivo da suspensão',
  'plat.pl.modulos': 'Módulos',
  'plat.pl.habilitado': 'Habilitado',
  'plat.pl.parcial': 'Parcial',
  'plat.pl.indisponivel': 'Indisponível',
  'plat.pl.entitlements': 'Capacidades (entitlements)',
  'plat.pl.liberado': 'Liberado',
  'plat.pl.negado': 'Negado',
  'plat.pl.historico': 'Alterações recentes',
  'plat.pl.semHistorico': 'Nenhuma alteração registrada.',
  'plat.pl.alterarAssentos': 'Alterar assentos',
  'plat.pl.alterarSituacao': 'Alterar situação da assinatura',
  'plat.pl.antes': 'Antes',
  'plat.pl.depois': 'Depois',
  'plat.pl.impacto':
    'As capacidades passam a valer na próxima requisição do tenant. Nenhum dado é apagado; dados de módulos retirados são preservados.',
  'plat.pl.novoPlano': 'Novo plano',
  'plat.pl.novoLimite': 'Novo limite',
  'plat.pl.novaSituacao': 'Nova situação',
  'plat.pp.titulo': 'Pessoas da plataforma',
  'plat.pp.subtitulo':
    'Quem tem papel de administração ou suporte da plataforma. A senha nunca aparece nem é alterada aqui.',
  'plat.pp.conceder': 'Conceder papel a uma pessoa',
  'plat.pp.umUnicoAdmin':
    'Há apenas um administrador da plataforma ativo. Ele não pode ser removido até que outra pessoa receba o papel.',
  'plat.pp.vazio': 'Ninguém com papel de plataforma',
  'plat.pp.papel': 'Papel',
  'plat.pp.concessao': 'Concessão',
  'plat.pp.ultimoAcesso': 'Último acesso administrativo',
  'plat.pp.nuncaRegistrado': 'Nenhum registrado',
  'plat.pp.eventos': 'Eventos do papel',
  'plat.pp.voce': 'você',
  'plat.pp.proprioPapel': 'Seu próprio papel não é alterável aqui',
  'plat.pp.alterar': 'Alterar papel',
  'plat.pp.escolher': 'Escolher',
  'plat.pp.alterarTitulo': 'Alterar papel de plataforma',
  'plat.pp.impacto':
    'As sessões desta pessoa na administração da plataforma serão encerradas. O acesso dela aos tenants não muda.',
  'plat.pp.ultimoAdmin':
    'Esta é a última pessoa administradora ativa: o servidor vai recusar a retirada.',
  'plat.pp.duplaRotulo': 'Dupla confirmação: digite o e-mail completo da pessoa',
  'plat.pp.novoPapel': 'Novo papel',
  'plat.pp.revogar': 'Revogar papel de plataforma',
  'plat.saude.titulo': 'Saúde operacional',
  'plat.saude.subtitulo':
    'Situação da aplicação, do banco, das migrações, das filas e das integrações. Segredos e conteúdo nunca aparecem.',
  'plat.saude.aplicacao': 'Aplicação',
  'plat.saude.commit': 'Commit',
  'plat.saude.construidoEm': 'Construída em',
  'plat.saude.api': 'API',
  'plat.saude.desde': 'desde',
  'plat.saude.banco': 'Banco de dados',
  'plat.saude.operacional': 'Operacional',
  'plat.saude.falha': 'Falha',
  'plat.saude.indisponivel': 'Indisponível',
  'plat.saude.naoConfigurado': 'Não configurado',
  'plat.saude.desabilitado': 'Desabilitado',
  'plat.saude.migracoes': 'Migrações',
  'plat.saude.aplicadas': 'Aplicadas',
  'plat.saude.pendentes': 'Pendentes',
  'plat.saude.inacabadas': 'Inacabadas',
  'plat.saude.ultima': 'Última aplicada',
  'plat.saude.filas': 'Filas',
  'plat.saude.eventosPendentes': 'Eventos pendentes',
  'plat.saude.eventosComErro': 'Eventos com erro',
  'plat.saude.entregasPendentes': 'Entregas pendentes',
  'plat.saude.entregasMortas': 'Entregas mortas',
  'plat.saude.em7Dias': 'nos últimos 7 dias',
  'plat.saude.publicacao': 'Publicação e backup',
  'plat.saude.ultimoDeploy': 'Último deploy',
  'plat.saude.ultimoBackup': 'Último backup',
  'plat.saude.verificavel': 'com soma de verificação',
  'plat.saude.semSoma': 'sem soma de verificação',
  'plat.saude.integracoes': 'Integrações globais',
  'plat.ev.titulo': 'Eventos e auditoria',
  'plat.ev.subtitulo':
    'Atos administrativos da plataforma e dos tenants, com justificativa, resultado e resumo seguro.',
  'plat.ev.de': 'De',
  'plat.ev.ate': 'Até',
  'plat.ev.acao': 'Ação',
  'plat.ev.resultado': 'Resultado',
  'plat.ev.correlacao': 'Identificador de correlação',
  'plat.ev.limparAtor': 'Limpar filtro de ator',
  'plat.ev.vazio': 'Nenhum evento encontrado',
  'plat.ev.vazioDica': 'Ajuste o período ou os filtros.',
  'plat.ev.quando': 'Quando',
  'plat.ev.ator': 'Ator',
  'plat.ev.tenant': 'Tenant afetado',
  'plat.ev.sistema': 'Sistema',
  'plat.ev.aplicado': 'Aplicado',
  'plat.ev.recusado': 'Recusado',
  'plat.ev.negado': 'Negado',
  'plat.ev.registrado': 'Registrado',
  // CERES-ADM-003 — Administração da organização.
  'org.adm.titulo': 'Administração da organização',
  'org.adm.subtitulo':
    'Pessoas, convites, perfis, assentos, segurança e histórico desta organização.',
  'org.adm.faixa': 'Administração da organização',
  'org.adm.faixaTexto': 'você está administrando acessos, não operando o CRM',
  'org.adm.voltarAoCrm': 'Voltar ao CRM',
  'org.adm.abaResumo': 'Resumo',
  'org.adm.abaMarca': 'Marca',
  'criar.grupoRegistros': 'Registros',
  'criar.grupoDocumentos': 'Documentos comerciais',
  'marca.identidade': 'Identidade institucional',
  'marca.nomeInstitucional': 'Nome institucional',
  'marca.nomeExibicao': 'Nome de exibição',
  'marca.cores': 'Cores',
  'marca.corPrimaria': 'Primária',
  'marca.corSecundaria': 'Secundária',
  'marca.corDestaque': 'Destaque',
  'marca.corTexto': 'Texto',
  'marca.corFundo': 'Fundo',
  'marca.tipografia': 'Tipografia e página',
  'marca.fonteTitulos': 'Fonte dos títulos',
  'marca.fonteCorpo': 'Fonte do corpo',
  'marca.entrelinha': 'Entrelinha',
  'marca.margem': 'Margem (mm)',
  'marca.fonteAjuda':
    'A geração não busca fontes remotas. Fontes não incorporáveis usam fallback seguro.',
  'marca.institucional': 'Dados institucionais e padrões',
  'marca.endereco': 'Endereço',
  'marca.telefone': 'Telefone',
  'marca.email': 'E-mail',
  'marca.site': 'Site',
  'marca.assinatura': 'Assinatura institucional',
  'marca.cabecalho': 'Padrão de cabeçalho',
  'marca.rodape': 'Padrão de rodapé',
  'marca.confidencialidade': 'Aviso de confidencialidade',
  'marca.herancaAjuda':
    'Modelos neutros herdam esta identidade. Personalizações explícitas do modelo têm precedência, e cada proposta congela a identidade aplicada.',
  'marca.salvar': 'Salvar identidade visual',
  'marca.logotipos': 'Logotipos',
  'marca.logotiposAjuda':
    'Envie as três variações. Quando uma faltar, o nome da empresa será usado com contraste seguro.',
  'marca.logoPrincipal': 'Enviar logo principal',
  'marca.logoNegativo': 'Enviar logo para fundo escuro',
  'marca.simbolo': 'Enviar símbolo compacto',
  'marca.enviarFonte': 'Enviar fonte licenciada',
  'marca.licencaFonte': 'Informe a licença ou declaração de direito de uso da fonte.',
  'marca.ativoEnviado': 'Ativo enviado. Salve a identidade para aplicá-lo.',
  'marca.erroAtivo': 'Não foi possível enviar o ativo.',
  'marca.direcao': 'Personalidade e direção visual',
  'marca.personalidade': 'Personalidade da marca',
  'marca.regrasDeUso': 'Regras de utilização',
  'marca.fotografia': 'Preferências de fotografia',
  'marca.ilustracao': 'Preferências de ilustração',
  'marca.previa': 'Prévia em tempo real',
  'marca.previaAjuda':
    'A identidade aplicada ao modelo Editorial. Propostas emitidas preservam o snapshot anterior.',
  'marca.previaTipo': 'PROPOSTA',
  'marca.previaTitulo': 'Estratégia clara. Execução que gera resultado.',
  'marca.previaTexto':
    'Uma proposta criada para transformar oportunidades em crescimento sustentável.',
  'marca.previaConfidencial': 'Confidencial',
  'marca.salva': 'Identidade visual salva.',
  'marca.erro': 'Não foi possível salvar a identidade visual.',
  'org.adm.abaPessoas': 'Pessoas e acessos',
  'org.adm.abaConvites': 'Convites',
  'org.adm.abaPerfis': 'Perfis e permissões',
  'org.adm.abaPlano': 'Plano e assentos',
  'org.adm.abaSeguranca': 'Segurança e sessões',
  'org.adm.abaHistorico': 'Histórico administrativo',
  'org.adm.ativas': 'Pessoas ativas',
  'org.adm.suspensas': 'Suspensas',
  'org.adm.convites': 'Convites pendentes',
  'org.adm.encerradas': 'Acessos encerrados',
  'org.adm.proprietarios': 'Proprietários',
  'org.adm.assentos': 'Assentos',
  'org.adm.alertaSemDono':
    'Esta organização está sem proprietário ativo. Peça apoio à administração da plataforma.',
  'org.adm.alertaAssentosEsgotados':
    'Todos os assentos contratados estão ocupados: novos aceites e reativações serão recusados.',
  'org.adm.alertaAssentosExcedidos': 'O uso passou do limite de assentos contratado.',
  'org.adm.alertaConvitesVencidos': 'Há convites vencidos: reenvie ou cancele.',
  'org.adm.voceEDono': 'Você é proprietário desta organização.',
  'org.adm.voceNaoEDono':
    'Você administra esta organização, mas não é proprietário: o perfil de proprietário só é concedido ou retirado por um proprietário.',
  'org.adm.buscarPessoa': 'Buscar por nome ou e-mail',
  'org.adm.situacao': 'Situação',
  'org.adm.filtroAtivas': 'Acesso ativo',
  'org.adm.filtroSuspensas': 'Suspensas',
  'org.adm.filtroConvites': 'Convites pendentes',
  'org.adm.filtroEncerradas': 'Acesso encerrado',
  'org.adm.filtroTodas': 'Todas',
  'org.adm.ninguem': 'Ninguém nesta situação',
  'org.adm.ninguemDica': 'Ajuste a busca ou o filtro.',
  'org.adm.pessoa': 'Pessoa',
  'org.adm.perfis': 'Perfis',
  'org.adm.desde': 'Desde',
  'org.adm.ultimoAcesso': 'Último acesso nesta organização',
  'org.adm.naoDisponivel': 'Não disponível',
  'org.adm.voce': 'você',
  'org.adm.proprietario': 'Proprietário',
  'org.adm.ativo': 'Ativo',
  'org.adm.suspenso': 'Suspenso',
  'org.adm.convidado': 'Convidado',
  'org.adm.pendente': 'Pendente',
  'org.adm.inativoSuspenso': 'Inativo · suspenso',
  'org.adm.inativoEncerrado': 'Inativo · encerrado',
  'org.adm.convitePendente': 'Convite pendente',
  'org.adm.conviteVencido': 'Convite vencido',
  'org.adm.encerrado': 'Acesso encerrado',
  'org.adm.alterarPerfis': 'Alterar perfis',
  'org.adm.tornarDono': 'Tornar proprietário',
  'org.adm.retirarDono': 'Retirar proprietário',
  'org.adm.suspender': 'Suspender acesso',
  'org.adm.reativar': 'Reativar acesso',
  'org.adm.encerrarSessoes': 'Encerrar sessões',
  'org.adm.encerrarAcesso': 'Encerrar acesso',
  'org.adm.impactoPerfis':
    'Os perfis definem o que a pessoa pode fazer nesta organização. A mudança vale na próxima ação dela.',
  'org.adm.impactoTornarDono':
    'A pessoa passa a responder pela organização e a administrar pessoas, acessos e perfis. O perfil de proprietário não dá acesso comercial.',
  'org.adm.impactoRetirarDono':
    'A pessoa deixa de ser proprietária. O último proprietário ativo nunca é retirado.',
  'org.adm.impactoSuspender':
    'A pessoa perde o acesso a esta organização imediatamente e as sessões dela aqui são encerradas. Nada é apagado e a reativação é possível. O último proprietário ativo não pode ser suspenso.',
  'org.adm.impactoReativar':
    'A pessoa volta a acessar esta organização com os perfis que tinha. A reativação ocupa um assento.',
  'org.adm.impactoSessoes':
    'As sessões abertas desta pessoa nesta organização são encerradas agora; ela precisará entrar de novo. O acesso continua.',
  'org.adm.impactoEncerrar':
    'O acesso desta pessoa a esta organização termina: os perfis daqui são retirados e as sessões encerradas. O cadastro da pessoa e o histórico são preservados; para voltar, é preciso um novo convite.',
  'org.adm.atoAplicado': 'Feito. O ato ficou registrado no histórico administrativo.',
  'org.adm.perfisDaPessoa': 'Perfis nesta organização',
  'org.adm.legadoNaoConcedido': '(legado: pode ser mantido ou retirado, não concedido)',
  'org.adm.convitesPendentes': 'Convites pendentes',
  'org.adm.convidar': 'Convidar pessoa',
  'org.adm.semConvites': 'Nenhum convite pendente',
  'org.adm.semConvitesDica': 'Use “Convidar pessoa” para chamar alguém para esta organização.',
  'org.adm.expira': 'Vale até',
  'org.adm.dataDoConvite': 'Data do convite',
  'org.adm.reenviar': 'Reenviar convite',
  'org.adm.cancelarConvite': 'Cancelar convite',
  'org.adm.impactoConvidar':
    'A pessoa recebe um convite com prazo. Ela só passa a ter acesso depois de aceitar — e o aceite ocupa um assento. Quem já tem cadastro no Ceres aceita pelo próprio menu.',
  'org.adm.impactoReenviar':
    'O prazo do convite é renovado. Se a pessoa ainda não tem cadastro, o link anterior deixa de valer.',
  'org.adm.impactoCancelar':
    'O convite deixa de valer imediatamente. A pessoa não chega a ter acesso.',
  'org.adm.enviarConvite': 'Enviar convite',
  'org.adm.confirmar': 'Confirmar',
  'org.adm.email': 'E-mail',
  'org.adm.nome': 'Nome',
  'org.adm.perfisDoConvite': 'Perfis que a pessoa terá ao aceitar',
  'org.adm.linkTitulo': 'Entregue este link por um canal seguro',
  'org.adm.linkTexto':
    'O envio de e-mail não está ativo. O link é de uso único, vale até o prazo do convite e não será mostrado de novo.',
  'org.adm.linkEntregue': 'Já entreguei',
  'org.adm.semSegundoFator': 'Ainda sem segundo fator',
  'org.adm.sessoesAtivas': 'Sessões ativas nesta organização',
  'org.adm.semSessoes': 'Nenhuma sessão ativa',
  'org.adm.iniciada': 'Iniciada',
  'org.adm.ultimoUso': 'Último uso',
  'org.adm.origem': 'Origem',
  'org.adm.segundoFator': 'Segundo fator',
  'org.adm.verificado': 'Verificado',
  'org.adm.naturezaLegado': 'Legado',
  'org.adm.naturezaAdministrativo': 'Administrativo',
  'org.adm.naturezaComercial': 'Comercial',
  'org.adm.naturezaMaster': 'Administrador master',
  'org.adm.naturezaProprio': 'Perfil da organização',
  'org.adm.perfil.explicacao': 'Perfis do sistema são fixos. Crie perfis da organização para delegar acesso restrito por módulo e função.',
  'org.adm.perfil.criar': 'Criar perfil',
  'org.adm.perfil.editar': 'Editar perfil',
  'org.adm.perfil.arquivar': 'Arquivar perfil',
  'org.adm.perfil.salvar': 'Salvar perfil',
  'org.adm.perfil.nome': 'Nome do perfil',
  'org.adm.perfil.descricao': 'Descrição',
  'org.adm.perfil.canal': 'Perfil de canal (para usuários de parceiro)',
  'org.adm.perfil.canalDica': 'Só pode ser dado a usuários de parceiro, que continuam vendo só o que é do próprio parceiro. Permissões proibidas a canal não entram.',
  'org.adm.perfil.permissoes': 'Permissões',
  'org.adm.perfil.soAsSuas': 'Você só pode conceder permissões que você mesmo tem nesta organização.',
  'org.adm.perfil.voceNaoTem': 'Você não tem esta permissão e por isso não pode concedê-la.',
  'org.adm.perfil.impactoEditar': 'Quem tiver este perfil passa a ter exatamente as permissões marcadas, dentro dos módulos contratados.',
  'org.adm.perfil.impactoArquivar': 'O perfil sai da lista e todas as pessoas que o têm perdem as permissões dele. O histórico é preservado.',
  'org.adm.modulo.account': 'Empresas',
  'org.adm.modulo.activity': 'Atividades',
  'org.adm.modulo.agent': 'Assistente',
  'org.adm.modulo.approval': 'Aprovações',
  'org.adm.modulo.audit': 'Auditoria',
  'org.adm.modulo.cadence': 'Cadências',
  'org.adm.modulo.catalog': 'Catálogo comercial',
  'org.adm.modulo.channel': 'Canais de comunicação',
  'org.adm.modulo.commission': 'Comissões',
  'org.adm.modulo.communication': 'Conversas',
  'org.adm.modulo.conflict': 'Conflitos de carteira',
  'org.adm.modulo.contact': 'Contatos',
  'org.adm.modulo.content': 'Conteúdos',
  'org.adm.modulo.contract': 'Contratos',
  'org.adm.modulo.cs': 'Sucesso do cliente',
  'org.adm.modulo.dashboard': 'Painéis',
  'org.adm.modulo.demo': 'Dados de demonstração',
  'org.adm.modulo.event': 'Eventos técnicos',
  'org.adm.modulo.export': 'Exportação',
  'org.adm.modulo.feature': 'Recursos',
  'org.adm.modulo.finance': 'Financeiro',
  'org.adm.modulo.forecast': 'Previsão de vendas',
  'org.adm.modulo.icp': 'Perfil de cliente ideal',
  'org.adm.modulo.import': 'Importação',
  'org.adm.modulo.integration': 'Integrações',
  'org.adm.modulo.lead': 'Leads',
  'org.adm.modulo.meeting': 'Reuniões',
  'org.adm.modulo.memory': 'Memória do Assistente',
  'org.adm.modulo.opportunity': 'Oportunidades',
  'org.adm.modulo.partner': 'Parceiros',
  'org.adm.modulo.people': 'Pessoas',
  'org.adm.modulo.pipeline': 'Funis',
  'org.adm.modulo.playbook': 'Playbook',
  'org.adm.modulo.policy': 'Políticas',
  'org.adm.modulo.pre_sales': 'Pré-vendas',
  'org.adm.modulo.pricing': 'Preços',
  'org.adm.modulo.proposal': 'Propostas',
  'org.adm.modulo.reservation': 'Reservas',
  'org.adm.modulo.role': 'Perfis',
  'org.adm.modulo.tag': 'Etiquetas',
  'org.adm.modulo.user': 'Usuários',
  'org.adm.modulo.outros': 'Outros',
  'calendario.vazio': 'Nenhuma data cadastrada. Os feriados nacionais entram sozinhos quando o parâmetro estiver ligado.',
  'calendario.data': 'Data',
  'calendario.tipo': 'Tipo',
  'calendario.nome': 'Nome',
  'calendario.uf': 'UF',
  'calendario.municipio': 'Município',
  'calendario.remover': 'Remover',
  'calendario.adicionar': 'Adicionar data',
  'calendario.tipoPropria': 'Própria',
  'calendario.tipoMunicipal': 'Municipal',
  'calendario.tipoEstadual': 'Estadual',
  'calendario.tipoNacional': 'Nacional',
  'pipelines.permiteSalto': 'Permite pular etapas',
  'pipelines.saltoAjuda': 'Ligado, avançar pulando etapas exige motivo e cumpre os requisitos da etapa de destino. Desligado, o avanço segue a sequência.',
  'pipelines.saltoLigado': 'Este funil permite pular etapas ao avançar, com motivo registrado no histórico.',
  'pipelines.saltoDesligado': 'Este funil exige a sequência: para avançar, passe por todas as etapas.',
  'pipelines.saltoGravado': 'Configuração de salto de etapas gravada.',
  'pipeline.motivoDoSalto': 'Motivo do salto de etapas',
  'pipeline.cancelarSalto': 'Cancelar',
  'pipeline.moverComMotivo': 'Mover com este motivo',
  'pv.distribuicao.titulo': 'Distribuição',
  'pv.distribuicao.texto': 'Como os ciclos novos sem responsável chegam a alguém. Ciclos sem responsável ficam na fila "Aguardando responsável" da carteira.',
  'pv.distribuicao.gestor': 'O gestor distribui',
  'pv.distribuicao.gestorTexto': 'Os ciclos novos ficam aguardando até o gestor escolher o responsável.',
  'pv.distribuicao.rodizio': 'Rodízio automático',
  'pv.distribuicao.rodizioTexto': 'Cada ciclo novo vai para a próxima pessoa da lista que estiver com acesso ativo.',
  'pv.distribuicao.quem': 'Quem entra no rodízio',
  'pv.distribuicao.gravada': 'Distribuição gravada.',
  'pv.distribuicao.aguardando': 'Aguardando responsável',
  'pv.distribuicao.motivoDaTroca': 'Motivo da troca (vai para o histórico)',
  'pv.config.diasParaParada': 'Dias para parado',
  'reserva.excecaoAoLimite': 'Exceção ao limite de prorrogações (opcional)',
  'reserva.excecaoAjuda': 'Só preencha se a reserva já usou todas as prorrogações. A justificativa fica no histórico.',
  'reserva.cancelar': 'Cancelar a reserva',
  'reserva.cancelarAjuda': 'A empresa fica livre na hora, sem proteção residual. O histórico é preservado.',
  'org.adm.naoConcedivel': 'Não está disponível para novas concessões.',
  'org.adm.verPermissoes': 'Ver o que este perfil permite',
  'org.adm.planoSoConsulta':
    'Consulta apenas. Plano, módulos e limites são alterados pela administração da plataforma.',
  'org.adm.assinatura': 'Assinatura',
  'org.adm.plano': 'Plano',
  'org.adm.situacaoAssinatura': 'Situação',
  'org.adm.vigente': 'Vigente',
  'org.adm.naoVigente': 'Não vigente',
  'org.adm.vigencia': 'Vigência',
  'org.adm.livres': 'livres',
  'org.adm.modulos': 'Módulos',
  'org.adm.verPlanoEUso': 'Ver plano e uso em detalhe',
  'org.convites.titulo': 'Convites recebidos',
  'org.convites.subtitulo':
    'Organizações que convidaram você. O acesso só começa depois que você aceita.',
  'org.convites.nenhum': 'Nenhum convite pendente',
  'org.convites.por': 'Convidado por',
  'org.convites.vencido': 'Convite vencido — peça para reenviarem',
  'org.convites.valeAte': 'Vale até',
  'org.convites.aceitar': 'Aceitar',
  'org.convites.recusar': 'Recusar',
  'org.convites.aceito': 'Convite aceito. A organização já aparece na sua lista.',
  'org.convites.recusado': 'Convite recusado.',
  'plat.pl.habilitar': 'Habilitar',
  'plat.pl.desabilitar': 'Desabilitar',
  'plat.pl.modalidade': 'Modalidade da concessão',
  'plat.pl.modalidade.ADDITIONAL': 'Adicional contratado',
  'plat.pl.modalidade.TRIAL': 'Trial',
  'plat.pl.modalidade.COMPLIMENTARY': 'Cortesia',
  'plat.pl.modalidade.TEMPORARY': 'Temporária',
  'plat.pl.fimObrigatorio': 'Fim da vigência (obrigatório)',
  'plat.pl.fimOpcional': 'Fim da vigência (opcional)',
  'plat.pl.impactoModulo':
    'As capacidades do módulo passam a valer (ou deixam de valer) na próxima requisição do tenant. Nenhum dado é apagado.',
  'plat.td.editarCadastro': 'Editar cadastro',
  'plat.td.editarImpacto':
    'Altera os dados cadastrais do tenant. Situação e plano têm ações próprias e não mudam aqui.',
  'plat.td.fuso': 'Fuso horário',
  'plat.tn.criar': 'Criar tenant',
  'plat.tn.criarImpacto':
    'Cria a organização com assinatura, pipeline inicial e o proprietário indicado (que não pode ser você). Se o e-mail já tem cadastro, nasce só o vínculo.',
  'plat.tn.criado.ok': 'Tenant criado.',
  'plat.tn.nome': 'Nome',
  'plat.tn.emailDono': 'E-mail do proprietário',
  'plat.tn.nomeDono': 'Nome do proprietário',
  // CERES-ADM-004 — Suporte controlado e auditoria.
  'org.adm.abaSuporte': 'Suporte da plataforma',
  'plat.alerta.casosParados': 'Casos de suporte parados',
  'plat.alerta.sessoesExpirando': 'Sessões de suporte perto de expirar',
  'plat.alerta.trilhaDivergente': 'Trilha de auditoria divergente',
  'plat.ev.detalhe': 'Detalhe do evento',
  'plat.ev.exportar': 'Exportar (CSV mascarado)',
  'plat.ev.origem': 'Origem',
  'plat.ev.origemAutomatizada': 'Rotina automatizada',
  'plat.ev.origemPlataforma': 'Plataforma',
  'plat.ev.origemTenant': 'Tenant',
  'plat.ev.relacionados': 'Eventos da mesma operação',
  'plat.nav.suporte': 'Suporte',
  'sup.titulo': 'Casos de suporte',
  'sup.subtitulo':
    'Atendimentos da plataforma aos tenants. Abrir um caso não dá acesso a nada: o acesso é pedido no caso e aprovado pelo proprietário do tenant.',
  'sup.abrirCaso': 'Abrir caso',
  'sup.abrirImpacto':
    'O caso registra o atendimento e passa a reunir pedidos de acesso, decisões e eventos relacionados. Nenhum dado do tenant fica visível por abrir um caso.',
  'sup.casoAberto': 'Caso aberto.',
  'sup.semCasos': 'Nenhum caso nesta situação',
  'sup.tenant': 'Tenant',
  'sup.motivo': 'Motivo',
  'sup.situacao': 'Situação',
  'sup.prioridade': 'Prioridade',
  'sup.responsavel': 'Responsável',
  'sup.atualizado': 'Atualizado',
  'sup.descricao': 'Descrição administrativa',
  'sup.sessaoAtiva': 'Sessão ativa',
  'sup.caso': 'Caso',
  'sup.caso.aberto': 'Aberto',
  'sup.caso.andamento': 'Em andamento',
  'sup.caso.resolvido': 'Resolvido',
  'sup.caso.encerrado': 'Encerrado',
  'sup.prioridade.baixa': 'Baixa',
  'sup.prioridade.normal': 'Normal',
  'sup.prioridade.alta': 'Alta',
  'sup.prioridade.urgente': 'Urgente',
  'sup.solicitante': 'Solicitante',
  'sup.aberto': 'Aberto em',
  'sup.encerradoEm': 'Encerrado em',
  'sup.correlacoes': 'Correlation ids relacionados',
  'sup.acessos': 'Pedidos de acesso e sessões',
  'sup.semAcessos': 'Nenhum pedido de acesso neste caso.',
  'sup.aprovadoPor': 'aprovado por',
  'sup.expira': 'expira',
  'sup.iniciar': 'Iniciar sessão',
  'sup.abrirSessao': 'Abrir sessão',
  'sup.encerrar': 'Encerrar',
  'sup.historico': 'Histórico do caso',
  'sup.mudarSituacao': 'Mudar situação',
  'sup.pedirAcesso': 'Pedir acesso ao tenant',
  'sup.pedirBreakGlass': 'Acesso emergencial',
  'sup.aprovarBreakGlass': 'Aprovar acesso emergencial',
  'sup.bgSoUrgente': 'Acesso emergencial só para caso urgente.',
  'sup.bgIndisponivel': 'Acesso emergencial indisponível.',
  'sup.acessoImpacto':
    'O pedido vai para o proprietário do tenant, que vê quem pede, por quê, o escopo e o prazo. Só depois da aprovação a sessão pode começar — uma única vez —, e ela expira sozinha.',
  'sup.bgImpacto':
    'Exceção para incidente grave: exige caso urgente, segundo fator verificado, confirmação reforçada e a aprovação de OUTRO administrador da plataforma. O tenant é notificado e pode revogar. Duração máxima de 30 minutos.',
  'sup.continuaBloqueado':
    'Continua bloqueado em qualquer acesso de suporte: empresas, contatos, leads, oportunidades, propostas, atividades e inteligência comercial. Não há exportação.',
  'sup.situacaoImpacto':
    'Caso resolvido ou encerrado encerra também os pedidos e sessões abertos dele.',
  'sup.encerrarImpacto':
    'O acesso termina agora e não pode ser reaberto: um novo acesso exige um novo pedido e uma nova aprovação.',
  'sup.escopoPedido': 'Escopo',
  'sup.duracao': 'Duração',
  'sup.digiteFrase': 'Para confirmar, digite:',
  'sup.escopo.situacao': 'Situação do tenant',
  'sup.escopo.plano': 'Plano e módulos',
  'sup.escopo.pessoas': 'Pessoas e acessos (e-mails mascarados)',
  'sup.escopo.convites': 'Convites',
  'sup.escopo.sessoes': 'Sessões',
  'sup.escopo.integracoes': 'Saúde das integrações (sem segredos)',
  'sup.escopo.erros': 'Erros técnicos sanitizados',
  'sup.escopo.eventos': 'Eventos administrativos',
  'sup.situacao.pedido': 'Aguardando aprovação',
  'sup.situacao.aprovado': 'Aprovado',
  'sup.situacao.ativo': 'Sessão ativa',
  'sup.situacao.encerrado': 'Encerrado',
  'sup.situacao.expirado': 'Expirado',
  'sup.situacao.revogado': 'Revogado',
  'sup.situacao.recusado': 'Recusado',
  'sup.breakGlass': 'Emergencial',
  'sup.faixaSuporte': 'Sessão de suporte',
  'sup.faixaEmergencia': 'ACESSO EMERGENCIAL (break-glass)',
  'sup.expiraEm': 'expira em',
  'sup.encerrarSessao': 'Encerrar sessão',
  'sup.voltarAosCasos': 'Voltar aos casos',
  'sup.sessaoInativa':
    'Esta sessão não está ativa (encerrada, expirada ou revogada). Nada mais pode ser lido por ela.',
  'sup.sessaoEncerrada': 'Sessão de suporte encerrada.',
  'sup.tenantExplicacao':
    'Aqui a equipe da plataforma pede, quando precisa, acesso temporário de diagnóstico a esta organização. Cada pedido mostra quem pede, por quê, o que poderá ver e por quanto tempo. Dados comerciais nunca fazem parte.',
  'sup.soProprietarioDecide': 'Somente um proprietário da organização aprova, recusa ou revoga.',
  'sup.tenantSemPedidos': 'Nenhum pedido de suporte',
  'sup.quemPede': 'Quem pede',
  'sup.oQueVera': 'O que poderá ver',
  'sup.porQuantoTempo': 'Por quanto tempo',
  'sup.aprovar': 'Aprovar',
  'sup.recusar': 'Recusar',
  'sup.revogar': 'Revogar acesso',
  'sup.aprovarImpacto':
    'A aprovação vale para ESTE pedido, uma vez, por até 24 horas para começar. A sessão expira sozinha e pode ser revogada a qualquer momento.',
  'sup.recusarImpacto': 'O pedido é recusado e não pode ser usado.',
  'sup.revogarImpacto': 'O acesso termina imediatamente, inclusive uma sessão em andamento.',
  'sup.saude.titulo': 'Suporte',
  'sup.saude.casosAbertos': 'Casos abertos',
  'sup.saude.casosParados': 'Parados há mais de 7 dias',
  'sup.saude.sessoesAtivas': 'Sessões de suporte ativas',
  'sup.saude.expirando': 'Expirando em 10 minutos',
  'sup.saude.negadas': 'Acessos negados em 24 h',
  'sup.saude.recusadas': 'atos recusados',
  'sup.integ.titulo': 'Integridade da trilha de auditoria',
  'sup.integ.verificar': 'Verificar',
  'sup.integ.selar': 'Selar',
  'sup.integ.ultimaVerificacao': 'Última verificação',
  'sup.integ.ultimoSelo': 'Selada até',
  'sup.integ.falhas': 'Falhas de gravação',
  'sup.integ.retencao': 'Retenção',
  'sup.integ.retencaoIndefinida': 'Indefinida (nada é descartado)',
  'sup.integ.dias': 'dias',
  'sup.integ.integraCurto': 'Íntegra',
  'sup.integ.divergenteCurto': 'Divergente',
  'sup.integ.selada': 'Trilha selada.',
  'sup.integ.integra': 'Trilha íntegra.',
  'sup.integ.divergente': 'A trilha DIVERGE dos selos: investigue.',
  'sup.integ.situacao': 'Situação da cadeia',
  'sup.integ.estado.naoInicializada': 'Não inicializada',
  'sup.integ.estado.naoVerificada': 'Não verificada',
  'sup.integ.estado.valida': 'Válida',
  'sup.integ.estado.invalida': 'Inválida',
  'sup.integ.naoInicializadaAdmin':
    'Nenhum evento está protegido. Use "Selar" para criar o primeiro selo; a rotina periódica continua a cadeia a partir dele.',
  'sup.integ.naoInicializadaSuporte':
    'Nenhum evento está protegido. O primeiro selo é ato do administrador da plataforma.',
  'sup.integ.rotina': 'Selagem periódica',
  'sup.integ.rotinaDesligada': 'Desligada',
  'sup.integ.diaria': 'Diária',
  'sup.integ.aCada': 'A cada',
  'sup.integ.proxima': 'Próxima execução:',
  'sup.integ.proximaPendente': 'na próxima passada do executor',
  'sup.integ.ultimaExecucao': 'Última execução da rotina',
  'sup.integ.nuncaExecutada': 'Ainda não executada',
  'sup.integ.res.selado': 'Selou',
  'sup.integ.res.semEventos': 'Sem eventos novos',
  'sup.integ.res.naoInicializada': 'Não inicializada',
  'sup.integ.res.cadeiaInvalida': 'Cadeia inválida — não selou',
  'sup.integ.res.falha': 'Falhou',
  'plat.alerta.trilhaNaoSelada': 'Trilha de auditoria sem selo inicial',
  'plat.alerta.selagemFalhou': 'Selagem periódica falhou',
  'usuarios.semSenhaNaCriacao':
    'Sem senha: a pessoa recebe o convite e define a própria senha pelo link. O acesso só começa depois disso.',
  'org.adm.avisoIdentidadeExistente':
    'Esta pessoa já tem cadastro no Ceres: o convite aparece para ela em "Convites recebidos" e só vale depois do aceite.',
  'org.adm.emailEnviado': 'Aviso por e-mail enviado.',
  'org.adm.emailPendente':
    'Aviso por e-mail NÃO enviado: a entrega de e-mail não está configurada neste ambiente. O aviso interno foi mantido; avise a pessoa por outro canal.',
  'org.adm.emailFalhou':
    'O envio do aviso por e-mail FALHOU. O aviso interno foi mantido; reenvie o convite ou avise a pessoa por outro canal.',
  'org.convites.perfilProposto': 'Perfil proposto:',
  'plat.tn.conviteDoDono':
    'O proprietário foi CONVIDADO: o tenant só terá proprietário ativo depois que ele aceitar.',
  // CERES-ADM-004-CORRECAO-02 — contexto neutro e convites sem tenant ativo.
  'ctx.titulo': 'Escolha como continuar',
  'ctx.opcoes': 'Onde você pode entrar',
  'ctx.subtituloComConvites':
    'Você ainda não tem uma organização ativa. Aceite um convite para começar — o acesso só existe depois do aceite.',
  'ctx.subtituloConvitesVencidos':
    'Você ainda não tem uma organização ativa, e os convites abaixo venceram. Peça a quem administra a organização que reenvie o convite.',
  'ctx.convitesDestaque': 'Você recebeu convites para participar de organizações',
  'ctx.convidadoEm': 'em',
  'ctx.vencidoOrientacao':
    'Este convite venceu e não pode mais ser aceito. Peça a quem administra a organização que o reenvie.',
  'ctx.aceito': 'Acesso confirmado. A organização agora está disponível para você.',
  'ctx.recusado': 'Convite recusado. Nenhum acesso foi criado.',
  'ctx.entrarNaOrganizacao': 'Entrar na organização',
  'ctx.voltarAEscolha': 'Voltar à escolha',
  'ctx.semOpcao.titulo': 'Nenhuma organização disponível',
  'ctx.semOpcao.contaExiste': 'Sua conta existe e a entrada foi confirmada.',
  'ctx.semOpcao.semOrganizacao': 'No momento você não tem acesso ativo a nenhuma organização.',
  'ctx.semOpcao.semConvite': 'Também não há convite pendente para você.',
  'ctx.semOpcao.peca':
    'Peça acesso a quem administra a organização. Quando o convite chegar, ele aparecerá aqui.',
  'plat.voltarAEscolha': 'Escolha de contexto e convites',
  // CERES-WA-001 — Integrações · WhatsApp Cloud API
  'plat.nav.whatsapp': 'Integrações · WhatsApp',
  'plat.alerta.whatsappIncompleto': 'WhatsApp ligado sem configuração completa',
  'plat.alerta.whatsappPausado': 'Filas do WhatsApp pausadas',
  'plat.alerta.whatsappCartasMortas': 'WhatsApp com cartas mortas',
  'plat.alerta.whatsappAutenticacao': 'WhatsApp: falha de autenticação na Meta',
  'plat.alerta.whatsappFilaParada': 'Fila do WhatsApp parada',
  'wa.titulo': 'Integrações · WhatsApp Cloud API',
  'wa.subtitulo':
    'Canal oficial e central da plataforma na API oficial da Meta. Fundação técnica: recebe, valida e guarda notificações; não executa comandos no CRM.',
  'wa.aviso.desabilitado': 'Integração desabilitada.',
  'wa.aviso.naoConfigurado':
    'Integração ligada, mas sem configuração completa: o canal está fechado.',
  'wa.aviso.semEfeito':
    'Nenhuma mensagem é recebida como legítima nem enviada à Meta até que as credenciais oficiais existam e a ativação seja autorizada.',
  'wa.documentacao': 'Documentação de ativação',
  'wa.secao.situacao': 'Situação',
  'wa.secao.configuracao': 'Configuração',
  'wa.secao.atividade': 'Atividade',
  'wa.secao.filas': 'Filas',
  'wa.secao.cartasMortas': 'Cartas mortas',
  'wa.secao.erros': 'Erros recentes (sanitizados)',
  'wa.secao.retencao': 'Retenção',
  'wa.secao.pendencias': 'Pendências operacionais',
  'wa.sit.desabilitado': 'Desabilitado',
  'wa.sit.naoConfigurado': 'Não configurado',
  'wa.sit.configurado': 'Configurado',
  'wa.sit.verificado': 'Verificado',
  'wa.sit.ativo': 'Ativo',
  'wa.sit.degradado': 'Degradado',
  'wa.sit.suspenso': 'Suspenso',
  'wa.item.configurado': 'Configurado',
  'wa.item.ausente': 'Ausente',
  'wa.item.invalido': 'Inválido',
  'wa.cfg.appSecret': 'App Secret',
  'wa.cfg.verifyToken': 'Verify Token do webhook',
  'wa.cfg.accessToken': 'Access Token (usuário do sistema)',
  'wa.cfg.phoneNumberId': 'Phone Number ID',
  'wa.cfg.wabaId': 'WABA ID (conta do WhatsApp Business)',
  'wa.cfg.appId': 'App ID',
  'wa.cfg.versao': 'Versão da Graph API',
  'wa.semValores': 'Os valores nunca aparecem aqui — só se cada item está configurado.',
  'wa.canal': 'Canal',
  'wa.habilitado': 'Habilitado',
  'wa.configuracaoCompleta': 'Configuração completa',
  'wa.numero': 'Número (mascarado)',
  'wa.processamento': 'Processamento',
  'wa.pausado': 'Pausado',
  'wa.emAndamento': 'Em andamento',
  'wa.urlDoWebhook': 'URL do webhook a cadastrar na Meta',
  'wa.semUrlPublica': 'PUBLIC_BASE_URL não definida: complete com o domínio público.',
  'wa.verificacao': 'Handshake verificado',
  'wa.naoVerificado': 'Ainda não verificado',
  'wa.ultimoHandshake': 'Último handshake',
  'wa.ultimoWebhookValido': 'Último webhook válido',
  'wa.ultimaAssinaturaInvalida': 'Última assinatura inválida',
  'wa.registradas': 'registradas (amostragem de 1 s)',
  'wa.ultimoEnvio': 'Último envio aceito',
  'wa.ultimaFalhaDeAutenticacao': 'Última falha de autenticação',
  'wa.ultimoErro': 'Último erro',
  'wa.ultimoTeste': 'Último teste de configuração',
  'wa.nunca': 'Nunca',
  'wa.sim': 'Sim',
  'wa.nao': 'Não',
  'wa.dias': 'dias',
  'wa.tentativas': 'tentativa(s)',
  'wa.entrada': 'Entrada',
  'wa.saida': 'Saída',
  'wa.descartado': 'Descartado',
  'wa.fila.pendentes': 'Entrada pendente',
  'wa.fila.processando': 'Processando',
  'wa.fila.retentando': 'Retentando',
  'wa.fila.processados': 'Processados',
  'wa.fila.ignorados': 'Ignorados',
  'wa.fila.cartaMorta': 'Cartas mortas',
  'wa.fila.duplicidades': 'Duplicidades',
  'wa.fila.maisAntigo': 'Pendente mais antigo',
  'wa.saida.aguardando': 'Outbox aguardando',
  'wa.saida.aceitas': 'Aceitas pela Meta',
  'wa.saida.entregues': 'Enviadas · entregues · lidas',
  'wa.saida.falhas': 'Falhas',
  'wa.saida.canceladas': 'Canceladas',
  'wa.saida.legenda':
    '“Aceita pela Meta” não é “entregue”: a entrega e a leitura chegam depois, por webhook.',
  'wa.semCartasMortas': 'Nenhuma carta morta.',
  'wa.semErros': 'Nenhum erro registrado.',
  'wa.semCredenciais': 'Indisponível sem credenciais completas e habilitadas.',
  'wa.ret.expurgo': 'Expurgo periódico',
  'wa.ret.conteudo': 'Texto das mensagens',
  'wa.ret.eventos': 'Eventos concluídos',
  'wa.ret.cartaMorta': 'Trecho bruto em carta morta',
  'wa.ret.mensagens': 'Metadados de mensagens',
  'wa.ret.ultima': 'Última execução',
  'wa.pend.contaMeta': 'Conta Meta ainda não criada.',
  'wa.pend.credenciais': 'Credenciais Meta ainda não disponíveis.',
  'wa.pend.webhook': 'Webhook ainda não registrado na Meta.',
  'wa.pend.numero': 'Número oficial ainda não registrado.',
  'wa.pend.email': 'Provedor de e-mail ainda não configurado.',
  'wa.pend.segundoAdmin': 'Segundo PLATFORM_ADMIN com MFA ainda recomendado.',
  'wa.pend.selo': 'Primeiro selo da auditoria depende de ação administrativa, se ainda pendente.',
  'wa.ato.pausar': 'Pausar processamento',
  'wa.ato.retomar': 'Retomar processamento',
  'wa.ato.testar': 'Testar configuração',
  'wa.ato.declarar': 'Declarar mudança de configuração',
  'wa.ato.reprocessar': 'Reprocessar',
  'wa.ato.descartar': 'Descartar',
  'wa.ato.pausarImpacto':
    'As filas de entrada e de saída param de ser processadas. O webhook continua recebendo e gravando: nada se perde, e tudo volta a andar ao retomar.',
  'wa.ato.retomarImpacto': 'As filas voltam a ser processadas na próxima passada.',
  'wa.ato.testarImpacto': 'Lê os dados do próprio número na Graph API. Nenhuma mensagem é enviada.',
  'wa.ato.declararImpacto':
    'Registra na trilha que a configuração foi alterada no ambiente (rotação de segredo, retenção ou versão). Nenhum valor é gravado.',
  'wa.ato.reprocessarImpacto':
    'Devolve o item à fila. A chave de idempotência é mantida: o efeito não se duplica.',
  'wa.ato.descartarImpacto':
    'Descarta definitivamente a carta morta. O trecho guardado é apagado; o registro técnico fica para a idempotência.',
  'wa.ato.descartarMotivo': 'O descarte exige motivo com pelo menos 10 caracteres.',
  'wa.decl.rotulo': 'O que foi alterado',
  'wa.decl.accessToken': 'Rotação do Access Token',
  'wa.decl.appSecret': 'Rotação do App Secret',
  'wa.decl.verifyToken': 'Rotação do Verify Token',
  'wa.decl.retencao': 'Mudança de retenção',
  'wa.decl.versao': 'Mudança de versão da Graph API',
  'wa.saude.titulo': 'WhatsApp Cloud API',
  'wa.saude.naoConectado':
    'Nunca conectado: sem credenciais e ativação, os indicadores não dizem nada sobre a integração.',
  'wa.saude.eventos': 'Eventos recebidos',
  'wa.saude.duplicadas': 'duplicadas',
  'wa.saude.filas': 'Pendentes · processando · retentando',
  'wa.saude.outbox': 'Outbox aguardando · enviando',
  'wa.saude.falhas': 'Falhas de autenticação · permanentes (24 h)',
  // CERES-WA-002 — Meu WhatsApp, uso por tenant e vínculos
  'meuwa.titulo': 'Meu WhatsApp',
  'meuwa.indisponivel': 'A conexão com o WhatsApp ainda não está disponível neste ambiente.',
  'meuwa.situacao.conectado': 'Conectado',
  'meuwa.situacao.suspenso': 'Suspenso',
  'meuwa.situacao.emAnalise': 'Em análise',
  'meuwa.emAnaliseOrientacao': 'Este vínculo está em análise. Procure o suporte da plataforma.',
  'meuwa.numero': 'Número',
  'meuwa.vinculadoEm': 'Conectado em',
  'meuwa.conectar': 'Conectar meu WhatsApp',
  'meuwa.trocarNumero': 'Trocar organização ou número',
  'meuwa.cancelarCodigo': 'Cancelar código',
  'meuwa.codigoPendente': 'Há um código pendente, válido até',
  'meuwa.instrucao':
    'Envie este código, do seu celular, para o número oficial do Ceres no WhatsApp. O número que enviar será conectado à sua conta.',
  'meuwa.numeroOficial': 'Número oficial do Ceres',
  'meuwa.expiraEm': 'Válido até',
  'meuwa.usoUnico': 'uso único',
  'meuwa.copiar': 'Copiar mensagem',
  'meuwa.abrirWhatsapp': 'Abrir no WhatsApp',
  'meuwa.codigoCopiado': 'Código copiado.',
  'meuwa.copiaFalhou': 'Não foi possível copiar. Selecione e copie o código.',
  'meuwa.codigoCancelado': 'Código cancelado.',
  'meuwa.desconectar': 'Desconectar WhatsApp',
  'meuwa.desconectarTitulo': 'Desconectar o WhatsApp?',
  'meuwa.desconectarMensagem':
    'O número deixa de ser reconhecido imediatamente. Para usar de novo, será preciso conectar com um novo código.',
  'meuwa.desconectado': 'WhatsApp desconectado.',
  'meuwa.usoPorOrganizacao': 'Uso por organização',
  'meuwa.usoPorOrganizacaoDica':
    'O número é seu; aqui você escolhe em quais organizações ele pode ser usado.',
  'meuwa.uso.habilitado': 'Habilitado',
  'meuwa.uso.desabilitadoPorVoce': 'Desabilitado por você',
  'meuwa.uso.bloqueado': 'Bloqueado pela organização',
  'meuwa.habilitarAqui': 'Habilitar aqui',
  'meuwa.desabilitarAqui': 'Desabilitar aqui',
  'org.adm.abaWhatsapp': 'WhatsApp',
  'orgwa.explicacao':
    'O número é de cada pessoa e só ela o conecta. Aqui você vê quem está conectado e decide se o uso é permitido nesta organização — sem afetar as outras organizações da pessoa.',
  'orgwa.vazio': 'Nenhuma pessoa nesta organização.',
  'orgwa.conectado': 'Conectado',
  'orgwa.naoConectado': 'Não conectado',
  'orgwa.suspenso': 'Suspenso nesta organização',
  'orgwa.ultimaVerificacao': 'Última verificação',
  'orgwa.suspender': 'Suspender uso aqui',
  'orgwa.permitir': 'Permitir uso aqui',
  'orgwa.suspenderTitulo': 'Suspender o uso do WhatsApp nesta organização',
  'orgwa.permitirTitulo': 'Permitir o uso do WhatsApp nesta organização',
  'orgwa.suspenderImpacto':
    'As mensagens da pessoa deixam de valer para esta organização. O número dela e as outras organizações não mudam.',
  'orgwa.permitirImpacto':
    'As mensagens da pessoa voltam a valer para esta organização, se ela mesma tiver habilitado o uso.',
  'wa.secao.identidade': 'Identidade e vínculos',
  'wa.secao.chaves': 'Chaves de proteção',
  'wa.secao.vinculos': 'Vínculos de número',
  'wa.vin.pendentes': 'Códigos pendentes',
  'wa.vin.verificados': 'Verificados',
  'wa.vin.suspensos': 'Suspensos',
  'wa.vin.revogados': 'Revogados',
  'wa.vin.emQuarentena': 'Em quarentena',
  'wa.vin.pendente': 'Pendente',
  'wa.vin.verificado': 'Verificado',
  'wa.vin.suspenso': 'Suspenso',
  'wa.vin.revogado': 'Revogado',
  'wa.vin.quarentena': 'Quarentena',
  'wa.seg.falhasCripto': 'Falhas de descriptografia',
  'wa.seg.colisoes': 'Tentativas de colisão',
  'wa.seg.limite': 'Bloqueios por limite',
  'wa.seg.invalidos': 'Códigos inválidos',
  'wa.seg.janela': 'Eventos de segurança dos últimos 7 dias.',
  'wa.cfg.chaveDeDados': 'Chave de criptografia dos dados',
  'wa.cfg.chaveDeBusca': 'Chave do índice de busca',
  'wa.cfg.pepper': 'Pepper dos códigos de vinculação',
  'wa.cfg.numeroOficial': 'Número oficial (exibição)',
  'wa.chaves.ativa': 'ativa',
  'wa.chaves.aceitas': 'aceitas',
  'wa.chaves.nuncaAparecem':
    'Só a situação e os identificadores de versão. O material das chaves nunca aparece.',
  'wa.semVinculos': 'Nenhum vínculo registrado.',
  'wa.urlNaoConfigurada': 'URL pública não configurada',
  'wa.urlInvalida': 'URL pública inválida',
  'wa.caminhoInterno': 'Caminho interno a encaminhar',
  'wa.pend.chaves': 'Chaves de criptografia, de busca e pepper ainda não geradas.',
  'wa.pend.url': 'URL pública do webhook ainda não definida.',
  'wa.ato.quarentena': 'Quarentena',
  'wa.ato.retirarQuarentena': 'Retirar da quarentena',
  'wa.ato.revogar': 'Revogar',
  'wa.ato.quarentenaImpacto':
    'O número deixa de identificar a pessoa em todos os tenants até a plataforma revisar. Nada é apagado.',
  'wa.ato.retirarQuarentenaImpacto':
    'O vínculo volta a valer como verificado. Recusado se o número ou a pessoa já tiver outro vínculo ativo.',
  'wa.ato.revogarImpacto':
    'O vínculo é encerrado por segurança, com efeito imediato. Para voltar, a pessoa precisa de um novo código.',
  // CERES-WA-002-CORREÇÃO-01 — configuração global do WhatsApp
  'wag.soAdministrador':
    'A configuração global do WhatsApp é mantida apenas pelo administrador da plataforma.',
  'wag.secao.situacao': 'Situação geral',
  'wag.secao.identificacao': 'Identificação Meta',
  'wag.secao.credenciais': 'Credenciais protegidas',
  'wag.secao.acoes': 'Ações operacionais',
  'wag.secao.resultado': 'Resultado',
  'wag.ativa': 'Ativa',
  'wag.inativa': 'Inativa',
  'wag.op.inativa': 'Inativa',
  'wag.op.operacional': 'Operacional',
  'wag.op.degradada': 'Degradada',
  'wag.op.erro': 'Com erro',
  'wag.tokenConfigurado': 'Token configurado',
  'wag.validadeDoToken': 'Validade do token',
  'wag.tok.naoValidado': 'Não validado',
  'wag.tok.valido': 'Válido',
  'wag.tok.expirado': 'Expirado',
  'wag.tok.invalido': 'Inválido',
  'wag.numeroOficialConfigurado': 'Número oficial configurado',
  'wag.wabaConfigurada': 'WABA configurada',
  'wag.webhookNoCeres': 'Webhook configurado no Ceres',
  'wag.handshakeDaMeta': 'Handshake da Meta',
  'wag.naoConfirmado': 'Ainda não confirmado',
  'wag.appNaWaba': 'Aplicativo inscrito na WABA',
  'wag.assinaturaMessages': 'Assinatura de messages',
  'wag.endpointPublico': 'Endpoint público do webhook',
  'wag.naoConsultado': 'Não consultado',
  'wag.ultimaConexao': 'Última conexão bem-sucedida',
  'wag.ultimoWebhook': 'Último webhook recebido',
  'wag.ultimoEnvio': 'Último envio',
  'wag.ultimoErro': 'Último erro (sanitizado)',
  'wag.ultimaAlteracao': 'Última alteração',
  'wag.por': 'por',
  'wag.proximoPasso': 'Próximo passo',
  'wag.impedimentos': 'O que impede a ativação',
  'wag.passo.rotacionar':
    'Substituir a credencial marcada com rotação obrigatória (exposição confirmada).',
  'wag.passo.preencher': 'Completar a identificação Meta, o App Secret e o verify token.',
  'wag.passo.substituirToken':
    'Substituir o token de acesso pelo token PERMANENTE do usuário do sistema e testar as credenciais.',
  'wag.passo.testarCredenciais': 'Testar as credenciais (App Secret e token de acesso).',
  'wag.passo.testarWebhook': 'Testar o webhook (endpoint público).',
  'wag.passo.ativar': 'Ativar a integração.',
  'wag.passo.webhookNaMeta':
    'Configurar o webhook no painel da Meta (URL de callback e verify token) e clicar em Verificar e salvar.',
  'wag.passo.inscrever': 'Inscrever o aplicativo na WABA.',
  'wag.passo.messages': 'Assinar o campo messages do webhook no painel da Meta.',
  'wag.passo.nenhum': 'Nenhum: a integração está completa.',
  'wag.imp.tokenAusente': 'Token de acesso não configurado.',
  'wag.imp.tokenNaoValidado': 'Token de acesso ainda não validado (use Testar credenciais).',
  'wag.imp.tokenExpirado': 'Token de acesso expirado.',
  'wag.imp.tokenInvalido': 'Token de acesso inválido.',
  'wag.imp.appSecretAusente': 'App Secret não configurado.',
  'wag.imp.appSecretInvalido': 'App Secret inválido.',
  'wag.imp.verifyAusente': 'Verify token não configurado.',
  'wag.imp.appId': 'App ID ausente ou inválido.',
  'wag.imp.waba': 'WABA ID ausente ou inválido.',
  'wag.imp.phone': 'Phone Number ID ausente ou inválido.',
  'wag.imp.versao': 'Versão da Graph API ausente ou inválida.',
  'wag.imp.numero': 'Número oficial ausente ou inválido.',
  'wag.imp.url': 'URL pública do webhook ausente ou inválida.',
  'wag.imp.chaves': 'Chaves de criptografia do ambiente ausentes ou inválidas.',
  'wag.imp.descriptografia': 'Um segredo gravado não pôde ser descriptografado.',
  'wag.imp.rotacao': 'Há credencial com rotação obrigatória.',
  'wag.imp.endpoint': 'Endpoint público ainda não confirmado (use Testar webhook).',
  'wag.numeroOficial': 'Número oficial (exibição — só dígitos, com país)',
  'wag.urlPublica': 'URL pública do webhook',
  'wag.urlDica': 'Sem "www": o certificado atual não cobre esse host.',
  'wag.naoSaoSegredos': 'Estes identificadores não são segredos.',
  'wag.seg.naoConfigurado': 'Não configurado',
  'wag.seg.configurado': 'Configurado',
  'wag.seg.invalido': 'Inválido',
  'wag.seg.expirado': 'Expirado',
  'wag.rotacaoObrigatoria': 'Rotação obrigatória',
  'wag.substituir': 'Substituir',
  'wag.manterAtual': 'Manter o atual',
  'wag.definidoEm': 'Definido em',
  'wag.validadoEm': 'validado em',
  'wag.novoValor': 'Novo valor (não será exibido depois de salvo)',
  'wag.writeOnly':
    'Valores secretos nunca são exibidos nem preenchidos. Campo vazio mantém o valor atual.',
  'wag.ato.salvar': 'Salvar configuração',
  'wag.ato.testarCredenciais': 'Testar credenciais',
  'wag.ato.testarNumero': 'Testar número',
  'wag.ato.testarWebhook': 'Testar webhook',
  'wag.ato.gerarVerify': 'Gerar novo verify token',
  'wag.ato.ativar': 'Ativar integração',
  'wag.ato.desativar': 'Desativar integração',
  'wag.ato.inscrever': 'Inscrever aplicativo na WABA',
  'wag.ato.consultar': 'Consultar assinatura da WABA',
  'wag.ato.atualizar': 'Atualizar situação',
  'wag.impacto.salvar':
    'Grava a identificação e, se informados, os novos segredos. O canal passa a usar os valores novos sem reinício.',
  'wag.impacto.substituiAppSecret':
    'O App Secret atual será SUBSTITUÍDO e precisará ser testado de novo.',
  'wag.impacto.substituiToken':
    'O token de acesso atual será SUBSTITUÍDO e precisará ser testado de novo.',
  'wag.impacto.testarCredenciais':
    'Consulta a Meta com o App Secret e o token gravados (debug_token). Nenhuma mensagem é enviada.',
  'wag.impacto.testarNumero': 'Lê o número oficial na Graph API. Nenhuma mensagem é enviada.',
  'wag.impacto.testarWebhook':
    'Chama o endpoint público do webhook pela internet, sem token. Não conta como handshake da Meta.',
  'wag.impacto.gerarVerify':
    'Um novo verify token substitui o atual IMEDIATAMENTE: o valor anterior deixa de valer e o handshake registrado é descartado. Será preciso atualizar o valor no painel da Meta e verificar de novo.',
  'wag.impacto.ativar':
    'O canal passa a aceitar notificações da Meta e a enviar mensagens. Recusado com token não validado ou expirado, configuração incompleta ou endpoint público indisponível.',
  'wag.impacto.desativar':
    'O canal fecha na hora: o webhook recusa e nada é enviado. As filas são preservadas.',
  'wag.impacto.inscrever': 'Inscreve o aplicativo do Ceres na WABA (ato com efeito na Meta).',
  'wag.impacto.consultar':
    'Consulta na Meta a inscrição do aplicativo na WABA e a assinatura de messages. Nada é alterado.',
  'wag.res.resultado': 'Resultado',
  'wag.res.expiraEm': 'Expira em',
  'wag.res.nuncaExpira': 'Sem expiração',
  'wag.res.escoposAusentes': 'Permissões ausentes',
  'wag.res.numero': 'Número (mascarado)',
  'wag.res.qualidade': 'Qualidade',
  'wag.res.status': 'HTTP',
  'wag.res.callback': 'URL de callback na Meta confere',
  'wag.res.erro': 'Erro',
  'wag.res.detalhe': 'Detalhe',
  'wag.vt.titulo': 'Novo verify token — exibição única',
  'wag.vt.aviso':
    'Copie agora e cole no painel da Meta (Webhooks → Verificar e salvar). Este valor não será exibido de novo.',
  'wag.vt.copiar': 'Copiar',
  'wag.vt.copiado': 'Copiado',
  'wag.vt.fechar': 'Já copiei — fechar',
  // CERES-WA-003
  'meuwa.gerarCodigo': 'Gerar código de vinculação',
  'meuwa.escolhaOrganizacao': 'Organização que o WhatsApp vai consultar',
  'meuwa.organizacao': 'Organização',
  'meuwa.organizacaoAtiva': 'Organização ativa',
  'meuwa.ultimoUso': 'Último uso',
  'meuwa.envie': 'Envie esta mensagem pelo WhatsApp para',
  'meuwa.trocarTitulo': 'Trocar organização ou número?',
  'meuwa.trocarMensagem':
    'Você já tem um WhatsApp conectado. Um novo código, quando confirmado pelo celular, passa a valer para a organização escolhida (ou para o número que enviar) e substitui a vinculação atual.',
  'meuwa.trocarConfirmar': 'Gerar novo código',
  'wa.secao.conversa': 'Conversa pelo WhatsApp',
  'wa.conv.recebidas': 'Mensagens recebidas',
  'wa.conv.interpretadas': 'Mensagens interpretadas',
  'wa.conv.comandos': 'Consultas executadas',
  'wa.conv.naoVinculados': 'Remetentes não vinculados',
  'wa.conv.enfileiradas': 'Respostas enfileiradas',
  'wa.conv.aguardando': 'Respostas aguardando',
  'wa.conv.aceitas': 'Aceitas pela Meta',
  'wa.conv.entregues': 'Entregues',
  'wa.conv.falhas': 'Falhas de resposta',
  'wa.conv.ultimoComando': 'Último comando',
  'wa.conv.semTexto':
    'Só contagens, nomes de comando e desfechos — nunca o texto das mensagens nem o telefone.',
  'wa.cod.pendentes': 'Códigos pendentes',
  'wa.cod.usados': 'Usados',
  'wa.cod.expirados': 'Expirados',
  'wa.cod.revogados': 'Cancelados',
  'wa.cod.bloqueados': 'Bloqueados',
  'wa.vin.semOrganizacao': 'Sem organização ativa',
  'wa.vin.ultimoUso': 'Último uso:',
  'wa.pend.desativada': 'Integração desativada.',
  'wa.pend.token': 'Token de acesso não validado ou expirado.',
  'wa.pend.waba': 'Aplicativo não inscrito na WABA.',
  'wa.pend.messages': 'Campo messages não assinado no webhook da Meta.',
  // CERES-WA-004
  'wa.ctx.ativos': 'Conversas com contexto ativo',
  'wa.ctx.expirados': 'Contextos expirados',
  'wa.ctx.selecoes': 'Seleções por número',
  'wa.ctx.empresas': 'Consultas de empresa',
  'wa.ctx.contatos': 'Consultas de contato',
  'wa.ctx.oportunidades': 'Consultas de oportunidade',
  'wa.ctx.atividades': 'Consultas de atividade',
  'wa.ctx.pipeline': 'Consultas de pipeline',
  'wa.ctx.escrita': 'Pedidos de escrita recusados',
  'wa.ctx.naoCompreendidos': 'Comandos não compreendidos',
  'wa.ctx.tempoMedio': 'Tempo médio de processamento (7 dias)',
  'wa.acoes.titulo': 'Alterações pelo WhatsApp (todas exigem confirmação)',
  'wa.acoes.propostas': 'Propostas geradas',
  'wa.acoes.aguardando': 'Aguardando confirmação',
  'wa.acoes.executadas': 'Confirmadas e executadas',
  'wa.acoes.canceladas': 'Canceladas',
  'wa.acoes.expiradas': 'Expiradas',
  'wa.acoes.recusadas': 'Recusadas ou com falha',
  'wa.acoes.duplicidades': 'Duplicidades bloqueadas',
  'wa.acoes.conflitos': 'Conflitos de concorrência',
  'wa.acoes.desfeitas': 'Desfazimentos',
  'wa.acoes.porTipo': 'Executadas por tipo',
  'wa.acoes.falhasRecentes': 'Recusas e falhas recentes',
  // CERES-WA-006: IA conversacional e áudio
  'wai.secao.ia': 'Inteligência conversacional',
  'wai.secao.audio': 'Áudio e transcrição',
  'wai.secao.protecoes': 'Proteções',
  'wai.secao.pessoas': 'Liberação por pessoa (ativação gradual)',
  'wai.secao.orcamento': 'Orçamento',
  'wai.secao.configuracaoIa': 'Configuração da IA: limites e orçamentos',
  'wai.secao.configuracaoAudio': 'Configuração do áudio e da transcrição',
  'wai.sit.ambienteIndisponivel': 'Ambiente indisponível',
  'wai.sit.habilitada': 'Habilitada',
  'wai.sit.desabilitada': 'Desabilitada',
  'wai.sit.habilitado': 'Habilitado',
  'wai.sit.desabilitado': 'Desabilitado',
  'wai.sit.pausada': 'Pausada',
  'wai.sit.pausado': 'Pausado',
  'wai.sit.pronto': 'Pronto para transcrever',
  'wai.sit.naoPronto': 'Configuração incompleta',
  'wai.circuito': 'Proteção contra falhas em sequência',
  'wai.circuito.fechado': 'Normal',
  'wai.circuito.abertoAte': 'Circuito aberto até',
  'wai.provedor': 'Provedor',
  'wai.saiDaRede':
    'A mensagem interpretada sai da rede do Ceres: é enviada ao serviço do provedor de IA (Anthropic, pela assinatura).',
  'wai.provedor.nenhum': 'Nenhum',
  'wai.provedor.openaiCompativel': 'Compatível com OpenAI',
  'wai.modelo': 'Modelo',
  'wai.endpoint': 'Endpoint',
  'wai.chave': 'Chave de transcrição',
  'wai.chave.ausente': 'Ausente',
  'wai.chave.configurada': 'Configurada',
  'wai.chave.invalida': 'Inválida',
  'wai.formatos': 'Formatos aceitos',
  'wai.duracaoMaxima': 'Duração máxima',
  'wai.tamanhoMaximo': 'Tamanho máximo',
  'wai.retencao': 'Retenção do áudio',
  'wai.versaoDoPrompt': 'Versão do prompt',
  'wai.versaoDoCatalogo': 'Versão do catálogo de ferramentas',
  'wai.ultimoSucesso': 'Última chamada bem-sucedida',
  'wai.ultimaTranscricao': 'Última transcrição bem-sucedida',
  'wai.ultimaFalha': 'Última falha',
  'wai.latenciaMedia': 'Latência média (hoje)',
  'wai.orc.chamadasHoje': 'Chamadas hoje',
  'wai.orc.chamadasNoMes': 'Chamadas no mês',
  'wai.orc.custoHoje': 'Custo hoje',
  'wai.orc.custoNoMes': 'Custo no mês',
  'wai.orc.restante': 'Chamadas restantes no mês',
  'wai.orc.minutosNoMes': 'Minutos de áudio no mês',
  'wai.orc.atingido': 'Orçamento atingido',
  'wai.consumo.metrica': 'Métrica',
  'wai.consumo.hoje': 'Hoje',
  'wai.consumo.mes': 'Mês',
  'wai.consumo.chamadas': 'Chamadas',
  'wai.consumo.sucessos': 'Sucessos',
  'wai.consumo.falhas': 'Falhas',
  'wai.consumo.timeouts': 'Tempo esgotado',
  'wai.consumo.tokensDeEntrada': 'Tokens de entrada',
  'wai.consumo.tokensDeSaida': 'Tokens de saída',
  'wai.consumo.custo': 'Custo estimado',
  'wai.consumo.pelaIA': 'Mensagens interpretadas pela IA',
  'wai.consumo.audiosRecebidos': 'Áudios recebidos',
  'wai.consumo.audiosRecusados': 'Áudios recusados',
  'wai.consumo.minutos': 'Minutos de áudio',
  'wai.teste.ok': 'Funcionou',
  'wai.teste.falhou': 'Falhou',
  'wai.teste.codigo': 'Código',
  'wai.teste.latencia': 'Latência',
  'wai.teste.intencao': 'Intenção reconhecida',
  'wai.teste.ferramentas': 'Ferramentas propostas',
  'wai.teste.frase': 'Frase de teste',
  'wai.teste.nadaExecutado':
    'Teste com frase sintética: nada foi executado nem gravado no CRM, e nenhuma mensagem foi enviada.',
  'wai.teste.semAudio':
    'O teste confere endpoint, credencial e modelo sem enviar o áudio de ninguém.',
  'wai.frase.atividades': 'Quais são minhas atividades de hoje?',
  'wai.frase.tarefa': 'Crie uma tarefa de teste para amanhã às 10h.',
  'wai.frase.panorama': 'Me dá um panorama comercial.',
  'wai.prot.fallback': 'Interpretação determinística de reserva',
  'wai.prot.sempreAtivo': 'Sempre ativa',
  'wai.prot.inativo': 'Inativa',
  'wai.prot.saidasInvalidas': 'Saídas inválidas do modelo',
  'wai.prot.injecoes': 'Tentativas de injeção bloqueadas',
  'wai.prot.ambiguidades': 'Ambiguidades devolvidas à pessoa',
  'wai.prot.deterministicoNaFila': 'Respondidas sem IA por fila cheia',
  'wai.prot.circuitoIa': 'Proteção contra falhas (IA)',
  'wai.prot.circuitoAudio': 'Proteção contra falhas (transcrição)',
  'wai.prot.limites': 'Limites de uso',
  'wai.prot.dentroDoLimite': 'Dentro do limite',
  'wai.prot.filaPendente': 'Fila pendente',
  'wai.prot.falhasNaSemana': 'Falhas nos últimos 7 dias',
  'wai.prot.orfaos': 'Arquivos temporários órfãos removidos',
  'wai.prot.legenda':
    'Contagens no formato hoje · mês. A interpretação determinística responde sempre que a IA não pode ou não deve responder.',
  'wai.erros.titulo': 'Erros recentes por código',
  'wai.erros.vazio': 'Nenhum erro recente.',
  'wai.erros.tipo': 'Tipo',
  'wai.erros.codigo': 'Código',
  'wai.erros.quantidade': 'Quantidade',
  'wai.erros.ultimaVez': 'Última vez',
  'wai.pes.legenda':
    'A IA e o áudio só valem para quem for liberado aqui; os demais seguem com a interpretação determinística.',
  'wai.pes.vazio': 'Nenhuma pessoa com WhatsApp vinculado.',
  'wai.pes.pessoa': 'Pessoa',
  'wai.pes.organizacao': 'Organização',
  'wai.pes.numero': 'Número',
  'wai.pes.ia': 'IA',
  'wai.pes.audio': 'Áudio',
  'wai.pes.liberar': 'Liberar',
  'wai.pes.retirar': 'Retirar',
  'wai.cfg.habilitarIa': 'Interpretação por IA habilitada',
  'wai.cfg.habilitarAudio': 'Mensagens de áudio habilitadas',
  'wai.cfg.tempoLimite': 'Tempo-limite da chamada',
  'wai.cfg.maximoDeCaracteres': 'Máximo de caracteres por mensagem',
  'wai.cfg.tetoPorChamada': 'Teto de custo por chamada',
  'wai.cfg.limiteDiario': 'Limite diário de chamadas',
  'wai.cfg.limiteMensal': 'Limite mensal de chamadas',
  'wai.cfg.limiteMensalPorTenant': 'Limite mensal por organização',
  'wai.cfg.limiteDiarioPorUsuario': 'Limite diário por pessoa',
  'wai.cfg.custoDiario': 'Orçamento diário',
  'wai.cfg.custoMensal': 'Orçamento mensal',
  'wai.cfg.custoPorMinuto': 'Custo por minuto de áudio',
  'wai.cfg.maximoDeBytes': 'Tamanho máximo do áudio',
  'wai.cfg.maximoDeSegundos': 'Duração máxima do áudio (s)',
  'wai.cfg.tempoDoDownload': 'Tempo-limite do download',
  'wai.cfg.tempoDaTranscricao': 'Tempo-limite da transcrição',
  'wai.cfg.limitePorHora': 'Áudios por pessoa por hora',
  'wai.cfg.minutosNoMes': 'Minutos de áudio por mês',
  'wai.cfg.foraDoLimite': 'Valor fora dos limites permitidos.',
  'wai.cfg.legendaUnidades':
    'Valores em dólar são estimativas em US$; tempos em segundos. Limite zero bloqueia o uso.',
  'wai.cfg.descartar': 'Descartar alterações',
  'wai.cfg.chaveVaziaMantem': 'Vazio mantém a chave atual',
  'wai.cfg.substituirChave': 'Substituir a chave de transcrição atual pela nova',
  'wai.cfg.chaveWriteOnly': 'A chave nunca é exibida: só é possível substituí-la.',
  'wai.ato.salvarIa': 'Salvar configuração da IA',
  'wai.ato.salvarAudio': 'Salvar configuração do áudio',
  'wai.ato.pausarIa': 'Pausar IA',
  'wai.ato.retomarIa': 'Retomar IA',
  'wai.ato.pausarAudio': 'Pausar áudio',
  'wai.ato.retomarAudio': 'Retomar áudio',
  'wai.ato.testarIa': 'Testar interpretação',
  'wai.ato.testarAudio': 'Testar configuração do áudio',
  'wai.ato.liberarIa': 'Liberar IA para a pessoa',
  'wai.ato.retirarIa': 'Retirar IA da pessoa',
  'wai.ato.liberarAudio': 'Liberar áudio para a pessoa',
  'wai.ato.retirarAudio': 'Retirar áudio da pessoa',
  'wai.impacto.salvarIa':
    'A nova configuração da IA (modelo, limites e orçamentos) passa a valer na próxima mensagem interpretada.',
  'wai.impacto.salvarAudio':
    'A nova configuração do áudio passa a valer no próximo áudio recebido.',
  'wai.impacto.substituiChave':
    'A chave de transcrição atual será substituída e não poderá ser recuperada.',
  'wai.impacto.pausarIa':
    'A interpretação por IA para; o WhatsApp continua funcionando com a interpretação determinística.',
  'wai.impacto.retomarIa': 'A interpretação por IA volta a ser usada para as pessoas liberadas.',
  'wai.impacto.pausarAudio':
    'Áudios recebidos deixam de ser transcritos; a pessoa recebe um aviso para escrever a mensagem. O WhatsApp continua funcionando.',
  'wai.impacto.retomarAudio':
    'Áudios recebidos voltam a ser transcritos para as pessoas liberadas.',
  'wai.impacto.testarIa':
    'Uma frase sintética é enviada ao modelo para conferir a interpretação. Nada é executado no CRM e o custo da chamada entra no consumo.',
  'wai.impacto.testarAudio':
    'Confere endpoint, credencial e modelo da transcrição sem enviar o áudio de ninguém.',
  'wai.impacto.liberarIa': 'As mensagens desta pessoa passam a ser interpretadas pela IA:',
  'wai.impacto.retirarIa': 'As mensagens desta pessoa voltam à interpretação determinística:',
  'wai.impacto.liberarAudio': 'Esta pessoa passa a poder enviar mensagens de áudio:',
  'wai.impacto.retirarAudio': 'Esta pessoa deixa de ter áudios transcritos:',
  'wai.conflito': 'A configuração foi alterada por outra pessoa. Recarregue e refaça a alteração.',
  'wai.recarregar': 'Recarregar',
  'wai.soLeitura':
    'Somente leitura: alterações de IA e áudio são feitas por um administrador da plataforma.',
  // CERES-WA-007 — notificações da pessoa e comunicação proativa (plataforma).
  'notif.titulo': 'Notificações pelo WhatsApp',
  'notif.explicacao':
    'Resumos, lembretes e alertas da SUA carteira no seu número vinculado, só com o seu consentimento. Você pode pausar ou parar a qualquer momento — aqui ou respondendo PARAR no WhatsApp.',
  'notif.sit.ativas': 'Ativas',
  'notif.sit.pausadas': 'Pausadas',
  'notif.sit.desligadas': 'Desligadas',
  'notif.semVinculo': 'Conecte o seu WhatsApp acima para poder receber notificações.',
  'notif.outraOrganizacao':
    'O seu WhatsApp está vinculado a outra organização. As notificações seguem a organização do número.',
  'notif.naoLiberada':
    'A comunicação automática ainda não foi liberada para você pela administração do Ceres. Você pode registrar suas preferências; nada será enviado até a liberação.',
  'notif.escolhaCategorias': 'O que você quer receber',
  'notif.aceito': 'Li e aceito os termos de notificações do Ceres',
  'notif.ativar': 'Ativar notificações',
  'notif.ativadas': 'Notificações ativadas.',
  'notif.numero': 'Número',
  'notif.consentidoEm': 'Consentimento em',
  'notif.ultimoEnvio': 'Último envio',
  'notif.proximoEnvio': 'Próximo envio calculado',
  'notif.categoriasAceitas': 'Categorias aceitas',
  'notif.fuso': 'Fuso horário',
  'notif.janelaAte': 'Janela de atendimento aberta até',
  'notif.horario': 'Horário do resumo',
  'notif.carencia': 'dias de carência',
  'notif.janelaDeFechamento': 'dias à frente',
  'notif.diaDoSemanal': 'Dia do resumo semanal',
  'notif.dias': 'Dias',
  'notif.silencioDe': 'Silêncio a partir de',
  'notif.silencioAte': 'Silêncio até',
  'notif.silencioFimDeSemana': 'Silêncio nos fins de semana',
  'notif.faltaConsentir': 'Para ligar, aceite também',
  'notif.aceitarESalvar': 'Aceitar e salvar',
  'notif.salvar': 'Salvar preferências',
  'notif.salvas': 'Preferências salvas.',
  'notif.retomar': 'Retomar',
  'notif.retomadas': 'Notificações retomadas.',
  'notif.pausarAte': 'Pausar até a data',
  'notif.pausarSemData': 'Pausar',
  'notif.pausadas': 'Notificações pausadas.',
  'notif.testar': 'Testar para mim',
  'notif.resultadoDoTeste': 'Resultado do teste',
  'notif.parar': 'Parar notificações',
  'notif.pararConfirmacao':
    'Você deixa de receber qualquer notificação automática do Ceres, e o que estava programado é cancelado. O registro do seu consentimento fica guardado como evidência.',
  'notif.paradas': 'Notificações paradas.',
  'notif.historico': 'Últimos envios',
  'notif.semHistorico': 'Nenhum envio ainda.',
  'notif.regra.resumoDiario': 'Resumo diário',
  'notif.regra.lembrete': 'Lembrete de atividade',
  'notif.regra.atrasadas': 'Atividades atrasadas',
  'notif.regra.semAtividade': 'Oportunidades sem próxima atividade',
  'notif.regra.fechamentos': 'Fechamentos previstos',
  'notif.regra.semanal': 'Resumo semanal',
  'notif.regra.operacional': 'Alertas técnicos da plataforma',
  'notif.dia.seg': 'Seg',
  'notif.dia.ter': 'Ter',
  'notif.dia.qua': 'Qua',
  'notif.dia.qui': 'Qui',
  'notif.dia.sex': 'Sex',
  'notif.dia.sab': 'Sáb',
  'notif.dia.dom': 'Dom',
  'notif.lembrete.desativado': 'Desativado',
  'notif.lembrete.m15': '15 minutos antes',
  'notif.lembrete.m30': '30 minutos antes',
  'notif.lembrete.h1': '1 hora antes',
  'notif.lembrete.inicioDoDia': 'No início do dia',
  'notif.atraso.desativado': 'Desativado',
  'notif.atraso.diario': 'Todos os dias',
  'notif.atraso.diasUteis': 'Dias úteis',
  'notif.hist.tipo': 'Tipo',
  'notif.hist.quando': 'Quando',
  'notif.hist.situacao': 'Situação',
  'notif.hist.entrega': 'Entrega',
  'notif.hist.teste': 'teste',
  'notif.hist.enviada': 'Enviada à fila',
  'notif.hist.bloqueada': 'Não enviada (bloqueio)',
  'notif.hist.cancelada': 'Cancelada',
  'notif.hist.descartada': 'Descartada',
  'notif.hist.expirada': 'Perdeu a utilidade',
  'notif.hist.falha': 'Falha',
  'notif.hist.outra': 'Em andamento',
  'notif.entrega.naFila': 'Na fila',
  'notif.entrega.aceita': 'Aceita pela Meta',
  'notif.entrega.enviada': 'Enviada',
  'notif.entrega.entregue': 'Entregue',
  'notif.entrega.lida': 'Lida',
  'notif.entrega.falhou': 'Falhou',
  'notif.entrega.cancelada': 'Cancelada',
  'wpro.titulo': 'Comunicação proativa',
  'wpro.sit.ligada': 'Ligada',
  'wpro.sit.desligada': 'Desligada',
  'wpro.sit.canalFechado': 'Canal fechado',
  'wpro.circuitoAbertoAte': 'Circuito aberto até',
  'wpro.orc.atingido': 'Orçamento atingido',
  'wpro.orc.alertaAtingido': 'Orçamento perto do limite',
  'wpro.scheduler': 'Scheduler',
  'wpro.scheduler.ativo': 'Rodando',
  'wpro.scheduler.parado': 'Sem passada recente',
  'wpro.templatesSincronizados': 'Templates sincronizados em',
  'wpro.dominio': 'Domínio dos links',
  'wpro.termos': 'Versão dos termos',
  'wpro.ultimoAceite': 'Último aceite da Meta',
  'wpro.ultimaEntrega': 'Última entrega',
  'wpro.ultimaLeitura': 'Última leitura',
  'wpro.circuito': 'Circuito (falhas · minutos)',
  'wpro.explicacao':
    'Notificações automáticas aos PRÓPRIOS usuários vinculados, sempre por template aprovado pela Meta. Para uma mensagem sair, tudo precisa estar ligado: plataforma, tenant, pessoa, consentimento da pessoa, categoria, regra com template aprovado, horário permitido, limite e orçamento. Liberar não é consentir.',
  'wpro.templates': 'Templates da Meta',
  'wpro.semTemplates': 'Nenhum template sincronizado. Use "Sincronizar templates".',
  'wpro.tpl.aprovados': 'Aprovados',
  'wpro.tpl.pendentes': 'Pendentes',
  'wpro.tpl.rejeitados': 'Rejeitados',
  'wpro.tpl.pausados': 'Pausados',
  'wpro.tpl.desabilitados': 'Desabilitados',
  'wpro.tpl.outros': 'Outros',
  'wpro.tpl.removidos': 'Removidos da Meta',
  'wpro.tpl.nome': 'Nome',
  'wpro.tpl.idioma': 'Idioma',
  'wpro.tpl.categoria': 'Categoria',
  'wpro.tpl.status': 'Status na Meta',
  'wpro.tpl.qualidade': 'Qualidade',
  'wpro.tpl.sincronizado': 'Sincronizado em',
  'wpro.tpl.usadoPor': 'Usado por',
  'wpro.tpl.reclassificado': 'reclassificado; antes',
  'wpro.tpl.removido': 'Removido',
  'wpro.regras': 'Regras',
  'wpro.regra': 'Regra',
  'wpro.template': 'Template',
  'wpro.avaliacao': 'Avaliação',
  'wpro.situacao': 'Situação',
  'wpro.regraBloqueada': 'Bloqueada (template)',
  'wpro.habilitada': 'Habilitada',
  'wpro.desabilitada': 'Desabilitada',
  'wpro.associar': 'Associar',
  'wpro.habilitar': 'Habilitar',
  'wpro.desabilitar': 'Desabilitar',
  'wpro.modelos': 'Modelos do Ceres',
  'wpro.modelos.explicacao':
    'O conteúdo que o Ceres propõe para cada regra. "Submeter à Meta" cria o template na WABA (a Meta aprova, rejeita ou reclassifica). Sem submissão pela API, cadastre o mesmo nome, idioma, categoria, corpo, rodapé e botões no WhatsApp Manager e depois sincronize. Mudou o conteúdo: é uma nova versão (_v2), nunca a edição de um template em uso.',
  'wpro.modelos.foraDaMeta': 'Ainda não está na Meta',
  'wpro.envios': 'Envios e bloqueios',
  'wpro.env.programadas': 'Programadas',
  'wpro.env.enfileiradas': 'Enviadas à fila',
  'wpro.env.aceitas': 'Aceitas pela Meta',
  'wpro.env.entregues': 'Entregues',
  'wpro.env.lidas': 'Lidas',
  'wpro.env.falhas': 'Falhas',
  'wpro.env.canceladas': 'Canceladas',
  'wpro.env.descartadas': 'Descartadas ou sem utilidade',
  'wpro.bloq.consentimento': 'Bloqueadas: consentimento',
  'wpro.bloq.silencio': 'Bloqueadas: período silencioso',
  'wpro.bloq.limite': 'Bloqueadas: limite',
  'wpro.bloq.autorizacao': 'Bloqueadas: autorização',
  'wpro.bloq.template': 'Bloqueadas: template',
  'wpro.bloq.orcamento': 'Bloqueadas: orçamento',
  'wpro.bloq.liberacao': 'Bloqueadas: liberação',
  'wpro.bloq.preferencia': 'Bloqueadas: preferência ou pausa',
  'wpro.bloq.circuito': 'Bloqueadas: circuito',
  'wpro.ultimasFalhas': 'Últimas falhas (sanitizadas)',
  'wpro.custos': 'Custos estimados',
  'wpro.custo.mensagensNoMes': 'Mensagens no mês',
  'wpro.custo.estimadoNoMes': 'Custo estimado no mês',
  'wpro.custo.semPreco': 'Mensagens sem preço cadastrado',
  'wpro.custo.usoDoOrcamento': 'Uso do orçamento',
  'wpro.custo.observacao':
    'Custo ESTIMADO pela tabela de preços abaixo, na moeda dela. Mensagens sem preço cadastrado não entram na soma; a cobrança real é a da fatura da Meta.',
  'wpro.custo.porRegra': 'Por regra',
  'wpro.custo.porTenant': 'Por organização',
  'wpro.precos': 'Tabela de preços (por mensagem)',
  'wpro.semPrecos': 'Nenhum preço cadastrado: o custo aparece como "sem preço".',
  'wpro.mercado': 'Mercado (DDI ou *)',
  'wpro.valor': 'Preço',
  'wpro.moeda': 'Moeda',
  'wpro.salvarPreco': 'Salvar preço',
  'wpro.remover': 'Remover',
  'wpro.limites': 'Limites e orçamento',
  'wpro.lim.porHoraGlobal': 'Por hora (plataforma)',
  'wpro.lim.porDiaGlobal': 'Por dia (plataforma)',
  'wpro.lim.porDiaTenant': 'Por dia (organização)',
  'wpro.lim.porDiaPessoa': 'Por dia (pessoa)',
  'wpro.lim.porDiaPessoaRegra': 'Por dia (pessoa e regra)',
  'wpro.lim.lembretesPorDia': 'Lembretes por dia (pessoa)',
  'wpro.lim.intervalo': 'Intervalo mínimo (min)',
  'wpro.orc.mensagensGlobal': 'Mensagens no mês (plataforma)',
  'wpro.orc.mensagensTenant': 'Mensagens no mês (organização)',
  'wpro.orc.alerta': 'Alerta de consumo (%)',
  'wpro.orc.custoGlobal': 'Teto de custo no mês (plataforma)',
  'wpro.orc.custoTenant': 'Teto de custo no mês (organização)',
  'wpro.liberacoes': 'Liberação gradual',
  'wpro.liberacoes.explicacao':
    'Libere primeiro a organização e só a pessoa piloto. Liberar não é consentir: a pessoa ainda precisa aceitar as próprias notificações.',
  'wpro.organizacoes': 'Organizações',
  'wpro.pessoas': 'Pessoas vinculadas',
  'wpro.liberada': 'Liberada',
  'wpro.naoLiberada': 'Não liberada',
  'wpro.liberar': 'Liberar',
  'wpro.retirar': 'Retirar',
  'wpro.semConsentimento': 'Sem consentimento',
  'wpro.comConsentimento': 'Consentiu',
  'wpro.soLeitura': 'Somente leitura: alterações são feitas por um administrador da plataforma.',
  'wpro.ato.ligar': 'Ligar a comunicação proativa',
  'wpro.ato.desligar': 'Desligar a comunicação proativa',
  'wpro.ato.sincronizar': 'Sincronizar templates',
  'wpro.ato.fecharCircuito': 'Fechar o circuito',
  'wpro.ato.salvarLimites': 'Salvar limites',
  'wpro.ato.submeter': 'Submeter à Meta',
  'wpro.ato.regra': 'Alterar regra',
  'wpro.ato.tenant': 'Liberação da organização',
  'wpro.ato.pessoa': 'Liberação da pessoa',
  'wpro.ato.preco': 'Tabela de preços',
  'wpro.impacto.ligar':
    'A plataforma passa a processar notificações de quem estiver liberado E tiver consentido. Ninguém recebe nada sem consentimento.',
  'wpro.impacto.desligar':
    'Nenhuma notificação proativa sai mais; o que estava na fila de envio é cancelado.',
  'wpro.impacto.sincronizar':
    'Lê os templates da WABA na Meta e atualiza o espelho (nenhuma mensagem é enviada).',
  'wpro.impacto.fecharCircuito':
    'Os envios voltam a ser tentados agora, antes do fim automático do circuito.',
  'wpro.impacto.salvarLimites': 'Os novos limites valem a partir da próxima passada do scheduler.',
  'wpro.impacto.submeter':
    'Cria o template na WABA pela API da Meta. A Meta decide: aprova, rejeita ou reclassifica. Nada é enviado.',
  'wpro.impacto.regra':
    'A regra usa o template associado; só é habilitada com template aprovado e compatível.',
  'wpro.impacto.tenant': 'Muda a liberação desta organização. A pessoa ainda precisa consentir.',
  'wpro.impacto.pessoa': 'Muda a liberação desta pessoa. Ela ainda precisa consentir.',
  'wpro.impacto.preco': 'Atualiza a tabela usada para ESTIMAR o custo (não muda a fatura da Meta).',
} as const;

export type ChaveDeTexto = keyof typeof PT;

/**
 * O DICIONÁRIO CARREGADO ATÉ AGORA.
 *
 * O português está sempre aqui: ele é a reserva de toda chave, e adiá-lo
 * deixaria a primeira tela sem texto nenhum. O inglês entra quando (e se)
 * alguém usar a tela em inglês — ver `carregarIdioma`.
 */
export const DICIONARIO: Record<IdiomaDaTela, Record<string, string>> = {
  'pt-BR': PT,
  en: PT,
};

/**
 * Traz o dicionário do idioma pedido, se ele ainda não estiver na memória.
 *
 * Chamado no arranque (antes do primeiro render, em `main.tsx`) e na troca de
 * idioma. Enquanto o inglês não chega, `t()` devolve o PORTUGUÊS — que é a
 * reserva declarada desde a §20.3, e não uma chave crua na tela.
 */
export async function carregarIdioma(idioma: IdiomaDaTela): Promise<void> {
  if (idioma !== 'en' || carregado.en) return;
  const { EN } = await import('./i18n.en');
  DICIONARIO.en = EN;
  carregado.en = true;
}

const carregado = { en: false };

/**
 * O IDIOMA INICIAL VEM DO NAVEGADOR (§20.5).
 *
 * Sem sessão não há preferência gravada — e é exatamente nas páginas públicas
 * (apresentação, status, acessibilidade, contato, entrar) que a pessoa ainda
 * não entrou. Começar sempre em português deixaria quem chegou de fora lendo
 * uma língua que não escolheu, justamente na primeira tela.
 *
 * A preferência da CONTA vence assim que a sessão chega: o navegador é palpite,
 * a conta é decisão.
 */
function idiomaDoNavegador(): IdiomaDaTela {
  try {
    return (navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'pt-BR';
  } catch {
    // Ambiente sem `navigator` (renderização em teste, por exemplo).
    return 'pt-BR';
  }
}

let idiomaAtivo: IdiomaDaTela = idiomaDoNavegador();

export function definirIdioma(idioma: string): void {
  idiomaAtivo = idioma === 'en' ? 'en' : 'pt-BR';
  // Sem `await`: a troca de idioma não pode travar a tela. Até o dicionário
  // chegar, `t()` devolve o português — a reserva de sempre.
  void carregarIdioma(idiomaAtivo);
}

export function idiomaDaTela(): IdiomaDaTela {
  return idiomaAtivo;
}

export function t(chave: ChaveDeTexto): string {
  // A reserva é o PORTUGUÊS, e nunca a chave: `nav.empresas` na tela seria um
  // defeito que a pessoa lê antes de nós (§20.3).
  return DICIONARIO[idiomaAtivo][chave] ?? PT[chave] ?? '';
}
